// ===== BEGIN FILE: src/App.tsx =====
import React, { useCallback, useEffect, useMemo, useState, useRef, lazy, Suspense } from 'react';
// BEGIN REPLACE: MyChart import (no hook)
import MyChart from './components/MyChart';
// END REPLACE

// ===== Core app bits =====
import AuthPanel, { SignOutButton } from './components/AuthPanel';
import QuickAccessMenu from './components/QuickAccessMenu';
import UserForm from './components/UserForm';
import LifeAreasDashboard from './components/LifeAreasDashboard';
import HouseScoresCompactView from './components/HouseScoresCompactView';
import TransitDateSlider from './components/TransitDateSlider';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import CalculatorWidgets from './components/widgets/CalculatorWidgets';
import LifeEventTimelineAnalyzer from './components/widgets/LifeEventTimelineAnalyzer';
import VimshottariDashaTimeline from './components/VimshottariDashaTimeline';
import DashaFocusOverview from './components/DashaFocusOverview';
import VastuChart from './components/VastuChart';
import VastuLifeAreasTable from './components/VastuLifeAreasTable';
import VastuRoomPlanner from './components/VastuRoomPlanner';
import ChatPanel from './components/ChatPanel';
import MobileChatList from './components/MobileChatList';
import MobileChatInterface from './components/MobileChatInterface';
import FloatingChartModal from './components/FloatingChartModal';
import type { LifeAreaKey } from './utils/lifeAreas';
import { LIFE_AREAS } from './utils/lifeAreas';
import FutureEventAlerts from './components/FutureEventAlerts';
import { computeFutureEvents } from './utils/futureEventPredictor';
import { TrialBanner, QuotaWarning, ExpiredTrialOverlay, MySubscriptionModal } from './components/SubscriptionGate';
import SplashScreen from './components/SplashScreen';
import WelcomeWizard from './components/WelcomeWizard';
import LandingPage from './components/LandingPage';
import PendingApprovalBanner from './components/PendingApprovalBanner';
import WhatsAppOptInModal from './components/WhatsAppOptInModal';
import WhatsAppOptInBanner from './components/WhatsAppOptInBanner';
import { useSubscription } from './hooks/useSubscription';
import { useIsMobile } from './hooks/useIsMobile';
import { getFeatureConfig } from './config/mobileFeatures';
import { buildChatContext } from './utils/chatContext';
import { computeMahadashaTimelineAround, getCurrentDashaLevels } from './utils/vimshottari';
import { computeDashaImpactByHouse, computeTransitImpactByHouse } from './utils/houseTemporalScores';
import FeedsList from './components/FeedsList';
import { generateFeeds, type FunctionalClassification, type DignityStatus } from './astro/feeds/generateFeedsV2';
import { preloadClassicalYogas } from './astro/feeds/classicalYogasIntegration';
import type { DashaContext, TransitContext, HouseNum } from './astro/feeds/types';
import { HOUSE_TO_LIFE } from './astro/feeds/types';
import { generateChartImages } from './utils/generateChartSvg';
import { useSession } from './session/useSession';
import { useKarmicFriction } from './hooks/useKarmicFriction';
import { fetchMyUserProfile } from './utils/userProfile';
import type { TabValue } from './components/AppShell';
import AppShell from './components/AppShell';
import BuyChatCreditsModal from './components/BuyChatCreditsModal';
import { initializeAnalytics, startTabActivity, endTabActivity } from './utils/analytics';
import { 
  extractOpportunities, 
  extractCautions, 
  extractActiveYogas,
  generatePredictions,
  type YogaData,
  type EnhancedPredictionContext
} from './utils/notificationUtils';
import type {
  OpportunityNotification,
  CautionNotification,
  YogaNotification,
  PredictionNotification
} from './components/NotificationCenter';

import { DateTime } from 'luxon';
import Select from 'react-select';
import NorthIndianChartVedicUMD from './components/NorthIndianChartVedicUMD';
import AdvancedNorthIndianChart from './components/AdvancedNorthIndianChart';
import HousePredictionsTable from './components/HousePredictionsTable';
import PlanetDetailsSidePanel from './components/PlanetDetailsSidePanel';
import HouseDetailsSidePanel from './components/HouseDetailsSidePanel';
import AudioGuide from './components/AudioGuide';
import RemediesModal from './components/RemediesModal';
import NotificationPreferences from './components/NotificationPreferences';
import { registerFCMToken } from './utils/pushNotifications';
import TermsOverlay from './components/TermsOverlay';
import TermsPage from './components/TermsPage';
// === DELETE-START: unused import ===
// import VimshottariDasha from './components/VimshottariDasha';
// === DELETE-END: unused import ===
import { CityOption, resolveTimezoneForCoords } from './utils/cityUtils';
import { supabase } from './utils/supabaseClient';
import { calculateSadeSati, checkKaalSarpYoga } from './utils/yogas';
import { calculateSaturnSignTransits } from './utils/saturnTransits';
import { computeHouseInfluenceRows } from './utils/d1HouseInfluence';
import { computeBhavaBala } from './utils/bhavaBala';
import { fetchHouseInterpretations, type HouseInterpretation } from './utils/fetchHouseInterpretations';
import type { AspectEdge } from './utils/houseScores';
// ---- Joy UI only ----
import { Box, Button, Card, Chip, Divider, Grid, IconButton, Select as JoySelect, Option, Stack, Switch, Tooltip, Typography, Modal, ModalDialog, ModalClose, Sheet, CircularProgress } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import { CssVarsProvider, extendTheme as joyExtendTheme } from '@mui/joy/styles';
import type { CombustionMap, PlanetName } from './utils/astroEngine';
import './styles/gradientBorder.css';
// VALUES + HELPERS from astroEngine
import {
  assignPlanetsToHouses,
  classifyFunctionalForLagna,
  computeImplementedVargas,
  computeIshtaKashtaPhala,
  computeShadbalaPercentages,
  estimateShadbalaRatios,
  getAscendantSignAndNakshatra,
  getAspects,
  getCombustionStatusSidereal,
  getEnhancedExaltDebil,
  getEqualHouseCuspsSidereal,
  getGrahaLongitudesAndMotionSidereal,
  getMoonNakshatraAndRashi,
  getNakshatraPadaFromLongitude,
  getPlanetDignity,
  getRahuKetuSidereal,
  getWholeSignCuspsSidereal,
  NAKSHATRA_NAMES,
  PLANET_ORDER,
  RASHI_NAMES,
  SIGN_LORDS,
  vargaToPlacements,
  type CoreGrahaName
} from './utils/astroEngine';
import type { ChartData, HouseNumber } from './types/chartData';
import { CURRENT_CHART_DATA_VERSION } from './types/chartData';
import { saveChartDataToProfile as saveChartDataHelper } from './utils/chartDataCache';
import { getHousesRuledByPlanet, computeLordDusthanaPenalties, type LordDusthanaPenalty } from './utils/lordshipUtils';
import { calculateFiveFoldFriendships } from './utils/fiveFoldFriendship';
import { applyBhavaSandhiToStrength, getBhavaSandhiMultiplier } from './utils/bhavaSandhi';

// === Report V2 components ===
import { ReportGenerator } from './components/report-v2/ReportGenerator';
import { buildReportData } from './components/report-v2/reportDataBuilder';
import type { ReportData } from './components/report-v2/types';

// TYPES from astroEngine

import { AyanamsaKey, getAyanamsaProvider } from './utils/ayanamsa';

// === Tour integration - Custom Joy UI implementation ===
import { useTour } from './hooks/useTour';
import { TOUR_CONFIGS } from './tours';
import { WIDGET_TOURS } from './tours/widgetTours';
import TourGuide, { type TourStep } from './components/TourGuide';

// === Video Tutorial integration ===
import { useVideoTutorial } from './hooks/useVideoTutorial';
import VideoTutorial from './components/VideoTutorial';
import { tabToVideoMap } from './config/videoTutorials';

// === Quick Navigation FAB ===
import QuickNavigationFAB from './components/QuickNavigationFAB';
import { CALCULATOR_SECTIONS, CALCULATOR_SECTIONS_MOBILE, DASHA_SECTIONS, FEEDS_SECTIONS, FEEDS_SECTIONS_MOBILE, MYCHART_SECTIONS } from './config/navigationSections';

// === Lazy load heavy admin components (loaded on-demand) ===
const AdminConsole = lazy(() => import('./components/AdminConsole'));
const FamilyManager = lazy(() => import('./components/FamilyManager'));
const RuleBuilder = lazy(() => import('./components/RuleBuilder'));
const ChatDesktopLayout = lazy(() => import('./components/chat/ChatDesktopLayout'));
const KarmicPatternDetection = lazy(() => import('./components/widgets/KarmicPatternDetection'));

// Typed planet keys to satisfy PlanetsInHouses indexing
const PLANET_KEYS = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as const;
//type PKey = typeof PLANET_KEYS[number];

// put these near your other types / helpers
type HouseIndex = 1|2|3|4|5|6|7|8|9|10|11|12;
type PKey = 'Sun'|'Moon'|'Mars'|'Mercury'|'Jupiter'|'Venus'|'Saturn'|'Rahu'|'Ketu';

// canonical 1..12 normalizer returning the union type
const toHouseIndex = (n: number): HouseIndex =>
  ((((n - 1) % 12) + 12) % 12 + 1) as HouseIndex;

// ---- Joy theme (one-time) ----
const joyTheme = joyExtendTheme({
  colorSchemes: {
    light: {
      palette: {
        text: {
          primary: 'rgba(0, 0, 0, 0.87)',
          secondary: 'rgba(0, 0, 0, 0.6)',
          tertiary: 'rgba(0, 0, 0, 0.5)',
        },
      },
    },
  },
});

/* ----------------- Helpers ----------------- */
// ===== DELETE-START: ChartScaler (not used here) =====
// (removed)
// ===== DELETE-END: ChartScaler (not used here) =====

const DEBIL_SIGN: Record<'Sun'|'Moon'|'Mercury'|'Venus'|'Mars'|'Jupiter'|'Saturn', number> = {
  Sun: 7, Moon: 8, Mars: 4, Mercury: 12, Jupiter: 10, Venus: 6, Saturn: 1
};
const EXALT_SIGN: Record<'Sun'|'Moon'|'Mercury'|'Venus'|'Mars'|'Jupiter'|'Saturn', number> = {
  Sun: 1, Moon: 2, Mars: 10, Mercury: 6, Jupiter: 4, Venus: 12, Saturn: 7
};

function signNumFromLon(lonDeg: number) {
  const idx0 = Math.floor(((lonDeg % 360) + 360) % 360 / 30);
  return idx0 + 1;
}
function formatLonWithSign(lonDeg: number) {
  const norm360x = (x: number) => (x % 360 + 360) % 360;
  const lon = norm360x(lonDeg);
  const signIndex0 = Math.floor(lon / 30);
  const signName = RASHI_NAMES[signIndex0];
  const inSign = lon - signIndex0 * 30;
  let d = Math.floor(inSign);
  let mFloat = (inSign - d) * 60;
  let m = Math.floor(mFloat);
  let s = Math.round((mFloat - m) * 60);
  if (s === 60) { s = 0; m += 1; }
  if (m === 60) { m = 0; d += 1; }
  if (d === 30) { d = 0; }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${signName} ${d}deg${pad(m)}'${pad(s)}"`;
}
// ===== BEGIN ADD: SV dignity + baselineScore for blend (local copy) =====
type PName = 'Sun'|'Moon'|'Mars'|'Mercury'|'Jupiter'|'Venus'|'Saturn'|'Rahu'|'Ketu';

const OWN_SIGN_SV: Record<Exclude<PName,'Rahu'|'Ketu'>, number[]> = {
  Sun:[5], Moon:[4], Mars:[1,8], Mercury:[3,6], Jupiter:[9,12], Venus:[2,7], Saturn:[10,11],
};
const MOOLATRIKONA_SV: Partial<Record<Exclude<PName,'Rahu'|'Ketu'>, number>> = {
  Sun:5, Moon:4, Mars:1, Mercury:6, Jupiter:9, Venus:7, Saturn:11,
};
const EXALT_SIGN_SV: Record<PName, number> = {
  Sun:1, Moon:2, Mars:10, Mercury:6, Jupiter:4, Venus:12, Saturn:7,
  Rahu:2, Ketu:8,
};
const DEBIL_SIGN_SV: Record<PName, number> = {
  Sun:7, Moon:8, Mars:4,  Mercury:12, Jupiter:10, Venus:6,  Saturn:1,
  Rahu:8, Ketu:2,
};
const SIGN_LORD_SV: (PName|null)[] = [
  null,'Mars','Venus','Mercury','Moon','Sun','Mercury','Venus','Mars','Jupiter','Saturn','Saturn','Jupiter'
] as const;
const PERM_FRIENDS_SV: Record<Exclude<PName,'Rahu'|'Ketu'>, PName[]> = {
  Sun:['Moon','Mars','Jupiter'], Moon:['Sun','Mercury'], Mars:['Sun','Moon','Jupiter'],
  Mercury:['Sun','Venus'], Jupiter:['Sun','Moon','Mars'], Venus:['Mercury','Saturn'],
  Saturn:['Mercury','Venus'],
};
const PERM_ENEMIES_SV: Record<Exclude<PName,'Rahu'|'Ketu'>, PName[]> = {
  Sun:['Venus','Saturn'], Moon:[], Mars:['Mercury'], Mercury:['Moon'],
  Jupiter:['Venus'], Venus:['Sun','Moon'], Saturn:['Sun','Moon','Mars'],
};
const TEMP_FRIENDLY_OFFSETS_SV = new Set([2,3,4,10,11,12]);
const add12 = (a:number,b:number)=> ((a-1 + (b-1)) % 12)+1;
const offset12 = (from:number,to:number)=> ((to - from + 12) % 12) + 1;
const getLordSV = (sign:number)=> (SIGN_LORD_SV[sign] ?? 'Sun') as PName;
const permRel = (p:PName,l:PName):'friend'|'enemy'|'neutral' => {
  if (p==='Rahu'||p==='Ketu') return 'neutral';
  const x = p as Exclude<PName,'Rahu'|'Ketu'>;
  if (PERM_FRIENDS_SV[x]?.includes(l)) return 'friend';
  if (PERM_ENEMIES_SV[x]?.includes(l)) return 'enemy';
  return 'neutral';
};
const tempRel = (pHouse?:number,lHouse?:number):'friend'|'enemy'|'neutral' => {
  if (!pHouse || !lHouse) return 'neutral';
  const o1 = offset12(pHouse,lHouse);
  const o2 = offset12(lHouse,pHouse);
  const friendly = TEMP_FRIENDLY_OFFSETS_SV.has(o1) && TEMP_FRIENDLY_OFFSETS_SV.has(o2);
  return friendly ? 'friend' : 'enemy';
};
const compBonus = (perm:'friend'|'enemy'|'neutral', temp:'friend'|'enemy'|'neutral'):number => {
  const v = (perm==='friend'?1:perm==='enemy'?-1:0) + (temp==='friend'?1:temp==='enemy'?-1:0);
  if (v>= 2) return +0.75;
  if (v===1) return +0.25;
  if (v===0) return 0;
  if (v===-1) return -0.25;
  return -0.75;
};
/** baseline score in a varga cell: [-2..+2] */
function baselineScoreSV(
  planet: PName, 
  sign: number, 
  planetHouse?: number, 
  signLordHouse?: number,
  fiveFoldFriendships?: ReturnType<typeof calculateFiveFoldFriendships>
): number {
  if (EXALT_SIGN_SV[planet] === sign) return 2.0;
  if (DEBIL_SIGN_SV[planet] === sign) return -2.0;
  if (planet === 'Rahu' || planet === 'Ketu') return 0;
  if (MOOLATRIKONA_SV[planet as Exclude<PName, 'Rahu' | 'Ketu'>] === sign) return 1.5;
  if (OWN_SIGN_SV[planet as Exclude<PName, 'Rahu' | 'Ketu'>]?.includes(sign)) return 1.0;
  
  const lord = getLordSV(sign);
  
  // Use five-fold friendships if available (only for classical planets, not nodes)
  if (fiveFoldFriendships) {
    const friendship = fiveFoldFriendships.find(f => f.from === planet && f.to === lord);
    if (friendship) {
      // Map friendship score to bonus: great_friend(2) = +0.75, friend(1) = +0.25, neutral(0) = 0, enemy(-1) = -0.25, great_enemy(-2) = -0.75
      const score = friendship.score; // -2 to +2
      if (score >= 2) return +0.75;
      if (score === 1) return +0.25;
      if (score === 0) return 0;
      if (score === -1) return -0.25;
      return -0.75;
    }
  }
  
  // Fallback to classical permanent + temporary friendship calculation
  const perm = permRel(planet, lord);
  const temp = tempRel(planetHouse, signLordHouse);
  return compBonus(perm, temp);
}
// ===== END ADD: SV dignity + baselineScore for blend (local copy) =====

/* ----------------- Types ----------------- */
type MoonRes = ReturnType<typeof getMoonNakshatraAndRashi>;
type AscRes  = ReturnType<typeof getAscendantSignAndNakshatra>;
type Grahas  = ReturnType<typeof getGrahaLongitudesAndMotionSidereal>;
type Nodes   = ReturnType<typeof getRahuKetuSidereal>;

type DbUserRow = {
  id: string;
  full_name: string;
  gender: string | null;
  dob_day: number | null;
  dob_month: number | null;
  dob_year: number | null;
  tob_hour: number | null;
  tob_minute: number | null;
  tob_second: number | null;
  place_label: string | null;
  place_lat: number | null;
  place_lng: number | null;
  place_timezone: string | null;
};

type ProfileRow = {
  id: string;
  owner_id: string;
  full_name: string;
  gender: string | null;
  relationship?: string | null;
  family_of_profile_id?: string | null;
  dob_day: number | null;
  dob_month: number | null;
  dob_year: number | null;
  tob_hour: number | null;
  tob_minute: number | null;
  tob_second: number | null;
  place_label: string | null;
  place_lat: number | null;
  place_lng: number | null;
  place_timezone: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type AnySavedRow = DbUserRow | ProfileRow;

type BirthFormPayload = {
  full_name: string | null;
  gender: string | null;
  relationship?: string | null; // Optional relationship field for family members
  dob_year: number | null;
  dob_month: number | null;
  dob_day: number | null;
  tob_hour: number | null;
  tob_minute: number | null;
  tob_second: number | null;
  place_label: string | null;
  place_lat: number | null;
  place_lng: number | null;
  place_timezone: string | null;
};

type AstroData = {
  moon: MoonRes;
  asc: AscRes;
  cusps: number[];
  grahas: Grahas;
  nodes: Nodes;
  placements: ReturnType<typeof assignPlanetsToHouses>;
  aspects: ReturnType<typeof getAspects>;
};

/* ----------------- Component ----------------- */

const App = () => {
  // Mobile detection (needed early for chart sizing and style)
  const { isMobile, isTablet } = useIsMobile();

  // Hidden calculator inputs (kept for math)
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [localDateTime, setLocalDateTime] = useState<string>('');
  const [calcNonce, setCalcNonce] = useState(0);
  const [houseNames, setHouseNames] = useState<Record<number, string>>({});
  const [selectedGender, setSelectedGender] = useState<'male' | 'female' | 'other'>('other'); // Store gender for feed rules
  const [showLifeAreasAnalysis, setShowLifeAreasAnalysis] = useState(false); // Toggle for inline life areas analysis
  const [lifeAreasAnalysisResponse, setLifeAreasAnalysisResponse] = useState<string>(''); // Store GPT response
  // Viewport-based size helpers
  const BASE_SIZE = Math.max(
    300,
    Math.min(680, Math.floor((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.7))
  );
  const WW = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const LEFT_PCT  = 0.34;
  const RIGHT_PCT = 1 - LEFT_PCT;
  const LEFT_MAX  = Math.floor(WW * LEFT_PCT  - 40);
  const RIGHT_MAX = Math.floor(WW * RIGHT_PCT - 40);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const CHART_SIZE_LEFT  = Math.max(260, Math.min(BASE_SIZE, LEFT_MAX));
  const CHART_SIZE_RIGHT = Math.max(300, Math.min(BASE_SIZE, RIGHT_MAX));

  // ===== BEGIN ADD: fixed-cm sizing for D1 Joy card (mobile-responsive) =====
  const CM2PX = 37.8;
  // Smaller dimensions for mobile to fit screen width (5.25cm x 3.5cm for mobile)
  const D1_W_CM = isMobile ? 5.25 : 15;
  const D1_H_CM = isMobile ? 3.5 : 10;
  const D1_PAD_PX = isMobile ? 4 : 12;
  const D1_INNER_W_PX = Math.round(D1_W_CM * CM2PX) - D1_PAD_PX * 2;
  const D1_INNER_H_PX = Math.round(D1_H_CM * CM2PX) - D1_PAD_PX * 2;
  // ===== END ADD =====

  // Calculation state
  const [astroData, setAstroData] = useState<AstroData | null>(null);
  const [utcDateTime, setUtcDateTime] = useState<string | null>(null);
  const [combust, setCombust] = useState<CombustionMap | null>(null);
  const [debil, setDebil] = useState<Partial<Record<PlanetName, boolean>> | null>(null);
  const [exalt, setExalt] = useState<Partial<Record<PlanetName, boolean>> | null>(null);
  const [conjunctDebil, setConjunctDebil] = useState<Partial<Record<PlanetName, boolean>> | null>(null);
  const [conjunctExalt, setConjunctExalt] = useState<Partial<Record<PlanetName, boolean>> | null>(null);
  const [shadbalaPct, setShadbalaPct] = useState<ReturnType<typeof computeShadbalaPercentages> | null>(null);
  const [shadbalaRatio, setShadbalaRatio] = useState<ReturnType<typeof estimateShadbalaRatios> | null>(null);
  const [ikPhala, setIkPhala] = useState<ReturnType<typeof computeIshtaKashtaPhala> | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [calcError, setCalcError] = useState<string | null>(null);
  
  // Houses metadata for activities enrichment
  const [housesData, setHousesData] = useState<Array<{ id: number; activities: string | null }> | null>(null);
  
  // Natal yogas for YogasWidget (computed from feeds)
  const [natalYogaFeeds, setNatalYogaFeeds] = useState<Array<{
    title: string;
    description: string;
    type?: 'benefic' | 'malefic' | 'neutral';
  }> | null>(null);
  
  // OPTIMIZED: Fetch all initial data in parallel on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Preload classical yogas from database (115+ yogas)
        preloadClassicalYogas().catch(err => 
          console.warn('[App] Failed to preload classical yogas:', err)
        );
        
        const [housesResult, houseNamesResult] = await Promise.all([
          supabase
            .from('houses')
            .select('id, activities')
            .order('id', { ascending: true }),
          supabase
            .from('houses')
            .select('id, name')
        ]);
        
        // Process houses data
        if (housesResult.error) {
          console.error('[App] Failed to fetch houses:', housesResult.error);
        } else {
          setHousesData(housesResult.data);
        }

        // Process house names
        if (houseNamesResult.error) {
          console.error('Failed to load house names:', houseNamesResult.error.message);
        } else if (houseNamesResult.data) {
          const map: Record<number, string> = {};
          houseNamesResult.data.forEach((row: any) => {
            if (typeof row?.id === 'number' && row.id >= 1 && row.id <= 12) {
              const raw = typeof row.name === 'string' ? row.name.trim() : '';
              map[row.id] = raw || `House ${row.id}`;
            }
          });
          setHouseNames(map);
        }
      } catch (err) {
        console.error('[App] Error fetching initial data:', err);
      }
    };
    fetchInitialData();
  }, []);

  // Sub-tab inside Calculator
  const [calcTab, setCalcTab] = useState<'d1'|'divisional'|'transit'|'vastu'|'chalit'|'interactive'>('d1');

  // Chart style state (default on desktop, classic on mobile for better fit)
  // Note: isMobile is declared at the top of the component
  const [chartStyle, setChartStyle] = useState<'default'|'classic'|'modern'|'royal'>(() => 
    isMobile ? 'classic' : 'default'
  );

  // SWOT Analysis modal state
  const [showSwotModal, setShowSwotModal] = useState(false);
  const [swotPrompt, setSwotPrompt] = useState('');
  const [swotOutput, setSwotOutput] = useState('');
  const [swotLoading, setSwotLoading] = useState(false);
  const [swotError, setSwotError] = useState('');

  // Current Period Analysis modal state
  const [showCurrentPeriodModal, setShowCurrentPeriodModal] = useState(false);
  const [currentPeriodOutput, setCurrentPeriodOutput] = useState('');
  const [currentPeriodLoading, setCurrentPeriodLoading] = useState(false);
  const [currentPeriodError, setCurrentPeriodError] = useState('');

  // Life Area Overview modal state
  const [showLifeAreaOverviewModal, setShowLifeAreaOverviewModal] = useState(false);
  const [lifeAreaOverviewOutput, setLifeAreaOverviewOutput] = useState('');
  const [lifeAreaOverviewLoading, setLifeAreaOverviewLoading] = useState(false);
  const [lifeAreaOverviewError, setLifeAreaOverviewError] = useState('');
  const [currentLifeAreaKey, setCurrentLifeAreaKey] = useState<LifeAreaKey | null>(null);

  // Report V2 modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportV2Data, setReportV2Data] = useState<ReportData | null>(null);

  // Report AI analysis state (admin-only, no credit deduction)
  const [reportSwotContent, setReportSwotContent] = useState('');
  const [reportSwotLoading, setReportSwotLoading] = useState(false);
  const [reportDetailedContent, setReportDetailedContent] = useState('');
  const [reportDetailedLoading, setReportDetailedLoading] = useState(false);
  const [reportCurrentPeriodContent, setReportCurrentPeriodContent] = useState('');
  const [reportCurrentPeriodLoading, setReportCurrentPeriodLoading] = useState(false);
  const [reportYearForecastContent, setReportYearForecastContent] = useState('');
  const [reportYearForecastLoading, setReportYearForecastLoading] = useState(false);

  // Handler for Quick Access: Current Period Analysis
  const handleQuickCurrentPeriodAnalysis = useCallback(() => {
    if (!astroData) {
      alert('Please calculate your chart first (Save & Calculate on the left)');
      return;
    }
    // Open modal and trigger streaming analysis
    setCurrentPeriodOutput('');
    setCurrentPeriodError('');
    setCurrentPeriodLoading(true);
    setShowCurrentPeriodModal(true);
    callCurrentPeriodAnalysis();
  }, [astroData]);

  // Current Period Analysis with streaming — comprehensive version with week-by-week + 3-month summary
  const callCurrentPeriodAnalysis = async () => {
    try {
      if (!canAskChatQuestion('general')) {
        setCurrentPeriodError('Chat limit reached! Upgrade your plan to continue using AI analysis.');
        setCurrentPeriodLoading(false);
        return;
      }

      // ── COMMON HELPERS (shared with Year Ahead) ─────────────────────────────
      const ascSign = astroData?.asc?.ascendantSignIndex ?? 1;
      const funcMap = classifyFunctionalForLagna(ascSign) as Record<string, string>;
      const moonLonVal = astroData?.moon?.siderealLongitudeDeg ?? (astroData?.grahas as any)?.Moon?.lon ?? 0;
      const birthISO = utcDateTime ?? '';
      const ayanamsa = 24.14;
      const LIFE_AREA_LABELS = ['Health','Wealth','Career','Business','Marriage','Children','Property','Education'];
      const LIFE_AREA_HOUSES = [[1,6,8,12],[1,2,5,9,11,12],[1,6,10],[1,3,7,11],[1,7],[1,5],[1,4],[2,4,5]];
      const pAbbr: Record<string,string> = { Sun:'Su',Moon:'Mo',Mars:'Ma',Mercury:'Me',Jupiter:'Ju',Venus:'Ve',Saturn:'Sa',Rahu:'Ra',Ketu:'Ke' };
      const toHouseFromLon = (lon: number) => { const s = Math.floor(((lon%360)+360)%360/30)+1; return ((s-ascSign+12)%12)+1; };

      // Sets to collect all dasha lords for remedy fetch (built during loop below)
      const pdSdLordSet = new Set<string>();
      const mdAdLordSet = new Set<string>();

      // ── NATIVE PROFILE ────────────────────────────────────────────────────────
      const name = reportV2Data?.birthDetails?.name?.split(' ')[0] ?? 'the native';
      const ascendantStr = reportV2Data?.birthDetails?.ascendant ?? astroData?.asc?.ascendantSign ?? '';
      const moonSignStr  = reportV2Data?.birthDetails?.moonSign ?? '';
      const dobStr = reportV2Data?.birthDetails?.dateOfBirth ?? localDateTime ?? birthISO;
      let ageYears = 35;
      try { if (dobStr) { const diff = Date.now() - new Date(dobStr).getTime(); ageYears = Math.floor(diff / (365.25*24*3600*1000)); } } catch {}
      const ageBracket = ageYears < 25 ? 'young' : ageYears < 60 ? 'adult' : 'senior';
      const ageContextLine = ageBracket === 'young'
        ? `Age ${ageYears} (10–24). Priority life areas: Education, Health, Love relationships, early Career.`
        : ageBracket === 'adult'
        ? `Age ${ageYears} (25–60). Priority life areas: Career, Business, Wealth, Marriage, Children, Health.`
        : `Age ${ageYears} (60+). Priority life areas: Health, Financial security, Spiritual growth, Family.`;

      // Current dasha chain
      let curMD = '', curAD = '', curPD = '', curSD: string|undefined;
      try {
        const lv0 = getCurrentDashaLevels(birthISO, moonLonVal, new Date().toISOString());
        curMD = lv0?.md?.lord ?? ''; curAD = lv0?.ad?.lord ?? '';
        curPD = lv0?.pd?.lord ?? ''; curSD = lv0?.sd?.lord;
      } catch {}
      [curMD,curAD].filter(Boolean).forEach(p => mdAdLordSet.add(p));
      [curPD,...(curSD?[curSD]:[])].filter(Boolean).forEach(p => pdSdLordSet.add(p));
      const curDashaStr = [
        curMD && `${pAbbr[curMD]??curMD} MD(${funcMap[curMD]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curMD as PlanetName]??50)}%)`,
        curAD && `${pAbbr[curAD]??curAD} AD(${funcMap[curAD]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curAD as PlanetName]??50)}%)`,
        curPD && `${pAbbr[curPD]??curPD} PD(${funcMap[curPD]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curPD as PlanetName]??50)}%)`,
        curSD && `${pAbbr[curSD]??curSD} SD(${funcMap[curSD]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curSD as PlanetName]??50)}%)`,
      ].filter(Boolean).join(' · ');

      // ── ACTIVE DOSHAS ─────────────────────────────────────────────────────────
      const doshasLines: string[] = [];
      const placements = astroData?.placements ?? {};
      if (sadeSatiData?.isActive) {
        const phase = sadeSatiData.currentPhase ?? 'unknown';
        const phaseEffect = phase==='peak'    ? 'PEAK phase — maximum Saturn pressure on Moon-sign matters; health, career and finances face headwinds'
                          : phase==='rising'  ? 'RISING phase — challenges building gradually; delays, health issues and emotional strain possible'
                          : phase==='setting' ? 'SETTING phase — challenges easing; gradual relief as Saturn moves away from Moon sign'
                          : 'active phase';
        doshasLines.push(`Shani Sadesaati ACTIVE — ${phaseEffect}. Period: ${sadeSatiData.from ?? 'N/A'} → ${sadeSatiData.to ?? 'N/A'}. MUST be mentioned every week.`);
      }
      if (kaalSarpData && (kaalSarpData.isPresent || (kaalSarpData as any).present)) {
        const ksName = (kaalSarpData as any).specificType || kaalSarpData.type || 'Kaal Sarp';
        const ksDesc = (kaalSarpData as any).specificEffects || kaalSarpData.details || 'All planets hemmed between Rahu-Ketu axis.';
        doshasLines.push(`Kaal Sarp Yoga PRESENT — ${ksName} (Rahu H${(kaalSarpData as any).rahuHouse}, Ketu H${(kaalSarpData as any).ketuHouse}). ${ksDesc}`);
      }
      { const marsPl = (placements as any)['Mars']; if (marsPl && new Set([1,2,4,7,8,12]).has(marsPl.house)) doshasLines.push(`Mangal Dosha PRESENT — Mars in H${marsPl.house}. Heightens intensity in relationships/marriage.`); }
      { const jupPl=(placements as any)['Jupiter'], rahuPl2=(placements as any)['Rahu'], ketuPl2=(placements as any)['Ketu'];
        if (jupPl && rahuPl2 && jupPl.house===rahuPl2.house) doshasLines.push(`Guru Chandal Yoga PRESENT — Jupiter conjunct Rahu in H${jupPl.house}. Unconventional judgment; gains through non-traditional means.`);
        else if (jupPl && ketuPl2 && jupPl.house===ketuPl2.house) doshasLines.push(`Guru Ketu Yoga PRESENT — Jupiter conjunct Ketu in H${jupPl.house}. Deep spiritual wisdom; detachment from material pursuits.`);
      }

      // ── NATAL AFFLICTIONS & STRENGTHS ────────────────────────────────────────
      const natalAfflictions: string[] = [];
      if (combust)       { const l = Object.entries(combust).filter(([,v])=>v).map(([p])=>p);       if (l.length) natalAfflictions.push(`Combust (weakened by Sun): ${l.join(', ')}`); }
      if (debil)         { const l = Object.entries(debil).filter(([,v])=>v).map(([p])=>p);         if (l.length) natalAfflictions.push(`Debilitated: ${l.join(', ')}`); }
      if (exalt)         { const l = Object.entries(exalt).filter(([,v])=>v).map(([p])=>p);         if (l.length) natalAfflictions.push(`Exalted: ${l.join(', ')}`); }
      if (conjunctDebil) { const l = Object.entries(conjunctDebil).filter(([,v])=>v).map(([p])=>p); if (l.length) natalAfflictions.push(`Debility enhanced by conjunctions: ${l.join(', ')}`); }
      if (conjunctExalt) { const l = Object.entries(conjunctExalt).filter(([,v])=>v).map(([p])=>p); if (l.length) natalAfflictions.push(`Exaltation strengthened by conjunctions: ${l.join(', ')}`); }
      { const grahas = astroData?.grahas ?? {} as any;
        const retroList = (['Mars','Mercury','Jupiter','Venus','Saturn'] as const).filter(p => grahas[p]?.retrograde === true);
        if (retroList.length) natalAfflictions.push(`Natal retrograde: ${retroList.join(', ')} — internalized energy; patience needed before results manifest`); }
      { const rPl=(placements as any)['Rahu'], kPl=(placements as any)['Ketu'];
        if (rPl?.house && kPl?.house) natalAfflictions.push(`Nodal axis: Rahu H${rPl.house} (desire/obsession) opposite Ketu H${kPl.house} (past mastery/detachment)`); }
      const DUSTHANA = new Set([6,8,12]);
      const KEY_HOUSE_LABEL: Record<number,string> = { 5:'children/creativity', 7:'relationships/marriage', 9:'luck/dharma', 10:'career/status' };
      if (houseLords) { for (const keyH of [5,7,9,10] as const) { const lord = houseLords[keyH]; if (!lord) continue; const lordPl = (placements as any)[lord]; if (lordPl && DUSTHANA.has(lordPl.house)) natalAfflictions.push(`H${keyH} lord ${lord} in H${lordPl.house} (dusthana) — pressure on ${KEY_HOUSE_LABEL[keyH]}`); } }
      const h7Malefics = Object.entries(placements).filter(([p, pl]: [string,any]) => pl.house===7 && (funcMap[p]??'neutral')==='malefic').map(([p])=>p);
      if (h7Malefics.length) natalAfflictions.push(`Malefics in H7: ${h7Malefics.join(', ')} — natal relationship/marriage tension`);

      // ── YOGA LINES ────────────────────────────────────────────────────────────
      const allYogaFeeds = [
        ...permanentYogasFromFeeds.filter((f: any) => !f.ruleKey?.startsWith('sade_sati')),
        ...activeYogasFromFeeds.filter((f: any)    => !f.ruleKey?.startsWith('sade_sati')),
      ];
      const seenYogaKeys = new Set<string>();
      const dedupedYogaFeeds = allYogaFeeds.filter((f: any) => {
        const k = (f.ruleKey || f.id || '').replace(/_natal$/, '').replace(/_timing$/, '');
        if (seenYogaKeys.has(k)) return false; seenYogaKeys.add(k); return true;
      }).slice(0, 16);
      const yogaLines = dedupedYogaFeeds.length
        ? dedupedYogaFeeds.map((f: any) => {
            const yName = f.message?.split(':')[0]?.trim() || f.ruleKey || 'Yoga';
            const isPerm = f.isPermanent !== undefined ? f.isPermanent : f.ruleType === 'natal';
            return `${yName} [${f.lifeArea||''}] (${f.severity||'mixed'}, ${isPerm?'natal':'dasha-active'}): ${(f.description||f.message||'').substring(0,120)}`;
          }).join('\n')
        : 'None detected';

      // ── PLANETARY COMBINATIONS ────────────────────────────────────────────────
      const seenYogaRuleKeys = new Set(dedupedYogaFeeds.map((f: any) => (f.ruleKey||f.id||'').replace(/_natal$/,'').replace(/_timing$/,'')));
      const IS_COMBO = (rk: string) => rk.startsWith('saturn_') || rk.includes('_conjunction') || rk.includes('_combination');
      const commonCombos = [...permanentYogasFromFeeds, ...(reportV2Data?.feedWindows ?? [])].filter((f: any) => {
        const rk: string = f.ruleKey || f.id || '';
        if (!rk || rk.startsWith('sade_sati') || rk.startsWith('kaal_sarp')) return false;
        if (seenYogaRuleKeys.has(rk.replace(/_natal$/,'').replace(/_timing$/,''))) return false;
        return IS_COMBO(rk);
      });
      const seenComboKeys = new Set<string>();
      const comboPredLines = commonCombos.filter((f: any) => { const k=(f.ruleKey||f.id||'').replace(/_natal$/,'').replace(/_timing$/,''); if(seenComboKeys.has(k))return false; seenComboKeys.add(k); return true; })
        .sort((a: any,b: any) => Math.abs(b.score??0)-Math.abs(a.score??0)).slice(0,12)
        .map((f: any) => {
          const cName=(f.message||f.ruleKey||'').split(':')[0]?.trim()||'Combination';
          const pol=(f.score??0)>0?'+':((f.score??0)<0?'⚠':'~');
          const win=f.window?.from&&f.window?.to?` [${new Date(f.window.from).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}–${new Date(f.window.to).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}]`:'';
          return `${pol} ${cName} [${f.lifeArea||''}]${win}: ${(f.evidence?.[0]||f.message||'').substring(0,160)}`;
        }).join('\n') || 'None detected';

      // ── REMEDIES BY MALEFIC DASHA LORDS ──────────────────────────────────────
      // (collect lords from weekly/monthly loops too — fetch done after loops)
      // Pre-build with currently known lords; will re-fetch if loops add more
      const remedyByPlanet: Record<string, string[]> = {};

      // ── LIFE AREA SCORE HELPER ────────────────────────────────────────────────
      const computeLifeAreaScores = (MD: string, AD: string, PD: string, SD: string|undefined): string[] => {
        const hs = d1HouseScores ?? {};
        return LIFE_AREA_LABELS.map((label, i) => {
          const houses = LIFE_AREA_HOUSES[i];
          let natal = 0, nc = 0;
          for (const h of houses) { natal += Math.max(0,Math.min(100,(hs[h]??0))); nc++; }
          natal = nc ? natal/nc : 50;
          let adj = 0;
          for (const p of [MD,AD,PD,...(SD?[SD]:[])].filter(x=>x&&x!=='Unknown')) {
            const n = funcMap[p]??'neutral';
            const blended = blendedPlanetPct?.[p as PlanetName] ?? null;
            if (n==='yoga_karaka') {
              adj+=15;
            } else if (n==='benefic') {
              adj+=8;
            } else if (n==='malefic') {
              // Functional malefic with net positive blended = net beneficial in this period.
              // Only penalise when blended is negative (actual net harm).
              if (blended !== null && blended > 0) {
                adj += Math.min(8, blended / 12); // positive boost, capped at +8
              } else {
                adj -= 10;
              }
            }
          }
          const score = Math.max(0,Math.min(100,Math.round(natal*0.5+50+adj*0.3)));
          const r = score>=75?'Strong':score>=55?'Good':score>=40?'Moderate':'Challenging';
          return `${label}:${score}(${r})`;
        });
      };

      // ── SIX WEEKLY ROWS (Moon fully tracked — option C) ───────────────────────
      const weekRows: string[] = [];
      const NAK_NAMES = ['Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra','Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hasta','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha','Uttara Ashadha','Shravana','Dhanishta','Shatabhisha','Purva Bhadrapada','Uttara Bhadrapada','Revati'];
      for (let w = 0; w < 6; w++) {
        const wkStart = new Date(); wkStart.setDate(wkStart.getDate() + w*7);
        const wkMid   = new Date(); wkMid.setDate(wkMid.getDate() + w*7 + 3);
        const wkEnd   = new Date(); wkEnd.setDate(wkEnd.getDate() + w*7 + 7);

        // Dasha chain at week midpoint
        let wMD='Unknown', wAD='Unknown', wPD='Unknown', wSD: string|undefined;
        let sdWindowStr = '';
        if (birthISO && moonLonVal) {
          try {
            const lv = getCurrentDashaLevels(birthISO, moonLonVal, wkMid.toISOString());
            wMD=lv?.md?.lord??'Unknown'; wAD=lv?.ad?.lord??'Unknown'; wPD=lv?.pd?.lord??'Unknown'; wSD=lv?.sd?.lord;
            if (lv?.sd?.start && lv?.sd?.end) {
              const sdS=new Date(lv.sd.start), sdE=new Date(lv.sd.end);
              sdWindowStr = ` | SD:${sdS.toLocaleDateString('en-US',{month:'short',day:'numeric'})}→${sdE.toLocaleDateString('en-US',{month:'short',day:'numeric'})}`;
              if (Math.abs(sdS.getTime()-wkMid.getTime())<5*86400000) sdWindowStr += ` [⚡STARTS ~${sdS.toLocaleDateString('en-US',{month:'short',day:'numeric'})}]`;
              if (Math.abs(sdE.getTime()-wkMid.getTime())<5*86400000) sdWindowStr += ` [⚡ENDS ~${sdE.toLocaleDateString('en-US',{month:'short',day:'numeric'})}]`;
            }
          } catch {}
        }
        [wMD,wAD].filter(p=>p!=='Unknown').forEach(p=>mdAdLordSet.add(p));
        [wPD,...(wSD?[wSD]:[])].filter(p=>p!=='Unknown').forEach(p=>pdSdLordSet.add(p));

        const wDashaStr = [`${pAbbr[wMD]??wMD} MD(${funcMap[wMD]??'neutral'})`,`${pAbbr[wAD]??wAD} AD(${funcMap[wAD]??'neutral'})`,`${pAbbr[wPD]??wPD} PD(${funcMap[wPD]??'neutral'})`,...(wSD?[`${pAbbr[wSD]??wSD} SD(${funcMap[wSD]??'neutral'})`]:[])].join(' · ');

        const wScores = computeLifeAreaScores(wMD, wAD, wPD, wSD);
        // Only flag malefic lords where blended score is actually negative — a functional malefic
        // with a positive blended score is delivering net beneficial results and must NOT be warned.
        const wWeak    = [wPD,...(wSD?[wSD]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)<0);
        const wElevatedMalefic = [wPD,...(wSD?[wSD]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)>=0);
        const wLowEn   = [wPD,...(wSD?[wSD]:[])].filter(p=>{if(!p||p==='Unknown')return false; return (funcMap[p]??'neutral')!=='malefic'&&(blendedPlanetPct?.[p as PlanetName]??50)<35;});
        const wFlags   = [
          ...wWeak.map(p=>`⚠${p}(malefic,net-negative-blended:${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%)`),
          ...wElevatedMalefic.map(p=>`✓${p}(malefic-but-net-positive-blended:+${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%,contained—interpret-as-neutral/disciplined-not-damaging)`),
          ...wLowEn.map(p=>`↓${p}(low,str:${Math.round(blendedPlanetPct?.[p as PlanetName]??50)}%)`),
        ].join(',');

        // Transits
        let transitLine='', moonLine='';
        try {
          const Astro = require('astronomy-engine');
          const tS=Astro.MakeTime(wkStart), tM=Astro.MakeTime(wkMid), tE=Astro.MakeTime(wkEnd);
          const sid=(body:any,t:any)=>{const v=Astro.GeoVector(body,t,true);const e=Astro.Ecliptic(v);return((e.elon-ayanamsa)%360+360)%360;};

          // Slow+medium planets at midpoint
          const sunH=toHouseFromLon(sid(Astro.Body.Sun,tM)), merH=toHouseFromLon(sid(Astro.Body.Mercury,tM));
          const venH=toHouseFromLon(sid(Astro.Body.Venus,tM)), marH=toHouseFromLon(sid(Astro.Body.Mars,tM));
          const jupH=toHouseFromLon(sid(Astro.Body.Jupiter,tM)), satH=toHouseFromLon(sid(Astro.Body.Saturn,tM));
          const T=(wkMid.getTime()/86400000-10957.5)/36525;
          const rahuH=toHouseFromLon(((((125.04455501-1934.1361849*T+0.0020762*T*T)%360+360)%360)-ayanamsa+360)%360);
          const ketuH=((rahuH-1+6)%12)+1;

          // Sign-change flags for fast movers (compare start vs end of week)
          const chk=(body:any,abbr:string)=>{const hs=toHouseFromLon(sid(body,tS)),he=toHouseFromLon(sid(body,tE));return hs!==he?`⚡${abbr}→H${he}`:null;};
          const changes=[chk(Astro.Body.Sun,'Su'),chk(Astro.Body.Mercury,'Me'),chk(Astro.Body.Venus,'Ve'),chk(Astro.Body.Mars,'Ma')].filter(Boolean).join(' ');
          transitLine=`Su→H${sunH} Me→H${merH} Ve→H${venH} Ma→H${marH} Ju→H${jupH}(${funcMap['Jupiter']??'neutral'}) Sa→H${satH}(${funcMap['Saturn']??'neutral'}) Ra→H${rahuH} Ke→H${ketuH}${changes?` | CHANGES:${changes}`:''}`;

          // Moon — start / mid / end of week (option C — full tracking)
          const moHS=toHouseFromLon(sid(Astro.Body.Moon,tS)), moHM=toHouseFromLon(sid(Astro.Body.Moon,tM)), moHE=toHouseFromLon(sid(Astro.Body.Moon,tE));
          const moNak=NAK_NAMES[Math.floor(sid(Astro.Body.Moon,tM)/(360/27))]??'';
          if (moHS===moHM&&moHM===moHE)                    moonLine=`Mo→H${moHS} all week (${moNak} mid-week)`;
          else if (moHS===moHM&&moHM!==moHE)               moonLine=`Mo→H${moHS} Mon–Thu → H${moHE} Fri–Sun (${moNak} mid-week)`;
          else if (moHS!==moHM&&moHM===moHE)               moonLine=`Mo→H${moHS} Mon–Tue → H${moHM} Wed–Sun (${moNak} mid-week)`;
          else                                              moonLine=`Mo→H${moHS} Mon → H${moHM} mid-week → H${moHE} Sun (${moNak} mid-week)`;
        } catch { transitLine='transit calc unavailable'; moonLine=''; }

        const wLabel=`${wkStart.toLocaleDateString('en-US',{month:'short',day:'numeric'})}–${wkEnd.toLocaleDateString('en-US',{month:'short',day:'numeric'})}`;
        weekRows.push(
          `## Week ${w+1} (${wLabel})\nDasha: ${wDashaStr}${sdWindowStr}\nTransits: ${transitLine}\nMoon: ${moonLine}\nScores: ${wScores.join(' | ')}\nFlags: ${wFlags||'none'}`
        );
      }

      // ── THREE-MONTH SUMMARY ROWS ──────────────────────────────────────────────
      const monthRows: string[] = [];
      for (let m = 0; m < 3; m++) {
        const mDate = new Date(); mDate.setMonth(mDate.getMonth()+m, 15);
        let MD='Unknown', AD='Unknown', PD='Unknown', SD: string|undefined;
        if (birthISO && moonLonVal) {
          try { const lv=getCurrentDashaLevels(birthISO, moonLonVal, mDate.toISOString()); MD=lv?.md?.lord??'Unknown'; AD=lv?.ad?.lord??'Unknown'; PD=lv?.pd?.lord??'Unknown'; SD=lv?.sd?.lord; } catch {}
        }
        [MD,AD].filter(p=>p!=='Unknown').forEach(p=>mdAdLordSet.add(p));
        [PD,...(SD?[SD]:[])].filter(p=>p!=='Unknown').forEach(p=>pdSdLordSet.add(p));

        let mTransit='';
        try {
          const Astro=require('astronomy-engine'); const t=Astro.MakeTime(mDate);
          const sid2=(b:any)=>{const v=Astro.GeoVector(b,t,true);const e=Astro.Ecliptic(v);return((e.elon-ayanamsa)%360+360)%360;};
          const jH=toHouseFromLon(sid2(Astro.Body.Jupiter)), sH=toHouseFromLon(sid2(Astro.Body.Saturn)), maH=toHouseFromLon(sid2(Astro.Body.Mars));
          const T2=(mDate.getTime()/86400000-10957.5)/36525;
          const rH=toHouseFromLon(((((125.04455501-1934.1361849*T2)%360+360)%360)-ayanamsa+360)%360);
          const kH=((rH-1+6)%12)+1;
          mTransit=`Ju→H${jH}(${funcMap['Jupiter']??'neutral'}) Sa→H${sH}(${funcMap['Saturn']??'neutral'}) Ma→H${maH}(${funcMap['Mars']??'neutral'}) Ra→H${rH} Ke→H${kH}`;
        } catch { mTransit='transit calc unavailable'; }

        const mScores = computeLifeAreaScores(MD, AD, PD, SD);
        // Only flag malefic lords where blended score is actually negative.
        const mWeak   = [PD,...(SD?[SD]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)<0);
        const mElevatedMalefic = [PD,...(SD?[SD]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)>=0);
        const mLowEn  = [PD,...(SD?[SD]:[])].filter(p=>{if(!p||p==='Unknown')return false;return(funcMap[p]??'neutral')!=='malefic'&&(blendedPlanetPct?.[p as PlanetName]??50)<35;});
        let sdDates='';
        try { const lv2=getCurrentDashaLevels(birthISO,moonLonVal,mDate.toISOString()); if(lv2?.sd?.start&&lv2?.sd?.end) sdDates=` | SDstarts:${new Date(lv2.sd.start).toLocaleDateString('en-US',{month:'short',day:'numeric'})} SDends:${new Date(lv2.sd.end).toLocaleDateString('en-US',{month:'short',day:'numeric'})}`; } catch {}
        const mDashaStr=[`${pAbbr[MD]??MD} MD(${funcMap[MD]??'neutral'})`,`${pAbbr[AD]??AD} AD(${funcMap[AD]??'neutral'})`,`${pAbbr[PD]??PD} PD(${funcMap[PD]??'neutral'})`,...(SD?[`${pAbbr[SD]??SD} SD(${funcMap[SD]??'neutral'})`]:[])].join(' · ');
        const mWeakStr=mWeak.length?` | MaleficLords(net-negative):[${mWeak.map(p=>`${p}(blended:${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%)`).join(',')}]`:'';
        const mElevatedStr=mElevatedMalefic.length?` | MaleficButNetPositive:[${mElevatedMalefic.map(p=>`${p}(blended:+${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%,contained—interpret-as-neutral/disciplined-not-damaging)`).join(',')}]`:'';
        const mLowStr =mLowEn.length?` | LowEnergy:[${mLowEn.map(p=>`${p}(str:${Math.round(blendedPlanetPct?.[p as PlanetName]??50)}%)`).join(',')}]`:'';
        const mLabel=mDate.toLocaleDateString('en-US',{month:'short',year:'numeric'});
        monthRows.push(`## ${mLabel}\nDasha: ${mDashaStr}\nTransits: ${mTransit}${mWeakStr}${mElevatedStr}${mLowStr}${sdDates}\nScores: ${mScores.join(' | ')}`);
      }

      // ── REMEDIES FETCH (after all loops so lord sets are complete) ────────────
      // Only recommend remedies for malefic lords with a net-negative blended score.
      // A positively-blended functional malefic is already delivering good results — remedies unnecessary.
      const allWeakLords = new Set([...pdSdLordSet,...mdAdLordSet].filter(p => p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)<0));
      if (allWeakLords.size > 0) {
        const { data: rData } = await supabase.from('remedies').select('title,description,category,related_entity').in('related_entity',[...allWeakLords]);
        if (rData) { for (const r of rData) { if (!remedyByPlanet[r.related_entity]) remedyByPlanet[r.related_entity]=[]; if (remedyByPlanet[r.related_entity].length<3) remedyByPlanet[r.related_entity].push(`[${r.category}] ${r.title}: ${(r.description||'').substring(0,110)}`); } }
      }
      const remedySection = Object.entries(remedyByPlanet).map(([planet, rems]) =>
        `${planet} (${funcMap[planet]??'neutral'}, str:${Math.round(blendedPlanetPct?.[planet as PlanetName]??0)}%):\n${rems.map(r=>`  • ${r}`).join('\n')}`
      ).join('\n\n');

      // ── THE PROMPT ────────────────────────────────────────────────────────────
      const comprehensivePrompt = `You are an expert Vedic astrologer providing a personalised current period analysis with a week-by-week breakdown and 3-month summary.

VOICE & TONE:
- Address the native DIRECTLY using "you" and "your" throughout. NEVER use third-person.
- Tone: warm, honest, expert advisor — direct and specific, never generic.

NATIVE PROFILE:
Name: ${name} | Age: ${ageYears} years
${ageContextLine}
Ascendant: ${ascendantStr} | Moon Sign: ${moonSignStr}
Functional nature for THIS chart: ${Object.entries(funcMap).map(([p,n])=>`${p}=${n}`).join(', ')}
Current Dasha chain: ${curDashaStr}

ACTIVE DOSHAS — MANDATORY to mention every week:
${doshasLines.length ? doshasLines.join('\n') : 'None active'}

NATAL CHART AFFLICTIONS & STRENGTHS:
${natalAfflictions.length ? natalAfflictions.join('\n') : 'No major natal afflictions'}

ACTIVE YOGAS (cite only when relevant to that week's/month's life area scores):
${yogaLines}

NATAL PLANETARY COMBINATIONS (Saturn/conjunction/combination patterns):
${comboPredLines}

REMEDIES FOR MALEFIC DASHA LORDS (weave one remedy into each week/month where these lords are active as PD/SD):
${remedySection || 'None required — no functionally malefic lords active in this period'}

6-WEEK FORECAST DATA:
${weekRows.join('\n\n')}

3-MONTH SUMMARY DATA:
${monthRows.join('\n\n')}

====================
CLASSICAL VEDIC RULES — apply whenever chart data matches
====================

MARRIAGE & RELATIONSHIPS:
- Ketu in H7: dissatisfaction, detachment, separation risk. EXCEPTION: Jupiter aspecting H7 mitigates significantly.
- 12th lord in H7: marriage problems (loss, isolation bleeding into partnership).
- Saturn + Rahu both in H7: serious marriage difficulties — delays, power struggles or breakdown. State directly.
- 7th lord in dusthana (H6/8/12): marital stress. Provide profession-based coping strategy.

GENERAL PREDICTIVE PRINCIPLES:
- Natural malefics (Saturn, Mars, Sun, Rahu, Ketu) in upachaya houses (3,6,11): GOOD results. Do NOT treat as problematic.
- Jupiter's aspect is ALWAYS auspicious even when Jupiter is a functional malefic — its dasha/lordship can be problematic but the ASPECT remains positive.
- 8th house planet's dasha = fundamental life transformation (endings + new beginnings).
- Empty house: results delivered through opposite house during relevant dashas/transits.
- BLENDED SCORE RULES (CRITICAL — override all defaults): The blended/str% shown for each planet is a SIGNED net score: positive = net beneficial karmic energy, negative = net harmful. (a) A functional malefic flagged as ✓malefic-but-net-positive-blended MUST be read as delivering GOOD results — do NOT warn against it. (b) Only planets flagged ⚠malefic,net-negative-blended are genuinely harmful this period. (c) Remedies are only needed for ⚠ planets, never for ✓ planets.

PLANET–ILLNESS MAPPING (for health weeks):
Sun=acidity/eyes/heart, Moon=anxiety/sleep/depression, Mars=blood/skin, Saturn=stomach/bones/arthritis (chronic), Venus=reproductive/throat/diabetes, Mercury=nervous system/respiratory, Jupiter=liver/lungs, Ketu=mysterious/misdiagnosed, Rahu=mental health/anxiety

SADE SATI NUANCES:
- Saturn in H2/H5/H9 during Sade Sati: GOOD results — discipline, structure, meaningful growth.
- Saturn in H3/H6/H8/H12 during Sade Sati: PAINFUL — career collapse, health crises, financial hardship. Be direct.
- Remedies (always provide when Sade Sati is active): Shani Gayatri mantra, 7 Mukhi Rudraksha, offer oil to shami tree, donate mustard oil and black sesame on Saturdays.

INTERPRETATION HIERARCHY (for every planet-in-house statement):
1. PRIMARY: housePlanetNote — most specific classical rule for THIS planet in THIS house
2. SECONDARY: base planet-in-house prediction — broader thematic layer
3. TERTIARY: nakshatra-specific nuance — timing and fine-tuning
Blend all three into ONE flowing sentence. Never list layers separately.

VARGA CONFIRMATION:
- Career (H10) → check D10: confirm or contradict career momentum
- Relationships (H7) → check D9: confirm or contradict relationship harmony
- Property (H4) → check D4 if available
- Children (H5) → check D7 if available

CHARA KARAKA ANALYSIS (when karakas are present in context):
- AmK (Amatya Karaka) = career/intellectual development — reference for career-scoring weeks
- DK (Dara Karaka) = spouse/partnerships — reference for marriage-scoring weeks
- PuK (Putra Karaka) = children/creativity — reference for children-scoring weeks
- GK (Gnati Karaka) = health/obstacles — reference for health-scoring weeks

YOGA ACTIVATION LOGIC:
A yoga activates when the current dasha lord is CONNECTED to its house(s):
1. OCCUPATION (strongest): Dasha lord SITS IN the yoga house → "fully active NOW"
2. ASPECT (moderate): Dasha lord ASPECTS the yoga house → "active via aspect"
3. LORDSHIP (mild): Dasha lord RULES the yoga house → "mildly active via lordship"
Cite the activation level explicitly for any yoga you mention this week.

====================
WRITING INSTRUCTIONS — MANDATORY OUTPUT STRUCTURE
====================

BE CONCISE. Every section must be SHORT. The entire response must be completable in one reply — never cut off.

Write EXACTLY in this sequence:

## Current State
2–3 sentences only: dominant MD/AD energy, the single most critical life area score right now, and any active dosha phase. Use "you/your". No more than 60 words.

## Week 1 ([dates from data]) through ## Week 6 ([dates from data])
Write exactly 3 sentences per week — no more. Each week paragraph MUST cover:
(a) The PD/SD active + one key life area score (highest or lowest) + one transit effect
(b) Moon's house journey this week + nakshatra at mid-week in one phrase
(c) ONE specific action, remedy, or caution — if MaleficLord or Sadesaati flag, name it and give the remedy; if ⚡STARTS/ENDS, call out the date shift

## Month by Month
Write 3 monthly paragraphs in format "## [Month Year]" — 3–4 sentences each. Cover:
(a) Dasha arc + biggest life area shift vs current month
(b) One transit effect (Ju/Sa/Ma) most relevant to that month's scores
(c) One remedy or action if malefic lords or Sadesaati active; include SDstarts/SDends as a concrete date shift if present

## Key Dates & Turning Points
Bullet list of 3–5 dates only (the most impactful). Format: "**[Date range]** — [one sentence on what shifts and why]"

## What To Do Now
2 sentences maximum. Name one specific planet/house/dasha lord. No generic advice.

====================
ANTI-REPETITION RULES
====================
- Yogas: do NOT cite the same yoga in consecutive weeks. Rotate across the yoga list.
- Sade Sati: if active every week, vary the description — different life area, different remedy each time.
- Remedies: if the same malefic PD/SD lord recurs across weeks, choose a DIFFERENT remedy each time.
- Moon: vary sentence structure — sometimes lead with emotional tone, sometimes health, sometimes timing.
- Each week must contain at least ONE observation unique to that week (a SD shift date, transit change, score crossing a threshold, or nakshatra effect).

Score guide: ≥75=flourishing · 55-74=steady growth · 40-54=needs attention · <40=challenging
NEVER call a planet malefic unless the Functional Nature table above explicitly says 'malefic' for this chart.`;

      // Build context
      const context = chatContextData ? buildChatContext(chatContextData, {
        includeFeeds: true,
        includeInterpretations: true,
        includeVargas: true,
      }) : null;

      if (!context) {
        setCurrentPeriodError('Unable to build chart context. Please calculate your chart first.');
        setCurrentPeriodLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setCurrentPeriodError('Not authenticated. Please sign in to use AI analysis.');
        setCurrentPeriodLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ messages: [{ role: 'user', content: comprehensivePrompt }], context, model: 'gpt-5.4', questionType: 'general' })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle streaming response
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/event-stream')) {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        if (reader) {
          setCurrentPeriodLoading(false);
          let lineBuffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            lineBuffer += decoder.decode(value, { stream: true });
            const lines = lineBuffer.split('\n');
            lineBuffer = lines.pop() ?? '';
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try { const parsed = JSON.parse(line.slice(6)); if (parsed.content) { fullContent += parsed.content; setCurrentPeriodOutput(fullContent); } } catch {}
              }
            }
          }
          if (lineBuffer.startsWith('data: ')) { try { const p = JSON.parse(lineBuffer.slice(6)); if (p.content) { fullContent += p.content; setCurrentPeriodOutput(fullContent); } } catch {} }
        }
      } else {
        const result = await response.json();
        setCurrentPeriodOutput(result.reply || 'No response received.');
        setCurrentPeriodLoading(false);
      }
    } catch (err: any) {
      console.error('[CurrentPeriodAnalysis] Error:', err);
      setCurrentPeriodError(`Error: ${err.message}`);
      setCurrentPeriodLoading(false);
    }
  };
  // Helper function to scroll to element with offset
  const scrollToElement = useCallback((elementId: string, tabValue?: TabValue) => {
    if (tabValue) {
      setActiveTab(tabValue);
    }
    const attemptScroll = (delay: number, retriesLeft: number) => {
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        } else if (retriesLeft > 0) {
          attemptScroll(500, retriesLeft - 1);
        } else {
          // Element not found after all retries — widget may be hidden in Widget Manager
          alert('This widget is not currently visible. Please enable it in Admin → Widget Manager.');
        }
      }, delay);
    };
    attemptScroll(tabValue ? 400 : 0, 2);
  }, []);

  // Quick Access handlers
  const handleQuickLifeAreasDashboard = useCallback(() => {
    scrollToElement('section-life-areas-default', 'calculator');
  }, [scrollToElement]);

  const handleQuickYogasAnalysis = useCallback(() => {
    scrollToElement('section-active-yogas', 'dasha');
  }, [scrollToElement]);

  const handleQuickDashaTimeline = useCallback(() => {
    scrollToElement('section-dasha-timeline', 'dasha');
  }, [scrollToElement]);

  const handleQuickFutureEvents = useCallback(() => {
    scrollToElement('section-future-events', 'dasha');
  }, [scrollToElement]);

  const handleQuickPlanetImpact = useCallback(() => {
    scrollToElement('section-planet-factors', 'mychart');
  }, [scrollToElement]);

  const handleQuickActivitiesBalance = useCallback(() => {
    scrollToElement('section-d1-house-scores', 'mychart');
  }, [scrollToElement]);

  const handleQuickMarriageCompatibility = useCallback(() => {
    scrollToElement('section-marriage-compatibility', 'calculator');
  }, [scrollToElement]);

  const handleQuickFamilyScoreCard = useCallback(() => {
    scrollToElement('section-family-houses', 'calculator');
  }, [scrollToElement]);

  const handleQuickLifeEventFinder = useCallback(() => {
    scrollToElement('section-life-event-timeline', 'calculator');
  }, [scrollToElement]);

  const handleQuickPersonalMuhurta = useCallback(() => {
    scrollToElement('section-muhurta-advisor', 'calculator');
  }, [scrollToElement]);

  const handleQuickHouseDashboard = useCallback(() => {
    scrollToElement('section-house-dashboard', 'calculator');
  }, [scrollToElement]);

  // Floating Chart state (admin-only)
  const [showFloatingChart, setShowFloatingChart] = useState(false);
  const handleQuickFloatingChart = useCallback(() => {
    if (!astroData) {
      alert('Please calculate your chart first (Save & Calculate on the left)');
      return;
    }
    setShowFloatingChart(true);
  }, [astroData]);

  // Transit date state (defaults to current date)
  const [transitDate, setTransitDate] = useState<DateTime>(DateTime.now());
  // Hover state for transit chart planet tooltips
  const [hoveredTransitPlanet, setHoveredTransitPlanet] = useState<PlanetName | null>(null);
  const [transitTooltipPos, setTransitTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  // Varga state
  type VargaKey =
    | 'D1' | 'D2' | 'D3' | 'D4' | 'D7' | 'D9' | 'D10' | 'D12'
    | 'D16' | 'D20' | 'D24' | 'D27' | 'D30' | 'D40' | 'D45' | 'D60';

  const [vargaKey, setVargaKey] = useState<VargaKey>('D9');
  const VARGAS: VargaKey[] = [
    'D1','D2','D3','D4','D7','D9','D10','D12','D16','D20','D24','D27','D30','D40','D45','D60'
  ];

  // compute varga map once per calc
  const vargaMap = useMemo(() => {
    if (!astroData) return null;
    try {
      return computeImplementedVargas(
        astroData.asc.siderealLongitudeDeg,
        {
          Sun: astroData.grahas.Sun.lon,
          Moon: astroData.grahas.Moon.lon,
          Mercury: astroData.grahas.Mercury.lon,
          Venus: astroData.grahas.Venus.lon,
          Mars: astroData.grahas.Mars.lon,
          Jupiter: astroData.grahas.Jupiter.lon,
          Saturn: astroData.grahas.Saturn.lon,
          Rahu: astroData.nodes.Rahu,
          Ketu: astroData.nodes.Ketu
        }
      );
    } catch {
      return null;
    }
  }, [astroData]);

  // chosen varga for Divisional sub-tab
  const divData = useMemo(() => {
    if (!astroData || !vargaMap) return null;
    const chosen = (vargaMap as any)[vargaKey];
    if (!chosen) return null;

    const base = (chosen.ascSign - 1) * 30;
    const cusps = Array.from({ length: 12 }, (_, i) => ((base + i * 30) % 360 + 360) % 360);
    const placements = vargaToPlacements(chosen);

    const ascLon = base; // ascendant longitude in degrees (0-360)

    return { cusps, placements, ascLon };
  }, [astroData, vargaMap, vargaKey]);

  // D9 for My Chart
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const divD9 = useMemo(() => {
    if (!astroData || !vargaMap) return null;
    const d9 = (vargaMap as any)['D9'];
    if (!d9) return null;
    const base = (d9.ascSign - 1) * 30;
    const cusps = Array.from({ length: 12 }, (_, i) => ((base + i * 30) % 360 + 360) % 360);
    const placements = vargaToPlacements(d9);
    return { cusps, placements };
  }, [astroData, vargaMap]);

  // mobileFeatures config (isMobile already declared above for chartStyle)
  const mobileFeatures = getFeatureConfig(isMobile);

  // Session & roles
  const { user, loading } = useSession();
  const [showSplash, setShowSplash] = useState(() => {
    // Show splash only if not authenticated AND hasn't been dismissed
    if (user) return false;
    const dismissed = sessionStorage.getItem('splashDismissed');
    return !dismissed;
  });
  const [showLanding, setShowLanding] = useState(() => {
    // Show landing page after splash is dismissed, but before auth
    if (user) return false;
    const landingDismissed = sessionStorage.getItem('landingDismissed');
    const splashDismissed = sessionStorage.getItem('splashDismissed');
    return splashDismissed && !landingDismissed;
  });
  const [authMode, setAuthMode] = useState<'trial' | 'paid' | 'login'>('trial');
  const [authError, setAuthError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'trial' | 'silver' | 'gold' | null>(null);
  // Seed from localStorage so isAdmin is correct immediately on mount —
  // prevents Generate Report button from flashing away during 30-min token refreshes.
  const [profile, setProfile] = useState<{ role: 'admin'|'member'; approved_family_quota: number } | null>(() => {
    try {
      const cached = localStorage.getItem('shreemastro_profile_cache');
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return null;
  });
  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    // On mobile, start on the calculator (dashboard) tab, not chat.
    // Reads viewport width directly (same logic as useIsMobile) since hooks
    // can't be called inside a useState initializer.
    const w = typeof window !== 'undefined'
      ? (window.visualViewport?.width ?? document.documentElement.clientWidth ?? window.innerWidth)
      : 1200;
    return w < 768 ? 'calculator' : 'chat';
  });
  const isDashaTab = activeTab === 'dasha';
  const [profileErr, setProfileErr] = useState<string | null>(null);
  const [selectedPlanetInProfile, setSelectedPlanetInProfile] = useState<PlanetName | null>(null);
  const [selectedHouseInProfile, setSelectedHouseInProfile] = useState<number | null>(null);
  const [activeChartCue, setActiveChartCue] = useState<string | null>(null);
  const [pendingScrollTarget, setPendingScrollTarget] = useState<{ itemId: string; highlightColor: string; fallbackId?: string } | null>(null);

  // Set default selected planet to Lagna Lord when switching to interactive tab
  useEffect(() => {
    if (calcTab === 'interactive' && astroData && !selectedPlanetInProfile && !selectedHouseInProfile) {
      const lagnaSignIndex = astroData.asc.ascendantSignIndex; // 1-based (1=Aries, 12=Pisces)
      const lagnaLord = SIGN_LORDS[lagnaSignIndex - 1]; // SIGN_LORDS is 0-based array
      setSelectedPlanetInProfile(lagnaLord);
    }
  }, [calcTab, astroData, selectedPlanetInProfile, selectedHouseInProfile]);

  // ── Audio Guide: scroll helper ────────────────────────────────────────────
  // Scrolls the cue element's TOP edge into view below the fixed header.
  // Reads the header height from the DOM so it works at every breakpoint.
  const scrollToCueElement = (el: Element) => {
    const hint = (el as HTMLElement).dataset.audioScroll;

    // Explicit scroll hint on the cue element: 'top' | 'bottom'
    if (hint === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (hint === 'bottom') {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      return;
    }

    // Fallback: element has no layout box (display:contents) or is position:fixed.
    // In both cases the rect is unreliable — scroll to top so the user sees the header.
    const rect = el.getBoundingClientRect();
    const noBox = rect.width === 0 && rect.height === 0;
    const isFixed = (node: Element | null): boolean => {
      while (node && node !== document.body) {
        if (window.getComputedStyle(node as HTMLElement).position === 'fixed') return true;
        node = node.parentElement;
      }
      return false;
    };
    if (noBox || isFixed(el)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Normal scrollable page element: place section header just below fixed app header.
    const headerEl = document.querySelector('[data-audio-guide-header]') as HTMLElement | null;
    const headerHeight = headerEl ? headerEl.offsetHeight : 100;
    const absoluteTop = rect.top + window.scrollY;
    window.scrollTo({ top: absoluteTop - headerHeight - 8, behavior: 'smooth' });
  };

  // ── Interactive Chart Audio Guide cues ───────────────────────────────────
  // UPDATE timestamps after recording + uploading guide-interactive-chart.mp3
  const CHART_AUDIO_CUES = [
    { from:  4.5, to: 18.0, fieldCue: 'chart-house-header' },       // "click a house / header shows..."
    { from: 18.0, to: 27.0, fieldCue: 'chart-house-signification' }, // "primary signification..."
    { from: 27.0, to: 44.0, fieldCue: 'chart-planet-impact' },       // "Planet Impact score..."
    { from: 44.0, to: 64.0, fieldCue: 'chart-house-lord' },          // "House Lord section..."
    { from: 64.0, to: 74.0, fieldCue: 'chart-lord-hint' },           // "quick tip at the bottom..."
  ] as const;

  const handleChartAudioTime = (t: number) => {
    let found: string | null = null;
    for (const cue of CHART_AUDIO_CUES) {
      if (t >= cue.from && t < cue.to) found = cue.fieldCue;
    }
    setActiveChartCue(found);
  };

  // Toggle pulse class on the targeted side-panel element.
  // Remove+add in the same effect body (not cleanup) so both DOM writes are atomic
  // — no browser paint can occur between them.
  useEffect(() => {
    document.querySelectorAll('.audio-cue-active').forEach(el => el.classList.remove('audio-cue-active'));
    if (activeChartCue) {
      const el = document.querySelector(`[data-audio-cue="${activeChartCue}"]`);
      if (el) {
        el.classList.add('audio-cue-active');
        scrollToCueElement(el);
      }
    }
  }, [activeChartCue]);

  // ── Home Page Audio Guide cues ───────────────────────────────────
  // Ranges are contiguous (each end = next start) so activeHomeCue never
  // momentarily becomes null between sections, preventing pulse dropout.
  const HOME_AUDIO_CUES = [
    { from:  45.26, to:  55.3,  fieldCue: 'home-birth-details' },       // "Start by adding your birth details..."
    { from:  55.3,  to:  64.70, fieldCue: 'home-house-dashboard' },      // "look to the right...Twelve Houses dashboard..."
    { from:  64.70, to:  82.04, fieldCue: 'home-show-chart-btn' },       // "button called Show Chart...stay with the dashboard"
    { from:  82.04, to: 112.9,  fieldCue: 'home-analyse-chart-btn' },    // "Analyze Chart...complete cosmic report card"
    { from: 112.9,  to: 153.34, fieldCue: 'home-get-remedies-btn' },     // "Get Remedies...designed to create real impact"
    { from: 153.34, to: 198.7,  fieldCue: 'home-karmic-friction-btn' },  // "Get Karmic Friction Index...see your progress"
    { from: 198.7,  to: 229.92, fieldCue: 'home-life-areas' },           // "Life Areas dashboard...health, wealth, job, business, marriage, children"
    { from: 229.92, to: 267.26, fieldCue: 'home-vimshottari' },          // "Vimshottari Dasha timeline...green/yellow/red...Analyse My Current Time"
    { from: 267.26, to: 338.0,  fieldCue: 'home-widgets-area' },         // "widgets below...Muhurta...Yogas...Family...Marriage...Life Event Timeline"
    { from: 338.0,  to: 353.79, fieldCue: 'home-bell-icon' },            // "bell icon...daily/weekly/monthly forecasts"
    { from: 353.79, to: 366.0,  fieldCue: 'home-quick-access' },         // "Quick Access button...shortcuts"
    { from: 366.0,  to: 391.46, fieldCue: 'home-bottom-nav' },           // "bottom of every page...navigation shortcuts"
  ] as const;

  const [activeHomeCue, setActiveHomeCue] = useState<string | null>(null);

  const handleHomeAudioTime = (t: number) => {
    let found: string | null = null;
    for (const cue of HOME_AUDIO_CUES) {
      if (t >= cue.from && t < cue.to) found = cue.fieldCue;
    }
    setActiveHomeCue(found);
  };

  // Remove+add in same effect body (no cleanup) — atomic DOM update, no flicker.
  useEffect(() => {
    document.querySelectorAll('.audio-cue-active').forEach(el => el.classList.remove('audio-cue-active'));
    if (activeHomeCue) {
      const el = document.querySelector(`[data-audio-cue="${activeHomeCue}"]`);
      if (el) {
        el.classList.add('audio-cue-active');
        scrollToCueElement(el);
      }
    }
  }, [activeHomeCue]);

  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [selectedMobileLifeArea, setSelectedMobileLifeArea] = useState<LifeAreaKey | null>(null);

  // Navigation history for back button (mobile only)
  const [navigationHistory, setNavigationHistory] = useState<TabValue[]>(() => {
    const w = typeof window !== 'undefined'
      ? (window.visualViewport?.width ?? document.documentElement.clientWidth ?? window.innerWidth)
      : 1200;
    return [w < 768 ? 'calculator' : 'chat'];
  });
  
  // Unique toggle for Active Yogas & Predictions
  const [showUniqueActiveYogas, setShowUniqueActiveYogas] = useState(false);

  // Track compact house scores dashboard view state
  const [showCompactHouseScores, setShowCompactHouseScores] = useState(false);

  // Mobile dashboard toggle state (for Calculator tab mobile view) - default to true to show dashboard first
  const [showMobileDashboard, setShowMobileDashboard] = useState(true);

  // Karmic Pattern Detection modal state
  const [showKarmicPattern, setShowKarmicPattern] = useState(false);

  // Mobile birth details collapse state - default to collapsed if chart exists
  const [isBirthDetailsCollapsed, setIsBirthDetailsCollapsed] = useState(false);

  // Auto-collapse birth details on mobile when chart is calculated
  useEffect(() => {
    if (isMobile && astroData) {
      setIsBirthDetailsCollapsed(true);
    }
  }, [isMobile, astroData]);

  // Extract birth date from localDateTime for age-based notification ordering
  const birthDate = useMemo(() => {
    if (!localDateTime) return undefined;
    try {
      return DateTime.fromISO(localDateTime).toJSDate();
    } catch {
      return undefined;
    }
  }, [localDateTime]);

  // Terms acceptance tracking
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean | null>(null);
  const [isCheckingTerms, setIsCheckingTerms] = useState(true);
  const [currentTermsVersion, setCurrentTermsVersion] = useState<string>('1.0');

  // Check if user has accepted current terms version
  useEffect(() => {
    if (!user) {
      setIsCheckingTerms(false);
      setHasAcceptedTerms(null);
      return;
    }

    const checkTermsAcceptance = async () => {
      // Check if a previous acceptance write is still pending (fire-and-forget fallback)
      const pendingKey = `terms_pending_${user.id}`;
      const acceptedKey = `terms_accepted_${user.id}`;
      const hasPending = localStorage.getItem(pendingKey) === '1';
      const locallyAccepted = localStorage.getItem(acceptedKey) === '1';

      try {
        // 5-second timeout to prevent indefinite hang blocking app load
        const rpcPromise = supabase.rpc('get_terms_acceptance_status', { p_user_id: user.id });
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Terms check timeout')), 5000)
        );
        const { data, error } = await Promise.race([rpcPromise, timeoutPromise]) as Awaited<ReturnType<typeof supabase.rpc>>;

        if (error) {
          console.error('Terms check error:', error);
          // Fall back to localStorage — don't punish user for DB error
          setHasAcceptedTerms(locallyAccepted);
        } else {
          const status = data as { accepted: boolean; current_version: string } | null;
          const accepted = status?.accepted === true;
          const version = status?.current_version ?? '1.0';
          setCurrentTermsVersion(version);
          // Sync localStorage with DB result
          if (accepted) {
            localStorage.setItem(acceptedKey, '1');
            if (hasPending) localStorage.removeItem(pendingKey);
          }
          setHasAcceptedTerms(accepted);
        }
      } catch (error) {
        console.error('Terms check failed:', error);
        // On timeout or error, trust localStorage to avoid repeated interruptions
        setHasAcceptedTerms(locallyAccepted);
      } finally {
        setIsCheckingTerms(false);
      }
    };

    checkTermsAcceptance();
  }, [user]);

  // Subscription management
  const { subscription, usage, chatCredits, canCreateChart, canAskChatQuestion, getRemainingQuota, getUpgradeMessage, refresh: refreshSubscription, forceRefresh } = useSubscription(user?.id ?? null);

  // Effective chart quota: admin-set quota overrides plan default when different.
  // This is the single source of truth for chart creation gating.
  // Formula: use approved_family_quota when admin has explicitly overridden it (quota > 0 AND differs from planMax)
  const effectiveMaxCharts = React.useMemo(() => {
    if (profile?.role === 'admin') return 999999;
    const planMax = subscription?.limits.maxCharts ?? 4;
    const quota = profile?.approved_family_quota ?? 0;
    // Use admin-set quota only when it has been explicitly set and differs from plan default
    return (quota > 0 && quota !== planMax) ? quota : planMax;
  }, [profile, subscription]);

  // AI credit badges — show for all users (admin sees credits for testing visibility)
  const isAdminPlan = subscription?.plan === 'admin';
  const aiCreditsGeneral: number | null = subscription ? getRemainingQuota('chat_general') : null;
  const aiCreditsLifeArea: number | null = subscription ? getRemainingQuota('chat_life_area') : null;

  // Chat credits top-up
  const chatCreditsRemaining: number | null = chatCredits ? chatCredits.remaining : null;
  const [showBuyCreditsModal, setShowBuyCreditsModal] = React.useState(false);
  const [showNotifPreferences, setShowNotifPreferences] = useState(false);
  const [reopenSubscriptionAfterCredits, setReopenSubscriptionAfterCredits] = React.useState(false);

  // WhatsApp opt-in modal
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  // My Subscription modal
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [whatsAppOptedIn, setWhatsAppOptedIn] = useState<boolean | null>(null);

  // Fetch WhatsApp opt-in status once after user loads
  useEffect(() => {
    if (!user) { setWhatsAppOptedIn(null); return; }
    supabase
      .from('profiles')
      .select('whatsapp_opted_in, whatsapp_phone')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setWhatsAppOptedIn(!!(data?.whatsapp_opted_in && data?.whatsapp_phone));
      });
  }, [user]);

  // Tour management
  const {
    tourState,
    activeTour,
    isRunning: isTourRunning,
    loading: tourLoading,
    startTour,
    completeTour,
    skipTour,
    shouldAutoStartTour
  } = useTour();

  // Video tutorial management
  const {
    isOpen: isVideoOpen,
    currentVideo,
    openTutorial,
    closeTutorial
  } = useVideoTutorial();

  // Auto-start tour for first-time users on Calculator tab
  useEffect(() => {
    // Don't auto-start tour if there's a deep link (URL has tab/section params)
    const params = new URLSearchParams(window.location.search);
    const hasDeepLink = params.has('tab') || params.has('section');
    
    // Auto-start tour disabled
    if (false && !tourLoading && activeTab === 'calculator' && shouldAutoStartTour('calculator') && !hasDeepLink) {
      // Delay to ensure DOM is ready with data-tour attributes
      const timer = setTimeout(() => {
        startTour('calculator');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [activeTab, tourLoading, shouldAutoStartTour, startTour]);

  // Persist ?tab= param in sessionStorage so it survives magic-link auth redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      sessionStorage.setItem('deepLinkTab', tabParam);
    }
  }, []);

  // Detect Supabase auth errors in URL hash (e.g. expired magic link)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && (hash.includes('error_code=') || hash.includes('error='))) {
      const params = new URLSearchParams(hash.slice(1));
      const errorCode = params.get('error_code');
      if (errorCode === 'otp_expired') {
        window.history.replaceState({}, '', window.location.pathname);
        setAuthMode('login');
        setShowSplash(false);
        setShowLanding(false);
        setAuthError('Your sign-in link has expired (links are valid for 1 hour). Enter your email below to get a new one — no need to re-register.');
      }
    }
  }, []);

  // Deep linking: Handle URL parameters for tab, section, and ?subscribe=1
  useEffect(() => {
    if (!user) return;
    
    const params = new URLSearchParams(window.location.search);

    // ?subscribe=1 deeplink — opens the My Subscription modal (used in WhatsApp digest links)
    if (params.get('subscribe') === '1') {
      setShowSubscriptionModal(true);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // ?optin=whatsapp deeplink — opens WhatsApp opt-in modal (used in email digest CTA links)
    if (params.get('optin') === 'whatsapp') {
      setShowWhatsAppModal(true);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    const storedTab = sessionStorage.getItem('deepLinkTab');
    const targetTab = params.get('tab') || storedTab;
    if (storedTab) sessionStorage.removeItem('deepLinkTab');
    const targetSection = params.get('section');
    
    if (targetTab) {
      // Map 'home' to 'calculator' tab (home sections are in calculator tab)
      const mappedTab = targetTab === 'home' ? 'calculator' : targetTab;
      setActiveTab(mappedTab as TabValue);
      
      if (targetSection) {
        // Wait for tab content to load, then scroll to section
        const scrollToSection = (attempts = 0) => {
          const element = document.getElementById(targetSection) || 
                         document.querySelector(`[data-section="${targetSection}"]`);
          
          if (element) {
            // Small delay to ensure rendering is complete
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Clear URL params after navigation
              window.history.replaceState({}, '', window.location.pathname);
            }, 300);
          } else if (attempts < 15) {
            // Retry if element not found yet (max 3 seconds)
            setTimeout(() => scrollToSection(attempts + 1), 200);
          }
        };
        
        // Start scrolling after tab switch
        setTimeout(() => scrollToSection(), 500);
      } else {
        // Clear URL params if only tab specified
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [user]);

  // Handle tour completion
  const handleTourComplete = useCallback(() => {
    if (activeTour) {
      completeTour(activeTour);
    }
  }, [activeTour, completeTour]);

  // Handle tour skip
  const handleTourSkip = useCallback(() => {
    skipTour();
  }, [skipTour]);

  // Get current tour steps based on active tab (convert to custom TourStep format)
  const currentTourSteps = useMemo((): TourStep[] => {
    if (!activeTour) return [];
    
    // Check if it's a widget-specific tour
    if (activeTour.startsWith('widget-')) {
      const widgetKey = activeTour.replace('widget-', '');
      return WIDGET_TOURS[widgetKey] || [];
    }
    
    // Otherwise it's a tab tour
    const tourTab = activeTour as 'calculator' | 'feeds' | 'vastu' | 'chat' | 'admin' | 'family';
    const config = TOUR_CONFIGS[tourTab];
    const steps = config?.steps || [];
    
    // Convert from tour config format to TourStep format
    return steps.map(step => ({
      target: typeof step.target === 'string' ? step.target : 'body',
      content: step.content as string,
      title: step.title as string | undefined,
      placement: step.placement as 'top' | 'bottom' | 'left' | 'right' | 'center' | undefined
    }));
  }, [activeTour]);

  // Function to start a widget-specific tour
  const startWidgetTour = useCallback((widgetKey: string) => {
    startTour(`widget-${widgetKey}` as any);
  }, [startTour]);

  // Scroll to top when tab changes & track analytics (unless there's a pending scroll target)
  const prevTabRef = React.useRef<string | null>(null);
  useEffect(() => {
    // Only scroll if tab actually changed (not on re-render) and no pending scroll target
    if (prevTabRef.current !== null && prevTabRef.current !== activeTab && !pendingScrollTarget) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevTabRef.current = activeTab;
    
    // Track tab change for analytics - DISABLED to reduce request load
    // if (user) {
    //   startTabActivity(activeTab);
    // }

    // Update navigation history on mobile
    if (isMobile) {
      setNavigationHistory(prev => {
        // Don't add duplicates if navigating back
        if (prev[prev.length - 1] === activeTab) return prev;
        return [...prev, activeTab];
      });
    }
  }, [activeTab, pendingScrollTarget, isMobile]); // Removed 'user' dependency to prevent re-renders

  // Handle scrolling to pending target after tab content renders
  useEffect(() => {
    if (!pendingScrollTarget) return;

    let retries = 0;
    const MAX_RETRIES = 12; // ~2.5s total before falling back to section
    const { itemId, highlightColor, fallbackId } = pendingScrollTarget;

    const scrollToTarget = () => {
      const element = document.getElementById(itemId) ||
                     document.querySelector(`[data-feed-id="${itemId}"]`) ||
                     document.querySelector(`[data-yoga-id="${itemId}"]`);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the item briefly
        element.style.transition = 'background-color 0.5s';
        element.style.backgroundColor = highlightColor;
        setTimeout(() => { element.style.backgroundColor = ''; }, 2000);
        setPendingScrollTarget(null);
      } else if (retries < MAX_RETRIES) {
        retries++;
        setTimeout(scrollToTarget, 200);
      } else {
        // Specific item not found — fall back to the containing section
        const fallback = fallbackId ? document.getElementById(fallbackId) : null;
        if (fallback) {
          const yOffset = -100;
          const y = fallback.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
        setPendingScrollTarget(null);
      }
    };

    // Wait for tab content to render
    const timer = setTimeout(scrollToTarget, 100);
    return () => clearTimeout(timer);
  }, [pendingScrollTarget, activeTab]);

  function asProfile(row: any | null): { role: 'admin'|'member'; approved_family_quota: number } | null {
    if (!row) return null;
    const roleNorm = String(row.role ?? 'member').toLowerCase() === 'admin' ? 'admin' : 'member';
    const quota = Number.isFinite(Number(row.approved_family_quota)) ? Number(row.approved_family_quota) : 0;
    return { role: roleNorm, approved_family_quota: quota };
  }

  // Initialize analytics when user logs in & hide splash
  useEffect(() => {
    if (user) {
      const cleanup = initializeAnalytics();
      setShowSplash(false); // Hide splash when user is authenticated
      
      // Cleanup on unmount or when user changes
      return cleanup;
    }
  }, [user]);

  // On login: (a) auto-register FCM token if push already enabled on another device,
  //           (b) show notification prefs modal for existing users who haven't seen it yet
  //               (catches users who have a saved chart but never triggered the modal).
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data: prefRow } = await supabase
          .from('notification_preferences')
          .select('push_enabled, preferences_seen')
          .eq('user_id', user.id)
          .maybeSingle();

        // (a) Handle FCM token registration based on this device's notification permission:
        //   - 'granted'  → permission already given on this device, silently register token
        //   - 'default'  → permission never asked on this device; if push_enabled=true on
        //                   another device, show the modal so this device can also opt in.
        //                   (preferences_seen=true is intentionally ignored here — it only
        //                   means the modal was seen somewhere, not on this specific device)
        //   - 'denied'   → user blocked notifications on this device, do nothing
        if ('Notification' in window) {
          if (prefRow?.push_enabled && Notification.permission === 'granted') {
            // Already enabled + permission granted on this device — register silently
            registerFCMToken(user.id).catch(() => {/* non-critical */});
          } else if (prefRow?.push_enabled && Notification.permission === 'default') {
            // Push enabled on another device but never asked on this one — show modal
            setShowNotifPreferences(true);
          }
        }

        // (b) If user has never seen the modal anywhere, check if they have a saved primary
        //     chart and show the modal for the first time.
        if (!prefRow?.preferences_seen && !prefRow?.push_enabled) {
          const { data: ownProfile } = await supabase
            .from('birth_profiles')
            .select('id')
            .eq('owner_id', user.id)
            .is('family_of_profile_id', null)
            .not('chart_data', 'is', null)
            .limit(1)
            .maybeSingle();
          if (ownProfile) {
            setShowNotifPreferences(true);
          }
        }
      } catch { /* non-critical */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Re-register FCM token after a service worker update.
  // When a new SW is deployed, serviceWorkerRegistration.ts sends SKIP_WAITING
  // which triggers 'controllerchange'. The page reloads but user?.id dep doesn't
  // change so the login useEffect above won't re-fire. This listener catches it.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleControllerChange = () => {
      if (!user) return;
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      supabase
        .from('notification_preferences')
        .select('push_enabled')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.push_enabled) {
            registerFCMToken(user.id).catch(() => {/* non-critical */});
          }
        });
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  }, [user?.id]);

  useEffect(() => {
    (async () => {
      if (!user) {
        // Don't wipe profile on transient token-refresh events where Supabase
        // briefly fires onAuthStateChange with session=null before the new token.
        // Only clear on a genuine logout (confirmed by getSession returning null).
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setProfile(null);
          setProfileErr(null);
          try { localStorage.removeItem('shreemastro_profile_cache'); } catch { /* ignore */ }
        }
        return;
      }
      setProfileErr(null);
      const { data, error } = await supabase
        .from('profiles')
        .select('role, approved_family_quota')
        .eq('id', user.id)
        .maybeSingle();
      if (error) { setProfileErr(error.message); return; }
      if (!data) {
        const { error: insErr } = await supabase
          .from('profiles')
          .upsert({ id: user.id }, { onConflict: 'id' });
        if (insErr) { setProfileErr(insErr.message); return; }
        // Persist WhatsApp opt-in from registration metadata on first login
        const meta = (user as any)?.user_metadata;
        if (meta?.whatsapp_opted_in) {
          await supabase.from('profiles').update({
            whatsapp_opted_in: true,
            whatsapp_phone: meta.whatsapp_phone ?? null,
          }).eq('id', user.id);
          // Immediately update local state so the banner never shows for newly registered verified users
          setWhatsAppOptedIn(!!(meta.whatsapp_phone));
        }
        const { data: data2, error: e2 } = await supabase
          .from('profiles')
          .select('role, approved_family_quota')
          .eq('id', user.id)
          .maybeSingle();
        if (e2) { setProfileErr(e2.message); return; }
        const p2 = asProfile(data2);
        setProfile(p2);
        try { localStorage.setItem('shreemastro_profile_cache', JSON.stringify(p2)); } catch { /* ignore */ }
        return;
      }
      const p = asProfile(data);
      setProfile(p);
      try { localStorage.setItem('shreemastro_profile_cache', JSON.stringify(p)); } catch { /* ignore */ }
    })();
  }, [user]);

  // "My Profile" and user-specific data - OPTIMIZED with parallel fetch
  const [savedRow, setSavedRow] = useState<DbUserRow | null>(null);
  const [savedRowLoaded, setSavedRowLoaded] = useState(false);
  useEffect(() => {
    if (!user) { 
      setSavedRow(null);
      setSavedRowLoaded(false);
      setHouseNames({});
      return; 
    }
    
    let cancelled = false;
    (async () => {
      try {
        // Fetch user profile and house names in parallel
        const [profileResult] = await Promise.all([
          fetchMyUserProfile(user.id)
        ]);
        
        if (!cancelled) {
          setSavedRow(profileResult as DbUserRow | null);
          setSavedRowLoaded(true);
        }
      } catch (e) {
        console.error('Failed to fetch user data:', e);
        if (!cancelled) setSavedRowLoaded(true);
      }
    })();
    
    return () => {
      cancelled = true;
    };
  }, [user]);
  
  // Family/saved profiles
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const hasAutoSelected = useRef(false);
  // Guard: prevent concurrent saveChartDataToProfile calls (race → duplicate birth_profiles rows)
  const isSavingChartRef = useRef(false);

  const profileOptions = useMemo(() => {
    const opts = profiles.map(p => ({
      value: p.id,
      label: `${p.full_name || 'Unnamed'} - ${p.dob_day ?? '--'}/${p.dob_month ?? '--'}/${p.dob_year ?? '----'} - ${p.place_label ?? 'Unknown'}`
    }));
    if (savedRow) {
      return [
        { value: 'my_profile', label: `My Profile - ${savedRow.full_name} - ${savedRow.dob_day ?? '--'}/${savedRow.dob_month ?? '--'}/${savedRow.dob_year ?? '----'} - ${savedRow.place_label ?? 'Unknown'}` },
        ...opts
      ];
    }
    return opts;
  }, [profiles, savedRow]);

  // ── One-time migration: purge all report_ai_v1 contaminated entries ──
  // Runs only once per browser (flag stored in localStorage). Safe to leave in permanently.
  useEffect(() => {
    try {
      if (localStorage.getItem('report_ai_migration_v2_done')) return;
      const toDelete: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('report_ai_') && !key.startsWith('report_ai_v2_')) toDelete.push(key);
      }
      toDelete.forEach(k => localStorage.removeItem(k));
      localStorage.setItem('report_ai_migration_v2_done', '1');
      if (toDelete.length > 0) console.log(`[ReportCache] Migrated: purged ${toDelete.length} v1 cache entries`);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty deps = runs once on mount only

  // ── Report AI cache (localStorage, 30-day expiry, keyed by user+profile) ──
  // selectedProfileId === 'my_profile' → use savedRow.id (birth_profiles PK).
  // For any other profile (family member) → use selectedProfileId directly.
  const reportAiCacheKey = useMemo(() => {
    const uid = user?.id;
    if (!uid) return null;
    const pid = selectedProfileId === 'my_profile'
      ? (savedRow?.id ?? uid)
      : (selectedProfileId ?? uid);
    return `report_ai_v2_${uid}_${pid}`;  // v2: fixed race-condition cache contamination
  }, [user?.id, selectedProfileId, savedRow?.id]);

  // Resolve birth profile ID for the Karmic Friction hook and modal.
  // Own profile ("my_profile") → null, because the user's birth data is in the
  // `users` table (id = auth.uid()), not in `birth_profiles`. Saving with
  // birth_profile_id = auth.uid() would violate the FK constraint.
  // Family profiles → selectedProfileId (a valid birth_profiles.id UUID).
  const resolvedKarmicProfileId = useMemo(() => {
    if (!selectedProfileId || selectedProfileId === 'my_profile') return null;
    return selectedProfileId; // family profile — birth_profiles.id IS the profile id
  }, [selectedProfileId]);

  // Resolve the user ID for the Karmic Friction hook.
  // When an admin selects another user's birth profile, the survey data is stored
  // under that profile's owner_id — not the admin's user ID. Without this, the
  // hook queries with the wrong user_id and returns empty results.
  const resolvedKarmicUserId = useMemo(() => {
    if (!selectedProfileId || selectedProfileId === 'my_profile') return user?.id ?? null;
    const selectedProfile = profiles.find(p => p.id === selectedProfileId);
    return selectedProfile?.owner_id ?? user?.id ?? null;
  }, [selectedProfileId, profiles, user?.id]);

  // Karmic Friction hook — placed here (after resolvedKarmicProfileId) so the correct
  // birth_profile_id is passed on every render, ensuring each profile gets its own scores.
  const { frictionScoresByPlanet, planetResults, latestRoundId, saveResponse: saveKarmicResponse, loadLatestScores: reloadKarmicScores } = useKarmicFriction(resolvedKarmicUserId, resolvedKarmicProfileId);

  // Load cached AI content whenever the active profile changes
  useEffect(() => {
    // Always clear stale content first — profile/chart changed, old content must not linger
    setReportSwotContent('');
    setReportDetailedContent('');
    setReportCurrentPeriodContent('');
    setReportYearForecastContent('');

    if (!reportAiCacheKey) return;
    const loadCached = (type: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
      try {
        const raw = localStorage.getItem(`${reportAiCacheKey}_${type}`);
        if (!raw) return;
        const parsed: { content: string; expiresAt: string } = JSON.parse(raw);
        if (new Date(parsed.expiresAt) > new Date()) {
          setter(parsed.content);
        } else {
          localStorage.removeItem(`${reportAiCacheKey}_${type}`);
        }
      } catch { /* ignore corrupt entries */ }
    };
    loadCached('swot', setReportSwotContent);
    loadCached('detailed', setReportDetailedContent);
    loadCached('current_period', setReportCurrentPeriodContent);
    loadCached('year_forecast', setReportYearForecastContent);
  }, [reportAiCacheKey]);

  // Persist SWOT content to localStorage after generation completes.
  // NOTE: reportAiCacheKey intentionally excluded from deps — including it causes a race
  // condition where profile-switch fires this effect with old content + new key, corrupting
  // the new profile's cache slot. We only want to save when content is freshly generated.
  useEffect(() => {
    if (!reportAiCacheKey || !reportSwotContent || reportSwotLoading) return;
    try {
      const exp = new Date(); exp.setDate(exp.getDate() + 30);
      localStorage.setItem(`${reportAiCacheKey}_swot`, JSON.stringify({ content: reportSwotContent, expiresAt: exp.toISOString() }));
    } catch { /* storage full */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSwotContent, reportSwotLoading]);

  // Persist Detailed content to localStorage after generation completes.
  // reportAiCacheKey intentionally excluded from deps (see SWOT note above)
  useEffect(() => {
    if (!reportAiCacheKey || !reportDetailedContent || reportDetailedLoading) return;
    try {
      const exp = new Date(); exp.setDate(exp.getDate() + 30);
      localStorage.setItem(`${reportAiCacheKey}_detailed`, JSON.stringify({ content: reportDetailedContent, expiresAt: exp.toISOString() }));
    } catch { /* storage full */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetailedContent, reportDetailedLoading]);

  // Persist Current Period content to localStorage after generation completes.
  // reportAiCacheKey intentionally excluded from deps (see SWOT note above)
  useEffect(() => {
    if (!reportAiCacheKey || !reportCurrentPeriodContent || reportCurrentPeriodLoading) return;
    try {
      const exp = new Date(); exp.setDate(exp.getDate() + 30);
      localStorage.setItem(`${reportAiCacheKey}_current_period`, JSON.stringify({ content: reportCurrentPeriodContent, expiresAt: exp.toISOString() }));
    } catch { /* storage full */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportCurrentPeriodContent, reportCurrentPeriodLoading]);

  // Persist Year Forecast content — 90-day cache (year predictions change slowly)
  // reportAiCacheKey intentionally excluded from deps (see SWOT note above)
  useEffect(() => {
    if (!reportAiCacheKey || !reportYearForecastContent || reportYearForecastLoading) return;
    try {
      const exp = new Date(); exp.setDate(exp.getDate() + 90);
      localStorage.setItem(`${reportAiCacheKey}_year_forecast`, JSON.stringify({ content: reportYearForecastContent, expiresAt: exp.toISOString() }));
    } catch { /* storage full */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportYearForecastContent, reportYearForecastLoading]);

  async function loadFamilyList(ownerId: string) {
    // Admins load ALL profiles, regular users load only their own
    const isAdmin = profile?.role === 'admin';
    
    let query = supabase
      .from('birth_profiles')
      .select('id, owner_id, full_name, gender, relationship, family_of_profile_id, dob_day, dob_month, dob_year, tob_hour, tob_minute, tob_second, place_label, place_lat, place_lng, place_timezone, created_at, updated_at')
      .order('created_at', { ascending: false });
    
    // Regular users: filter by owner_id
    if (!isAdmin) {
      query = query.eq('owner_id', ownerId);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error('Error loading family list:', error);
    }
    if (data) setProfiles(data as ProfileRow[]);
    setProfilesLoaded(true);
  }
  useEffect(() => {
    if (!user) { 
      setProfiles([]);
      setProfilesLoaded(false);
      setSelectedProfileId(null);
      return; 
    }
    // Wait for profile to load before fetching family list (needed for admin check)
    if (profile !== null) {
      loadFamilyList(user.id);
    }
  }, [user, profile]);
  
  // Auto-select profile when data loads (with ref to prevent infinite loop)
  useEffect(() => {
    // Only run once after initial data load
    if (hasAutoSelected.current) return;
    
    // Wait for data to be loaded
    if (!user) return;
    
    // If user manually selected, don't auto-select
    if (selectedProfileId) {
      hasAutoSelected.current = true;
      return;
    }
    
    // Auto-select logic
    if (savedRow) {
      // If user has own profile, select it by default
      setSelectedProfileId('my_profile');
      loadUserSelectionById('my_profile');
      hasAutoSelected.current = true;
    } else if (profiles.length === 1) {
      // If only one profile exists (family member), auto-select it
      setSelectedProfileId(profiles[0].id);
      loadUserSelectionById(profiles[0].id);
      hasAutoSelected.current = true;
    }
  }, [profiles, savedRow, selectedProfileId, user]);
  // Note: loadUserSelectionById is wrapped in useCallback, so it's stable and doesn't need to be in deps
  
  useEffect(() => {
    const onFamilyRefresh = () => { if (user) loadFamilyList(user.id); };
    window.addEventListener('family:refresh', onFamilyRefresh);
    
    // Expose selectedProfileId to UserForm for family linkage
    const onGetSelectedProfile = () => {
      (window as any).__selectedProfileId = selectedProfileId === 'my_profile' ? null : selectedProfileId;
    };
    window.addEventListener('userform:get-selected-profile', onGetSelectedProfile);
    
    return () => {
      window.removeEventListener('family:refresh', onFamilyRefresh);
      window.removeEventListener('userform:get-selected-profile', onGetSelectedProfile);
    };
  }, [user, selectedProfileId]);

  /* ----------------- Listen for Life Area Overview events from MyChart ----------------- */
  useEffect(() => {
    const handleLifeAreaOverview = (e: any) => {
      const { lifeAreaName } = e.detail;
      // Map life area name to LifeAreaKey
      const lifeAreaMap: Record<string, LifeAreaKey> = {
        'Health': 'health',
        'Wealth': 'wealth',
        'Job': 'job',
        'Business': 'business',
        'Marriage': 'marriage',
        'Children': 'children'
      };
      const lifeAreaKey = lifeAreaMap[lifeAreaName] || 'health';
      setCurrentLifeAreaKey(lifeAreaKey);
      setLifeAreaOverviewOutput('');
      setLifeAreaOverviewError('');
      setLifeAreaOverviewLoading(true);
      setShowLifeAreaOverviewModal(true);
      callLifeAreaOverview(lifeAreaKey);
    };
    window.addEventListener('openLifeAreaOverview', handleLifeAreaOverview);
    return () => window.removeEventListener('openLifeAreaOverview', handleLifeAreaOverview);
  }, [canAskChatQuestion]); // Removed chatContextData - not needed as dependency

  // Listen for navigate to active yogas event (from YogasWidget "View Details" link)
  useEffect(() => {
    const handleNavigateToActiveYogas = () => {
      scrollToElement('section-active-yogas', 'dasha');
    };
    window.addEventListener('navigate:activeYogas', handleNavigateToActiveYogas);
    return () => window.removeEventListener('navigate:activeYogas', handleNavigateToActiveYogas);
  }, [scrollToElement]);

  /* ----------------- Terms Acceptance Check (see active useEffect near line 935) ----------------- */

  const handleTermsAccept = () => {
    // Write persistent accepted flag + clear any pending retry flag
    if (user) {
      localStorage.setItem(`terms_accepted_${user.id}`, '1');
      localStorage.removeItem(`terms_pending_${user.id}`);
    }
    setHasAcceptedTerms(true);
  };

  const handleTermsDecline = async () => {
    // Sign out user if they decline terms
    await supabase.auth.signOut();
    window.location.reload();
  };

  /* ----------------- Content Protection: Disable right-click and keyboard shortcuts (except for admins) ----------------- */
  useEffect(() => {
    // Allow all functionality for admin users
    const isAdmin = profile?.role === 'admin';
    
    // Add/remove content-protected class for CSS user-select control
    if (isAdmin) {
      document.body.classList.remove('content-protected');
    } else {
      document.body.classList.add('content-protected');
    }
    
    if (isAdmin) return; // Skip event listeners for admins

    // Prevent right-click context menu
    const preventRightClick = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    // Prevent Ctrl+A, Ctrl+C, Ctrl+X, Ctrl+U, F12, etc.
    const preventKeyboardShortcuts = (e: KeyboardEvent) => {
      // Disable Ctrl+A (Select All)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+C (Copy)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+X (Cut)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        return false;
      }
      // Disable F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+Shift+I (DevTools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+Shift+J (Console)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+Shift+C (Inspect Element)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', preventRightClick);
    document.addEventListener('keydown', preventKeyboardShortcuts);

    return () => {
      document.removeEventListener('contextmenu', preventRightClick);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
    };
  }, [profile]);

  /* ----------------- Mobile back button handling ----------------- */
  // Stable refs so the popstate handler always reads current values without
  // needing them in the dependency array (avoids spurious pushState calls).
  const _isBirthDetailsCollapsedRef = useRef(isBirthDetailsCollapsed);
  _isBirthDetailsCollapsedRef.current = isBirthDetailsCollapsed;
  const _showMobileDashboardRef = useRef(showMobileDashboard);
  _showMobileDashboardRef.current = showMobileDashboard;
  const _hasAstroDataRef = useRef<boolean>(!!astroData);
  _hasAstroDataRef.current = !!astroData;
  const _mobileChatOpenRef = useRef(mobileChatOpen);
  _mobileChatOpenRef.current = mobileChatOpen;
  const _selectedMobileLifeAreaRef = useRef(selectedMobileLifeArea);
  _selectedMobileLifeAreaRef.current = selectedMobileLifeArea;
  /** Filled by <ChatDesktopLayout onRegisterBackHandler={...} /> */
  const chatBackHandlerRef = useRef<(() => boolean) | null>(null);
  /** Tracks whether the chat tab has been visited — keeps it mounted after first render */
  const chatEverVisited = useRef(false);

  useEffect(() => {
    // Only enable back button handling when user is logged in and on mobile
    if (!isMobile || !user) return;

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      
      // If Life Area Overview modal is open, close it
      if (showLifeAreaOverviewModal) {
        setShowLifeAreaOverviewModal(false);
        window.history.pushState(null, '', window.location.href);
        return;
      }

      // ── Mobile chat overlay is open (Ask Shreem) — handle before tab logic.
      // Note: on mobile, opening chat does NOT change activeTab (stays 'calculator'),
      // so without this check the exit dialog would show incorrectly.
      if (_mobileChatOpenRef.current) {
        if (_selectedMobileLifeAreaRef.current) {
          // Inside a life-area chat → go back to the life-area selection list
          setSelectedMobileLifeArea(null);
        } else {
          // On the life-area list → close the chat overlay entirely
          setMobileChatOpen(false);
        }
        window.history.pushState(null, '', window.location.href);
        return;
      }

      // ── Calculator sub-states: handle before tab-level navigation
      if (activeTab === 'calculator') {
        // Birth details form is expanded with chart data → collapse it first
        if (_hasAstroDataRef.current && !_isBirthDetailsCollapsedRef.current) {
          setIsBirthDetailsCollapsed(true);
          window.history.pushState(null, '', window.location.href);
          return;
        }
        // In chart view (not dashboard) → go back to dashboard view
        if (!_showMobileDashboardRef.current) {
          setShowMobileDashboard(true);
          window.history.pushState(null, '', window.location.href);
          return;
        }
      }

      // ── Chat internal states: quick-questions panel or selected life area
      if (activeTab === 'chat' && chatBackHandlerRef.current?.()) {
        window.history.pushState(null, '', window.location.href);
        return;
      }
      
      // If we're on the home page (calculator) with no more history, confirm exit
      if (activeTab === 'calculator' && navigationHistory.length <= 1) {
        const confirmExit = window.confirm('Do you want to exit ShreemAstro?');
        if (confirmExit) {
          // Let the browser handle the default back action (close tab/go to previous site)
          window.history.back();
        } else {
          // Push state again to prevent default back behavior
          window.history.pushState(null, '', window.location.href);
        }
      }
      // If we have navigation history, go back one step
      else if (navigationHistory.length > 1) {
        const newHistory = [...navigationHistory];
        newHistory.pop(); // Remove current tab
        const previousTab = newHistory[newHistory.length - 1];
        setNavigationHistory(newHistory);
        setActiveTab(previousTab);
      } 
      // For other tabs, go back to home (chat on desktop, calculator on mobile)
      else {
        const homeTab: TabValue = isMobile ? 'calculator' : 'chat';
        setActiveTab(homeTab);
        setNavigationHistory([homeTab]);
      }
    };

    // Push initial state
    window.history.pushState(null, '', window.location.href);
    
    // Listen for back button
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isMobile, user, activeTab, navigationHistory, showLifeAreaOverviewModal]);

  /* ----------------- Bridge to prefill the visible form ----------------- */
  const pushPayloadToUserForm = useCallback((p: BirthFormPayload) => {
    try { window.dispatchEvent(new CustomEvent('userform:load-from-saved', { detail: { payload: p } })); } catch {}
    try { window.dispatchEvent(new CustomEvent('userform:load',            { detail: { payload: p } })); } catch {}
  }, []);

  /* ----------------- Helpers to drive hidden inputs ----------------- */
  const resetComputed = useCallback(() => {
    setAstroData(null); setUtcDateTime(null);
    setCombust(null); setDebil(null); setExalt(null);
    setShadbalaPct(null); setShadbalaRatio(null);
    setIkPhala(null);
    setCalcError(null);
  }, []);
  const setCalculatorFromPayload = useCallback(async (rawPayload: BirthFormPayload) => {
    resetComputed();
    setSelectedCity(null);
    setLocalDateTime('');
    setCalcError(null);

    const normalized: BirthFormPayload = { ...rawPayload };

    const hasCoords = normalized.place_lat != null && normalized.place_lng != null;
    const lat = hasCoords ? Number(normalized.place_lat) : null;
    const lon = hasCoords ? Number(normalized.place_lng) : null;

    let tz =
      typeof normalized.place_timezone === 'string' && normalized.place_timezone
        ? normalized.place_timezone
        : null;

    if (hasCoords && lat != null && lon != null && (!tz || tz.toLowerCase() === 'utc')) {
      const lookedUp = await resolveTimezoneForCoords(lat, lon);
      if (lookedUp) {
        tz = lookedUp;
        normalized.place_timezone = lookedUp;
      }
    }

    pushPayloadToUserForm(normalized);

    if (!hasCoords || lat == null || lon == null) {
      setCalcError('Saved record is missing coordinates. Please re-select a city.');
      return;
    }

    if (!tz) {
      setCalcError('Could not determine timezone for the saved city. Please re-select it.');
      return;
    }

    const label = normalized.place_label || `${tz} (${lat.toFixed(2)}, ${lon.toFixed(2)})`;
    const opt: CityOption = {
      label,
      value: `${lat.toFixed(5)},${lon.toFixed(5)}|${tz}`,
      city: normalized.place_label || 'SavedPlace',
      region: undefined,
      country: '',
      lat,
      lon,
      lng: lon,
      tz,
      timezone: tz,
      source: 'static',
    };

    setSelectedCity(opt);

    const year = normalized.dob_year ?? 2000;
    const month = normalized.dob_month ?? 1;
    const day = normalized.dob_day ?? 1;
    const hour = normalized.tob_hour ?? 0;
    const minute = normalized.tob_minute ?? 0;
    const second = normalized.tob_second ?? 0;

    const dtLocal = DateTime.fromObject(
      { year, month, day, hour, minute, second },
      { zone: tz }
    );

    if (!dtLocal.isValid) {
      setCalcError(`Invalid date/time in saved record (${dtLocal.invalidReason || 'unknown'}).`);
      return;
    }

    setLocalDateTime(dtLocal.toFormat("yyyy-LL-dd'T'HH:mm:ss"));
    
    // Capture gender for feed rules
    const genderStr = (normalized.gender || '').toString().trim().toLowerCase();
    const normalizedGender = (genderStr === 'male' || genderStr === 'm') ? 'male' 
      : (genderStr === 'female' || genderStr === 'f') ? 'female' 
      : 'other';
    setSelectedGender(normalizedGender);
    
    setCalcNonce(n => n + 1);
  }, [pushPayloadToUserForm, resetComputed]);
  const setCalculatorFromRow = useCallback(async (row: AnySavedRow) => {
    const payload: BirthFormPayload = {
      full_name: (row as any).full_name ?? null,
      gender: (row as any).gender ?? null,
      relationship: (row as any).relationship ?? null, // Include relationship field
      dob_year: (row as any).dob_year ?? null,
      dob_month: (row as any).dob_month ?? null,
      dob_day: (row as any).dob_day ?? null,
      tob_hour: (row as any).tob_hour ?? null,
      tob_minute: (row as any).tob_minute ?? null,
      tob_second: (row as any).tob_second ?? 0,
      place_label: (row as any).place_label ?? null,
      place_lat: (row as any).place_lat ?? null,
      place_lng: (row as any).place_lng ?? null,
      place_timezone: (row as any).place_timezone ?? null,
    };
    await setCalculatorFromPayload(payload);
  }, [setCalculatorFromPayload]);
  const loadUserSelectionById = useCallback((id: string) => {
    if (id === 'my_profile' && savedRow) {
      void setCalculatorFromRow(savedRow);
      return;
    }
    const p = profiles.find(x => x.id === id);
    if (p) void setCalculatorFromRow(p);
  }, [profiles, savedRow, setCalculatorFromRow]);

  /* ----------------- Calculation ----------------- */
  const [ayanKey] = useState<AyanamsaKey>('lahiri');
  const [customAyanDeg] = useState<string>('24.0');
  const ayanProvider = useMemo(
    () => getAyanamsaProvider(ayanKey, parseFloat(customAyanDeg)),
    [ayanKey, customAyanDeg]
  );

  // Compute current transits for Transit Chart
  const transitData = useMemo(() => {
    if (!astroData || !selectedCity) return null;
    
    try {
      const currentISO = transitDate.toISO()!;
      
      // Get current planetary positions
      const transitGrahas = getGrahaLongitudesAndMotionSidereal(
        { isoDate: currentISO, latitude: selectedCity.lat, longitude: selectedCity.lon },
        ayanProvider
      );
      
      const transitNodes = getRahuKetuSidereal(
        { isoDate: currentISO, latitude: selectedCity.lat, longitude: selectedCity.lon },
        ayanProvider
      );
      
      // Convert to planet longitudes
      const transitLongs: Partial<Record<PlanetName, number>> = {
        Sun: transitGrahas.Sun.lon,
        Moon: transitGrahas.Moon.lon,
        Mercury: transitGrahas.Mercury.lon,
        Venus: transitGrahas.Venus.lon,
        Mars: transitGrahas.Mars.lon,
        Jupiter: transitGrahas.Jupiter.lon,
        Saturn: transitGrahas.Saturn.lon,
        Rahu: transitNodes.Rahu,
        Ketu: transitNodes.Ketu,
      };
      
      // Calculate dignity for each transiting planet
      const transitDignities: Partial<Record<PlanetName, string>> = {};
      (Object.keys(transitLongs) as PlanetName[]).forEach((planet) => {
        const lon = transitLongs[planet];
        if (lon !== undefined) {
          const sign = Math.floor(lon / 30) + 1; // 1-indexed to match EXALT/DEBIL/OWN/MOOLATRIKONA constants
          
          // Check exaltation
          if (EXALT_SIGN_SV[planet] === sign) {
            transitDignities[planet] = 'exalted';
          }
          // Check debilitation
          else if (DEBIL_SIGN_SV[planet] === sign) {
            transitDignities[planet] = 'debilitated';
          }
          // Check own sign (exclude Rahu/Ketu)
          else if (planet !== 'Rahu' && planet !== 'Ketu' && OWN_SIGN_SV[planet as Exclude<PlanetName, 'Rahu' | 'Ketu'>]?.includes(sign)) {
            transitDignities[planet] = 'own';
          }
          // Check moolatrikona (exclude Rahu/Ketu)
          else if (planet !== 'Rahu' && planet !== 'Ketu' && MOOLATRIKONA_SV[planet as Exclude<PlanetName, 'Rahu' | 'Ketu'>] === sign) {
            transitDignities[planet] = 'moolatrikona';
          }
          // Otherwise, default to neutral
          else {
            transitDignities[planet] = 'neutral';
          }
        }
      });
      
      console.log('[App] Transit dignities calculated:', transitDignities);
      
      // Assign to natal houses (using natal cusps)
      const transitPlacements = assignPlanetsToHouses(astroData.cusps, transitLongs);

      // Retrograde map from GrahaMotion (speed < 0)
      const retrogrades: Partial<Record<string, boolean>> = {};
      (['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'] as const).forEach(p => {
        retrogrades[p] = transitGrahas[p].retrograde;
      });
      // Rahu/Ketu are always retrograde by definition
      retrogrades['Rahu'] = true;
      retrogrades['Ketu'] = true;

      // Combustion map — classical orb thresholds from transit Sun
      const COMBUST_ORBS: Partial<Record<string, number>> = {
        Moon: 12, Mars: 17, Mercury: 14, Jupiter: 11, Venus: 10, Saturn: 15,
      };
      const sunLon = transitLongs.Sun ?? 0;
      const combust: Partial<Record<string, boolean>> = {};
      (['Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'] as const).forEach(p => {
        const pLon = transitLongs[p];
        if (pLon === undefined) return;
        const orb = COMBUST_ORBS[p] ?? 10;
        const diff = Math.abs(((pLon - sunLon + 540) % 360) - 180);
        combust[p] = diff <= orb;
      });

      // Lagna sign index (1-12) for sign-lord friendship scoring
      const ascLagnaSign = Math.floor(astroData.asc.siderealLongitudeDeg / 30) + 1;

      // ── Sign window helper (speed-based estimate; accurate to ±days for slow planets) ──
      const _signWindow = (
        lon: number | undefined,
        absSpeedDpd: number,
        retrograde: boolean
      ): { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number } | undefined => {
        if (lon === undefined || absSpeedDpd < 0.0001) return undefined;
        const lonNorm = ((lon % 360) + 360) % 360;
        const s0 = Math.floor(lonNorm / 30);
        const lonInSign = lonNorm % 30;
        let daysAgo: number;
        let daysForward: number;
        let nextS0: number;
        if (!retrograde) {
          daysAgo     = lonInSign / absSpeedDpd;
          daysForward = (30 - lonInSign) / absSpeedDpd;
          nextS0 = (s0 + 1) % 12;
        } else {
          daysAgo     = (30 - lonInSign) / absSpeedDpd; // entered from upper boundary
          daysForward = lonInSign / absSpeedDpd;          // exits at lower boundary
          nextS0 = ((s0 - 1) + 12) % 12;
        }
        return {
          currentSign: s0 + 1,
          enteredOn: transitDate.minus({ days: Math.round(daysAgo) }).toFormat('MMM d, yyyy'),
          exitsOn:   transitDate.plus({ days: Math.round(daysForward) }).toFormat('MMM d, yyyy'),
          nextSign:  nextS0 + 1,
        };
      };
      const RAHU_SPEED_DPD = 0.0529; // Rahu/Ketu avg deg/day (always retrograde)
      const signDates = {
        sunSignDates:     _signWindow(transitLongs.Sun,     Math.abs(transitGrahas.Sun.speedDegPerDay),     false),
        moonSignDates:    _signWindow(transitLongs.Moon,    Math.abs(transitGrahas.Moon.speedDegPerDay),    false),
        marsSignDates:    _signWindow(transitLongs.Mars,    Math.abs(transitGrahas.Mars.speedDegPerDay),    transitGrahas.Mars.retrograde),
        mercurySignDates: _signWindow(transitLongs.Mercury, Math.abs(transitGrahas.Mercury.speedDegPerDay), transitGrahas.Mercury.retrograde),
        venusSignDates:   _signWindow(transitLongs.Venus,   Math.abs(transitGrahas.Venus.speedDegPerDay),   transitGrahas.Venus.retrograde),
        jupiterSignDates: _signWindow(transitLongs.Jupiter, Math.abs(transitGrahas.Jupiter.speedDegPerDay), transitGrahas.Jupiter.retrograde),
        saturnSignDates:  _signWindow(transitLongs.Saturn,  Math.abs(transitGrahas.Saturn.speedDegPerDay),  transitGrahas.Saturn.retrograde),
        rahuSignDates:    _signWindow(transitLongs.Rahu,    RAHU_SPEED_DPD, true),
        ketuSignDates:    _signWindow(transitLongs.Ketu,    RAHU_SPEED_DPD, true),
      };

      const result = { 
        cusps: astroData.cusps, // Use natal cusps
        placements: transitPlacements,
        longitudes: transitLongs,
        dignities: transitDignities,
        retrogrades,
        combust,
        ascLagnaSign,
        ascLon: astroData.asc.siderealLongitudeDeg,
        date: transitDate.toFormat('MMM dd, yyyy HH:mm'),
        signDates,
      };
      
      console.log('[App] Returning transitData with dignities:', result);
      return result;
    } catch (err) {
      console.error('[App] Error computing transits:', err);
      return null;
    }
  }, [astroData, selectedCity, ayanProvider, transitDate]);

  // Moves transit date by delta and highlights any planet that changed sign during the jump
  const moveTransitDate = useCallback((delta: { days?: number; weeks?: number }) => {
    setTransitDate(prev => prev.plus(delta));
  }, []);

  // ===== TRANSIT CHART HOVER TOOLTIP =====
  // Key: planet → signDates field name
  const TRANSIT_SIGN_DATE_KEY: Record<string, string> = {
    Sun: 'sunSignDates', Moon: 'moonSignDates', Mercury: 'mercurySignDates',
    Venus: 'venusSignDates', Mars: 'marsSignDates', Jupiter: 'jupiterSignDates',
    Saturn: 'saturnSignDates', Rahu: 'rahuSignDates', Ketu: 'ketuSignDates',
  };
  // Parashara aspect offsets (offset from house = aspect_num - 1)
  const TRANSIT_ASPECT_OFFSETS: Record<string, number[]> = {
    Sun: [6], Moon: [6], Mercury: [6], Venus: [6],
    Mars: [3, 6, 7], Jupiter: [4, 6, 8], Saturn: [2, 6, 9],
    Rahu: [4, 6, 8], Ketu: [4, 6, 8],
  };

  const transitHoverTooltipData = useMemo(() => {
    if (!hoveredTransitPlanet || !transitData) return null;
    const planet = hoveredTransitPlanet;
    const signKey = TRANSIT_SIGN_DATE_KEY[planet];
    const signInfo = signKey ? (transitData.signDates as any)[signKey] as { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number } | undefined : undefined;
    const nextSign: number | undefined = signInfo?.nextSign;
    const exitsOnRaw: string | undefined = signInfo?.exitsOn;
    const currentSign: number | undefined = signInfo?.currentSign;

    // Format "MMM d, yyyy" → "5th April 2026"
    const formatOrdinalDate = (ds: string): string => {
      const MONTH_FULL: Record<string, string> = {
        Jan:'January', Feb:'February', Mar:'March', Apr:'April', May:'May', Jun:'June',
        Jul:'July', Aug:'August', Sep:'September', Oct:'October', Nov:'November', Dec:'December',
      };
      const m = ds.match(/^(\w{3})\s+(\d+),\s+(\d{4})$/);
      if (!m) return ds;
      const day = parseInt(m[2], 10);
      const mod = day % 10;
      const suffix = (day > 10 && day < 14) ? 'th' : mod === 1 ? 'st' : mod === 2 ? 'nd' : mod === 3 ? 'rd' : 'th';
      return `${day}${suffix} ${MONTH_FULL[m[1]] ?? m[1]} ${m[3]}`;
    };
    const exitsOnFormatted = exitsOnRaw ? formatOrdinalDate(exitsOnRaw) : undefined;

    // Dignity in next sign (mid-sign longitude)
    const nextDignity = nextSign !== undefined && planet !== 'Rahu' && planet !== 'Ketu'
      ? getPlanetDignity(planet as PlanetName, (nextSign - 1) * 30 + 15)
      : undefined;

    // Current dignity
    const currentDignity = (transitData.dignities as any)[planet] as string | undefined;

    // Next house in whole-sign (natal ascendant)
    const ascSign = Math.floor(transitData.ascLon / 30) + 1;
    const nextHouse = nextSign !== undefined ? ((nextSign - ascSign + 12) % 12) + 1 : undefined;
    const currentHouse = (transitData.placements as any)[planet]?.house as number | undefined;

    // Parashara aspects from next house
    const offsets = TRANSIT_ASPECT_OFFSETS[planet] ?? [6];
    const aspectHouses: number[] = nextHouse !== undefined
      ? offsets.map((o: number) => ((nextHouse - 1 + o) % 12) + 1)
      : [];

    const nextSignName = nextSign !== undefined ? RASHI_NAMES[nextSign - 1] : undefined;
    const currentSignName = currentSign !== undefined ? RASHI_NAMES[currentSign - 1] : undefined;

    return {
      planet, currentHouse, currentSignName, currentDignity,
      nextHouse, nextSignName, nextDignity, exitsOnFormatted,
      aspectHouses,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredTransitPlanet, transitData]);

  const handleTransitPlanetHover = useCallback((planet: PlanetName | null, x: number, y: number) => {
    setHoveredTransitPlanet(planet);
    if (planet) setTransitTooltipPos({ x, y });
  }, []);

  // ===== YOGAS AND SPECIAL CONDITIONS =====
  
  // Accurate Saturn sign ingress/egress dates from astronomy-engine (used in feeds pipeline)
  const [saturnTransitsData, setSaturnTransitsData] = useState<{ ingressDate: string; egressDate: string } | null>(null);

  // Sade Sati calculation with accurate Saturn transit dates
  const [sadeSatiData, setSadeSatiData] = useState<{
    isActive: boolean;
    currentPhase?: 'rising' | 'peak' | 'setting';
    from?: string;
    to?: string;
    moonSign: number;
    saturnSign?: number;
    allPhases?: Array<{ phase: 'rising' | 'peak' | 'setting'; from: string; to: string }>;
    totalFrom?: string;
    totalTo?: string;
  } | undefined>(undefined);
  
  useEffect(() => {
    if (!astroData || !transitData || !selectedCity) {
      setSadeSatiData(undefined);
      return;
    }
    
    const calculateAsync = async () => {
      try {
        const moonSign = astroData.moon.moonSignIndex;
        const saturnPlacement = transitData.placements.Saturn;
        if (!saturnPlacement) {
          setSadeSatiData(undefined);
          return;
        }
        
        const saturnHouse = saturnPlacement.house;
        
        // Calculate which sign Saturn is in based on house (rough estimate)
        const ascSign = astroData.asc.ascendantSignIndex;
        const saturnSignEstimate = ((ascSign + saturnHouse - 2) % 12) + 1;
        
        // Get accurate Saturn transit dates from astronomy-engine
        const location = { lat: selectedCity.lat, lon: selectedCity.lon };
        const saturnTransits = await calculateSaturnSignTransits(transitDate, location);

        // Store raw ingress/egress for use in the feeds pipeline
        if (saturnTransits) {
          setSaturnTransitsData({ ingressDate: saturnTransits.ingressDate, egressDate: saturnTransits.egressDate });
        } else {
          setSaturnTransitsData(null);
        }
        
        const sadeSati = calculateSadeSati({
          currentDate: transitDate.toISO()!,
          natalMoonSign: moonSign,
          saturnSign: saturnSignEstimate,
          saturnSignEntryDate: saturnTransits?.ingressDate // Use actual ingress date
        });
        
        setSadeSatiData({
          isActive: sadeSati.isActive,
          currentPhase: sadeSati.currentPhase?.phase as 'rising' | 'peak' | 'setting' | undefined,
          from: sadeSati.currentPhase?.from,
          to: sadeSati.currentPhase?.to,
          moonSign: moonSign,
          saturnSign: saturnSignEstimate,
          allPhases: sadeSati.allPhases.map(p => ({
            phase: p.phase,
            from: p.from,
            to: p.to
          })),
          totalFrom: sadeSati.totalPeriod?.from,
          totalTo: sadeSati.totalPeriod?.to
        });
      } catch (err) {
        console.error('[App] Error calculating Sade Sati:', err);
        setSadeSatiData(undefined);
      }
    };
    
    calculateAsync();
  }, [astroData, transitData, transitDate, selectedCity]);

  // Kaal Sarp Yoga calculation
  const kaalSarpData = useMemo(() => {
    if (!astroData) return undefined;
    
    try {
      // Convert placements object to array format
      const placementsArray = Object.entries(astroData.placements).map(([planet, data]) => ({
        planet: planet as PlanetName,
        house: data.house
      }));
      
      const yoga = checkKaalSarpYoga(placementsArray);
      return {
        isPresent: yoga.isPresent,
        present: yoga.isPresent, // Alias for backwards compatibility
        type: yoga.type,
        severity: yoga.type === 'none' ? undefined : yoga.type, // Map type to severity
        details: yoga.details,
        specificType: yoga.specificType,
        specificEffects: yoga.specificEffects,
        hasRajYogaCombination: yoga.hasRajYogaCombination,
        rahuHouse: yoga.rahuHouse,
        ketuHouse: yoga.ketuHouse,
        planetsBetween: yoga.planetsBetween
      };
    } catch (err) {
      console.error('[App] Error checking Kaal Sarp Yoga:', err);
      return undefined;
    }
  }, [astroData]);

  // Functional nature of planets
  const functionalNatureData = useMemo(() => {
    if (!astroData) return undefined;
    
    try {
      const ascSign = astroData.asc.ascendantSignIndex;
      return classifyFunctionalForLagna(ascSign);
    } catch (err) {
      console.error('[App] Error classifying functional nature:', err);
      return undefined;
    }
  }, [astroData]);

  // House interpretations (proprietary database content)
  const [houseInterpretations, setHouseInterpretations] = useState<HouseInterpretation[] | undefined>(undefined);

  useEffect(() => {
    if (!astroData) {
      setHouseInterpretations(undefined);
      return;
    }

    // Fetch house interpretations for chat context
    const fetchInterpretations = async () => {
      try {
        const longitudes: Partial<Record<PlanetName, number>> = {
          Sun: astroData.grahas.Sun?.lon,
          Moon: astroData.grahas.Moon?.lon,
          Mercury: astroData.grahas.Mercury?.lon,
          Venus: astroData.grahas.Venus?.lon,
          Mars: astroData.grahas.Mars?.lon,
          Jupiter: astroData.grahas.Jupiter?.lon,
          Saturn: astroData.grahas.Saturn?.lon,
          Rahu: astroData.nodes.Rahu,
          Ketu: astroData.nodes.Ketu
        };

        const interpretations = await fetchHouseInterpretations(
          astroData.asc.ascendantSignIndex,
          astroData.placements,
          longitudes
        );



        setHouseInterpretations(interpretations);
      } catch (err) {
        console.error('[App] Error fetching house interpretations:', err);
        setHouseInterpretations(undefined);
      }
    };

    fetchInterpretations();
  }, [astroData]);

  function handleCalculate() {
    try {
      setCalcError(null);
      if (!selectedCity || !localDateTime) throw new Error('Missing city or local time.');

      const localDT = DateTime.fromISO(localDateTime, { zone: selectedCity.tz });
      if (!localDT.isValid) throw new Error(`Invalid local time: ${localDateTime}`);
      const utcDT = localDT.toUTC().toISO();
      if (!utcDT) throw new Error('UTC conversion failed.');

      setUtcDateTime(utcDT);

      const moon = getMoonNakshatraAndRashi(utcDT, selectedCity.lat, selectedCity.lon, ayanProvider);
      const asc  = getAscendantSignAndNakshatra(utcDT, selectedCity.lat, selectedCity.lon, ayanProvider);

      const ascLonDeg = asc.siderealLongitudeDeg;
      const cusps = getWholeSignCuspsSidereal(ascLonDeg);

      const grahas = getGrahaLongitudesAndMotionSidereal(
        { isoDate: utcDT, latitude: selectedCity.lat, longitude: selectedCity.lon },
        ayanProvider
      );

      const nodes = getRahuKetuSidereal(
        { isoDate: utcDT, latitude: selectedCity.lat, longitude: selectedCity.lon },
        ayanProvider
      );

      const planetLongs: Partial<Record<PlanetName, number>> = {
        Sun:     grahas.Sun.lon,
        Moon:    grahas.Moon.lon,
        Mercury: grahas.Mercury.lon,
        Venus:   grahas.Venus.lon,
        Mars:    grahas.Mars.lon,
        Jupiter: grahas.Jupiter.lon,
        Saturn:  grahas.Saturn.lon,
        Rahu:    nodes.Rahu,
        Ketu:    nodes.Ketu
      };

      const placements = assignPlanetsToHouses(cusps, planetLongs);

      const aspects = getAspects(cusps, planetLongs, {
        mode: 'house',
        includeNodes: true,
        nodesUseSeven: true
      });

      const combustMap = getCombustionStatusSidereal(
        { isoDate: utcDT, latitude: selectedCity.lat, longitude: selectedCity.lon },
        ayanProvider
      );

      const debilMap: Partial<Record<PlanetName, boolean>> = {};
      const exaltMap: Partial<Record<PlanetName, boolean>> = {};
      const conjunctDebilMap: Partial<Record<PlanetName, boolean>> = {};
      const conjunctExaltMap: Partial<Record<PlanetName, boolean>> = {};

      // Natural dignity (planet in its exaltation/debilitation sign)
      (['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn'] as const).forEach(p => {
        const signNum = signNumFromLon(grahas[p].lon);
        debilMap[p] = DEBIL_SIGN[p] === signNum;
        exaltMap[p] = EXALT_SIGN[p] === signNum;
      });

      // Conjunct dignity (planet conjunct with lord of its exalt/debil sign)
      const enhanced = getEnhancedExaltDebil(
        placements,
        {
          Sun: grahas.Sun.lon, Moon: grahas.Moon.lon, Mars: grahas.Mars.lon,
          Mercury: grahas.Mercury.lon, Jupiter: grahas.Jupiter.lon, Venus: grahas.Venus.lon,
          Saturn: grahas.Saturn.lon, Rahu: nodes.Rahu, Ketu: nodes.Ketu
        }
      );

      // Store conjunct dignity separately AND merge into main maps for chart display
      (['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn'] as const).forEach(p => {
        conjunctExaltMap[p] = enhanced.exaltEnhanced[p] || false;
        conjunctDebilMap[p] = enhanced.debilEnhanced[p] || false;
        
        // Merge conjunct dignity into main maps so chart displays arrows
        if (enhanced.exaltEnhanced[p]) {
          exaltMap[p] = true;
        }
        if (enhanced.debilEnhanced[p]) {
          debilMap[p] = true;
        }
      });

      const isDaytime = localDT.hour >= 6 && localDT.hour < 18;

      const shadInput = {
        longitudes: {
          Sun: grahas.Sun.lon, Moon: grahas.Moon.lon, Mars: grahas.Mars.lon,
          Mercury: grahas.Mercury.lon, Jupiter: grahas.Jupiter.lon, Venus: grahas.Venus.lon,
          Saturn: grahas.Saturn.lon, Rahu: nodes.Rahu, Ketu: nodes.Ketu
        } as Record<PlanetName, number>,
        placements: Object.fromEntries(
          Object.entries(placements).map(([k, v]) => [k, v.house])
        ) as Record<PlanetName, number>,
        cusps,
        retroMap: {
          Sun: grahas.Sun.retrograde, Moon: grahas.Moon.retrograde,
          Mercury: grahas.Mercury.retrograde, Venus: grahas.Venus.retrograde,
          Mars: grahas.Mars.retrograde, Jupiter: grahas.Jupiter.retrograde,
          Saturn: grahas.Saturn.retrograde, Rahu: false, Ketu: false
        } as Record<PlanetName, boolean>,
        isDaytime,
      };

      // Compute vargas for classical Shadbala (Saptavargaja Bala)
      const vargasForShadbala = computeImplementedVargas(
        asc.siderealLongitudeDeg,
        {
          Sun: grahas.Sun.lon, Moon: grahas.Moon.lon, Mars: grahas.Mars.lon,
          Mercury: grahas.Mercury.lon, Jupiter: grahas.Jupiter.lon, Venus: grahas.Venus.lon,
          Saturn: grahas.Saturn.lon, Rahu: nodes.Rahu, Ketu: nodes.Ketu
        }
      );

      // Calculate five-fold friendships for personalized Shadbala
      const fiveFoldFriendships = calculateFiveFoldFriendships(placements);

      const chartDateObj = new Date(utcDT);
      const pct = computeShadbalaPercentages(shadInput, { 
        chartDate: chartDateObj, 
        vargas: vargasForShadbala,
        fiveFoldFriendships 
      });
      const ratio = estimateShadbalaRatios(shadInput, { 
        chartDate: chartDateObj, 
        vargas: vargasForShadbala,
        fiveFoldFriendships
      });

      const ik = computeIshtaKashtaPhala(shadInput, { isoDate: utcDT, formula: 'arithmetic' });
      setIkPhala(ik);

      setAstroData({ moon, asc, cusps, grahas, nodes, placements, aspects });
      setCombust(combustMap);
      setDebil(debilMap);
      setExalt(exaltMap);
      setConjunctDebil(conjunctDebilMap);
      setConjunctExalt(conjunctExaltMap);
      setShadbalaPct(pct);
      setShadbalaRatio(ratio);

      // D1 seed for other views
      const __divSeed = {
        utcDateTime: utcDT,
        ayanKey,
        ascSidereal: asc.siderealLongitudeDeg,
        cusps,
        longitudes: {
          Sun: grahas.Sun.lon, Moon: grahas.Moon.lon, Mars: grahas.Mars.lon,
          Mercury: grahas.Mercury.lon, Jupiter: grahas.Jupiter.lon, Venus: grahas.Venus.lon,
          Saturn: grahas.Saturn.lon, Rahu: nodes.Rahu, Ketu: nodes.Ketu
        },
        retroMap: {
          Sun: grahas.Sun.retrograde, Moon: grahas.Moon.retrograde,
          Mercury: grahas.Mercury.retrograde, Venus: grahas.Venus.retrograde,
          Mars: grahas.Mars.retrograde, Jupiter: grahas.Jupiter.retrograde,
          Saturn: grahas.Saturn.retrograde, Rahu: false, Ketu: false
        }
      };
      (window as any).__D1_LAST = __divSeed;
      window.dispatchEvent(new CustomEvent('d1:ready', { detail: __divSeed }));
      window.dispatchEvent(new CustomEvent('divisional:seed', { detail: __divSeed }));
      
      // Note: Chart data save moved to useEffect to wait for all state updates
    } catch (err: any) {
      console.error('Calculation error:', err);
      setCalcError(err?.message || String(err));
    }
  }

  // Save calculated chart data to birth_profiles for caching (uses new ChartData schema)
  async function saveChartDataToProfile() {
    // Prevent concurrent invocations (the calling useEffect can fire multiple times as
    // individual calculation signals resolve — without this guard each call would race
    // through the find-or-create path and insert duplicate primary profile rows).
    if (isSavingChartRef.current) {
      console.log('[App] saveChartDataToProfile skipped — already in progress');
      return;
    }
    isSavingChartRef.current = true;
    try {
      console.log('[App] saveChartDataToProfile called - selectedProfileId:', selectedProfileId, 'user:', user?.id);
      
      // Only save if user is logged in
      if (!user) {
        console.log('[App] Skipping chart save - no user logged in');
        return;
      }
      
      // Wait for all calculations to complete
      if (!astroData || !shadbalaPct || !ikPhala || !vargaMap || !utcDateTime || !localDateTime || !selectedCity) {
        console.log('[App] Skipping chart save - calculations incomplete', {
          hasAstroData: !!astroData,
          hasShadbala: !!shadbalaPct,
          hasIkPhala: !!ikPhala,
          hasVargas: !!vargaMap,
          hasUtc: !!utcDateTime,
          hasLocal: !!localDateTime,
          hasCity: !!selectedCity
        });
        return;
      }
      
      // Determine which profile to save to
      let targetProfileId = selectedProfileId;
      console.log('[App] Target profile ID before adjustment:', targetProfileId);
      
      // If viewing 'my_profile', find/create the birth_profiles row for this user's own chart.
      // Match by owner + family_of_profile_id IS NULL + DOB to correctly identify the own chart.
      // DOB matching is required for admin who also has consulting client charts with
      // family_of_profile_id IS NULL — without it we'd overwrite a client's chart data.
      if (selectedProfileId === 'my_profile' && savedRow) {
        const { data: existingProfiles } = await supabase
          .from('birth_profiles')
          .select('id')
          .eq('owner_id', user.id)
          .is('family_of_profile_id', null)
          .eq('dob_year',  savedRow.dob_year)
          .eq('dob_month', savedRow.dob_month)
          .eq('dob_day',   savedRow.dob_day)
          .limit(1);
        
        if (existingProfiles && existingProfiles.length > 0) {
          targetProfileId = existingProfiles[0].id;
        } else {
          const newProfile = {
            owner_id: user.id,
            full_name: savedRow.full_name,
            gender: savedRow.gender,
            relationship: 'self',
            dob_day: savedRow.dob_day,
            dob_month: savedRow.dob_month,
            dob_year: savedRow.dob_year,
            tob_hour: savedRow.tob_hour,
            tob_minute: savedRow.tob_minute,
            tob_second: savedRow.tob_second || 0,
            place_label: savedRow.place_label || '',
            place_lat: savedRow.place_lat || 0,
            place_lng: savedRow.place_lng || 0,
            place_timezone: savedRow.place_timezone || 'UTC',
            family_of_profile_id: null,
          };
          
          const { data: inserted, error: insertError } = await supabase
            .from('birth_profiles')
            .insert([newProfile])
            .select('id')
            .single();
          
          if (insertError) {
            // 23505 = unique_violation: another concurrent call already inserted.
            // Re-query to get the id that was just created.
            if ((insertError as any).code === '23505') {
              const { data: retryRow } = await supabase
                .from('birth_profiles')
                .select('id')
                .eq('owner_id', user.id)
                .is('family_of_profile_id', null)
                .limit(1)
                .maybeSingle();
              if (retryRow) {
                targetProfileId = retryRow.id;
              } else {
                console.error('[App] Could not recover from duplicate insert:', insertError);
                return;
              }
            } else {
              console.error('[App] Error creating birth_profiles entry:', insertError);
              return;
            }
          } else if (inserted) {
            targetProfileId = inserted.id;
            console.log('[App] Created birth_profiles entry:', targetProfileId);
            loadFamilyList(user.id);
          }
        }
      }
      
      if (!targetProfileId || targetProfileId === 'my_profile') {
        console.log('[App] Skipping chart data save - no valid profile ID');
        return;
      }
      
      console.log('[App] Starting chart data save for profile:', targetProfileId);
      
      // Build complete ChartData object according to new schema
      const planetsData = {} as Record<PlanetName, any>;
      
      Object.entries(astroData.placements).forEach(([planetName, placement]) => {
        const pName = planetName as PlanetName;
        let siderealLon: number;
        let speed: number;
        let retrograde: boolean;
        
        if (pName === 'Rahu') {
          siderealLon = astroData.nodes.Rahu;
          speed = 0;
          retrograde = true;
        } else if (pName === 'Ketu') {
          siderealLon = astroData.nodes.Ketu;
          speed = 0;
          retrograde = true;
        } else {
          const grahaName = pName as 'Sun'|'Moon'|'Mars'|'Mercury'|'Jupiter'|'Venus'|'Saturn';
          const grahaData = astroData.grahas[grahaName];
          siderealLon = grahaData.lon;
          speed = grahaData.speedDegPerDay;
          retrograde = grahaData.retrograde;
        }
        
        const signNum = Math.floor(siderealLon / 30) + 1;
        const signDeg = siderealLon % 30;
        const signName = RASHI_NAMES[signNum - 1];
        const nakshatraIndex = Math.floor((siderealLon / 360) * 27);
        const nakshatraName = NAKSHATRA_NAMES[nakshatraIndex];
        const nakshatraPada = getNakshatraPadaFromLongitude(siderealLon);
        
        planetsData[pName] = {
          lon: siderealLon,
          speed,
          retrograde,
          sign: signNum,
          signName,
          nakshatra: nakshatraName,
          nakshatra_pada: nakshatraPada.pada,
          house: placement.house,
          offsetDeg: placement.offsetDeg
        };
      });
      
      // Calculate five-fold friendships for complete chart data
      const fiveFoldFriendships = calculateFiveFoldFriendships(astroData.placements);
      
      // Enrich placements with nakshatra data from planetsData
      const enrichedPlacements: any = {};
      Object.entries(astroData.placements).forEach(([planetName, placement]) => {
        const pName = planetName as PlanetName;
        
        enrichedPlacements[pName] = {
          ...placement,
          nakshatra: planetsData[pName]?.nakshatra,
          retrograde: planetsData[pName]?.retrograde,
          siderealLongitude: planetsData[pName]?.lon,
          signDeg: planetsData[pName]?.lon % 30
        };
      });
      
      // Build complete ChartData according to new schema
      const chartData: ChartData = {
        version: CURRENT_CHART_DATA_VERSION,
        calculated_at: new Date().toISOString(),
        ayanamsa: {
          type: ayanKey,
          value: ayanProvider(new Date(utcDateTime))
        },
        utc_datetime: utcDateTime,
        local_datetime: localDateTime,
        planets: planetsData,
        placements: enrichedPlacements,
        ascendantSign: astroData.asc.ascendantSignIndex,
        ascendant: {
          lon: astroData.asc.siderealLongitudeDeg,
          sign: astroData.asc.ascendantSignIndex,
          signName: astroData.asc.ascendantSign,
          nakshatra: astroData.asc.ascendantNakshatra,
          nakshatra_pada: astroData.asc.nakIndex
        },
        house_system: 'whole-sign',
        cusps: astroData.cusps,
        vargas: vargaMap,
        shadbala: shadbalaPct ? Object.fromEntries(
          Object.entries(shadbalaPct).map(([planet, data]) => [
            planet,
            {
              components: data.components,
              total_pct: data.totalPct,
              virupa: shadbalaRatio?.[planet as CoreGrahaName]?.virupa || 0,
              ratio: shadbalaRatio?.[planet as CoreGrahaName]?.ratio
            }
          ])
        ) as any : {},
        ishta_kashta: ikPhala ? Object.fromEntries(
          Object.entries(ikPhala).map(([planet, data]) => [
            planet,
            {
              uccha_bala: data.ucchaBala,
              cheshta_bala: data.cheshtaBala,
              ishta: data.ishta,
              kashta: data.kashta,
              ishta_pct: data.ishtaPct,
              kashta_pct: data.kashtaPct
            }
          ])
        ) as any : {},
        five_fold_friendships: (fiveFoldFriendships || []).map(f => ({
          from: f.from,
          to: f.to,
          score: f.score,
          classification: f.score >= 2 ? 'Great Friend' : f.score === 1 ? 'Friend' : f.score === 0 ? 'Neutral' : f.score === -1 ? 'Enemy' : 'Great Enemy'
        })),
        combustion: combust ? Object.fromEntries(
          Object.entries(combust).map(([planet, data]) => [
            planet,
            {
              combust: data.combust,
              separation_deg: data.sepDeg,
              threshold_deg: data.thresholdDeg
            }
          ])
        ) as any : {},
        exaltation: Object.fromEntries(
          Object.keys(planetsData).map(p => {
            const planet = p as PlanetName;
            const planetSign = planetsData[planet].sign;
            const ownSign = OWN_SIGN_SV[planet as Exclude<PName, 'Rahu'|'Ketu'>]?.includes(planetSign) || false;
            const moolatrikona = MOOLATRIKONA_SV[planet as Exclude<PName, 'Rahu'|'Ketu'>] === planetSign;
            
            return [planet, {
              exalted: exalt?.[planet] || false,
              debilitated: debil?.[planet] || false,
              own_sign: ownSign,
              moolatrikona: moolatrikona,
              conjunct_exalt_lord: conjunctExalt?.[planet] || false,
              conjunct_debil_lord: conjunctDebil?.[planet] || false
            }];
          })
        ) as any,
        aspects: astroData.aspects.map(asp => ({
          from: asp.from as PlanetName,
          from_house: asp.fromHouse,
          to: asp.toHouse,
          type: asp.kind,
          is_benefic: ['Jupiter', 'Venus', 'Moon', 'Mercury'].includes(asp.from)
        })),
        functional_nature: classifyFunctionalForLagna(astroData.asc.ascendantSignIndex) as any,
        house_scores: d1HouseScores ? Object.fromEntries(
          Object.entries(d1HouseScores).map(([house, score]) => [parseInt(house), score])
        ) as Record<HouseNumber, number> : undefined,
        blended_planet_pct: blendedPlanetPct ?? undefined,
        vimshottariTimeline: (() => {
          // Save current dasha snapshot (MD/AD/PD/SD) using same functions as VimshottariDashaTimeline
          // This ensures perfect consistency between cached data and UI display
          if (!utcDateTime || !astroData.moon) return [];
          
          try {
            const centerDt = DateTime.fromISO(utcDateTime);
            if (!centerDt.isValid) return [];
            
            const centerISO = centerDt.toISO();
            if (!centerISO) return [];
            
            // Use getCurrentDashaLevels from vimshottari.ts (same as UI component uses)
            const currentLevels = getCurrentDashaLevels(utcDateTime, astroData.moon.siderealLongitudeDeg, centerISO);
            if (!currentLevels || !currentLevels.md) return [];
            
            const timeline: any[] = [];
            
            // Helper to get house placement, rulership, and aspects for a planet
            const getPlanetData = (planetName: string) => {
              const placement = astroData.placements[planetName as PlanetName];
              const house = placement?.house || 1;
              
              // Get ruled houses using sign lords
              const rulesHouses: number[] = [];
              const SIGN_LORD: Record<number, string> = {
                1:'Mars', 2:'Venus', 3:'Mercury', 4:'Moon', 5:'Sun', 6:'Mercury',
                7:'Venus', 8:'Mars', 9:'Jupiter', 10:'Saturn', 11:'Saturn', 12:'Jupiter'
              };
              
              for (let sign = 1; sign <= 12; sign++) {
                if (SIGN_LORD[sign] === planetName) {
                  // Convert sign to house number based on ascendant
                  const houseNum = ((sign - astroData.asc.ascendantSignIndex + 12) % 12) + 1;
                  rulesHouses.push(houseNum);
                }
              }
              
              // Get aspect houses (Parashara aspects)
              const aspectsHouses: number[] = [];
              if (planetName === 'Mars') {
                // 4th, 7th, 8th aspects
                aspectsHouses.push(((house + 3 - 1) % 12) + 1, ((house + 6 - 1) % 12) + 1, ((house + 7 - 1) % 12) + 1);
              } else if (planetName === 'Jupiter') {
                // 5th, 7th, 9th aspects
                aspectsHouses.push(((house + 4 - 1) % 12) + 1, ((house + 6 - 1) % 12) + 1, ((house + 8 - 1) % 12) + 1);
              } else if (planetName === 'Saturn') {
                // 3rd, 7th, 10th aspects
                aspectsHouses.push(((house + 2 - 1) % 12) + 1, ((house + 6 - 1) % 12) + 1, ((house + 9 - 1) % 12) + 1);
              } else {
                // All planets aspect 7th house
                aspectsHouses.push(((house + 6 - 1) % 12) + 1);
              }
              
              return { house, rules: rulesHouses, aspects: aspectsHouses };
            };
            
            // Build timeline array with all 4 levels
            const levels = [
              { key: 'md', data: currentLevels.md },
              { key: 'ad', data: currentLevels.ad },
              { key: 'pd', data: currentLevels.pd },
              { key: 'sd', data: currentLevels.sd }
            ];
            
            for (const { key, data } of levels) {
              if (data) {
                const planetData = getPlanetData(data.lord);
                timeline.push({
                  level: key.toUpperCase() as 'MD' | 'AD' | 'PD' | 'SD',
                  planet: data.lord,
                  house: planetData.house,
                  rules: planetData.rules,
                  aspects: planetData.aspects,
                  from: data.start,
                  to: data.end
                });
              }
            }
            
            console.log('[App] Built vimshottariTimeline with', timeline.length, 'levels:', 
              timeline.map(t => `${t.level}=${t.planet}`).join(', '));
            return timeline;
          } catch (err) {
            console.error('[App] Error building vimshottariTimeline:', err);
            return [];
          }
        })()
      };
      
      // ── Safety check: verify chart was computed from the profile's own birth data ──
      // Fetch the birth_profiles row to confirm DOB/time matches what we computed.
      // This prevents saving Chart-A's astroData to Profile-B's chart_data when
      // selectedProfileId changes before the new calculation completes.
      const { data: profileRow } = await supabase
        .from('birth_profiles')
        .select('dob_year, dob_month, dob_day, tob_hour, tob_minute')
        .eq('id', targetProfileId)
        .maybeSingle();
      if (profileRow) {
        const profileDobYear  = Number(profileRow.dob_year);
        const profileDobMonth = Number(profileRow.dob_month);
        const profileDobDay   = Number(profileRow.dob_day);
        const profileTobHour  = Number(profileRow.tob_hour ?? 0);
        const profileTobMin   = Number(profileRow.tob_minute ?? 0);
        // utcDateTime is in the local timezone of the birth place — compare date portion only
        // (UTC vs local offset can shift by ±1 day; check year+month+day OR ±1 day tolerance)
        const chartDt = new Date(chartData.local_datetime || chartData.utc_datetime || '');
        const chartYear  = chartDt.getFullYear();
        const chartMonth = chartDt.getMonth() + 1;
        const chartDay   = chartDt.getDate();
        const chartHour  = chartDt.getHours();
        const chartMin   = chartDt.getMinutes();
        const dobMatch = chartYear === profileDobYear && chartMonth === profileDobMonth && chartDay === profileDobDay;
        const tobClose = Math.abs((chartHour * 60 + chartMin) - (profileTobHour * 60 + profileTobMin)) <= 2;
        if (!dobMatch) {
          console.warn(
            `[App] ⚠️ CHART DATA MISMATCH — Skipping save to profile ${targetProfileId}.`,
            `Profile DOB: ${profileDobYear}-${profileDobMonth}-${profileDobDay} ${profileTobHour}:${profileTobMin}`,
            `Chart local_datetime: ${chartData.local_datetime} (year=${chartYear} month=${chartMonth} day=${chartDay})`
          );
          return;
        }
        if (!tobClose) {
          console.warn(
            `[App] ⚠️ Birth time mismatch (>${2}min) for profile ${targetProfileId} — saving anyway but flagging.`,
            `Profile: ${profileTobHour}:${profileTobMin}  Chart: ${chartHour}:${chartMin}`
          );
        }
      }
      // ── End safety check ──

      // Use helper function to save chart data
      const result = await saveChartDataHelper(targetProfileId, chartData);
      
      if (result.success) {
        console.log('[App] ✅ Chart data saved successfully for profile:', targetProfileId);
        console.log('[App] Cache size:', JSON.stringify(chartData).length, 'bytes');
        console.log('[App] Included house_scores:', !!chartData.house_scores);
        console.log('[App] Included blended_planet_pct:', !!chartData.blended_planet_pct);

        // Show notification preferences dialog if user hasn't seen it yet.
        // Only trigger for the user's OWN chart (my_profile), not family members.
        const isSelfChart = selectedProfileId === 'my_profile';
        if (user && isSelfChart) {
          try {
            const { data: prefRow } = await supabase
              .from('notification_preferences')
              .select('preferences_seen')
              .eq('user_id', user.id)
              .maybeSingle();
            if (!prefRow?.preferences_seen) {
              setShowNotifPreferences(true);
            }
          } catch (prefErr) {
            // Non-critical — don't block on this
            console.warn('[App] Could not check notification preferences:', prefErr);
          }
        }
      } else {
        console.error('[App] ❌ Error saving chart data:', result.error);
      }
    } catch (err) {
      console.error('[App] Exception saving chart data:', err);
    } finally {
      isSavingChartRef.current = false;
    }
  }

  // Run calculation AFTER state is committed
  useEffect(() => {
    if (!selectedCity || !localDateTime) return;
    handleCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcNonce]);

/* ----------------- Save & Calculate orchestration ----------------- */
useEffect(() => {
  const onSaveAndCalc = async (ev: any) => {
    try {
      const d = ev?.detail;

      if (d?.fullPayload) {
        await setCalculatorFromPayload(d.fullPayload as BirthFormPayload);
        return;
      }

      if (d?.city && d?.localDateTimeISO) {
        resetComputed();

        const c = d.city;
        const lon =
          typeof c.lon === 'number'
            ? c.lon
            : (typeof c.lng === 'number' ? c.lng : undefined);

        if (typeof c.lat !== 'number' || typeof lon !== 'number') {
          throw new Error('City payload is missing lat/lon.');
        }

        let tz =
          typeof c.tz === 'string' && c.tz
            ? c.tz
            : (typeof c.timezone === 'string' && c.timezone ? c.timezone : null);

        if (!tz) {
          tz = await resolveTimezoneForCoords(c.lat, lon);
        }

        if (!tz) {
          throw new Error('Could not determine timezone for selected city.');
        }

        const lat = Number(c.lat);
        const label = (c as any).label || tz;

        const opt: CityOption = {
          label,
          value: `${lat.toFixed(5)},${lon.toFixed(5)}|${tz}`,
          city: (c as any).city || (c as any).label || 'Unknown',
          region: (c as any).region ?? undefined,
          country: (c as any).country || '',
          lat,
          lon,
          lng: lon,
          tz,
          timezone: tz,
          source: ((c as any).source ?? 'static') as CityOption['source'],
        };

        setSelectedCity(opt);
        setLocalDateTime(d.localDateTimeISO);
        setCalcNonce(n => n + 1);
        return;
      }

      setCalcNonce(n => n + 1);
    } catch (e) {
      console.error('Save & Calculate pipeline failed:', e);
      setCalcError(e instanceof Error ? e.message : String(e));
    }
  };

  window.addEventListener('calculator:saveAndCalculate', onSaveAndCalc);
  return () => window.removeEventListener('calculator:saveAndCalculate', onSaveAndCalc);
}, [resetComputed, setCalculatorFromPayload]);


  /* ----------------- View Density (Cozy / Compact) ----------------- */
  const [density, setDensity] = useState<'cozy'|'compact'>(
    (localStorage.getItem('sap_density') as any) || 'cozy'
  );
  useEffect(() => { localStorage.setItem('sap_density', density); }, [density]);

  /* ----------------- Functional benefic/malefic colors ----------------- */

  // ===== Chart birth data for MyChart component =====
  const chartBirth = useMemo(() => {
    if (!selectedCity || !utcDateTime) return null;
    const local = DateTime.fromISO(utcDateTime, { zone: 'utc' }).setZone(selectedCity.tz);
    const tzone = local.offset / 60;
    return {
      day: local.day,
      month: local.month,
      year: local.year,
      hour: local.hour,
      min: local.minute,
      sec: Math.floor(local.second || 0),
      lat: Number(selectedCity.lat.toFixed(6)),
      lon: Number(selectedCity.lon.toFixed(6)),
      tzone: Number(tzone.toFixed(2))
    };
  }, [selectedCity, utcDateTime]);

  // ===== BEGIN ADD: functional benefic/malefic/neutral colours =====
  const funcColor = useMemo(() => {
    if (!astroData) return (_: string) => '#64748b';
    const fmap = classifyFunctionalForLagna(astroData.asc.ascendantSignIndex) as
      Record<string, 'benefic' | 'malefic' | 'neutral' | string>;
    return (planetName: string) => {
      const tag = fmap[planetName] ?? 'neutral';
      if (tag === 'benefic' || tag === 'functional_benefic' || tag === 'yoga_karaka') return '#16a34a';
      if (tag === 'malefic'  || tag === 'functional_malefic') return '#dc2626';
      return '#d4b106';
    };
  }, [astroData]);
  // ===== END ADD

  // ===== BEGIN ADD: house lords and functional benefics for Vastu =====
  const houseLords = useMemo(() => {
    if (!astroData) return null;
    const lords: Record<number, PlanetName> = {};
    for (let h = 1; h <= 12; h++) {
      const signIdx = astroData.cusps[h];
      lords[h] = SIGN_LORDS[signIdx];
    }
    return lords;
  }, [astroData]);

  const functionalBenefics = useMemo(() => {
    if (!astroData) return null;
    const fmap = classifyFunctionalForLagna(astroData.asc.ascendantSignIndex) as
      Record<string, string>;
    const benefics = new Set<PlanetName>();
    for (const planet of PLANET_ORDER) {
      const tag = fmap[planet];
      if (tag === 'benefic' || tag === 'yoga_karaka') {
        benefics.add(planet as PlanetName);
      }
    }
    return benefics;
  }, [astroData]);

  // Functional malefics calculation
  const functionalMalefics = useMemo(() => {
    if (!astroData) return null;
    const fmap = classifyFunctionalForLagna(astroData.asc.ascendantSignIndex) as
      Record<string, string>;
    const malefics = new Set<PlanetName>();
    for (const planet of PLANET_ORDER) {
      const tag = fmap[planet];
      if (tag === 'malefic') {
        malefics.add(planet as PlanetName);
      }
    }
    return malefics;
  }, [astroData]);

  // Yogakaraka planets calculation
  const yogakarakas = useMemo(() => {
    if (!astroData) return null;
    const fmap = classifyFunctionalForLagna(astroData.asc.ascendantSignIndex) as
      Record<string, string>;
    const yogas = new Set<PlanetName>();
    for (const planet of PLANET_ORDER) {
      const tag = fmap[planet];
      if (tag === 'yoga_karaka') {
        yogas.add(planet as PlanetName);
      }
    }
    return yogas;
  }, [astroData]);

  const simplePlacements = useMemo(() => {
    if (!astroData) return null;
    const simple: Record<string, number> = {};
    for (const [planet, placement] of Object.entries(astroData.placements)) {
      simple[planet] = typeof placement === 'object' ? placement.house : placement;
    }
    return simple;
  }, [astroData]);
  // ===== END ADD

  // ===== BEGIN ADD: compact graha lon map =====
  const grahaLon = useMemo(() => {
    if (!astroData) return null;
    return {
      Sun:     { lon: astroData.grahas.Sun.lon,     retro: astroData.grahas.Sun.retrograde },
      Moon:    { lon: astroData.grahas.Moon.lon,    retro: astroData.grahas.Moon.retrograde },
      Mars:    { lon: astroData.grahas.Mars.lon,    retro: astroData.grahas.Mars.retrograde },
      Mercury: { lon: astroData.grahas.Mercury.lon, retro: astroData.grahas.Mercury.retrograde },
      Jupiter: { lon: astroData.grahas.Jupiter.lon, retro: astroData.grahas.Jupiter.retrograde },
      Venus:   { lon: astroData.grahas.Venus.lon,   retro: astroData.grahas.Venus.retrograde },
      Saturn:  { lon: astroData.grahas.Saturn.lon,  retro: astroData.grahas.Saturn.retrograde },
      Rahu:    { lon: astroData.nodes.Rahu,         retro: false },
      Ketu:    { lon: astroData.nodes.Ketu,         retro: false },
    } as const;

  }, [astroData]);
  // ===== END ADD

  // ===== BEGIN ADD: Report data for Report Builder =====
  const reportData = useMemo((): any => {
    if (!astroData || !selectedCity || !utcDateTime) return null;

    const local = DateTime.fromISO(utcDateTime, { zone: 'utc' }).setZone(selectedCity.tz);
    
    // Get user name
    let rawName: string | null =
      (selectedProfileId === 'my_profile'
        ? savedRow?.full_name
        : profiles.find(p => p.id === selectedProfileId)?.full_name) ?? null;
    if (!rawName || rawName.trim().length < 2) {
      rawName = savedRow?.full_name || 'Chart User';
    }

    // Build planet data
    const planets = [
      { name: 'Sun', graha: astroData.grahas.Sun, node: null },
      { name: 'Moon', graha: astroData.grahas.Moon, node: null },
      { name: 'Mars', graha: astroData.grahas.Mars, node: null },
      { name: 'Mercury', graha: astroData.grahas.Mercury, node: null },
      { name: 'Jupiter', graha: astroData.grahas.Jupiter, node: null },
      { name: 'Venus', graha: astroData.grahas.Venus, node: null },
      { name: 'Saturn', graha: astroData.grahas.Saturn, node: null },
      { name: 'Rahu', graha: null, node: astroData.nodes.Rahu },
      { name: 'Ketu', graha: null, node: astroData.nodes.Ketu }
    ].map(({ name, graha, node }) => {
      const lon = graha?.lon ?? node ?? 0;
      const signIdx = Math.floor(lon / 30);
      const degInSign = lon % 30;
      const house = astroData.placements[name as keyof typeof astroData.placements]?.house ?? 1;
      const nakIdx = Math.floor((lon % 360) / (360 / 27));
      const pada = Math.floor((lon % (360/27)) / (360/27/4)) + 1;
      
      return {
        name,
        sign: RASHI_NAMES[signIdx] || 'Unknown',
        house,
        degree: Math.round(degInSign * 100) / 100,
        nakshatra: getNakshatraName(nakIdx),
        pada,
        lord: getSignLord(signIdx),
        retrograde: graha?.retrograde ?? false,
        combusted: (combust as any)?.[name] ?? false,
        dignity: getDignity(name, signIdx)
      };
    });

    // Build houses data
    const houses = astroData.cusps.map((cusp, idx) => {
      const houseNum = idx + 1;
      const signIdx = Math.floor(cusp / 30);
      return {
        number: houseNum,
        sign: RASHI_NAMES[signIdx] || 'Unknown',
        lord: getSignLord(signIdx),
        cusp: Math.round(cusp * 100) / 100
      };
    });

    // Build dasha data from Vimshottari calculations
    const dashas: any[] = [];
    if (utcDateTime && astroData.moon) {
      try {
        const centerDt = DateTime.fromISO(utcDateTime);
        if (!centerDt.isValid) {
          console.error('Invalid centerDt from utcDateTime:', utcDateTime);
        } else {
          const mdList = computeMahadashaTimelineAround(utcDateTime, astroData.moon.siderealLongitudeDeg, centerDt, 50);
          
          // Get current dasha levels
          const centerISO = centerDt.toISO();
          if (centerISO) {
            const currentLevels = getCurrentDashaLevels(utcDateTime, astroData.moon.siderealLongitudeDeg, centerISO);
            
            // Add all mahadashas
            mdList.forEach(md => {
              const isCurrent = currentLevels?.md?.lord === md.lord;
              dashas.push({
                planet: md.lord,
                from: md.start.split('T')[0],
                to: md.end.split('T')[0],
                level: 'MD',
                isCurrent
              });
            });
          }
        }
      } catch (err) {
        console.error('Failed to compute dashas:', err);
      }
    }

    // Build feeds data (will be populated later when dasha context is available)
    const feeds: any[] = [];

    // Generate chart images (D1, D9, D10)
    let chartImages = { d1: undefined, d9: undefined, d10: undefined };
    try {
      if (astroData && astroData.grahas && astroData.asc) {
        // Prepare placements for chart generation using grahas
        const placementsForChart: Record<string, any> = {};
        const planetNames = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
        
        planetNames.forEach(name => {
          const graha = astroData.grahas[name as keyof typeof astroData.grahas];
          if (graha) {
            const placement = astroData.placements[name as keyof typeof astroData.placements];
            placementsForChart[name] = {
              house: placement?.house || 1,
              sign: Math.floor(graha.lon / 30) + 1,
              siderealLongitudeDeg: graha.lon,
              retrograde: graha.retrograde
            };
          }
        });

        // Add nodes
        if (astroData.nodes) {
          const rahuPlacement = astroData.placements.Rahu;
          const ketuPlacement = astroData.placements.Ketu;
          placementsForChart['Rahu'] = {
            house: rahuPlacement?.house || 1,
            sign: Math.floor(astroData.nodes.Rahu / 30) + 1,
            siderealLongitudeDeg: astroData.nodes.Rahu,
            retrograde: false
          };
          placementsForChart['Ketu'] = {
            house: ketuPlacement?.house || 1,
            sign: Math.floor(astroData.nodes.Ketu / 30) + 1,
            siderealLongitudeDeg: astroData.nodes.Ketu,
            retrograde: false
          };
        }

        // Add ascendant
        placementsForChart['Ascendant'] = {
          house: 1,
          sign: astroData.asc.ascendantSignIndex,
          siderealLongitudeDeg: astroData.asc.siderealLongitudeDeg,
          retrograde: false
        };

        // Prepare vargas data if available
        const vargasForChart = vargaMap ? {
          D9: vargaMap.D9 ? Object.entries(vargaMap.D9).reduce((acc: any, [name, data]: [string, any]) => {
            acc[name] = {
              house: data.house || 1,
              sign: data.signIndex || 1
            };
            return acc;
          }, {}) : undefined,
          D10: vargaMap.D10 ? Object.entries(vargaMap.D10).reduce((acc: any, [name, data]: [string, any]) => {
            acc[name] = {
              house: data.house || 1,
              sign: data.signIndex || 1
            };
            return acc;
          }, {}) : undefined
        } : undefined;

        chartImages = generateChartImages(
          placementsForChart,
          astroData.asc.ascendantSignIndex,
          vargasForChart
        ) as any;
      }
    } catch (err) {
      console.error('Failed to generate chart images:', err);
    }

    return {
      profile: {
        name: rawName,
        dob: `${String(local.day).padStart(2, '0')}/${String(local.month).padStart(2, '0')}/${local.year}`,
        tob: `${String(local.hour).padStart(2, '0')}:${String(local.minute).padStart(2, '0')}`,
        location: selectedCity.label,
        latitude: selectedCity.lat,
        longitude: selectedCity.lon
      },
      charts: chartImages,
      planets,
      houses,
      dashas,
      feeds,
      // Add strength calculations for report tables
      planetFactors: {
        shadbala: shadbalaPct,
        ishtaKashta: ikPhala,
        blendedPct: null // Will be populated later when available
      },
      // House scores will be patched in later
      houseScores: null
    };
  }, [astroData, selectedCity, utcDateTime, selectedProfileId, savedRow, profiles, combust, vargaMap, shadbalaPct, ikPhala]);

  // Helper functions for reportData
  function getSignLord(signIdx: number): string {
    const lords = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
    return lords[signIdx] || 'Unknown';
  }

  function getNakshatraName(nakIdx: number): string {
    const nakshatras = [
      'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu', 
      'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta', 
      'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 
      'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha', 
      'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
    ];
    return nakshatras[nakIdx] || 'Unknown';
  }

  function getDignity(planet: string, signIdx: number): string {
    const exaltations: Record<string, number> = { Sun: 0, Moon: 1, Mars: 9, Mercury: 5, Jupiter: 3, Venus: 11, Saturn: 6 };
    const debilitations: Record<string, number> = { Sun: 6, Moon: 7, Mars: 3, Mercury: 11, Jupiter: 9, Venus: 5, Saturn: 0 };
    
    if (exaltations[planet] === signIdx) return 'Exalted';
    if (debilitations[planet] === signIdx) return 'Debilitated';
    
    const ownSigns: Record<string, number[]> = {
      Sun: [4], Moon: [3], Mars: [0, 7], Mercury: [2, 5], 
      Jupiter: [8, 11], Venus: [1, 6], Saturn: [9, 10]
    };
    if (ownSigns[planet]?.includes(signIdx)) return 'Own Sign';
    
    return 'Neutral';
  }
  // ===== END ADD

    // === INSERT-START: top-level Shodasha inputs derived from astroData ===
const shIn = useMemo(() => {
  if (!astroData) return null;
  return {
    ascLonDeg: astroData.asc.siderealLongitudeDeg,
    planetLongitudesDeg: {
      Sun: astroData.grahas.Sun.lon,
      Moon: astroData.grahas.Moon.lon,
      Mars: astroData.grahas.Mars.lon,
      Mercury: astroData.grahas.Mercury.lon,
      Jupiter: astroData.grahas.Jupiter.lon,
      Venus: astroData.grahas.Venus.lon,
      Saturn: astroData.grahas.Saturn.lon,
      Rahu: astroData.nodes.Rahu,
      Ketu: astroData.nodes.Ketu,
    } as const,
  };
}, [astroData]);
// === INSERT-END: top-level Shodasha inputs derived from astroData ===
// === INSERT-START: map Shadbala totals -> footer percentages for Shodashavarga ===
const shadbalaFooter = useMemo<Partial<Record<PlanetName, number>> | null>(() => {
  if (!shadbalaPct) return null;
  try {
    const out: Partial<Record<PlanetName, number>> = {};
    (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as const).forEach(p => {
      const row = (shadbalaPct as any)[p];
      if (row && typeof row.totalPct === 'number') out[p] = row.totalPct;
    });
    return out;
  } catch {
    return null;
  }
}, [shadbalaPct]);
// === INSERT-START: map Ista/Kasta -> footer rows for Shodashavarga ===
const ishtaKashtaFooter = useMemo<Partial<Record<PlanetName, { ishta: number; kashta: number }>> | null>(() => {
  if (!ikPhala) return null;
  try {
    const out: Partial<Record<PlanetName, { ishta: number; kashta: number }>> = {};
    (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as const).forEach(p => {
      const row = (ikPhala as any)[p];
      if (row && typeof row.ishta === 'number' && typeof row.kashta === 'number') {
        out[p] = { ishta: row.ishta, kashta: row.kashta };
      }
    });
    return out;
  } catch {
    return null;
  }
}, [ikPhala]);
// === INSERT-END: map Ista/Kasta -> footer rows for Shodashavarga ===

// === INSERT-END: map Shadbala totals -> footer percentages for Shodashavarga ===

// ===== BEGIN ADD: BlendedSigned% (per planet) + HouseScore (D1 only) =====
const svCalibratedPct = useMemo<Partial<Record<PName, number>> | null>(() => {
  if (!astroData) return null;
  try {
    const longs: Record<PName, number> = {
      Sun: astroData.grahas.Sun.lon,
      Moon: astroData.grahas.Moon.lon,
      Mars: astroData.grahas.Mars.lon,
      Mercury: astroData.grahas.Mercury.lon,
      Jupiter: astroData.grahas.Jupiter.lon,
      Venus: astroData.grahas.Venus.lon,
      Saturn: astroData.grahas.Saturn.lon,
      Rahu: astroData.nodes.Rahu,
      Ketu: astroData.nodes.Ketu,
    } as const;

    // Calculate five-fold friendships for personalized varga scoring
    const fiveFoldFriendships = calculateFiveFoldFriendships(astroData.placements);

    const vargaMap = computeImplementedVargas(astroData.asc.siderealLongitudeDeg, longs) as Record<string, any>;
    const VARGAS: Array<'D1'|'D2'|'D3'|'D4'|'D7'|'D9'|'D10'|'D12'|'D16'|'D20'|'D24'|'D27'|'D30'|'D40'|'D45'|'D60'> =
      ['D1','D2','D3','D4','D7','D9','D10','D12','D16','D20','D24','D27','D30','D40','D45','D60'];
    const PLANETS: PName[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

    const normalizeSign = (value: unknown): number => {
      const n = Number(value);
      if (!Number.isFinite(n)) return NaN;
      if (n >= 1 && n <= 12) return n;
      if (n >= 0 && n <= 11) return n + 1;
      return NaN;
    };

    const resolveSign = (v: any, planet: PName): number | undefined => {
      if (v?.planetSigns && typeof v.planetSigns[planet] !== 'undefined') {
        const ns = normalizeSign(v.planetSigns[planet]);
        return Number.isFinite(ns) ? ns : undefined;
      }
      const key = `${planet}Sign`;
      if (typeof v?.[key] !== 'undefined') {
        const ns = normalizeSign(v[key]);
        return Number.isFinite(ns) ? ns : undefined;
      }
      if (v?.planets && typeof v.planets[planet]?.sign !== 'undefined') {
        const ns = normalizeSign(v.planets[planet].sign);
        return Number.isFinite(ns) ? ns : undefined;
      }
      return undefined;
    };

    const svPct: Partial<Record<PName, number>> = {};

    for (const planet of PLANETS) {
      let sum01 = 0;
      let count = 0;

      for (const vk of VARGAS) {
        const v = vargaMap[vk];
        if (!v) continue;
        if ((planet === 'Rahu' || planet === 'Ketu') && vk !== 'D1') continue;

        const placements = vargaToPlacements(v);
        const lagnaSign = Number(v.ascSign);
        let sign = resolveSign(v, planet);

        if (!Number.isFinite(sign)) {
          const ph = placements[planet]?.house;
          if (Number.isFinite(ph)) {
            sign = add12(lagnaSign, Number(ph));
          }
        }
        if (!Number.isFinite(sign) && vk === 'D1') {
          sign = signNumFromLon(longs[planet]);
        }

        const sn = Number(sign);
        if (!Number.isFinite(sn)) continue;

        const planetHouse = placements[planet]?.house as number | undefined;
        const lord = getLordSV(sn);
        const lordHouse = placements[lord as Exclude<PName,'Rahu'|'Ketu'>]?.house as number | undefined;

        const score = baselineScoreSV(planet, sn, planetHouse, lordHouse, fiveFoldFriendships);
        const cell01 = Math.max(0, Math.min(1, (score + 2) / 4));
        sum01 += cell01;
        count += 1;
      }

      svPct[planet] = count ? Math.round((sum01 / count) * 100) : undefined;
    }

    return svPct;
  } catch {
    return null;
  }
}, [astroData]);

/**
 * Blended Planet Strength with Classical Vedic Adjustments
 * 
 * Combines: Varga Strength (SV) × Shadbala × Ishta-Kashta with tanh normalization
 * 
 * CLASSICAL ADJUSTMENTS:
 * 1. Viparita Raja Yoga: Debilitated planets in dusthana (3,6,8,11,12) → +15% to +25%
 *    - Reason: Weakness in difficult houses paradoxically produces good results
 *    - 8th house (most severe): +25%, 12th: +20%, others: +15%
 * 
 * 2. Exalted in Dusthana: Exalted planets in dusthana → -10% to -15%
 *    - Reason: Strength in difficult houses may amplify troubles
 *    - 8th house: -15%, 12th: -12%, others: -10%
 * 
 * 3. Natural Malefics in Dusthana: Sun/Mars/Saturn/Rahu/Ketu → +10% to +15%
 *    - Reason: Malefics control and thrive in difficult houses
 *    - 6th house (enemies): +15%, 3rd: +12%, others: +10%
 */
const blendedPlanetPct = useMemo(() => {
  if (!astroData || !svCalibratedPct) return null;

  const sbPct: Partial<Record<PName, number>> = {};
  if (shadbalaPct) {
    (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as PName[]).forEach(p => {
      const row = (shadbalaPct as any)[p];
      if (row && typeof row.totalPct === 'number') sbPct[p] = row.totalPct;
    });
  }

  const ik: Partial<Record<PName, { ishta: number; kashta: number }>> = {};
  if (ikPhala) {
    (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as PName[]).forEach(p => {
      const r = (ikPhala as any)[p];
      if (r && typeof r.ishta === 'number' && typeof r.kashta === 'number') {
        ik[p] = { ishta: r.ishta, kashta: r.kashta };
      }
    });
  }

  const K = 3.0;
  const blended: Partial<Record<PName, number>> = {};

  // Dusthana (difficult) houses: 3, 6, 8, 11, 12
  const DUSTHANA_HOUSES = new Set([3, 6, 8, 11, 12]);
  
  // Natural malefics (not functional - these are inherently malefic)
  const NATURAL_MALEFICS = new Set<PName>(['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu']);

  // Trishadaya houses (difficult): 3, 6, 11
  const TRISHADAYA_HOUSES = new Set([3, 6, 11]);
  
  // Upachaya houses (improve with time): 3, 6, 10, 11
  const UPACHAYA_HOUSES = new Set([3, 6, 10, 11]);

  // Helper: Get which houses a planet rules based on ascendant sign
  const getHousesRuledByPlanet = (planet: PName, ascendantSign: number): number[] => {
    if (planet === 'Rahu' || planet === 'Ketu') return []; // Nodes don't rule houses classically
    
    const SIGN_RULERS: Record<number, PName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const housesRuled: number[] = [];
    for (let house = 1; house <= 12; house++) {
      // Calculate which sign this house corresponds to (whole sign system)
      const houseSign = ((ascendantSign - 1 + house - 1) % 12) + 1;
      if (SIGN_RULERS[houseSign] === planet) {
        housesRuled.push(house);
      }
    }
    return housesRuled;
  };

  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as PName[]).forEach(p => {
    const sv = svCalibratedPct[p];
    const sb = sbPct[p];
    const pair = ik[p];
    if (!Number.isFinite(Number(sv)) || !Number.isFinite(Number(sb)) || !pair) {
      blended[p] = undefined;
      return;
    }

    const sv01 = Math.min(0.97, Math.max(0, Number(sv) / 100));
    const sb01 = Math.min(0.97, Math.max(0, Number(sb) / 100));
    const dirRaw = (pair.ishta - pair.kashta) / 60;
    const dirEff = Math.max(-0.85, Math.min(0.85, dirRaw));

    const base = sv01 * sb01 * dirEff;
    let signedPct = 100 * Math.tanh(K * base);

    // ===== VIPARITA RAJA YOGA & DUSTHANA HOUSE ADJUSTMENTS =====
    const planetHouse = astroData.placements[p]?.house;
    const isInDusthana = planetHouse && DUSTHANA_HOUSES.has(planetHouse);
    
    if (isInDusthana) {
      const isDebilitated = debil?.[p] === true;
      const isExalted = exalt?.[p] === true;
      const isNaturalMalefic = NATURAL_MALEFICS.has(p);

      // Viparita Raja Yoga: Debilitated planet in dusthana gives good results
      // Add bonus: +15% to +25% depending on house severity
      if (isDebilitated) {
        const dusthanaBonus = planetHouse === 8 ? 25 : planetHouse === 12 ? 20 : 15;
        signedPct += dusthanaBonus;
      }

      // Exalted planet in dusthana may amplify troubles
      // Deduct penalty: -10% to -15%
      if (isExalted) {
        const exaltPenalty = planetHouse === 8 ? 15 : planetHouse === 12 ? 12 : 10;
        signedPct -= exaltPenalty;
      }

      // Natural malefics do well in dusthana (they control difficulties)
      // Add bonus: +10% to +15%
      if (isNaturalMalefic && !isDebilitated) {
        const maleficBonus = planetHouse === 6 ? 15 : planetHouse === 3 ? 12 : 10;
        signedPct += maleficBonus;
      }
    }

    // ===== TRISHADAYA LORD IN UPACHAYA BONUS =====
    // When lord of 3rd, 6th, or 11th sits in Upachaya (3, 6, 10, 11), it gives good results
    const ascendantSign = astroData.asc.ascendantSignIndex;
    const housesRuled = getHousesRuledByPlanet(p, ascendantSign);
    const rulesTrishadaya = housesRuled.some(h => TRISHADAYA_HOUSES.has(h));
    const occupiesUpachaya = planetHouse && UPACHAYA_HOUSES.has(planetHouse);
    
    if (rulesTrishadaya && occupiesUpachaya) {
      const trishadayaBonus = planetHouse === 10 ? 18 : 12; // 10th house (career) is strongest
      signedPct += trishadayaBonus;
    }

    // ===== BHAVA SANDHI (CUSP PROXIMITY) ADJUSTMENT =====
    // Apply degree-based multiplier if global mode is enabled
    const bhavaSandhiMode = typeof window !== 'undefined' ? localStorage.getItem('bhavaSandhiMode') : null;
    if (bhavaSandhiMode === 'global' && planetHouse) {
      const placement = astroData.placements[p];
      if (placement?.offsetDeg !== undefined) {
        // Only apply to occupying planets (has house placement)
        signedPct = applyBhavaSandhiToStrength(signedPct, placement.offsetDeg, true);
      }
    }

    // Cap final result at -100 to +100
    signedPct = Math.max(-100, Math.min(100, signedPct));
    blended[p] = Math.round(signedPct * 10) / 10;
  });

  return blended;
}, [astroData, ikPhala, shadbalaPct, svCalibratedPct, debil, exalt]);

// ====== Bhava Sandhi Multipliers (for Planet Factors table display) ======
const bhavaSandhiMultipliers = useMemo(() => {
  if (!astroData) return null;
  const bhavaSandhiMode = typeof window !== 'undefined' ? localStorage.getItem('bhavaSandhiMode') : null;
  if (bhavaSandhiMode !== 'global') return null; // Only calculate when global mode is ON
  
  const multipliers: Partial<Record<PName, number>> = {};
  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as PName[]).forEach(p => {
    const placement = astroData.placements[p];
    if (placement?.offsetDeg !== undefined && placement.house) {
      // Only for occupying planets
      multipliers[p] = getBhavaSandhiMultiplier(placement.offsetDeg);
    }
  });
  return multipliers;
}, [astroData]);

// ====== D1 House Influence (shared with MyChart) ======
const d1AspectEdges = useMemo<AspectEdge[]>(() => {
  if (!astroData) return [];
  const seen = new Set<string>();
  const edges: AspectEdge[] = [];

  for (const row of astroData.aspects) {
    if (!row || row.mode !== 'house' || typeof row.toHouse !== 'number') continue;
    const key = `${row.from}-${row.toHouse}`;
    if (seen.has(key)) continue;
    seen.add(key);
    edges.push({ from: row.from as PName, toHouse: row.toHouse });
  }

  return edges;
}, [astroData]);

const d1HouseInfluence = useMemo(() => {
  if (!astroData) return null;
  const ascLon = astroData.asc?.siderealLongitudeDeg ?? null;
  const ascSign = typeof ascLon === 'number'
    ? toHouseIndex(Math.floor((((ascLon % 360) + 360) % 360) / 30) + 1)
    : 1;
  const funcMap = classifyFunctionalForLagna(ascSign) as Record<string, 'benefic' | 'malefic' | 'neutral'>;
  const mapped = d1AspectEdges.map(edge => ({
    from: edge.from,
    toHouse: edge.toHouse,
  }));
  const result = computeHouseInfluenceRows({
    cusps: astroData.cusps,
    placements: astroData.placements as any,
    primaryStrength: blendedPlanetPct ?? undefined,
    fallbackStrength: svCalibratedPct ?? undefined,
    aspects: undefined, // Let it auto-calculate Parashara aspects from placements
    autoParashariAspects: true, // Auto-calculate all aspects to all houses
    ascSign,
    functionalMap: funcMap,
  });
  
  // Enrich rows with activities from houses table
  if (result?.rows && housesData && astroData.placements) {
    result.rows.forEach(row => {
      // Find which house the lord is sitting in
      const lordPlacement = astroData.placements[row.lord];
      if (lordPlacement && lordPlacement.house) {
        // Get activities of that house
        const houseData = housesData.find(h => h.id === lordPlacement.house);
        if (houseData && houseData.activities) {
          row.activities = houseData.activities;
        }
      }
    });
  }
  
  return result;
}, [astroData, blendedPlanetPct, d1AspectEdges, svCalibratedPct, housesData]);

// Compute Bhava Bala (classical house strength) as percentage
const bhavaBalaRupas = useMemo(() => {
  if (!astroData?.asc?.siderealLongitudeDeg || !astroData?.placements || !d1AspectEdges || !shadbalaPct) {
    return null;
  }
  
  const ascSign = Math.floor(astroData.asc.siderealLongitudeDeg / 30) + 1;
  
  // Convert Shadbala to virupas for Bhavadhipati Bala
  // Astro-Sage uses actual virupas / 2, so we use 390 as scale factor
  const shadbalaVirupas: Partial<Record<PlanetName, number>> = {};
  for (const [planet, result] of Object.entries(shadbalaPct)) {
    const pct = typeof result === 'number' ? result : (result as any)?.totalPct ?? 0;
    // pct is 0-100 percentage, convert to virupas using 390 scale
    shadbalaVirupas[planet as PlanetName] = (pct / 100) * 390;
  }
  
  const bhavaBalaRows = computeBhavaBala({
    ascendantSign: ascSign,
    planetShadbalaVirupas: shadbalaVirupas,
    planetPlacements: astroData.placements as any,
    aspects: d1AspectEdges as any,
    planetStrengthPct: blendedPlanetPct as any,
    functionalMap: {}
  });
  
  // Return percentages indexed by house number
  const percentages: Record<number, number> = {};
  bhavaBalaRows.forEach((row, idx) => {
    percentages[idx + 1] = row.percentage;
  });
  
  return percentages;
}, [astroData, shadbalaPct, d1AspectEdges, blendedPlanetPct]);

// Lord dusthana penalties — computed from natal placements (permanent natal condition)
const lordDusthanaPenalties = useMemo<Record<number, LordDusthanaPenalty> | null>(() => {
  if (!astroData?.asc?.ascendantSignIndex || !astroData.placements) return null;
  const placements: Record<string, { house: number }> = {};
  Object.entries(astroData.placements).forEach(([p, data]: [string, any]) => {
    if (data?.house) placements[p] = { house: data.house };
  });
  return computeLordDusthanaPenalties(astroData.asc.ascendantSignIndex, placements);
}, [astroData]);

// d1HouseScores = Shadbala planetary influence totals ADJUSTED by lord dusthana penalties.
// Penalties flow automatically to: HouseScoresCompactView, LifeAreasDashboard, reportDataBuilder,
// all report sections, and AI context via chatContextData.
const d1HouseScores = useMemo(() => {
  if (!d1HouseInfluence) return null;
  const base = d1HouseInfluence.totals as Record<number, number>;
  if (!lordDusthanaPenalties || Object.keys(lordDusthanaPenalties).length === 0) return base;
  const adjusted: Record<number, number> = { ...base };
  for (const [h, info] of Object.entries(lordDusthanaPenalties)) {
    adjusted[Number(h)] = (adjusted[Number(h)] ?? 0) + info.penalty;
  }
  return adjusted;
}, [d1HouseInfluence, lordDusthanaPenalties]);

// Auto-save chart data when all calculations complete
useEffect(() => {
  if (astroData && blendedPlanetPct && svCalibratedPct && shadbalaPct && ikPhala && vargaMap) {
    (async () => {
      try {
        await saveChartDataToProfile();
        console.log('[App] ✅ Chart data saved successfully for profile:', selectedProfileId || user?.id);
        // Notify UserForm that calculation and save are complete
        window.dispatchEvent(new CustomEvent('calculation:complete', { 
          detail: { success: true, profileId: selectedProfileId || user?.id } 
        }));
      } catch (err) {
        console.error('[App] ❌ Failed to save chart data:', err);
        // Notify UserForm of error
        window.dispatchEvent(new CustomEvent('calculation:complete', { 
          detail: { success: false, error: err instanceof Error ? err.message : String(err) } 
        }));
      }
    })();
  }
}, [astroData, blendedPlanetPct, svCalibratedPct, shadbalaPct, ikPhala, vargaMap]);

// SWOT Analysis Handler - navigates to Chat and sends analysis prompt
const handleAnalyseChart = useCallback(() => {
  if (!astroData) {
    alert('Please calculate your chart first (Save & Calculate on the left)');
    return;
  }

  // Debug: Check if houseInterpretations are loaded

  if (!houseInterpretations || houseInterpretations.length === 0) {
    console.warn('[SWOT] WARNING: No house interpretations loaded. Database predictions will be missing.');
    const proceed = window.confirm(
      '⚠️ Database interpretations are not loaded yet.\n\n' +
      'The SWOT analysis will only include basic chart data (planet positions, strengths, yogas) but will miss:\n' +
      '• Planet-in-house predictions\n' +
      '• Lordship interpretations\n' +
      '• Nakshatra-specific notes\n' +
      '• Cautions and remedies\n\n' +
      'This usually happens if:\n' +
      '1. Database connection failed\n' +
      '2. Chart calculation is still in progress\n\n' +
      'Try:\n' +
      '• Click "Save & Calculate" again on the left panel\n' +
      '• Wait a few seconds for data to load\n' +
      '• Check your internet connection\n\n' +
      'Do you want to continue with basic analysis anyway?'
    );
    if (!proceed) {
      return;
    }
  }

  // Helper to get nakshatra name from longitude
  const getNakshatraName = (lon: number): string => {
    const idx = Math.floor((((lon % 360) + 360) % 360) / (360 / 27));
    return NAKSHATRA_NAMES[idx] || 'Unknown';
  };

  // Helper to get sign name from longitude
  const getSignName = (lon: number): string => {
    const idx = Math.floor((((lon % 360) + 360) % 360) / 30);
    return RASHI_NAMES[idx] || 'Unknown';
  };

  // Helper to get planet dignities
  const getDignities = (planet: PlanetName): string[] => {
    const dignities: string[] = [];
    if (exalt?.[planet]) dignities.push('Exalted');
    if (debil?.[planet]) dignities.push('Debilitated');
    if ((combust as any)?.[planet]?.combust) dignities.push('Combust');
    if (planet !== 'Rahu' && planet !== 'Ketu' && astroData.grahas[planet]?.retrograde) {
      dignities.push('Retrograde');
    }
    return dignities;
  };

  // Get functional classification for planets
  const ascSign = astroData.asc?.ascendantSignIndex ?? 1;
  const funcMap = classifyFunctionalForLagna(ascSign) as Record<string, 'benefic' | 'malefic' | 'neutral' | 'yoga_karaka'>;

  // Build comprehensive house collation data from astroData AND houseInterpretations
  let houseCollationData = '';
  
  for (let h = 1; h <= 12; h++) {
    const signIdx = Math.floor(astroData.cusps[h - 1] / 30);
    const signName = RASHI_NAMES[signIdx] || 'Unknown';
    const signLord = SIGN_LORDS[signIdx] as PlanetName;
    
    houseCollationData += `\n**House ${h} (${signName})**:\n`;
    
    // Get database interpretation for this house if available
    const houseInterp = houseInterpretations?.find(hi => hi.house === h);
    
    // Find planets in this house
    const planetsInHouse: Array<{ planet: PlanetName; lon: number }> = [];
    Object.entries(astroData.placements).forEach(([pName, placement]) => {
      if (placement.house === h) {
        const planet = pName as PlanetName;
        const lon = planet === 'Rahu' || planet === 'Ketu' 
          ? astroData.nodes[planet]
          : astroData.grahas[planet]?.lon ?? 0;
        planetsInHouse.push({ planet, lon });
      }
    });

    if (planetsInHouse.length > 0) {
      planetsInHouse.forEach(({ planet, lon }) => {
        const nakshatra = getNakshatraName(lon);
        const sign = getSignName(lon);
        const dignities = getDignities(planet);
        const blendedStr = blendedPlanetPct?.[planet] 
          ? ` (Strength: ${blendedPlanetPct[planet].toFixed(0)}%)` 
          : '';
        
        houseCollationData += `- **${planet}** in ${sign}, ${nakshatra} nakshatra`;
        if (dignities.length > 0) houseCollationData += ` [${dignities.join(', ')}]`;
        houseCollationData += blendedStr + `\n`;
        
        // Add functional nature
        const funcNature = funcMap?.[planet];
        if (funcNature) {
          houseCollationData += `  Functional: ${funcNature}\n`;
        }

        // **NEW: Add database interpretations for this planet in this house**
        if (houseInterp?.planets) {
          const planetInterp = houseInterp.planets.find(p => p.planet === planet);
          if (planetInterp) {
            if (planetInterp.interpretation) {
              houseCollationData += `  📖 Planet-in-House: ${planetInterp.interpretation}\n`;
            }
            if (planetInterp.nakshatraNote) {
              houseCollationData += `  🌟 Nakshatra Note: ${planetInterp.nakshatraNote}\n`;
            }
            if (planetInterp.nakshatraLordNote) {
              houseCollationData += `  👑 Nakshatra Lord Note: ${planetInterp.nakshatraLordNote}\n`;
            }
            if (planetInterp.housePlanetNote) {
              houseCollationData += `  ⚠️ House Note: ${planetInterp.housePlanetNote}\n`;
            }
          }
        }
      });
    } else {
      houseCollationData += `- No planets (Empty house)\n`;
    }
    
    // House lord placement with lordship interpretations
    const lordPlacement = astroData.placements[signLord];
    if (lordPlacement) {
      const lordLon = signLord === 'Rahu' || signLord === 'Ketu'
        ? astroData.nodes[signLord]
        : astroData.grahas[signLord]?.lon ?? 0;
      const lordNak = getNakshatraName(lordLon);
      houseCollationData += `- **Lord ${signLord}** placed in House ${lordPlacement.house}, ${lordNak} nakshatra\n`;
      
      // **NEW: Add lordship interpretations**
      if (houseInterp?.lordship) {
        houseInterp.lordship.forEach(lordInfo => {
          if (lordInfo.lord === signLord) {
            houseCollationData += `  🏛️ Lordship: ${lordInfo.position}\n`;
            if (lordInfo.interpretation) {
              houseCollationData += `  📖 ${lordInfo.interpretation}\n`;
            }
            if (lordInfo.overview) {
              houseCollationData += `  📋 Overview: ${lordInfo.overview}\n`;
            }
            if (lordInfo.strengths) {
              houseCollationData += `  ✅ Strengths: ${lordInfo.strengths}\n`;
            }
            if (lordInfo.challenges) {
              houseCollationData += `  ⚠️ Challenges: ${lordInfo.challenges}\n`;
            }
            if (lordInfo.remedies) {
              houseCollationData += `  💊 Remedies: ${lordInfo.remedies}\n`;
            }
          }
        });
      }
    }
    
    // **NEW: Add sign note if available**
    if (houseInterp?.signNote) {
      houseCollationData += `  📌 Sign in House: ${houseInterp.signNote}\n`;
    }
    
    // House score if available
    if (d1HouseScores && d1HouseScores[h] !== undefined) {
      const score = d1HouseScores[h];
      const impact = score >= 20 ? 'Favorable' : score >= 0 ? 'Neutral' : score >= -20 ? 'Challenging' : 'Afflicted';
      houseCollationData += `  📊 House Influence: ${score >= 0 ? '+' : ''}${score.toFixed(1)}% (${impact})\n`;
    }
    
    // **NEW: Add house overview if available**
    if (houseInterp?.overview) {
      houseCollationData += `  📝 House Overview: ${houseInterp.overview}\n`;
    }
  }

  // Gather yogas data (positive and negative) from natalYogaFeeds
  const positiveYogas: string[] = [];
  const negativeYogas: string[] = [];
  
  if (natalYogaFeeds && natalYogaFeeds.length > 0) {
    natalYogaFeeds.forEach((yoga: any) => {
      const type = yoga.type || 'neutral';
      const title = yoga.title || yoga.ruleKey || 'Unknown Yoga';
      const desc = yoga.description || '';
      
      if (type === 'benefic') {
        positiveYogas.push(`**${title}**: ${desc}`);
      } else if (type === 'malefic') {
        negativeYogas.push(`**${title}**: ${desc}`);
      }
    });
  }

  // Build SWOT prompt with D1 House Collation structure
  const prompt = `You are an expert Vedic astrologer creating a narrative SWOT analysis (Strengths, Weaknesses, Opportunities, Threats) of my birth chart.

**CRITICAL WRITING STYLE REQUIREMENTS:**
✓ Write as a FLOWING NARRATIVE STORY, not a bullet-point list
✓ WEAVE TOGETHER all chart elements (planets, houses, nakshatras, yogas, lordships) into cohesive paragraphs
✓ Create an OVERARCHING INTERPRETATION that synthesizes the entire chart
✓ Show how different placements INTERACT and INFLUENCE each other
✓ Use PERSONALIZED, WARM, and SIMPLE ENGLISH with empathetic tone
✓ Write as if you're a caring astrologer having a conversation with me
✓ Build thematic connections between different life areas
✓ Avoid lists - instead, connect ideas with phrases like "while...", "and this combines with...", "which further strengthens..."
✓ Include specific life examples and practical applications woven into the narrative

**IMPORTANT: The data below includes proprietary database interpretations marked with emojis:**
- 📖 Planet-in-House: Detailed interpretation from classical texts
- 🌟 Nakshatra Note: Specific nakshatra effects
- 👑 Nakshatra Lord Note: Nakshatra lord influences
- ⚠️ House Note: Cautions and warnings for this placement
- 🏛️ Lordship: House lord placement effects
- ✅ Strengths: Positive manifestations
- 💊 Remedies: Suggested remedies

**YOU MUST weave these detailed interpretations into your narrative. Don't just use basic planet positions - integrate the specific insights from the database predictions into a cohesive story.**

**D1 HOUSE COLLATION DATA (Complete Birth Chart):**
${houseCollationData}

**POSITIVE YOGAS (Opportunities):**
${positiveYogas.length > 0 ? positiveYogas.map((y, i) => `${i + 1}. ${y}`).join('\n') : 'No significant positive yogas detected yet. This may be normal for the current analysis depth.'}

**NEGATIVE YOGAS (Threats):**
${negativeYogas.length > 0 ? negativeYogas.map((y, i) => `${i + 1}. ${y}`).join('\n') : 'No significant negative yogas detected yet. This is generally favorable.'}

**CHART SUMMARY:**
- Ascendant: ${astroData.asc.ascendantSign} (${astroData.asc.ascendantNakshatra})
- Moon Sign: ${astroData.moon.moonSign} (${astroData.moon.nakshatra})

====================
INTERPRETATION HIERARCHY
====================
When interpreting a house or planet, apply this priority order:
1. housePlanetNote (Primary) — specific planet-in-house text is the most direct reading
2. planetInHouseOverview (Secondary) — broader thematic layer to deepen meaning
3. nakshatraNote (Tertiary) — refines tone, timing, and nuance
Higher-priority source always anchors the interpretation; lower sources add texture only.

====================
YOGA ACTIVATION LOGIC
====================
A yoga only manifests when the relevant planets/houses are activated by dasha or transit.
- Planet OCCUPIES a house → place in Strengths (direct manifestation)
- Planet ASPECTS a house → place in Strengths (indirect but real influence)
- Planet is LORD of a house only (no occupation or aspect) → place in Opportunities (potential, not yet active)
Apply this to all yogas: raj yogas, dhana yogas, and classical combinations.

====================
CHARA KARAKA ANALYSIS
====================
Natural Significators (Chara Karakas) must inform SWOT section placement:
- AtmaKaraka (AK) = soul's purpose → core Strengths and deepest Opportunities
- AmātyaKaraka (AmK) = career path → career Strengths; if afflicted → career Threats
- DāraKaraka (DK) = relationships → relationship Strengths/Weaknesses based on dignity
- PutraKaraka (PuK) = children/creativity → creative Strengths or Weaknesses
- GnātiKaraka (GK) = obstacles/enemies → Threats section; if well-placed → overcoming adversity
Use the karaka's sign, nakshatra, dignity, and house to assign SWOT section accurately.

====================
CLASSICAL VEDIC RULES (apply where relevant)
====================
Marriage & Relationships: 7th lord strength/affliction = partner quality; Venus dignity = harmony; strong Upapada Lagna = stable marriage; afflicted 7th/8th can indicate delays or friction.
Health: Sun/Moon/Lagna afflictions = vitality concerns; 6th lord strong = disease-prone; specific planet-illness links: Saturn→chronic, Mars→acute/surgical, Rahu→unusual ailments, Ketu→mysterious/mental.
Sade Sati nuances: Moon in Saturn's own sign (Capricorn/Aquarius) or Saturn exalted → peak phase is less harsh; 12th-house transit more spiritually disruptive than materially damaging.
Wealth: 2nd/11th house lords' strength determines income flow; Upachaya houses (3,6,10,11) improve with time — malefics here can be net positive long-term.
Empty house: its lord's placement fully governs results — always trace the lord's condition.

====================
NATAL HOUSE AFFLICTIONS (DUSTHANA LORD PENALTIES)
====================
The AI context includes a section titled "NATAL HOUSE AFFLICTIONS (LORD DUSTHANA PENALTIES)" — these are houses whose lords are placed in the 6th, 8th, or 12th from the owned house. EVERY afflicted house listed there MUST be addressed in the appropriate SWOT section. Do not address only one; cover ALL of them. For each:
- Name the house, its lord, and where the lord sits (e.g. "Venus rules H4 but sits in H11 — the 8th from its own house")
- Explain the specific life-area consequence (home, relationships, career, etc.)
- Factor in Jupiter protection if noted (score penalty remains but the worst outcomes are mitigated)
- Integrate this into the narrative rather than listing them as standalone facts

**YOUR TASK: Provide a comprehensive SWOT narrative that tells the story of this chart:**

Begin with an OPENING SYNTHESIS paragraph that captures the essence of this chart - the dominant themes, the interplay of strengths and challenges, and what makes this person unique. Set the tone for what follows.

Then create FOUR NARRATIVE SECTIONS (not lists):

1. **STRENGTHS** - Write 2-3 flowing paragraphs that:
   - Weave together the strongest planetary placements and their interactions
   - Show how exaltations, beneficial lordships (🏛️), and favorable nakshatras (🌟) reinforce each other
   - Integrate specific 📖 Planet-in-House interpretations naturally into sentences
   - Connect different life areas (e.g., "Your career strength flows from your 10th house Moon, which harmonizes beautifully with...")
   - Paint a picture of how these strengths manifest in daily life

2. **WEAKNESSES** - Write 2-3 flowing paragraphs that:
   - Narratively explore challenging placements and their ripple effects across the chart
   - Show how combustion, debilitation, or afflictions create patterns of difficulty
   - Weave in ⚠️ cautions and challenging 🏛️ lordship interpretations
   - Explain how these weaknesses interconnect (e.g., "The Venus weakness compounds relationship challenges, especially when combined with...")
   - Maintain compassionate tone while being honest

3. **OPPORTUNITIES** - Write 2-3 flowing paragraphs that:
   - Tell the story of how positive yogas create windows of possibility
   - Connect current/upcoming dashas with chart potentials
   - Show how ✅ strengths from lordship data can be leveraged
   - Weave together 🌟 beneficial nakshatra effects with 📖 favorable interpretations
   - Create a vision of growth potential by linking different opportunities

4. **THREATS** - Write 2-3 flowing paragraphs that:
   - Narratively explain how negative yogas and afflictions might manifest
   - Show patterns of vulnerability across life areas
   - Integrate ⚠️ cautions and challenges from lordship data
   - Describe how these threats interconnect and compound
   - End with hope by mentioning 💊 remedies woven into the narrative

Close with a CONCLUDING SYNTHESIS paragraph that:
- Ties together the four sections
- Offers an overarching interpretation of the life path suggested by this chart
- Provides a final perspective on how strengths can overcome weaknesses
- Leaves the person with a sense of direction and purpose

**DO NOT USE BULLET POINTS OR LISTS. Write in connected, flowing paragraphs. Show relationships between chart elements. Create a holistic interpretation, not a catalog of placements.**

COMPLETENESS RULE: The entire response must fit in one reply — never cut off. Keep each SWOT section to 1–2 paragraphs. Opening synthesis: 2–3 sentences. Closing synthesis: 2–3 sentences. Total target: ~600 words.`;

  // Show modal and call AI
  setSwotPrompt(prompt);
  setSwotOutput('');
  setSwotError('');
  setSwotLoading(true);
  setShowSwotModal(true);
  
  // Call AI to get SWOT analysis
  callSwotAnalysis(prompt);
}, [astroData, d1HouseScores, natalYogaFeeds, blendedPlanetPct, combust, debil, exalt, houseInterpretations]);

// ─────────────────────────────────────────────────────────────
// REPORT AI ANALYSIS FUNCTIONS  (admin-only, no credit deduction)
// ─────────────────────────────────────────────────────────────

/** Shared SSE streaming helper — streams response into a React state setter */
async function streamReportAI(
  prompt: string,
  context: any,
  model: string,
  setContent: React.Dispatch<React.SetStateAction<string>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  sessionToken: string,
  maxTokens?: number
) {
  const response = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      },
      body: JSON.stringify({
        messages: [{ role: 'user', content: prompt }],
        context,
        model,
        questionType: 'general',
        bypassCredits: true,   // signal to edge function (admin path)
        ...(maxTokens ? { maxTokens } : {})
      })
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${response.status}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/event-stream')) {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let full = '';
    if (reader) {
      setLoading(false);
      // SSE lines can be split across raw TCP/HTTP chunks.
      // We buffer incomplete lines and only process complete ones (terminated by \n).
      // Without this, a chunk boundary mid-line causes a JSON.parse failure and
      // those tokens are silently dropped → clipped words / missing letters.
      let lineBuffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        // Keep the last (potentially incomplete) fragment for the next iteration
        lineBuffer = lines.pop() ?? '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const parsed = JSON.parse(line.slice(6));
              if (parsed.content) { full += parsed.content; setContent(full); }
            } catch {}
          }
        }
      }
      // Flush any remaining complete line in the buffer after the stream ends
      if (lineBuffer.startsWith('data: ')) {
        try {
          const parsed = JSON.parse(lineBuffer.slice(6));
          if (parsed.content) { full += parsed.content; setContent(full); }
        } catch {}
      }
    }
  } else {
    const result = await response.json();
    setContent(result.reply || result.content || 'No response received');
    setLoading(false);
  }
}

/** Report: Add SWOT Analysis (admin bypass — reuses handleAnalyseChart prompt) */
const callReportSwotAnalysis = async () => {
  if (!astroData || profile?.role !== 'admin') return;
  if (reportAiCacheKey) localStorage.removeItem(`${reportAiCacheKey}_swot`);
  setReportSwotContent('');
  setReportSwotLoading(true);

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setReportSwotLoading(false); return; }

    // ── Build houseCollationData (same as handleAnalyseChart) ──
    const getNakshatraName = (lon: number): string => {
      const idx = Math.floor((((lon % 360) + 360) % 360) / (360 / 27));
      return NAKSHATRA_NAMES[idx] || 'Unknown';
    };
    const getSignName = (lon: number): string => {
      const idx = Math.floor((((lon % 360) + 360) % 360) / 30);
      return RASHI_NAMES[idx] || 'Unknown';
    };
    const getDignities = (planet: PlanetName): string[] => {
      const d: string[] = [];
      if (exalt?.[planet]) d.push('Exalted');
      if (debil?.[planet]) d.push('Debilitated');
      if ((combust as any)?.[planet]?.combust) d.push('Combust');
      if (planet !== 'Rahu' && planet !== 'Ketu' && astroData.grahas[planet]?.retrograde) d.push('Retrograde');
      return d;
    };
    const ascSign = astroData.asc?.ascendantSignIndex ?? 1;
    const funcMap = classifyFunctionalForLagna(ascSign) as Record<string, 'benefic' | 'malefic' | 'neutral' | 'yoga_karaka'>;

    let houseCollationData = '';
    for (let h = 1; h <= 12; h++) {
      const signIdx = Math.floor(astroData.cusps[h - 1] / 30);
      const signName = RASHI_NAMES[signIdx] || 'Unknown';
      const signLord = SIGN_LORDS[signIdx] as PlanetName;
      houseCollationData += `\n**House ${h} (${signName})**:\n`;
      const houseInterp = houseInterpretations?.find(hi => hi.house === h);
      const planetsInHouse: Array<{ planet: PlanetName; lon: number }> = [];
      Object.entries(astroData.placements).forEach(([pName, placement]) => {
        if (placement.house === h) {
          const planet = pName as PlanetName;
          const lon = planet === 'Rahu' || planet === 'Ketu' ? astroData.nodes[planet] : astroData.grahas[planet]?.lon ?? 0;
          planetsInHouse.push({ planet, lon });
        }
      });
      if (planetsInHouse.length > 0) {
        planetsInHouse.forEach(({ planet, lon }) => {
          const nakshatra = getNakshatraName(lon);
          const sign = getSignName(lon);
          const dignities = getDignities(planet);
          const blendedStr = blendedPlanetPct?.[planet] ? ` (Strength: ${blendedPlanetPct[planet].toFixed(0)}%)` : '';
          houseCollationData += `- **${planet}** in ${sign}, ${nakshatra} nakshatra`;
          if (dignities.length > 0) houseCollationData += ` [${dignities.join(', ')}]`;
          houseCollationData += blendedStr + `\n`;
          const funcNature = funcMap?.[planet];
          if (funcNature) houseCollationData += `  Functional: ${funcNature}\n`;
          if (houseInterp?.planets) {
            const pi = houseInterp.planets.find(p => p.planet === planet);
            if (pi) {
              if (pi.interpretation) houseCollationData += `  📖 Planet-in-House: ${pi.interpretation}\n`;
              if (pi.nakshatraNote) houseCollationData += `  🌟 Nakshatra Note: ${pi.nakshatraNote}\n`;
              if (pi.nakshatraLordNote) houseCollationData += `  👑 Nakshatra Lord Note: ${pi.nakshatraLordNote}\n`;
              if (pi.housePlanetNote) houseCollationData += `  ⚠️ House Note: ${pi.housePlanetNote}\n`;
            }
          }
        });
      } else {
        houseCollationData += `- No planets (Empty house)\n`;
      }
      const lordPlacement = astroData.placements[signLord];
      if (lordPlacement) {
        const lordLon = signLord === 'Rahu' || signLord === 'Ketu' ? astroData.nodes[signLord] : astroData.grahas[signLord]?.lon ?? 0;
        const lordNak = getNakshatraName(lordLon);
        houseCollationData += `- **Lord ${signLord}** placed in House ${lordPlacement.house}, ${lordNak} nakshatra\n`;
        if (houseInterp?.lordship) {
          houseInterp.lordship.forEach(li => {
            if (li.lord === signLord) {
              houseCollationData += `  🏛️ Lordship: ${li.position}\n`;
              if (li.interpretation) houseCollationData += `  📖 ${li.interpretation}\n`;
              if (li.overview) houseCollationData += `  📋 Overview: ${li.overview}\n`;
              if (li.strengths) houseCollationData += `  ✅ Strengths: ${li.strengths}\n`;
              if (li.challenges) houseCollationData += `  ⚠️ Challenges: ${li.challenges}\n`;
              if (li.remedies) houseCollationData += `  💊 Remedies: ${li.remedies}\n`;
            }
          });
        }
      }
      if (houseInterp?.signNote) houseCollationData += `  📌 Sign in House: ${houseInterp.signNote}\n`;
      if (d1HouseScores && d1HouseScores[h] !== undefined) {
        const score = d1HouseScores[h];
        const impact = score >= 20 ? 'Favorable' : score >= 0 ? 'Neutral' : score >= -20 ? 'Challenging' : 'Afflicted';
        houseCollationData += `  📊 House Influence: ${score >= 0 ? '+' : ''}${score.toFixed(1)}% (${impact})\n`;
      }
      if (houseInterp?.overview) houseCollationData += `  📝 House Overview: ${houseInterp.overview}\n`;
    }

    const positiveYogas: string[] = [];
    const negativeYogas: string[] = [];
    if (natalYogaFeeds?.length) {
      natalYogaFeeds.forEach((yoga: any) => {
        const title = yoga.title || yoga.ruleKey || 'Unknown Yoga';
        const desc = yoga.description || '';
        if (yoga.type === 'benefic') positiveYogas.push(`**${title}**: ${desc}`);
        else if (yoga.type === 'malefic') negativeYogas.push(`**${title}**: ${desc}`);
      });
    }

    const prompt = `You are an expert Vedic astrologer creating a narrative SWOT analysis (Strengths, Weaknesses, Opportunities, Threats) of my birth chart.

**CRITICAL WRITING STYLE REQUIREMENTS:**
✓ Write as a FLOWING NARRATIVE STORY, not a bullet-point list
✓ WEAVE TOGETHER all chart elements (planets, houses, nakshatras, yogas, lordships) into cohesive paragraphs
✓ Create an OVERARCHING INTERPRETATION that synthesizes the entire chart
✓ Show how different placements INTERACT and INFLUENCE each other
✓ Use PERSONALIZED, WARM, and SIMPLE ENGLISH with empathetic tone
✓ Write as if you're a caring astrologer having a conversation with me
✓ Build thematic connections between different life areas
✓ Avoid lists - instead, connect ideas with phrases like "while...", "and this combines with...", "which further strengthens..."
✓ Include specific life examples and practical applications woven into the narrative

**IMPORTANT: The data below includes proprietary database interpretations marked with emojis:**
- 📖 Planet-in-House: Detailed interpretation from classical texts
- 🌟 Nakshatra Note: Specific nakshatra effects
- 👑 Nakshatra Lord Note: Nakshatra lord influences
- ⚠️ House Note: Cautions and warnings for this placement
- 🏛️ Lordship: House lord placement effects
- ✅ Strengths: Positive manifestations
- 💊 Remedies: Suggested remedies

**YOU MUST weave these detailed interpretations into your narrative. Don't just use basic planet positions - integrate the specific insights from the database predictions into a cohesive story.**

**D1 HOUSE COLLATION DATA (Complete Birth Chart):**
${houseCollationData}

**POSITIVE YOGAS (Opportunities):**
${positiveYogas.length > 0 ? positiveYogas.map((y, i) => `${i + 1}. ${y}`).join('\n') : 'No significant positive yogas detected.'}

**NEGATIVE YOGAS (Threats):**
${negativeYogas.length > 0 ? negativeYogas.map((y, i) => `${i + 1}. ${y}`).join('\n') : 'No significant negative yogas detected.'}

**CHART SUMMARY:**
- Ascendant: ${astroData.asc.ascendantSign} (${astroData.asc.ascendantNakshatra})
- Moon Sign: ${astroData.moon.moonSign} (${astroData.moon.nakshatra})

====================
INTERPRETATION HIERARCHY
====================
When multiple notes exist for the same planet-in-house, prioritise in this order:
1. PRIMARY — housePlanetNote (⚠️ House Note): most specific classical rule for THIS planet in THIS house
2. SECONDARY — planetInHouseOverview (📖 Planet-in-House): general effect of this planet here
3. TERTIARY — nakshatraNote / nakshatraLordNote (🌟 👑): nakshatra-level colour
Blending rule: lead with the PRIMARY if present; weave SECONDARY and TERTIARY as supporting texture. Never repeat the same point from multiple sources verbatim.

====================
YOGA ACTIVATION LOGIC
====================
For each yoga listed above, determine and state its activation level:
1. OCCUPATION (full): A current dasha lord physically occupies the yoga's house — state "fully active"
2. ASPECT (moderate): A dasha lord aspects the yoga house — state "active via aspect"
3. LORDSHIP (mild): A dasha lord rules the yoga house — state "mildly active via lordship"
In the Strengths section cite only yogas at occupation or aspect level as major strengths. Lordship-only yogas belong in Opportunities (potential, not yet manifest).

====================
CHARA KARAKA ANALYSIS
====================
Chara Karakas are the soul-significators that reveal permanent life themes:
- AmK (AmatyaKaraka) = career destiny and professional path
- DK (DaraKaraka) = marriage and partnership nature
- PuK (PutraKaraka) = children and creative legacy
- AK (AtmaKaraka) = soul purpose and deepest dharma
- GK (GnatiKaraka) = health, obstacles, and karmic challenges
For each SWOT section, name the relevant karaka and its blendedStrength. AmK strength → how powerfully the career/Strengths theme manifests. DK strength → relationship Strengths or Weaknesses. GK weakness → a core Threat. AK placement → the overarching Opportunities theme.

====================
CLASSICAL VEDIC RULES (apply where relevant)
====================
Relationships / Marriage:
- 7th lord in 6th/8th/12th = delays or friction in marriage; name the specific challenge
- Saturn + Rahu in 7th = karmic relationship pattern; cite in Threats
- 12th lord in 7th or Ketu in 7th = foreign/spiritual partner or renunciation tendency
Health:
- Sun afflicted → heart, eyes, bones; Moon afflicted → mind, fluids, hormones
- Mars afflicted → blood, accidents, inflammation; Saturn afflicted → chronic conditions, joints
- Cite only the planets that ARE actually afflicted in this chart
Sade Sati nuances (if active):
- Phase in H12/8 = most intensive inner pressure; H12/6 = financial vigilance needed
- Phase over natal Moon = emotional peak; recommend: chanting Shani Stotra, service on Saturdays

====================
NATAL HOUSE AFFLICTIONS (DUSTHANA LORD PENALTIES)
====================
The AI context includes a section titled "NATAL HOUSE AFFLICTIONS (LORD DUSTHANA PENALTIES)" — these are houses whose lords are placed in the 6th, 8th, or 12th from the owned house. EVERY afflicted house listed there MUST be addressed in the appropriate SWOT section. Do not address only one; cover ALL of them. For each:
- Name the house, its lord, and where the lord sits (e.g. "Venus rules H4 but sits in H11 — the 8th from its own house")
- Explain the specific life-area consequence (home, relationships, career, etc.)
- Factor in Jupiter protection if noted (score penalty remains but the worst outcomes are mitigated)
- Integrate this into the narrative rather than listing them as standalone facts

**YOUR TASK:** Create a comprehensive SWOT narrative with an opening synthesis, four narrative sections (Strengths, Weaknesses, Opportunities, Threats), and a concluding synthesis. Use flowing paragraphs — no bullet lists. Integrate all chart data including the reference sections above into a cohesive story.

COMPLETENESS RULE: The entire response must fit in one reply — never cut off. Keep each SWOT section to 1–2 paragraphs. Opening synthesis: 2–3 sentences. Closing synthesis: 2–3 sentences. Total target: ~600 words.`;

    const contextData = { astroData, blendedPlanetPct, shadbalaPct, bhavaBalaRupas, combust, debil, exalt, d1HouseScores, dashaContext: dashaContextForFeeds, feeds: reportFeeds, utcDateTime: utcDateTime ?? undefined, selectedCity: selectedCity ?? undefined, ayanamsa: ayanProvider.name || 'Lahiri', gender: selectedGender, lordDusthanaPenalties: lordDusthanaPenalties ?? undefined, sadeSati: sadeSatiData ?? undefined, kaalSarpYoga: kaalSarpData ?? undefined, functionalNature: functionalNatureData as any ?? undefined, transitData: transitData ? { date: transitData.date, placements: transitData.placements, dignities: transitData.dignities, longitudes: transitData.longitudes, retrogrades: transitData.retrogrades, combust: transitData.combust, ...transitData.signDates } : undefined };
    const context = buildChatContext(contextData as any, { includeFeeds: true, includeInterpretations: true, maxInterpretationLength: 500 });

    await streamReportAI(prompt, context, 'gpt-5.4', setReportSwotContent, setReportSwotLoading, session.access_token);
  } catch (err: any) {
    console.error('[Report SWOT]', err);
    setReportSwotContent(`Error: ${err.message}`);
    setReportSwotLoading(false);
  }
};

/** Report: Add Detailed Chart Analysis (admin bypass — uses copyLLMPrompt structure) */
const callReportDetailedAnalysis = async () => {
  if (!astroData || profile?.role !== 'admin') return;
  if (reportAiCacheKey) localStorage.removeItem(`${reportAiCacheKey}_detailed`);
  setReportDetailedContent('');
  setReportDetailedLoading(true);

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setReportDetailedLoading(false); return; }

    const getNakshatraName = (lon: number): string => {
      const idx = Math.floor((((lon % 360) + 360) % 360) / (360 / 27));
      return NAKSHATRA_NAMES[idx] || 'Unknown';
    };
    const getDignities = (planet: PlanetName): string[] => {
      const d: string[] = [];
      if (exalt?.[planet]) d.push('Exalted');
      if (debil?.[planet]) d.push('Debilitated');
      if ((combust as any)?.[planet]?.combust) d.push('Combust');
      if (planet !== 'Rahu' && planet !== 'Ketu' && astroData.grahas[planet]?.retrograde) d.push('Retrograde');
      return d;
    };

    // Build house data for the detailed report
    const houseData = Array.from({ length: 12 }, (_, i) => i + 1).map(h => {
      const signIdx = Math.floor(astroData.cusps[h - 1] / 30);
      const signName = RASHI_NAMES[signIdx] || 'Unknown';
      const signLord = SIGN_LORDS[signIdx] as PlanetName;
      const houseInterp = houseInterpretations?.find(hi => hi.house === h);
      const influenceRow = d1HouseInfluence?.rows?.find((r: any) => r.house === h);

      const residentPlanets = Object.entries(astroData.placements)
        .filter(([, pl]) => pl.house === h)
        .map(([pName]) => {
          const planet = pName as PlanetName;
          const lon = planet === 'Rahu' || planet === 'Ketu' ? astroData.nodes[planet] : astroData.grahas[planet]?.lon ?? 0;
          const pi = houseInterp?.planets?.find(p => p.planet === planet);
          return {
            planet,
            nakshatra: getNakshatraName(lon),
            dignities: getDignities(planet),
            nakshatraNote: pi?.nakshatraNote,
            nakshatraLordNote: pi?.nakshatraLordNote,
            houseNote: pi?.housePlanetNote,
            planetInHouseOverview: pi?.interpretation
          };
        });

      const aspectingPlanets = (influenceRow?.aspectors || []).map((ap: any) => ({
        planet: ap.planet,
        nakshatra: ap.nakshatra || ''
      }));

      const primarySource = residentPlanets.length > 0 ? 'resident' : aspectingPlanets.length > 0 ? 'aspecting' : 'lordOnly';

      const lordPlacement = astroData.placements[signLord];
      const lordLon = signLord === 'Rahu' || signLord === 'Ketu' ? astroData.nodes[signLord] : astroData.grahas[signLord]?.lon ?? 0;

      return {
        house: h,
        houseName: `House ${h}`,
        houseDescription: houseInterp?.overview || '',
        sign: signName,
        signNote: houseInterp?.signNote || '',
        signLord,
        primarySource,
        houseLordshipInfo: (houseInterp?.lordship || []).map(li => ({
          lordOfHouse: h,
          prediction: li.interpretation,
          overview: li.overview,
          strengths: li.strengths,
          challenges: li.challenges,
          remedies: li.remedies
        })),
        residentPlanets,
        aspectingPlanets,
        lordPosition: lordPlacement ? `House ${lordPlacement.house}` : 'Unknown',
        lordNakshatra: getNakshatraName(lordLon)
      };
    });

    // Build dosha summary
    const doshasSummary: any[] = [];
    if (kaalSarpData && (kaalSarpData.isPresent || (kaalSarpData as any).present)) {
      doshasSummary.push({ name: 'Kaal Sarp Yoga', severity: kaalSarpData.severity || 'unknown', details: kaalSarpData.details || `${kaalSarpData.type || 'Unknown type'}`, rahuHouse: (kaalSarpData as any).rahuHouse, ketuHouse: (kaalSarpData as any).ketuHouse });
    }
    if (sadeSatiData) {
      doshasSummary.push({
        name: 'Shani Sadesaati',
        severity: sadeSatiData.isActive ? 'active' : 'not_active',
        isActive: sadeSatiData.isActive,
        currentPhase: sadeSatiData.isActive ? (sadeSatiData.currentPhase || null) : null,
        details: sadeSatiData.isActive
          ? `CURRENTLY ACTIVE — Phase: ${sadeSatiData.currentPhase || 'Unknown'}. Current phase: ${sadeSatiData.from || 'N/A'} → ${sadeSatiData.to || 'N/A'}. Full 7.5-yr period: ${sadeSatiData.totalFrom || 'N/A'} → ${sadeSatiData.totalTo || 'N/A'}.`
          : `Not currently active for this chart.`,
        allPhases: sadeSatiData.allPhases || [],
        totalFrom: sadeSatiData.totalFrom || null,
        totalTo: sadeSatiData.totalTo || null,
        moonSign: sadeSatiData.moonSign
      });
    }

    // Build house scores summary
    const houseScoresSummary = Object.entries(d1HouseScores || {}).map(([house, score]) => {
      const s = Number(score);
      return { house: Number(house), score: s, impact: s >= 50 ? 'Highly Favorable' : s >= 20 ? 'Favorable' : s >= -20 ? 'Neutral' : s >= -50 ? 'Challenging' : 'Severely Afflicted' };
    });

    // Build conjunction summary (planets sharing the same house)
    const houseGroups: Record<number, string[]> = {};
    Object.entries(astroData.placements).forEach(([planet, pl]) => {
      const h = pl.house;
      if (!houseGroups[h]) houseGroups[h] = [];
      houseGroups[h].push(planet);
    });
    const conjunctionsSummary = Object.entries(houseGroups).filter(([, ps]) => ps.length >= 2).map(([h, ps]) => ({ planets: ps.join(' + '), house: Number(h), effect: 'Combined planetary energies', score: 0 }));

    const prompt = `You are an expert Vedic astrologer creating a personalized, detailed birth chart report for a client. This report will be delivered to the end user (customer) and must be written in a warm, caring, and humanly manner.

CRITICAL FORMATTING REQUIREMENT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✗ DO NOT start with any greeting, salutation, or preamble (no "Dear Friend", "Hello", "Below is your reading", disclaimers about free will, or any introductory paragraph)
✓ Begin DIRECTLY with the first section heading and its content — the report starts immediately with the prediction

CRITICAL TONE & LANGUAGE REQUIREMENTS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✓ Write in PERSONALIZED, WARM, and SIMPLE ENGLISH
✓ Use PRACTICAL, LIFE-RELEVANT language that resonates with real human experiences
✓ Avoid robotic, mechanical, telegram-style, or overly technical language
✓ Write as if you're a caring astrologer speaking directly to the person
✓ Use empathetic, conversational phrasing (e.g., "You have a natural gift for..." instead of "Native possesses...")
✓ Make it feel like guidance from a trusted advisor, not a cold analysis
✓ Be encouraging while being honest about challenges
✓ Include specific life examples and practical applications

REQUIRED REPORT SECTIONS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
1. PERSONALITY & FIRST IMPRESSIONS - 1st house (body, temperament, self-expression, identity)
2. HEALTH & VITALITY - 1st, 6th, 8th, 12th houses
3. WEALTH & FINANCES - 2nd, 11th, 5th, 9th houses
4. CAREER, PROFESSION & WORK - 10th, 6th, 2nd, 7th, 3rd houses (subdivide: Jobs/Business/Professional Fields)
5. RELATIONSHIPS - 7th, 2nd, 8th, 12th (Spouse); 5th, 9th (Children); 3rd, 4th, 9th (Family)
6. EDUCATION & LEARNING - 4th, 5th, 9th, 2nd, 3rd houses
7. SPIRITUAL LIFE & INNER GROWTH - 12th, 9th, 8th, 5th houses
8. HOME, PROPERTY & VEHICLES - 4th, 2nd, 12th houses
9. REMEDIES & RECOMMENDATIONS - grouped by life area

CRITICAL INSTRUCTIONS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✓ Use flowing paragraphs with bullets only for key points
✓ Address the person directly using "you" and "your"
✓ Weave in technical data naturally — don't list it mechanically
✓ Include every nakshatra note, lordship placement, and planetary combination

DOSHAS & YOGAS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${doshasSummary.length > 0 ? JSON.stringify(doshasSummary, null, 2) : 'No major doshas detected.'}

HOUSE SCORES:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${JSON.stringify(houseScoresSummary, null, 2)}

CONJUNCTIONS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${conjunctionsSummary.length > 0 ? JSON.stringify(conjunctionsSummary, null, 2) : 'No major conjunctions.'}

STRUCTURED CHART DATA:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${JSON.stringify(houseData, null, 2)}

====================
INTERPRETATION HIERARCHY
====================
When multiple notes exist for the same planet-in-house, prioritise in this order:
1. PRIMARY — housePlanetNote (houseNote in data): most specific classical rule for THIS planet in THIS house
2. SECONDARY — planetInHouseOverview: general effect of this planet here
3. TERTIARY — nakshatraNote / nakshatraLordNote: nakshatra-level colour
Blending rule: lead with the PRIMARY if present; weave SECONDARY and TERTIARY as supporting texture. Never repeat the same point from multiple sources verbatim.

====================
VARGA / DIVISIONAL CHART CONFIRMATION
====================
For these life area sections, cross-check D1 with the relevant varga and state whether it confirms or contradicts:
- CAREER section → check D10 (Dasamsa): if planet is strong in D10 too = "D10 confirms professional success"; weak = "D10 suggests career needs consistent effort"
- RELATIONSHIPS section → check D9 (Navamsa): strong in D9 = "Navamsa confirms relationship harmony"; afflicted = "Navamsa indicates relationship karma to work through"
- PROPERTY / HOME section → check D4 (Chaturthamsa): strong = "D4 confirms real estate potential"
- CHILDREN section → check D7 (Saptamsa): strong = "D7 confirms children's blessings"; weak = "D7 suggests delays or karmic lessons around children"
If varga data is not available for a planet, state only D1 findings.

====================
CLASSICAL VEDIC RULES (apply where relevant per section)
====================
Relationships / Marriage section:
- 7th lord in 6th/8th/12th = delays or friction; name the specific challenge
- Saturn + Rahu in 7th = karmic relationship pattern
- 12th lord in 7th or Ketu in 7th = foreign/spiritual partner or renunciation tendency
- 7th lord in upachaya (3/6/10/11) = relationship improves with time
Health section:
- Sun afflicted → heart, eyes, bones; Moon afflicted → mind, fluids, hormones
- Mars afflicted → blood, accidents, inflammation; Saturn afflicted → chronic conditions, joints
- Cite ONLY the planets that are actually afflicted (combust/debilitated/with malefics) in this chart
Wealth section:
- Malefics in 11th in upachaya = wealth through persistent effort (not a weakness)
- 2nd and 11th lords in mutual kendras = strong dhan yoga; name it
General:
- Empty houses: interpret via the house lord position only; never say "no planets here means nothing happens"

====================
NATAL HOUSE AFFLICTIONS (DUSTHANA LORD PENALTIES)
====================
The AI context includes a section titled "NATAL HOUSE AFFLICTIONS (LORD DUSTHANA PENALTIES)" — these are houses whose lords are placed in the 6th, 8th, or 12th from the owned house. EVERY afflicted house listed there MUST be addressed in the relevant report section — do not skip any; cover ALL of them. For each:
- Name the house, its lord, and where the lord sits (e.g. "Mars rules H7 but sits in H2 — the 8th from its own house")
- Explain the specific life-area consequence in the relevant section (health, relationships, career, etc.)
- Factor in Jupiter protection if noted (score penalty remains but the worst outcomes are mitigated)
- Integrate into the narrative naturally, not as a separate list

COMPLETENESS RULE: The entire response must fit in one reply — never cut off. Limit each section to 2–3 short paragraphs (4–6 sentences each). Total target: ~1,200 words. If context is tight, write Sections 1, 3, 4, and 5 fully first, then cover the remaining sections in 3–4 sentences each.

Now create the report. Begin directly with Section 1 — no preamble.`;

    const contextData = { astroData, blendedPlanetPct, shadbalaPct, bhavaBalaRupas, combust, debil, exalt, d1HouseScores, dashaContext: dashaContextForFeeds, feeds: reportFeeds, utcDateTime: utcDateTime ?? undefined, selectedCity: selectedCity ?? undefined, ayanamsa: ayanProvider.name || 'Lahiri', gender: selectedGender, lordDusthanaPenalties: lordDusthanaPenalties ?? undefined, sadeSati: sadeSatiData ?? undefined, kaalSarpYoga: kaalSarpData ?? undefined, functionalNature: functionalNatureData as any ?? undefined, vargaData: vargaMap ? Object.entries(vargaMap).reduce((acc, [key, vargaResult]: [string, any]) => { if (vargaResult && vargaResult.ascSign && vargaResult.planetSigns) { acc[key as any] = { ascSign: vargaResult.ascSign, planetSigns: vargaResult.planetSigns, placements: vargaToPlacements(vargaResult) }; } return acc; }, {} as any) : undefined, transitData: transitData ? { date: transitData.date, placements: transitData.placements, dignities: transitData.dignities, longitudes: transitData.longitudes, retrogrades: transitData.retrogrades, combust: transitData.combust, ...transitData.signDates } : undefined };
    const context = buildChatContext(contextData as any, { includeFeeds: true, includeInterpretations: true, maxInterpretationLength: 400, includeVargas: true });

    await streamReportAI(prompt, context, 'gpt-5.4', setReportDetailedContent, setReportDetailedLoading, session.access_token, 12000);
  } catch (err: any) {
    console.error('[Report Detailed]', err);
    setReportDetailedContent(`Error: ${err.message}`);
    setReportDetailedLoading(false);
  }
};

/** Report: Add Current Period Analysis (admin bypass — full week-by-week + 3-month summary) */
const callReportCurrentPeriod = async () => {
  if (!astroData || profile?.role !== 'admin') return;
  if (reportAiCacheKey) localStorage.removeItem(`${reportAiCacheKey}_current_period`);
  setReportCurrentPeriodContent('');
  setReportCurrentPeriodLoading(true);

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setReportCurrentPeriodLoading(false); return; }

    // ── same data assembly as callCurrentPeriodAnalysis ─────────────────────
    const ascSign = astroData?.asc?.ascendantSignIndex ?? 1;
    const funcMap = classifyFunctionalForLagna(ascSign) as Record<string, string>;
    const moonLonVal = astroData?.moon?.siderealLongitudeDeg ?? (astroData?.grahas as any)?.Moon?.lon ?? 0;
    const birthISO = utcDateTime ?? '';
    const ayanamsa = 24.14;
    const LIFE_AREA_LABELS = ['Health','Wealth','Career','Business','Marriage','Children','Property','Education'];
    const LIFE_AREA_HOUSES = [[1,6,8,12],[1,2,5,9,11,12],[1,6,10],[1,3,7,11],[1,7],[1,5],[1,4],[2,4,5]];
    const pAbbr: Record<string,string> = { Sun:'Su',Moon:'Mo',Mars:'Ma',Mercury:'Me',Jupiter:'Ju',Venus:'Ve',Saturn:'Sa',Rahu:'Ra',Ketu:'Ke' };
    const toHouseFromLon = (lon: number) => { const s=Math.floor(((lon%360)+360)%360/30)+1; return ((s-ascSign+12)%12)+1; };
    const pdSdLordSet2 = new Set<string>(); const mdAdLordSet2 = new Set<string>();

    const name = reportV2Data?.birthDetails?.name?.split(' ')[0] ?? 'the native';
    const ascendantStr = reportV2Data?.birthDetails?.ascendant ?? astroData?.asc?.ascendantSign ?? '';
    const moonSignStr  = reportV2Data?.birthDetails?.moonSign ?? '';
    const dobStr = reportV2Data?.birthDetails?.dateOfBirth ?? localDateTime ?? birthISO;
    let ageYears = 35;
    try { if (dobStr) { const diff=Date.now()-new Date(dobStr).getTime(); ageYears=Math.floor(diff/(365.25*24*3600*1000)); } } catch {}
    const ageBracket = ageYears < 25 ? 'young' : ageYears < 60 ? 'adult' : 'senior';
    const ageContextLine = ageBracket==='young' ? `Age ${ageYears} (10–24). Priority: Education, Health, Love, early Career.` : ageBracket==='adult' ? `Age ${ageYears} (25–60). Priority: Career, Business, Wealth, Marriage, Children, Health.` : `Age ${ageYears} (60+). Priority: Health, Finances, Spiritual growth, Family.`;

    let curMD2='', curAD2='', curPD2='', curSD2: string|undefined;
    try { const lv0=getCurrentDashaLevels(birthISO,moonLonVal,new Date().toISOString()); curMD2=lv0?.md?.lord??''; curAD2=lv0?.ad?.lord??''; curPD2=lv0?.pd?.lord??''; curSD2=lv0?.sd?.lord; } catch {}
    [curMD2,curAD2].filter(Boolean).forEach(p=>mdAdLordSet2.add(p));
    [curPD2,...(curSD2?[curSD2]:[])].filter(Boolean).forEach(p=>pdSdLordSet2.add(p));
    const curDashaStr2 = [curMD2&&`${pAbbr[curMD2]??curMD2} MD(${funcMap[curMD2]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curMD2 as PlanetName]??50)}%)`,curAD2&&`${pAbbr[curAD2]??curAD2} AD(${funcMap[curAD2]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curAD2 as PlanetName]??50)}%)`,curPD2&&`${pAbbr[curPD2]??curPD2} PD(${funcMap[curPD2]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curPD2 as PlanetName]??50)}%)`,curSD2&&`${pAbbr[curSD2]??curSD2} SD(${funcMap[curSD2]??'neutral'}, str:${Math.round(blendedPlanetPct?.[curSD2 as PlanetName]??50)}%)`].filter(Boolean).join(' · ');

    const doshasLines2: string[] = [];
    const placements2 = astroData?.placements ?? {};
    if (sadeSatiData?.isActive) { const ph=sadeSatiData.currentPhase??'unknown'; doshasLines2.push(`Shani Sadesaati ACTIVE — ${ph==='peak'?'PEAK phase — maximum Saturn pressure':ph==='rising'?'RISING phase — challenges building':ph==='setting'?'SETTING phase — challenges easing':'active phase'}. Period: ${sadeSatiData.from??'N/A'} → ${sadeSatiData.to??'N/A'}. MUST be mentioned every week.`); }
    if (kaalSarpData&&(kaalSarpData.isPresent||(kaalSarpData as any).present)) { const ksN=(kaalSarpData as any).specificType||kaalSarpData.type||'Kaal Sarp'; doshasLines2.push(`Kaal Sarp Yoga PRESENT — ${ksN} (Rahu H${(kaalSarpData as any).rahuHouse}, Ketu H${(kaalSarpData as any).ketuHouse}). ${(kaalSarpData as any).specificEffects||kaalSarpData.details||''}`); }
    { const m=(placements2 as any)['Mars']; if(m&&new Set([1,2,4,7,8,12]).has(m.house)) doshasLines2.push(`Mangal Dosha PRESENT — Mars in H${m.house}.`); }
    { const jP=(placements2 as any)['Jupiter'],rP=(placements2 as any)['Rahu'],kP=(placements2 as any)['Ketu']; if(jP&&rP&&jP.house===rP.house) doshasLines2.push(`Guru Chandal Yoga PRESENT — Jupiter conjunct Rahu in H${jP.house}.`); else if(jP&&kP&&jP.house===kP.house) doshasLines2.push(`Guru Ketu Yoga PRESENT — Jupiter conjunct Ketu in H${jP.house}.`); }

    const natalAfflictions2: string[] = [];
    if (combust)       { const l=Object.entries(combust).filter(([,v])=>v).map(([p])=>p); if(l.length) natalAfflictions2.push(`Combust: ${l.join(', ')}`); }
    if (debil)         { const l=Object.entries(debil).filter(([,v])=>v).map(([p])=>p); if(l.length) natalAfflictions2.push(`Debilitated: ${l.join(', ')}`); }
    if (exalt)         { const l=Object.entries(exalt).filter(([,v])=>v).map(([p])=>p); if(l.length) natalAfflictions2.push(`Exalted: ${l.join(', ')}`); }
    if (conjunctDebil) { const l=Object.entries(conjunctDebil).filter(([,v])=>v).map(([p])=>p); if(l.length) natalAfflictions2.push(`Debility enhanced by conjunctions: ${l.join(', ')}`); }
    if (conjunctExalt) { const l=Object.entries(conjunctExalt).filter(([,v])=>v).map(([p])=>p); if(l.length) natalAfflictions2.push(`Exaltation strengthened by conjunctions: ${l.join(', ')}`); }
    { const g=astroData?.grahas??{}as any; const r=(['Mars','Mercury','Jupiter','Venus','Saturn']as const).filter(p=>g[p]?.retrograde===true); if(r.length) natalAfflictions2.push(`Natal retrograde: ${r.join(', ')}`); }
    { const rP=(placements2 as any)['Rahu'],kP=(placements2 as any)['Ketu']; if(rP?.house&&kP?.house) natalAfflictions2.push(`Nodal axis: Rahu H${rP.house} / Ketu H${kP.house}`); }
    const DUSTHANA2=new Set([6,8,12]); const KHL: Record<number,string>={5:'children/creativity',7:'relationships/marriage',9:'luck/dharma',10:'career/status'};
    if(houseLords){for(const kH of[5,7,9,10]as const){const lord=houseLords[kH];if(!lord)continue;const lPl=(placements2 as any)[lord];if(lPl&&DUSTHANA2.has(lPl.house))natalAfflictions2.push(`H${kH} lord ${lord} in H${lPl.house} (dusthana) — pressure on ${KHL[kH]}`);}}
    const h7m2=Object.entries(placements2).filter(([p,pl]:[string,any])=>pl.house===7&&(funcMap[p]??'neutral')==='malefic').map(([p])=>p); if(h7m2.length) natalAfflictions2.push(`Malefics in H7: ${h7m2.join(', ')}`);

    const allYogaFeeds2 = [...permanentYogasFromFeeds.filter((f:any)=>!f.ruleKey?.startsWith('sade_sati')),...activeYogasFromFeeds.filter((f:any)=>!f.ruleKey?.startsWith('sade_sati'))];
    const seenYK2=new Set<string>(); const dedup2=allYogaFeeds2.filter((f:any)=>{const k=(f.ruleKey||f.id||'').replace(/_natal$/,'').replace(/_timing$/,'');if(seenYK2.has(k))return false;seenYK2.add(k);return true;}).slice(0,16);
    const yogaLines2=dedup2.length?dedup2.map((f:any)=>`${f.message?.split(':')[0]?.trim()||f.ruleKey||'Yoga'} [${f.lifeArea||''}] (${f.severity||'mixed'}, ${(f.isPermanent!==undefined?f.isPermanent:f.ruleType==='natal')?'natal':'dasha-active'}): ${(f.description||f.message||'').substring(0,120)}`).join('\n'):'None detected';

    const seenYRK2=new Set(dedup2.map((f:any)=>(f.ruleKey||f.id||'').replace(/_natal$/,'').replace(/_timing$/,'')));
    const IS_COMBO2=(rk:string)=>rk.startsWith('saturn_')||rk.includes('_conjunction')||rk.includes('_combination');
    const combosRaw2=[...permanentYogasFromFeeds,...(reportV2Data?.feedWindows??[])].filter((f:any)=>{const rk=f.ruleKey||f.id||'';if(!rk||rk.startsWith('sade_sati')||rk.startsWith('kaal_sarp'))return false;if(seenYRK2.has(rk.replace(/_natal$/,'').replace(/_timing$/,'')))return false;return IS_COMBO2(rk);});
    const seenCK2=new Set<string>(); const comboPredLines2=combosRaw2.filter((f:any)=>{const k=(f.ruleKey||f.id||'').replace(/_natal$/,'').replace(/_timing$/,'');if(seenCK2.has(k))return false;seenCK2.add(k);return true;}).sort((a:any,b:any)=>Math.abs(b.score??0)-Math.abs(a.score??0)).slice(0,12).map((f:any)=>{const pol=(f.score??0)>0?'+':((f.score??0)<0?'⚠':'~');const win=f.window?.from&&f.window?.to?` [${new Date(f.window.from).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}–${new Date(f.window.to).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}]`:'';return `${pol} ${(f.message||f.ruleKey||'').split(':')[0]?.trim()||'Combination'} [${f.lifeArea||''}]${win}: ${(f.evidence?.[0]||f.message||'').substring(0,160)}`;}).join('\n')||'None detected';

    const computeScores2=(MD:string,AD:string,PD:string,SD:string|undefined)=>{const hs=d1HouseScores??{};return LIFE_AREA_LABELS.map((lbl,i)=>{const houses=LIFE_AREA_HOUSES[i];let nat=0,nc=0;for(const h of houses){nat+=Math.max(0,Math.min(100,(hs[h]??0)));nc++;}nat=nc?nat/nc:50;let adj=0;for(const p of[MD,AD,PD,...(SD?[SD]:[])].filter(x=>x&&x!=='Unknown')){const n=funcMap[p]??'neutral';const blended=blendedPlanetPct?.[p as PlanetName]??null;if(n==='yoga_karaka')adj+=15;else if(n==='benefic')adj+=8;else if(n==='malefic'){if(blended!==null&&blended>0){adj+=Math.min(8,blended/12);}else{adj-=10;}}}const score=Math.max(0,Math.min(100,Math.round(nat*0.5+50+adj*0.3)));const r=score>=75?'Strong':score>=55?'Good':score>=40?'Moderate':'Challenging';return `${lbl}:${score}(${r})`;});};

    const NAK_NAMES2=['Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra','Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hasta','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha','Uttara Ashadha','Shravana','Dhanishta','Shatabhisha','Purva Bhadrapada','Uttara Bhadrapada','Revati'];
    const weekRows2: string[] = [];
    for(let w=0;w<6;w++){
      const wkS=new Date();wkS.setDate(wkS.getDate()+w*7);const wkM=new Date();wkM.setDate(wkM.getDate()+w*7+3);const wkE=new Date();wkE.setDate(wkE.getDate()+w*7+7);
      let wMD2='Unknown',wAD2='Unknown',wPD2='Unknown',wSD2:string|undefined,sdWin2='';
      if(birthISO&&moonLonVal){try{const lv=getCurrentDashaLevels(birthISO,moonLonVal,wkM.toISOString());wMD2=lv?.md?.lord??'Unknown';wAD2=lv?.ad?.lord??'Unknown';wPD2=lv?.pd?.lord??'Unknown';wSD2=lv?.sd?.lord;if(lv?.sd?.start&&lv?.sd?.end){const sdS=new Date(lv.sd.start),sdE=new Date(lv.sd.end);sdWin2=` | SD:${sdS.toLocaleDateString('en-US',{month:'short',day:'numeric'})}→${sdE.toLocaleDateString('en-US',{month:'short',day:'numeric'})}`;if(Math.abs(sdS.getTime()-wkM.getTime())<5*86400000)sdWin2+=` [⚡STARTS ~${sdS.toLocaleDateString('en-US',{month:'short',day:'numeric'})}]`;if(Math.abs(sdE.getTime()-wkM.getTime())<5*86400000)sdWin2+=` [⚡ENDS ~${sdE.toLocaleDateString('en-US',{month:'short',day:'numeric'})}]`;}}catch{}}
      [wMD2,wAD2].filter(p=>p!=='Unknown').forEach(p=>mdAdLordSet2.add(p));[wPD2,...(wSD2?[wSD2]:[])].filter(p=>p!=='Unknown').forEach(p=>pdSdLordSet2.add(p));
      const wDS2=[`${pAbbr[wMD2]??wMD2} MD(${funcMap[wMD2]??'neutral'})`,`${pAbbr[wAD2]??wAD2} AD(${funcMap[wAD2]??'neutral'})`,`${pAbbr[wPD2]??wPD2} PD(${funcMap[wPD2]??'neutral'})`,...(wSD2?[`${pAbbr[wSD2]??wSD2} SD(${funcMap[wSD2]??'neutral'})`]:[])].join(' · ');
      const wSc2=computeScores2(wMD2,wAD2,wPD2,wSD2);
      const wWk2=[wPD2,...(wSD2?[wSD2]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)<0);const wElev2=[wPD2,...(wSD2?[wSD2]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)>=0);const wLE2=[wPD2,...(wSD2?[wSD2]:[])].filter(p=>{if(!p||p==='Unknown')return false;return(funcMap[p]??'neutral')!=='malefic'&&(blendedPlanetPct?.[p as PlanetName]??50)<35;});
      const wFl2=[...wWk2.map(p=>`⚠${p}(malefic,net-negative-blended:${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%)`),...wElev2.map(p=>`✓${p}(malefic-but-net-positive-blended:+${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%,interpret-as-beneficial)`),...wLE2.map(p=>`↓${p}(low,str:${Math.round(blendedPlanetPct?.[p as PlanetName]??50)}%)`)].join(',');
      let tL2='',mL2='';
      try{const A=require('astronomy-engine');const tS2=A.MakeTime(wkS),tM2=A.MakeTime(wkM),tE2=A.MakeTime(wkE);const sid2=(b:any,t:any)=>{const v=A.GeoVector(b,t,true);const e=A.Ecliptic(v);return((e.elon-ayanamsa)%360+360)%360;};const sH=toHouseFromLon(sid2(A.Body.Sun,tM2)),meH=toHouseFromLon(sid2(A.Body.Mercury,tM2)),vH=toHouseFromLon(sid2(A.Body.Venus,tM2)),maH=toHouseFromLon(sid2(A.Body.Mars,tM2)),jH=toHouseFromLon(sid2(A.Body.Jupiter,tM2)),saH=toHouseFromLon(sid2(A.Body.Saturn,tM2));const T2=(wkM.getTime()/86400000-10957.5)/36525;const rH2=toHouseFromLon(((((125.04455501-1934.1361849*T2+0.0020762*T2*T2)%360+360)%360)-ayanamsa+360)%360);const kH2=((rH2-1+6)%12)+1;const chk2=(b:any,ab:string)=>{const hs=toHouseFromLon(sid2(b,tS2)),he=toHouseFromLon(sid2(b,tE2));return hs!==he?`⚡${ab}→H${he}`:null;};const ch2=[chk2(A.Body.Sun,'Su'),chk2(A.Body.Mercury,'Me'),chk2(A.Body.Venus,'Ve'),chk2(A.Body.Mars,'Ma')].filter(Boolean).join(' ');tL2=`Su→H${sH} Me→H${meH} Ve→H${vH} Ma→H${maH} Ju→H${jH}(${funcMap['Jupiter']??'neutral'}) Sa→H${saH}(${funcMap['Saturn']??'neutral'}) Ra→H${rH2} Ke→H${kH2}${ch2?` | CHANGES:${ch2}`:''}`;const moHS2=toHouseFromLon(sid2(A.Body.Moon,tS2)),moHM2=toHouseFromLon(sid2(A.Body.Moon,tM2)),moHE2=toHouseFromLon(sid2(A.Body.Moon,tE2));const moNak2=NAK_NAMES2[Math.floor(sid2(A.Body.Moon,tM2)/(360/27))]??'';if(moHS2===moHM2&&moHM2===moHE2)mL2=`Mo→H${moHS2} all week (${moNak2} mid-week)`;else if(moHS2===moHM2)mL2=`Mo→H${moHS2} Mon–Thu → H${moHE2} Fri–Sun (${moNak2} mid-week)`;else if(moHM2===moHE2)mL2=`Mo→H${moHS2} Mon–Tue → H${moHM2} Wed–Sun (${moNak2} mid-week)`;else mL2=`Mo→H${moHS2} Mon → H${moHM2} mid-week → H${moHE2} Sun (${moNak2} mid-week)`;} catch{tL2='transit calc unavailable';}
      weekRows2.push(`## Week ${w+1} (${wkS.toLocaleDateString('en-US',{month:'short',day:'numeric'})}–${wkE.toLocaleDateString('en-US',{month:'short',day:'numeric'})})\nDasha: ${wDS2}${sdWin2}\nTransits: ${tL2}\nMoon: ${mL2}\nScores: ${wSc2.join(' | ')}\nFlags: ${wFl2||'none'}`);
    }

    const monthRows2: string[] = [];
    for(let m=0;m<3;m++){
      const mD=new Date();mD.setMonth(mD.getMonth()+m,15);
      let MD2='Unknown',AD2='Unknown',PD2='Unknown',SD2:string|undefined;
      if(birthISO&&moonLonVal){try{const lv=getCurrentDashaLevels(birthISO,moonLonVal,mD.toISOString());MD2=lv?.md?.lord??'Unknown';AD2=lv?.ad?.lord??'Unknown';PD2=lv?.pd?.lord??'Unknown';SD2=lv?.sd?.lord;}catch{}}
      [MD2,AD2].filter(p=>p!=='Unknown').forEach(p=>mdAdLordSet2.add(p));[PD2,...(SD2?[SD2]:[])].filter(p=>p!=='Unknown').forEach(p=>pdSdLordSet2.add(p));
      let mTr2='';try{const A=require('astronomy-engine');const t=A.MakeTime(mD);const sid3=(b:any)=>{const v=A.GeoVector(b,t,true);const e=A.Ecliptic(v);return((e.elon-ayanamsa)%360+360)%360;};const jH=toHouseFromLon(sid3(A.Body.Jupiter)),sH=toHouseFromLon(sid3(A.Body.Saturn)),maH=toHouseFromLon(sid3(A.Body.Mars));const T3=(mD.getTime()/86400000-10957.5)/36525;const rH=toHouseFromLon(((((125.04455501-1934.1361849*T3)%360+360)%360)-ayanamsa+360)%360);const kH=((rH-1+6)%12)+1;mTr2=`Ju→H${jH}(${funcMap['Jupiter']??'neutral'}) Sa→H${sH}(${funcMap['Saturn']??'neutral'}) Ma→H${maH}(${funcMap['Mars']??'neutral'}) Ra→H${rH} Ke→H${kH}`;}catch{mTr2='transit calc unavailable';}
      const mSc2=computeScores2(MD2,AD2,PD2,SD2);
      let sdD2='';try{const lv2=getCurrentDashaLevels(birthISO,moonLonVal,mD.toISOString());if(lv2?.sd?.start&&lv2?.sd?.end)sdD2=` | SDstarts:${new Date(lv2.sd.start).toLocaleDateString('en-US',{month:'short',day:'numeric'})} SDends:${new Date(lv2.sd.end).toLocaleDateString('en-US',{month:'short',day:'numeric'})}`;}catch{}
      const mDS2=[`${pAbbr[MD2]??MD2} MD(${funcMap[MD2]??'neutral'})`,`${pAbbr[AD2]??AD2} AD(${funcMap[AD2]??'neutral'})`,`${pAbbr[PD2]??PD2} PD(${funcMap[PD2]??'neutral'})`,...(SD2?[`${pAbbr[SD2]??SD2} SD(${funcMap[SD2]??'neutral'})`]:[])].join(' · ');
      // Only flag malefic lords where blended score is actually negative.
      const mWk2=[PD2,...(SD2?[SD2]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)<0);const mElev2=[PD2,...(SD2?[SD2]:[])].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)>=0);const mLE2=[PD2,...(SD2?[SD2]:[])].filter((p:string)=>{if(!p||p==='Unknown')return false;return(funcMap[p]??'neutral')!=='malefic'&&(blendedPlanetPct?.[p as PlanetName]??50)<35;});
      const mWStr2=mWk2.length?` | MaleficLords(net-negative):[${mWk2.map(p=>`${p}(blended:${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%)`).join(',')}]`:'';const mEStr2=mElev2.length?` | MaleficButNetPositive:[${mElev2.map(p=>`${p}(blended:+${Math.round(blendedPlanetPct?.[p as PlanetName]??0)}%,interpret-as-beneficial)`).join(',')}]`:'';const mLStr2=mLE2.length?` | LowEnergy:[${mLE2.map(p=>`${p}(str:${Math.round(blendedPlanetPct?.[p as PlanetName]??50)}%)`).join(',')}]`:'';
      monthRows2.push(`## ${mD.toLocaleDateString('en-US',{month:'short',year:'numeric'})}\nDasha: ${mDS2}\nTransits: ${mTr2}${mWStr2}${mEStr2}${mLStr2}${sdD2}\nScores: ${mSc2.join(' | ')}`);
    }

    // Remedies for malefic lords
    // Only recommend remedies where the malefic lord has a net-negative blended score.
    const allWL2=new Set([...pdSdLordSet2,...mdAdLordSet2].filter(p=>p&&p!=='Unknown'&&(funcMap[p]??'neutral')==='malefic'&&(blendedPlanetPct?.[p as PlanetName]??0)<0));
    const rByPlanet2: Record<string,string[]>={};
    if(allWL2.size>0){const{data:rD}=await supabase.from('remedies').select('title,description,category,related_entity').in('related_entity',[...allWL2]);if(rD){for(const r of rD){if(!rByPlanet2[r.related_entity])rByPlanet2[r.related_entity]=[];if(rByPlanet2[r.related_entity].length<3)rByPlanet2[r.related_entity].push(`[${r.category}] ${r.title}: ${(r.description||'').substring(0,110)}`);}}};
    const remedySection2=Object.entries(rByPlanet2).map(([planet,rems])=>`${planet} (${funcMap[planet]??'neutral'}, str:${Math.round(blendedPlanetPct?.[planet as PlanetName]??0)}%):\n${rems.map(r=>`  • ${r}`).join('\n')}`).join('\n\n');

    // Build the identical prompt structure (same as callCurrentPeriodAnalysis)
    const comprehensivePrompt = `You are an expert Vedic astrologer providing a personalised current period analysis with a week-by-week breakdown and 3-month summary.

VOICE & TONE:
- Address the native DIRECTLY using "you" and "your" throughout. NEVER use third-person.
- Tone: warm, honest, expert advisor — direct and specific, never generic.

NATIVE PROFILE:
Name: ${name} | Age: ${ageYears} years
${ageContextLine}
Ascendant: ${ascendantStr} | Moon Sign: ${moonSignStr}
Functional nature for THIS chart: ${Object.entries(funcMap).map(([p,n])=>`${p}=${n}`).join(', ')}
Current Dasha chain: ${curDashaStr2}

ACTIVE DOSHAS — MANDATORY to mention every week:
${doshasLines2.length ? doshasLines2.join('\n') : 'None active'}

NATAL CHART AFFLICTIONS & STRENGTHS:
${natalAfflictions2.length ? natalAfflictions2.join('\n') : 'No major natal afflictions'}

ACTIVE YOGAS (cite only when relevant to that week's/month's life area scores):
${yogaLines2}

NATAL PLANETARY COMBINATIONS:
${comboPredLines2}

REMEDIES FOR MALEFIC DASHA LORDS:
${remedySection2 || 'None required'}

6-WEEK FORECAST DATA:
${weekRows2.join('\n\n')}

3-MONTH SUMMARY DATA:
${monthRows2.join('\n\n')}

====================
CLASSICAL VEDIC RULES — apply whenever chart data matches
====================

MARRIAGE & RELATIONSHIPS:
- Ketu in H7: dissatisfaction, detachment, separation risk. EXCEPTION: Jupiter aspecting H7 mitigates.
- 12th lord in H7: marriage problems (loss, isolation bleeding into partnership).
- Saturn + Rahu both in H7: serious marriage difficulties. State directly.
- 7th lord in dusthana (H6/8/12): marital stress.

GENERAL PREDICTIVE PRINCIPLES:
- Natural malefics in upachaya houses (3,6,11): GOOD results. Do NOT treat as problematic.
- Jupiter's aspect is ALWAYS auspicious even when Jupiter is a functional malefic.
- 8th house planet's dasha = fundamental life transformation.
- Empty house: results delivered through opposite house during relevant dashas/transits.
- BLENDED SCORE RULES (CRITICAL - override all defaults): The blended/str% is a SIGNED net score: positive = net beneficial, negative = net harmful. A functional malefic flagged as malefic-but-net-positive-blended MUST be interpreted as delivering GOOD results - do NOT warn against it. Only malefic,net-negative-blended planets are genuinely harmful this period. Remedies only for net-negative planets.

PLANET–ILLNESS MAPPING:
Sun=acidity/eyes/heart, Moon=anxiety/sleep, Mars=blood/skin, Saturn=stomach/bones/arthritis (chronic), Venus=reproductive/throat/diabetes, Mercury=nervous system, Jupiter=liver/lungs, Ketu=mysterious illness, Rahu=mental health/anxiety

SADE SATI NUANCES:
- Saturn in H2/H5/H9 during Sade Sati: GOOD results.
- Saturn in H3/H6/H8/H12 during Sade Sati: PAINFUL — be direct.
- Remedies: Shani Gayatri mantra, 7 Mukhi Rudraksha, offer oil to shami tree, donate mustard oil and black sesame on Saturdays.

INTERPRETATION HIERARCHY:
1. PRIMARY: housePlanetNote
2. SECONDARY: base planet-in-house prediction
3. TERTIARY: nakshatra nuance
Blend all three into ONE flowing sentence.

VARGA CONFIRMATION: Career→D10, Relationships→D9, Property→D4, Children→D7.

CHARA KARAKA: AmK=career, DK=marriage/partner, PuK=children, GK=health/obstacles.

YOGA ACTIVATION LOGIC: OCCUPATION (fully active) > ASPECT (active via aspect) > LORDSHIP (mildly active). State level explicitly.

====================
MANDATORY OUTPUT STRUCTURE
====================

BE CONCISE. Every section must be SHORT. The entire response must be completable in one reply — never cut off.

## Current State  (2–3 sentences only; ≤60 words)
## Week 1 through ## Week 6  (3 sentences each; cover PD/SD+transit, Moon journey+nakshatra, one action/remedy/caution)
## Month by Month  (3–4 sentences each; dasha arc + best/worst score + one transit + one remedy if needed)
## Key Dates & Turning Points  (3–5 bullets — most impactful only)
## What To Do Now  (2 sentences max; name one specific planet/house/lord)

ANTI-REPETITION: Rotate yogas, dosha angles, remedies. Each week must contain one unique observation.
Score guide: ≥75=flourishing · 55-74=steady growth · 40-54=needs attention · <40=challenging`;

    const context = chatContextData ? buildChatContext(chatContextData, { includeFeeds: true, includeInterpretations: true, includeVargas: true }) : null;
    if (!context) { setReportCurrentPeriodLoading(false); return; }

    await streamReportAI(comprehensivePrompt, context, 'gpt-5.4', setReportCurrentPeriodContent, setReportCurrentPeriodLoading, session.access_token, 16000);
  } catch (err: any) {
    console.error('[Report CurrentPeriod]', err);
    setReportCurrentPeriodContent(`Error: ${err.message}`);
    setReportCurrentPeriodLoading(false);
  }
};

// ── Year-ahead Monthly Predictions (admin-only) ───────────────────────────────
const callReportYearForecast = async () => {
  if (!astroData || profile?.role !== 'admin') return;
  if (reportAiCacheKey) localStorage.removeItem(`${reportAiCacheKey}_year_forecast`);
  setReportYearForecastContent('');
  setReportYearForecastLoading(true);

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setReportYearForecastLoading(false); return; }

    const ascSign = astroData.asc?.ascendantSignIndex ?? 1;
    const funcMap = classifyFunctionalForLagna(ascSign) as Record<string, string>;
    const moonLon = astroData.moon?.siderealLongitudeDeg ?? astroData.grahas?.Moon?.lon ?? 0;
    const birthISO = utcDateTime ?? '';
    const ayanamsa = 24.14;
    const LIFE_AREA_LABELS = ['Health','Wealth','Career','Business','Marriage','Children','Property','Education'];
    const LIFE_AREA_HOUSES = [[1,6,8,12],[1,2,5,9,11,12],[1,6,10],[1,3,7,11],[1,7],[1,5],[1,4],[2,4,5]];
    const pAbbr: Record<string,string> = { Sun:'Su',Moon:'Mo',Mars:'Ma',Mercury:'Me',Jupiter:'Ju',Venus:'Ve',Saturn:'Sa',Rahu:'Ra',Ketu:'Ke' };
    const toHouseFromLon = (lon: number) => { const s = Math.floor(((lon%360)+360)%360/30)+1; return ((s-ascSign+12)%12)+1; };

    // Collect all PD/SD lords across months (for targeted remedies) and MD/AD (for year remedies)
    const pdSdLordSet = new Set<string>();
    const mdAdLordSet = new Set<string>();

    const months12 = Array.from({length:12},(_,i)=>{ const d=new Date(); d.setMonth(d.getMonth()+i,15); return d; });
    const monthRows: string[] = [];

    for (const date of months12) {
      let MD='Unknown', AD='Unknown', PD='Unknown', SD: string|undefined;
      if (birthISO && moonLon) {
        try {
          const lv = getCurrentDashaLevels(birthISO, moonLon, date.toISOString());
          MD = lv?.md?.lord ?? 'Unknown'; AD = lv?.ad?.lord ?? 'Unknown';
          PD = lv?.pd?.lord ?? 'Unknown'; SD = lv?.sd?.lord;
        } catch {}
      }
      [MD,AD].filter(p=>p!=='Unknown').forEach(p=>mdAdLordSet.add(p));
      [PD, ...(SD?[SD]:[])].filter(p=>p!=='Unknown').forEach(p=>pdSdLordSet.add(p));

      // Transit houses via astronomy-engine
      let transitLine = '';
      try {
        const Astro = require('astronomy-engine');
        const time = Astro.MakeTime(date);
        const toSid = (body: any) => { const v=Astro.GeoVector(body,time,true); const e=Astro.Ecliptic(v); return ((e.elon-ayanamsa)%360+360)%360; };
        const jupH = toHouseFromLon(toSid(Astro.Body.Jupiter));
        const satH = toHouseFromLon(toSid(Astro.Body.Saturn));
        const marH = toHouseFromLon(toSid(Astro.Body.Mars));
        const T = (date.getTime()/86400000-10957.5)/36525;
        const rahuTrop = ((125.04455501-1934.1361849*T+0.0020762*T*T)%360+360)%360;
        const rahuH = toHouseFromLon(((rahuTrop-ayanamsa)%360+360)%360);
        const ketuH = ((rahuH-1+6)%12)+1;
        transitLine = `Ju→H${jupH}(${funcMap['Jupiter']??'neutral'}) Sa→H${satH}(${funcMap['Saturn']??'neutral'}) Ma→H${marH}(${funcMap['Mars']??'neutral'}) Ra→H${rahuH} Ke→H${ketuH}`;
      } catch { transitLine = 'transit calc unavailable'; }

      // Life area scores: natal (avg house scores) + dasha adjustment
      const scoreLines: string[] = [];
      LIFE_AREA_LABELS.forEach((label, i) => {
        const houses = LIFE_AREA_HOUSES[i];
        const hs = d1HouseScores ?? {};
        let natal = 0, nc = 0;
        for (const h of houses) { natal += Math.max(0,Math.min(100,(hs[h]??0))); nc++; }
        natal = nc ? natal/nc : 50;
        let dashaAdj = 0;
        for (const p of [MD,AD,PD,...(SD?[SD]:[])].filter(x=>x!=='Unknown')) {
          const n = funcMap[p] ?? 'neutral';
          if (n==='yoga_karaka') dashaAdj += 15;
          else if (n==='benefic') dashaAdj += 8;
          else if (n==='malefic') dashaAdj -= 10;
        }
        const score = Math.max(0, Math.min(100, Math.round(natal*0.5 + 50 + dashaAdj*0.3)));
        const rating = score>=75?'Strong':score>=55?'Good':score>=40?'Moderate':'Challenging';
        scoreLines.push(`${label}:${score}(${rating})`);
      });

      // Flag functionally malefic PD/SD lords only — do NOT use blendedPlanetPct < 0 as proxy for malefic
      // (neutral planets with low strength are NOT malefic — funcMap is the source of truth per lagna)
      const weakLords = [PD,...(SD?[SD]:[])].filter(p => {
        if (!p||p==='Unknown') return false;
        return (funcMap[p]??'neutral')==='malefic';
      });
      // Separately flag low-energy lords (neutral/benefic but weak blended strength)
      const lowEnergyLords = [PD,...(SD?[SD]:[])].filter(p => {
        if (!p||p==='Unknown') return false;
        const n = funcMap[p]??'neutral';
        const str = blendedPlanetPct?.[p as PlanetName]??50;
        return n!=='malefic' && str < 35;
      });

      const mnLabel = date.toLocaleDateString('en-US',{month:'short',year:'numeric'});
      // SD start/end for important-date hints in the paragraph
      let sdDates = '';
      try {
        const lv2 = getCurrentDashaLevels(birthISO, moonLon, date.toISOString());
        if (lv2?.sd?.start && lv2?.sd?.end) {
          sdDates = ` | SDstarts:${new Date(lv2.sd.start).toLocaleDateString('en-US',{month:'short',day:'numeric'})} SDends:${new Date(lv2.sd.end).toLocaleDateString('en-US',{month:'short',day:'numeric'})}`;
        }
      } catch {}
      const dashaStr = [
        `${pAbbr[MD]??MD} MD(${funcMap[MD]??'neutral'})`,
        `${pAbbr[AD]??AD} AD(${funcMap[AD]??'neutral'})`,
        `${pAbbr[PD]??PD} PD(${funcMap[PD]??'neutral'})`,
        ...(SD ? [`${pAbbr[SD]??SD} SD(${funcMap[SD]??'neutral'})`] : []),
      ].join(' · ');
      const weakStr = weakLords.length ? ` | MaleficLords:[${weakLords.map(p=>`${p}(str:${Math.round(blendedPlanetPct?.[p as PlanetName]??50)}%)`).join(',')}]` : '';
      const lowEnergyStr = lowEnergyLords.length ? ` | LowEnergyLords:[${lowEnergyLords.map(p=>`${p}(${funcMap[p]??'neutral'},str:${Math.round(blendedPlanetPct?.[p as PlanetName]??50)}%)`).join(',')}]` : '';
      monthRows.push(`## ${mnLabel}\nDasha: ${dashaStr}\nTransits: ${transitLine}${weakStr}${lowEnergyStr}${sdDates}\nScores: ${scoreLines.join(' | ')}`);
    }

    // Fetch remedies: strictly functionally malefic lords only (not neutral/benefic with low strength)
    const allWeakLords = new Set([...pdSdLordSet, ...mdAdLordSet].filter(p => {
      if (!p||p==='Unknown') return false;
      return (funcMap[p]??'neutral')==='malefic';
    }));

    const remedyByPlanet: Record<string, string[]> = {};
    if (allWeakLords.size > 0) {
      const { data: rData } = await supabase
        .from('remedies')
        .select('title, description, category, related_entity')
        .in('related_entity', [...allWeakLords]);
      if (rData) {
        for (const r of rData) {
          if (!remedyByPlanet[r.related_entity]) remedyByPlanet[r.related_entity] = [];
          if (remedyByPlanet[r.related_entity].length < 3)
            remedyByPlanet[r.related_entity].push(`[${r.category}] ${r.title}: ${(r.description||'').substring(0,110)}`);
        }
      }
    }

    const remedySection = Object.entries(remedyByPlanet).map(([planet, rems]) =>
      `${planet} (${funcMap[planet]??'neutral'}, blended strength ${Math.round(blendedPlanetPct?.[planet as PlanetName]??0)}%):\n` +
      rems.map(r=>`  • ${r}`).join('\n')
    ).join('\n\n');

    // Build yoga lines from raw feed items (which carry lifeArea directly — not lost in reportV2Data mapping)
    // Merge permanent + active, deduplicate by ruleKey, take up to 16
    const allYogaFeeds = [
      ...permanentYogasFromFeeds.filter(f => !f.ruleKey?.startsWith('sade_sati')),
      ...activeYogasFromFeeds.filter(f => !f.ruleKey?.startsWith('sade_sati')),
    ];
    const seenYogaKeys = new Set<string>();
    const dedupedYogaFeeds = allYogaFeeds.filter(f => {
      const k = (f.ruleKey || f.id || '').replace(/_natal$/, '').replace(/_timing$/, '');
      if (seenYogaKeys.has(k)) return false;
      seenYogaKeys.add(k);
      return true;
    }).slice(0, 16);

    const yogaLines = dedupedYogaFeeds.length
      ? dedupedYogaFeeds.map(f => {
          const name = f.message?.split(':')[0]?.trim() || f.ruleKey || 'Yoga';
          const lifeArea = f.lifeArea || '';
          const severity = f.severity || 'mixed';
          const isPermanent = f.isPermanent !== undefined ? f.isPermanent : f.ruleType === 'natal';
          const stateTag = isPermanent ? 'natal' : 'dasha-active';
          const desc = (f.description || f.message || '').substring(0, 120);
          return `${name} [${lifeArea}] (${severity}, ${stateTag}): ${desc}`;
        }).join('\n')
      : (reportV2Data?.yogas ?? [])
          .filter(y => y.isActive || y.isPermanent)
          .slice(0, 14)
          .map(y => `${y.name} [${y.type}] (${y.severity}, ${y.isPermanent ? 'natal' : 'dasha-active'}): ${(y.description||'').substring(0, 120)}`)
          .join('\n') || 'None detected';

    // Planetary combination predictions — same data that powers the Opportunity & Caution Windows section
    // Sources: permanentYogasFromFeeds (carries ruleType) + reportV2Data.feedWindows (confirmed windows)
    // Filter: planet conjunction/combination keys; exclude sade_sati, kaal_sarp, and anything already in yogaLines
    const seenYogaRuleKeys = new Set(dedupedYogaFeeds.map((f: any) => (f.ruleKey || f.id || '').replace(/_natal$/, '').replace(/_timing$/, '')));
    const IS_COMBO = (rk: string) => rk.startsWith('saturn_') || rk.includes('_conjunction') || rk.includes('_combination');
    const comboSourceFeeds = [
      ...permanentYogasFromFeeds,
      ...(reportV2Data?.feedWindows ?? []),
    ].filter((f: any) => {
      const rk: string = f.ruleKey || f.id || '';
      if (!rk) return false;
      if (rk.startsWith('sade_sati') || rk.startsWith('kaal_sarp')) return false;
      const normKey = rk.replace(/_natal$/, '').replace(/_timing$/, '');
      if (seenYogaRuleKeys.has(normKey)) return false;
      return IS_COMBO(rk);
    });
    const seenComboKeys = new Set<string>();
    const dedupedCombos = comboSourceFeeds.filter((f: any) => {
      const k = (f.ruleKey || f.id || '').replace(/_natal$/, '').replace(/_timing$/, '');
      if (seenComboKeys.has(k)) return false;
      seenComboKeys.add(k);
      return true;
    }).sort((a: any, b: any) => Math.abs(b.score ?? 0) - Math.abs(a.score ?? 0)).slice(0, 12);

    const comboPredLines = dedupedCombos.length
      ? dedupedCombos.map((f: any) => {
          const name = (f.message || f.ruleKey || '').split(':')[0]?.trim() || f.ruleKey || 'Combination';
          const lifeArea = f.lifeArea || '';
          const score = f.score ?? 0;
          const polarity = score > 0 ? '+' : score < 0 ? '⚠' : '~';
          const windowStr = f.window?.from && f.window?.to
            ? ` [active ${new Date(f.window.from).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}–${new Date(f.window.to).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}]`
            : '';
          // First bullet line from evidence carries the richest detail
          const detail = (f.evidence?.[0] || f.message || '').substring(0, 160);
          return `${polarity} ${name} [${lifeArea}]${windowStr}: ${detail}`;
        }).join('\n')
      : 'None detected';

    // Active doshas — use already-computed sadeSatiData / kaalSarpData state (no recalculation)
    const doshasLines: string[] = [];
    const placements = astroData?.placements ?? {};
    if (sadeSatiData?.isActive) {
      const phase = sadeSatiData.currentPhase ?? 'unknown';
      const phaseEffect = phase==='peak'    ? 'PEAK phase — maximum Saturn pressure on Moon-sign matters; health, career and finances face headwinds'
                        : phase==='rising'  ? 'RISING phase — challenges building gradually; delays, health issues and emotional strain possible'
                        : phase==='setting' ? 'SETTING phase — challenges easing; gradual relief as Saturn moves away from Moon sign'
                        : 'active phase';
      doshasLines.push(`Shani Sadesaati ACTIVE — ${phaseEffect}. Period: ${sadeSatiData.from ?? 'N/A'} → ${sadeSatiData.to ?? 'N/A'}. MUST be mentioned every month — do NOT ignore this.`);
    }
    if (kaalSarpData && (kaalSarpData.isPresent || (kaalSarpData as any).present)) {
      // Use specificType ("Anant Kaal Sarp", "Takshak Kaal Sarp", etc.) — avoids "type=full, severity=full" redundancy
      const ksName = (kaalSarpData as any).specificType || kaalSarpData.type || 'Kaal Sarp';
      const ksDesc = (kaalSarpData as any).specificEffects || kaalSarpData.details || 'All planets hemmed between Rahu-Ketu axis; ambition is strong but obstacles appear before fulfilment.';
      doshasLines.push(`Kaal Sarp Yoga PRESENT — ${ksName} (Rahu H${(kaalSarpData as any).rahuHouse}, Ketu H${(kaalSarpData as any).ketuHouse}). ${ksDesc}`);
    }
    // Mangal Dosha — Mars in 1/2/4/7/8/12; use existing placements (no recalc)
    {
      const marsPl = (placements as any)['Mars'];
      if (marsPl && new Set([1,2,4,7,8,12]).has(marsPl.house)) {
        doshasLines.push(`Mangal Dosha PRESENT — Mars in H${marsPl.house}. Heightens intensity and conflict in marriage/relationships; delays partnership or causes friction. Especially significant for H7/H8 matters.`);
      }
    }
    // Guru Chandal Yoga — Rahu or Ketu conjunct Jupiter same house; use existing placements (no recalc)
    {
      const jupPl  = (placements as any)['Jupiter'];
      const rahuPl2 = (placements as any)['Rahu'];
      const ketuPl2 = (placements as any)['Ketu'];
      if (jupPl && rahuPl2 && jupPl.house === rahuPl2.house) {
        doshasLines.push(`Guru Chandal Yoga PRESENT — Jupiter conjunct Rahu in H${jupPl.house}. Unconventional wisdom; ethical/religious conflicts possible; gains through non-traditional means. Judgment may be clouded.`);
      } else if (jupPl && ketuPl2 && jupPl.house === ketuPl2.house) {
        doshasLines.push(`Guru Ketu Yoga PRESENT — Jupiter conjunct Ketu in H${jupPl.house}. Deep spiritual wisdom, past-life mastery of Jupiter themes; detachment from material pursuits; moksha-oriented focus.`);
      }
    }

    // Natal afflictions — use existing combust / debil / exalt / conjunctDebil / conjunctExalt state (no recalculation)
    const natalAfflictions: string[] = [];
    if (combust) {
      const combustList = Object.entries(combust).filter(([,v])=>v).map(([p])=>p);
      if (combustList.length) natalAfflictions.push(`Combust (weakened by Sun proximity): ${combustList.join(', ')}`);
    }
    if (debil) {
      const debilList = Object.entries(debil).filter(([,v])=>v).map(([p])=>p);
      if (debilList.length) natalAfflictions.push(`Debilitated in natal chart: ${debilList.join(', ')}`);
    }
    if (exalt) {
      const exaltList = Object.entries(exalt).filter(([,v])=>v).map(([p])=>p);
      if (exaltList.length) natalAfflictions.push(`Exalted (strong significations): ${exaltList.join(', ')}`);
    }
    // conjunctDebil / conjunctExalt — computed state previously unused here (same oversight as yoga lifeArea bug)
    if (conjunctDebil) {
      const list = Object.entries(conjunctDebil).filter(([,v])=>v).map(([p])=>p);
      if (list.length) natalAfflictions.push(`Debility enhanced by conjunctions: ${list.join(', ')} — weakened further by conjunct placement`);
    }
    if (conjunctExalt) {
      const list = Object.entries(conjunctExalt).filter(([,v])=>v).map(([p])=>p);
      if (list.length) natalAfflictions.push(`Exaltation enhanced by conjunctions: ${list.join(', ')} — extra strength from conjunct placement`);
    }
    // Natal retrograde planets — from astroData.grahas (no recalc needed)
    {
      const grahas = astroData?.grahas ?? {} as any;
      const retroList = (['Mars','Mercury','Jupiter','Venus','Saturn'] as const).filter(p => grahas[p]?.retrograde === true);
      if (retroList.length) natalAfflictions.push(`Natal retrograde: ${retroList.join(', ')} — internalized energy; patience needed before results manifest`);
    }
    // Rahu/Ketu nodal axis — add for context even when no Kaal Sarp; use existing placements
    {
      const rahuPl = (placements as any)['Rahu'];
      const ketuPl = (placements as any)['Ketu'];
      if (rahuPl?.house && ketuPl?.house) {
        natalAfflictions.push(`Nodal axis: Rahu H${rahuPl.house} (worldly desire/obsession) opposite Ketu H${ketuPl.house} (past mastery/detachment)`);
      }
    }
    // Key house lord placements using already-computed houseLords memo
    const DUSTHANA = new Set([6,8,12]);
    const KEY_HOUSE_LABEL: Record<number,string> = { 5:'children/creativity', 7:'relationships/marriage', 9:'luck/dharma', 10:'career/status' };
    if (houseLords) {
      for (const keyH of [5,7,9,10] as const) {
        const lord = houseLords[keyH];
        if (!lord) continue;
        const lordPl = (placements as any)[lord];
        if (!lordPl) continue;
        if (DUSTHANA.has(lordPl.house)) {
          natalAfflictions.push(`H${keyH} lord ${lord} in H${lordPl.house} (dusthana) — pressure on ${KEY_HOUSE_LABEL[keyH]}`);
        }
      }
    }
    // Malefics occupying 7th house
    const h7Malefics = Object.entries(placements).filter(([p, pl]: [string,any]) => pl.house===7 && (funcMap[p]??'neutral')==='malefic').map(([p])=>p);
    if (h7Malefics.length) natalAfflictions.push(`Malefic(s) in 7th house: ${h7Malefics.join(', ')} — natal relationship tension`);

    const name = reportV2Data?.birthDetails?.name?.split(' ')[0] ?? 'the native';
    const ascendantStr = reportV2Data?.birthDetails?.ascendant ?? astroData.asc?.ascendantSign ?? '';
    const moonSign = reportV2Data?.birthDetails?.moonSign ?? '';
    const curMD = reportV2Data?.dashas?.current?.MD?.planet ?? '';
    const curAD = reportV2Data?.dashas?.current?.AD?.planet ?? '';

    // Age calculation for life-area prioritisation
    const dobStr = reportV2Data?.birthDetails?.dateOfBirth ?? localDateTime ?? birthISO;
    let ageYears = 35; // fallback mid-range
    try { if (dobStr) { const diff = Date.now() - new Date(dobStr).getTime(); ageYears = Math.floor(diff / (365.25 * 24 * 3600 * 1000)); } } catch {}
    const ageBracket = ageYears < 25 ? 'young' : ageYears < 60 ? 'adult' : 'senior';
    const ageContextLine = ageBracket === 'young'
      ? `The native is ${ageYears} years old (age 10–24 bracket). Prioritise: Education, Health, Love relationships, and early Career in every month's narrative. Wealth/Business/Children are lower priority.`
      : ageBracket === 'adult'
      ? `The native is ${ageYears} years old (age 25–60 bracket). Prioritise: Career, Business, Wealth, Investments, Recognition, Children, Parents' health, and Health in every month's narrative. Education is background.`
      : `The native is ${ageYears} years old (60+ bracket). Prioritise: Health, Medical care, Investments/Financial security, Spiritual growth, and Children/Grandchildren relationships in every month's narrative.`;

    const prompt = `You are an expert Vedic astrologer writing personalised month-wise predictions directly addressed to the native.

IMPORTANT — VOICE & TONE:
- Address the native DIRECTLY using "you" and "your" throughout. NEVER use third-person ("he", "she", "Abhay experiences"). Write as if speaking directly to them.
- Tone: warm, honest, expert advisor having a personal conversation.

NATIVE PROFILE:
Name: ${name} | Age: ${ageYears} years
${ageContextLine}
Ascendant: ${ascendantStr} | Moon Sign: ${moonSign}
Functional nature for THIS chart (lagna-specific — do not override): ${Object.entries(funcMap).map(([p,n])=>`${p}=${n}`).join(', ')}
Current Mahadasha: ${curMD} (${funcMap[curMD]??'neutral'}, strength ${Math.round(blendedPlanetPct?.[curMD as PlanetName]??50)}%)
Current Antardasha: ${curAD} (${funcMap[curAD]??'neutral'}, strength ${Math.round(blendedPlanetPct?.[curAD as PlanetName]??50)}%)

ACTIVE DOSHAS — MANDATORY to mention every relevant month:
${doshasLines.length ? doshasLines.join('\n') : 'None active'}

NATAL CHART AFFLICTIONS & STRENGTHS:
${natalAfflictions.length ? natalAfflictions.join('\n') : 'No major natal afflictions'}

ACTIVE YOGAS (life-area mapped — cite only when relevant to that month's priority areas):
${yogaLines}

NATAL PLANETARY COMBINATIONS (Saturn conjunctions & other two-planet combinations — same data as Opportunity & Caution Windows):
${comboPredLines}

REMEDIES FOR MALEFIC DASHA LORDS (weave only into months where these lords are PD/SD):
${remedySection || 'None required — no functionally malefic lords active'}

12-MONTH FORECAST DATA:
${monthRows.join('\n\n')}

====================
CLASSICAL VEDIC RULES — apply whenever the chart data matches
====================

MARRIAGE & RELATIONSHIPS:
- Ketu in 7th house (D1): dissatisfaction, detachment, high risk of separation/divorce. EXCEPTION: Jupiter aspecting H7 mitigates significantly — state mitigation if present, otherwise state divorce/separation risk directly.
- 12th lord in 7th house: marriage problems (loss, isolation bleeding into partnership). Remedy: native settles away from birthplace after marriage, or spouse comes from a different culture/region/religion.
- Saturn + Rahu both in 7th house: serious marriage difficulties — delays, power struggles, or complete breakdown. State directly.
- 7th lord in dusthana (6th/8th/12th): marital stress. Provide profession-based remedy: 8th house lord to surgeon/healer/researcher/insurance; 6th to medical/legal/defence/service; 12th to spiritual/foreign/hospital work.
- Marriage timing: dasha of 7th lord (D1), Navamsa 7th/1st lord, Venus dasha, Jupiter dasha. Rahu/Saturn dasha if active marriage yogas present. Cross-check D9.

GENERAL PREDICTIVE PRINCIPLES:
- Natural malefics (Saturn, Mars, Sun, Rahu, Ketu) in upachaya houses (3,6,11): GOOD results. Do NOT treat as problematic.
- Jupiter's aspect is ALWAYS auspicious even when Jupiter is a functional malefic — its dasha/lordship can be problematic but the ASPECT remains a positive modifier.
- Empty house: results delivered through opposite house (7th from it) during relevant dashas/transits.
- 8th house planet's dasha = fundamental life transformation (endings + new beginnings). Direction depends on functional nature and blendedStrength.
- Rahu dasha/antardasha: natives frequently become blunt or harsh in speech; especially if Rahu connects to H2/H3.
- BLENDED SCORE RULES (CRITICAL - override all defaults): The blended/str% is a SIGNED net score: positive = net beneficial karmic energy, negative = net harmful. A functional malefic flagged as malefic-but-net-positive-blended MUST be read as delivering GOOD results - do NOT warn against it. Only malefic,net-negative-blended planets are genuinely harmful this period. Remedies only for net-negative planets, never for net-positive ones.

PLANET-ILLNESS MAPPING (health questions):
Sun=acidity/eyes/heart, Moon=anxiety/sleep/depression (female: PCOD/breast cancer), Mars=blood/skin/vitiligo, Saturn=stomach/bones/arthritis (Saturn involvement makes illness CHRONIC), Venus=reproductive/throat/diabetes, Mercury=nervous system/paralysis/respiratory, Jupiter=liver/gallbladder/lungs, Ketu=mysterious/misdiagnosed/karmic illness, Rahu=mental health/overthinking/anxiety

SADE SATI NUANCES:
- Saturn in H2/H5/H9 during Sade Sati: GOOD results — discipline, structure, meaningful growth. State favourably.
- Saturn in H3/H6/H8/H12 during Sade Sati: PAINFUL — career collapse, health crises, financial devastation. Be direct. Do NOT soften.
- Remedies (always provide when Sade Sati is active): Shani Gayatri mantra, Nilanjan Samaabhasam mantra, 7 Mukhi Rudraksha, offer water to shami tree, donate mustard oil/black sesame on Saturdays.

====================
INTERPRETATION HIERARCHY — use this priority order for every prediction
====================

1. PRIMARY: House-Planet Specific Notes (housePlanetNote in context) — combines planet nature WITH that house domain. Make this the foundation of every planet-in-house statement.
2. SECONDARY: Base Planet-in-House Predictions — general planet-house combination. Supplement the primary.
3. TERTIARY: Nakshatra-Specific Layer — degree-level nuance. Add for timing and fine-tuning.

Blending rule: Weave all three into a single flowing sentence — NEVER list layers separately.
CORRECT: "Your career is marked by innovative engineering driven by Mars filtered through Dhanishta ambition in Aquarius — pioneering new methods with relentless drive."
WRONG: "Mars in H10. Dhanishta gives ambition. Aquarius adds innovation."

====================
VARGA / DIVISIONAL CHART RULES — confirm D1 with vargas before stating conclusions
====================

D9=marriage/dharma, D10=career/profession, D4=property, D7=children, D2=wealth

Integration rules:
- Strong in BOTH D1 and relevant varga = powerful confirmed results; mention both charts.
- Weak in D1 but strong in varga = delayed success in that area; state the delay.
- Strong in D1 but weak in varga = initial promise that does not fully materialise; say so.
CORRECT: "Your D10 shows Jupiter in its own sign — the D1 career promise is confirmed and will become your actual profession, not just a potential."
WRONG: "Jupiter is in Sagittarius in D10." (bare fact without synthesis)

====================
CHARA KARAKA ANALYSIS — apply when charaKarakas are present in context
====================

AmK (Amatya Karaka) = career/intellectual development — reference for career/business months.
DK (Dara Karaka) = spouse/partnerships — reference for marriage/relationship months. Always name the DK planet: "Your Dara Karaka [planet] indicates..."
PuK (Putra Karaka) = children/creativity — reference for children months.
AK (Atma Karaka) = soul agenda/major life purpose — reference for spirituality discussions.
GK (Gnati Karaka) = competition/disease/obstacles — reference for health/challenge months.

Rule: When the month's priority life area maps to a Chara Karaka, name the karaka planet and state its blendedStrength and functional nature from NATIVE PROFILE above.

====================
YOGA ACTIVATION LOGIC — apply per month to judge whether a yoga is delivering NOW
====================

A yoga activates when the current dasha planet is CONNECTED to the yoga's house(s):
1. OCCUPATION (strongest): Dasha planet SITS IN the yoga house — state "fully active NOW"
2. ASPECT (moderate): Dasha planet ASPECTS the yoga house — state "active via aspect"
3. LORDSHIP (mild): Dasha planet RULES the yoga house — state "mildly active via lordship"

For each month: check the PD/SD lord's connection to yoga houses and cite the activation level explicitly.
Not-yet-active yoga: name which future dasha lord will connect to the yoga house and approximately when.

CORRECT: "Your Hamsa Yoga reaches FULL activation this month as Jupiter Antardasha runs — Jupiter itself occupies the yoga house, making this the peak window for blessings of wisdom and leadership."
CORRECT: "Your Gaja Kesari Yoga is mildly active via Saturn's lordship — its full power arrives when Moon Mahadasha begins in [year]."

WRITING INSTRUCTIONS:
BE CONCISE — all 12 months plus the summary must fit in one reply. Write exactly 3 sentences per month using "## [Month Year]" as heading:
(a) Open with PD/SD active this month + their functional natures + the one life area with the highest or lowest score (state the score); address "you" directly throughout
(b) One transit effect most relevant to the month (Ju/Sa/Ma/Ra/Ke) + Sadesaati phase and one remedy if active
(c) If MaleficLord or LowEnergyLord active: name the planet + one remedy. If SDstarts/SDends: state as a concrete date shift. Otherwise: one yoga activation level or planet combination relevant to the month's top life area.

Score guide: ≥75=flourishing · 55-74=steady growth · 40-54=needs attention · <40=challenging
Functional nature: benefic=supportive for this lagna · malefic=challenging · neutral=mixed

After all 12 months, write a "## Year at a Glance — Summary" section of 1–2 paragraphs (5–8 sentences total) using "you/your" throughout, covering: your peak months and most challenging months (with reasons), the main dasha arc, your strongest and weakest life area scores across the year, and the single most important action for this year.

Extra rules:
- Every sentence must reference actual data from the tables above — no generic astrology statements
- Be crisp and direct. Do NOT explain what dashas are or describe astrology theory.
- Begin each monthly paragraph with "Your" or "This [Month], your" — never with the native's name as subject
- NEVER call a planet malefic unless the Functional Nature table above explicitly says 'malefic' for that planet in this chart
- ANTI-REPETITION — Yogas: do NOT cite the same yoga every month; if a yoga was prominently cited last month, skip it or mention it only briefly unless its activation level changes. Rotate across the active yogas list month to month.
- ANTI-REPETITION — Chara Karakas: rotate which karaka you lead with. Do not open every career month with the same AmK sentence — vary with DK, AK, or PuK as supporting context.
- ANTI-REPETITION — Sade Sati / doshas: if active every month, vary the description each time — different life area emphasis, different consequence word, different remedy — never copy the same sentence from a prior month.
- ANTI-REPETITION — Remedies: if the same malefic PD/SD lord recurs across months, choose a different remedy from the REMEDIES section each time; never repeat the same remedy word-for-word in consecutive months.
- ANTI-REPETITION — Opening pattern: vary sentence openings across months. If the previous month opened with the dasha chain, open the next month with a transit effect or a life-area score. All mandatory data must still be covered — just not always in the same order or from the same angle.
- DIFFERENTIATOR: Every month must contain at least one observation that is unique to that specific month and could not appear in any other month — a specific SD shift date, a transit moving into a new house, a score crossing the 70 or 40 threshold for the first time, or a yoga changing its activation level.`;

    const context = chatContextData ? buildChatContext(chatContextData, { includeFeeds: true, includeInterpretations: true }) : null;
    if (!context) { setReportYearForecastLoading(false); return; }

    await streamReportAI(prompt, context, 'gpt-5.4', setReportYearForecastContent, setReportYearForecastLoading, session.access_token, 16000);
  } catch (err: any) {
    console.error('[Report YearForecast]', err);
    setReportYearForecastContent(`Error: ${err.message}`);
    setReportYearForecastLoading(false);
  }
};

// Call AI for SWOT analysis
const callSwotAnalysis = async (prompt: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setSwotError('Not authenticated. Please sign in to use AI analysis.');
      setSwotLoading(false);
      return;
    }

    // Fetch all mandatory/important remedies
    let allImportantRemedies: Array<{ title: string; description: string; category: string; planet: string }> = [];
    if (astroData?.placements && blendedPlanetPct) {
      const { data: remediesData, error: remediesError } = await supabase
        .from('remedies')
        .select('id, name, title, description, category, related_entity')
        .in('related_entity', Object.keys(astroData.placements));
      
      if (!remediesError && remediesData) {
        // Filter for important remedies (planets with negative strength)
        allImportantRemedies = remediesData
          .filter(r => {
            const planetStrength = blendedPlanetPct?.[r.related_entity as PlanetName];
            return planetStrength != null && planetStrength < 0;
          })
          .sort((a, b) => {
            const aStrength = blendedPlanetPct?.[a.related_entity as PlanetName] || 0;
            const bStrength = blendedPlanetPct?.[b.related_entity as PlanetName] || 0;
            return aStrength - bStrength; // Most negative first
          })
          .slice(0, 5) // Limit to top 5 most important
          .map(r => ({
            title: r.title,
            description: (r.description || '').substring(0, 150) + (r.description && r.description.length > 150 ? '...' : ''), // Truncate long descriptions
            category: r.category,
            planet: r.related_entity
          }));
      }
    }

    // Build chat context for AI
    const contextData = {
      astroData,
      blendedPlanetPct,
      shadbalaPct,
      bhavaBalaRupas,
      combust,
      debil,
      exalt,
      d1HouseScores,
      dashaContext: dashaContextForFeeds,
      feeds: reportFeeds,
      utcDateTime: utcDateTime ?? undefined,
      selectedCity: selectedCity ?? undefined,
      ayanamsa: ayanProvider.name || 'Lahiri',
      gender: selectedGender,
      lordDusthanaPenalties: lordDusthanaPenalties ?? undefined,
      sadeSati: sadeSatiData ?? undefined,
      kaalSarpYoga: kaalSarpData ?? undefined,
      functionalNature: functionalNatureData as any ?? undefined,
      transitData: transitData ? { date: transitData.date, placements: transitData.placements, dignities: transitData.dignities, longitudes: transitData.longitudes, retrogrades: transitData.retrogrades, combust: transitData.combust, ...transitData.signDates } : undefined,
    };

    const context = buildChatContext(contextData as any, {
      includeFeeds: true,
      includeInterpretations: true,
      maxInterpretationLength: 500,
      lifeAreaRemedies: allImportantRemedies.length > 0 ? allImportantRemedies : undefined
    });

    // Update prompt to be more realistic and practical
    const enhancedPrompt = `${prompt}\n\nIMPORTANT GUIDELINES:\n• Be REALISTIC - don't sugar-coat challenges or difficulties\n• Use SIMPLE LANGUAGE - avoid overly technical jargon\n• Focus on PRACTICAL implications - what does this mean in real life?\n• Be SPECIFIC about weaknesses and problem areas - this helps planning${allImportantRemedies.length > 0 ? `\n• Include a separate REMEDIES section at the end with the ${allImportantRemedies.length} provided remedies, organized by priority` : ''}\n\nWrite in a direct, honest, actionable style.`;

    // Check subscription limits
    if (!canAskChatQuestion('general')) {
      const upgradeMsg = getUpgradeMessage('chat');
      alert(`Chat limit reached! ${upgradeMsg}`);
      return;
    }

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: enhancedPrompt }],
          context,
          model: 'gpt-5.4', // gpt-5.4 for deep SWOT analysis
          questionType: 'general'
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle streaming response
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/event-stream')) {
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      if (!reader) {
        throw new Error('No response body available');
      }

      let lineBuffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() ?? '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const parsed = JSON.parse(line.slice(6));
              if (parsed.content) { fullContent += parsed.content; setSwotOutput(fullContent); }
            } catch {}
          }
        }
      }
      if (lineBuffer.startsWith('data: ')) {
        try { const p = JSON.parse(lineBuffer.slice(6)); if (p.content) { fullContent += p.content; setSwotOutput(fullContent); } } catch {}
      }

      setSwotLoading(false);
    } else {
      // Non-streaming response
      const result = await response.json();
      setSwotOutput(result.reply || result.content || 'No response received');
      setSwotLoading(false);
    }
  } catch (err: any) {
    console.error('SWOT analysis error:', err);
    setSwotError(err?.message || 'Failed to generate SWOT analysis');
    setSwotLoading(false);
  }
};

// Call AI for Life Area Overview
const callLifeAreaOverview = async (lifeAreaKey: LifeAreaKey) => {
  try {
    // Check subscription limits
    if (!canAskChatQuestion('life_area')) {
      const upgradeMsg = getUpgradeMessage('chat');
      setLifeAreaOverviewError(`Chat limit reached! ${upgradeMsg}`);
      setLifeAreaOverviewLoading(false);
      setShowLifeAreaOverviewModal(true);
      return;
    }

    // Check if chart data is available
    if (!chatContextData) {
      setLifeAreaOverviewError('Chart data not available. Please generate your chart first.');
      setLifeAreaOverviewLoading(false);
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setLifeAreaOverviewError('Not authenticated. Please sign in to use AI analysis.');
      setLifeAreaOverviewLoading(false);
      return;
    }

    const area = LIFE_AREAS[lifeAreaKey];
    
    // Fetch ALL remedies tagged as "Important" for planets in this life area
    let lifeAreaRemedies: Array<{ title: string; description: string; category: string; planet: string; frequency?: string; duration?: string }> = [];
    if (astroData?.placements && houseLords && astroData?.aspects) {
      // Get all planets relevant to this life area
      const residing = Object.entries(astroData.placements).filter(([_, placement]) => 
        area.houses.includes(typeof placement === 'number' ? placement : placement.house)
      ).map(([planet]) => planet);
      
      const aspecting = area.houses.flatMap(h => 
        astroData.aspects.filter(a => a.toHouse === h).map(a => a.from)
      );
      
      const lords = area.houses.map(h => houseLords[h]).filter(Boolean);
      const relevantPlanets = Array.from(new Set([...residing, ...aspecting, ...lords]));
      
      if (relevantPlanets.length > 0) {
        const { data: remediesData, error: remediesError } = await supabase
          .from('remedies')
          .select('id, name, title, description, category, related_entity, tags, frequency, duration, detailed_instructions')
          .in('related_entity', relevantPlanets)
          .contains('tags', ['Important']); // Only fetch remedies tagged as "Important"
        
        if (!remediesError && remediesData && remediesData.length > 0) {
          // Extract house from remedy name for matching
          const remediesWithHouse = remediesData.map(r => {
            const houseMatch = r.name.match(/(\d+)(?:st|nd|rd|th) House/);
            return {
              ...r,
              house: houseMatch ? parseInt(houseMatch[1]) : undefined
            };
          });
          
          // For each relevant planet, find the best matching remedy
          lifeAreaRemedies = relevantPlanets
            .map(planet => {
              const planetRemedies = remediesWithHouse.filter(r => r.related_entity === planet);
              if (planetRemedies.length === 0) return null;
              
              // Get planet's actual house placement
              const placement = astroData.placements[planet as PlanetName];
              const actualHouse = typeof placement === 'number' ? placement : placement?.house;
              
              // Priority 1: Remedy matching actual house
              let remedy = planetRemedies.find(r => r.house === actualHouse);
              // Priority 2: Generic planet remedy (no house)
              if (!remedy) remedy = planetRemedies.find(r => !r.house);
              // Priority 3: Any remedy for this planet
              if (!remedy) remedy = planetRemedies[0];
              
              return remedy ? {
                title: remedy.title,
                description: remedy.description, // Full description, no truncation
                category: remedy.category,
                planet: remedy.related_entity,
                frequency: remedy.frequency,
                duration: remedy.duration
              } : null;
            })
            .filter((r): r is NonNullable<typeof r> => r !== null)
            .sort((a, b) => {
              // Sort by planet strength (most negative first for priority)
              const aStrength = blendedPlanetPct?.[a.planet as PlanetName] || 0;
              const bStrength = blendedPlanetPct?.[b.planet as PlanetName] || 0;
              return aStrength - bStrength;
            })
            .slice(0, 10); // Include up to 10 important remedies
        }
      }
    }
    
    console.log('[Life Area Overview] Remedies:', {
      count: lifeAreaRemedies.length,
      planets: lifeAreaRemedies.map(r => r.planet),
      remedies: lifeAreaRemedies.map(r => ({ planet: r.planet, title: r.title, category: r.category }))
    });
    
    const prompt = `IMPORTANT: Provide a direct, realistic astrological analysis of ${area.label}. Be honest about both strengths AND challenges - avoid sugar-coating difficulties.

Analyze:
• Houses: ${area.houses.map(h => `H${h}`).join(', ')}
• Divisional charts: ${area.vargas.join(', ')}
• Current dasha impact (include specific dates and percentages)
• What's working vs. what needs attention

USE SIMPLE LANGUAGE - write as if explaining to someone new to astrology. Focus on practical, real-world implications.

${lifeAreaRemedies.length > 0 ? `**CRITICAL REMEDIES SECTION - MANDATORY:**

You MUST end your response with a "REMEDIES" section. I have provided ${lifeAreaRemedies.length} specific remedies from our database tagged as "Important" for this life area.

**INSTRUCTIONS FOR REMEDIES SECTION:**
1. Create a clear heading: "### REMEDIES"
2. List ALL ${lifeAreaRemedies.length} remedies provided in the context
3. For EACH remedy, use this EXACT format:

**[Remedy Title from database]** (for [Planet])
- **What to do:** [Copy the full description VERBATIM from database - DO NOT paraphrase or shorten]
- **Why:** [Brief 1-line explanation of why this planet needs attention for ${area.label}]
${lifeAreaRemedies.some(r => r.frequency) ? '- **When:** [Frequency/duration if provided in database]' : ''}

**CRITICAL:** Copy the remedy descriptions EXACTLY as provided in the context. Do not change, paraphrase, or summarize them. Use the complete text from the database.

The remedies are provided in the context under "Life Area Remedies" section.

` : ''}DO NOT include conversational endings like "Let me know if you need more help." End with the final remedy.`;


    const context = buildChatContext(chatContextData, {
      includeFeeds: true,
      includeInterpretations: true,
      maxInterpretationLength: 500,
      lifeArea: lifeAreaKey, // Include life area to get relevant vargas and houses
      includeVargas: true, // Ensure divisional charts are included
      lifeAreaRemedies: lifeAreaRemedies.length > 0 ? lifeAreaRemedies : undefined // Pass mandatory remedies
    });

    // Check subscription limits
    if (!canAskChatQuestion('life_area')) {
      alert('Chat limit reached! Upgrade your plan to continue using life area analysis.');
      return;
    }

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: prompt }],
          context,
          questionType: 'life_area'
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    // Handle streaming response
    if (response.body) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      let lineBuffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() ?? '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) { accumulated += data.content; setLifeAreaOverviewOutput(accumulated); }
              if (data.done) { setLifeAreaOverviewLoading(false); return; }
            } catch {}
          }
        }
      }
      if (lineBuffer.startsWith('data: ')) {
        try { const d = JSON.parse(lineBuffer.slice(6)); if (d.content) { accumulated += d.content; setLifeAreaOverviewOutput(accumulated); } } catch {}
      }
      setLifeAreaOverviewLoading(false);
    } else {
      // Non-streaming response
      const result = await response.json();
      setLifeAreaOverviewOutput(result.reply || result.content || 'No response received');
      setLifeAreaOverviewLoading(false);
    }
  } catch (err: any) {
    console.error('Life Area Overview error:', err);
    setLifeAreaOverviewError(err?.message || 'Failed to generate overview');
    setLifeAreaOverviewLoading(false);
  }
};

// Handler to copy SWOT output to clipboard
const handleCopySwotOutput = useCallback(() => {
  navigator.clipboard.writeText(swotOutput).then(() => {
    alert('SWOT analysis copied to clipboard!');
  }).catch(err => {
    console.error('Failed to copy:', err);
    alert('Failed to copy to clipboard');
  });
}, [swotOutput]);

// Handler to go to chat with SWOT prompt
const handleGoToChat = useCallback(() => {
  localStorage.setItem('pendingChatMessage', swotPrompt);
  setShowSwotModal(false);
  if (isMobile) {
    // Mobile: open the life-area overlay instead of setting the chat tab
    setMobileChatOpen(true);
    setSelectedMobileLifeArea(null);
  } else {
    setActiveTab('chat');
  }
}, [swotPrompt, isMobile]);

// Remedies Modal State and Handler
const [showRemediesModal, setShowRemediesModal] = useState(false);

const handleGetRemedies = useCallback(() => {
  if (!astroData) {
    alert('Please calculate your chart first (Save & Calculate on the left)');
    return;
  }
  setShowRemediesModal(true);
}, [astroData]);

// Compute dasha context for feeds (moved before handleGenerateReport)
const dashaContextForFeeds = useMemo(() => {
  if (!astroData || !utcDateTime || !d1HouseScores) return null;

  try {
    const now = transitDate;
    const windowStart = now.toISO();
    const windowEnd = now.plus({ months: 6 }).toISO();

    // Helper: Get ruled houses for a planet (by sign lordship relative to ascendant)
    const getRuledHouses = (planet: string): number[] => {
      const ascendantSign = astroData.asc.ascendantSignIndex;
      return getHousesRuledByPlanet(planet as PlanetName, ascendantSign);
    };

    // Helper: Get aspected houses (Parashara)
    const getAspectedHouses = (planet: string, occupiedHouse: number): number[] => {
      const PARASHARI_OFFSETS: Record<string, number[]> = {
        Sun:[7], Moon:[7], Mercury:[7], Venus:[7],
        Mars:[4,7,8], Jupiter:[5,7,9], Saturn:[3,7,10],
        Rahu:[5,7,9], Ketu:[5,7,9]
      };
      const offsets = PARASHARI_OFFSETS[planet] || [7];
      return offsets.map(off => ((occupiedHouse - 1 + (off - 1)) % 12) + 1);
    };

  // Compute actual current MD/AD/PD/SD using Moon's sidereal longitude at birth
  const nowISO = transitDate.toISO();
  if (!nowISO) {
    console.error('[dashaContextForFeeds] transitDate.toISO() returned null');
    return null;
  }
  
  const dashaLevels = getCurrentDashaLevels(utcDateTime, astroData.grahas.Moon.lon, nowISO);
  if (!dashaLevels) return null;
  const mdPlanet = dashaLevels.md.lord as PlanetName;
  const adPlanet = (dashaLevels.ad?.lord ?? mdPlanet) as PlanetName;
  const pdPlanet = (dashaLevels.pd?.lord ?? adPlanet) as PlanetName;
  const sdPlanet = (dashaLevels.sd?.lord ?? pdPlanet) as PlanetName;

  const buildLevel = (level: 'MD'|'AD'|'PD'|'SD', planet: PlanetName) => {
    const occupiedHouse = (astroData.placements as any)[planet]?.house ?? 1;
    return {
      level,
      planet,
      from: windowStart!,
      to: windowEnd!,
      rules: getRuledHouses(planet),
      occupies: occupiedHouse,
      aspects: getAspectedHouses(planet, occupiedHouse),
    };
  };

    // Use the most specific active sublevel for the feed window (SD > PD > AD > MD)
    const activeWindow = dashaLevels.sd
      ? { from: dashaLevels.sd.start, to: dashaLevels.sd.end }
      : dashaLevels.pd
      ? { from: dashaLevels.pd.start, to: dashaLevels.pd.end }
      : dashaLevels.ad
      ? { from: dashaLevels.ad.start, to: dashaLevels.ad.end }
      : { from: dashaLevels.md.start, to: dashaLevels.md.end };

    return {
      MD: { ...buildLevel('MD', mdPlanet), from: dashaLevels.md.start, to: dashaLevels.md.end },
      AD: dashaLevels.ad ? { ...buildLevel('AD', adPlanet), from: dashaLevels.ad.start, to: dashaLevels.ad.end } : buildLevel('AD', adPlanet),
      PD: dashaLevels.pd ? { ...buildLevel('PD', pdPlanet), from: dashaLevels.pd.start, to: dashaLevels.pd.end } : buildLevel('PD', pdPlanet),
      SD: dashaLevels.sd ? { ...buildLevel('SD', sdPlanet), from: dashaLevels.sd.start, to: dashaLevels.sd.end } : buildLevel('SD', sdPlanet),
      window: activeWindow,
    };
  } catch (err) {
    console.error('[dashaContextForFeeds] error:', err);
    return null;
  }
}, [astroData, utcDateTime, d1HouseScores, transitDate]);

// Temporal metric 1: Dasha Impact per house (0-100, 50=neutral)
const dashaImpactByHouse = useMemo(() => {
  if (!dashaContextForFeeds || !functionalNatureData) return undefined;
  return computeDashaImpactByHouse(
    dashaContextForFeeds as any,
    functionalNatureData as Record<string, string>
  );
}, [dashaContextForFeeds, functionalNatureData]);

// Temporal metric 2: Transit Impact per house (0-100, 50=neutral)
const transitImpactByHouse = useMemo(() => {
  if (!transitData?.placements || !functionalNatureData) {
    console.debug('[transitImpactByHouse] guard failed', { hasTransit: !!transitData?.placements, hasFunctional: !!functionalNatureData });
    return undefined;
  }
  const positions = Object.entries(transitData.placements)
    .filter(([, p]: [string, any]) => p?.house)
    .map(([planet, p]: [string, any]) => ({ planet, house: p.house as number }));
  const result = computeTransitImpactByHouse(
    positions,
    functionalNatureData as Record<string, string>,
    blendedPlanetPct ?? undefined,
    transitData.dignities as Partial<Record<string, string>>,
    transitData.ascLagnaSign ?? null,
    transitData.combust ?? null,
    transitData.retrogrades ?? null,
  );
  console.debug('[transitImpactByHouse] computed', result);
  return result;
}, [transitData, functionalNatureData, blendedPlanetPct]);

// Yoga feeds state (declared here so handleGenerateReport can reference them)
  const [activeYogasFromFeeds, setActiveYogasFromFeeds] = useState<any[]>([]);
  const [permanentYogasFromFeeds, setPermanentYogasFromFeeds] = useState<any[]>([]);
  // Dasha-lord remedies for chat context (fetched async, keyed by planet name)
  const [chatDashaLordRemedies, setChatDashaLordRemedies] = useState<
    Array<{ planet: string; title: string; description: string; category: string }>
  >([]);
  // Life area remedies state – populated when report is generated
  const [savedLifeAreaRemedies, setSavedLifeAreaRemedies] = useState<
    Record<string, Array<{ title: string; description?: string; planet: string; isImportant?: boolean }>> | undefined
  >(undefined);

  /* ----------------- Chart Name (declared before handleGenerateReport to avoid TDZ) ----------------- */
  const chartName = useMemo(() => {
    if (!user) return null;
    if (selectedProfileId && selectedProfileId !== 'my_profile') {
      const selectedProfile = profiles.find(p => p.id === selectedProfileId);
      if (selectedProfile?.full_name && selectedProfile.full_name.trim().length >= 2) {
        return selectedProfile.full_name.trim();
      }
    }
    if (selectedProfileId === 'my_profile' && savedRow?.full_name && savedRow.full_name.trim().length >= 2) {
      return savedRow.full_name.trim();
    }
    const fallbackName =
      savedRow?.full_name ||
      (user as any)?.user_metadata?.full_name ||
      (user as any)?.full_name ||
      (user as any)?.name ||
      null;
    return fallbackName && fallbackName.trim().length >= 2 ? fallbackName.trim() : null;
  }, [user, selectedProfileId, savedRow, profiles]);

// Fetches "mandatory" Do's & Don'ts remedies for Life Area cards.
// Called both on chart load (to show remedies in the widget immediately)
// and during report generation (to embed them in the PDF report).
const fetchLifeAreaRemedies = useCallback(async (): Promise<
  Record<string, Array<{ title: string; description?: string; planet: string; isImportant?: boolean }>> | undefined
> => {
  if (!astroData) return undefined;
  let reportLifeAreaRemedies: Record<string, Array<{ title: string; description?: string; planet: string; isImportant?: boolean }>> | undefined;
  try {
    const REPORT_LA_DEFS = [
      { name: 'Health',   houses: [1, 6, 8, 12] },
      { name: 'Wealth',   houses: [1, 2, 5, 9, 11, 12] },
      { name: 'Job',      houses: [1, 6, 10] },
      { name: 'Business', houses: [1, 3, 7, 11] },
      { name: 'Marriage', houses: [1, 7] },
      { name: 'Children', houses: [1, 5] },
    ];
    const EXCLUDED_CATS = ['rudraksha', 'gemstone', 'mantra', 'worship', 'donation'];

    const rPlac: Record<string, number> = {};
    for (const [planet, pl] of Object.entries(astroData.placements)) {
      rPlac[planet] = typeof pl === 'object' ? (pl as any).house : (pl as number);
    }

    const rLords: Record<number, string> = {};
    for (let h = 1; h <= 12; h++) {
      const signIdx = (astroData.cusps as any)[h];
      rLords[h] = (SIGN_LORDS as any)[signIdx] || 'Jupiter';
    }

    const addH = (h: number, offset: number) => ((h - 1 + offset) % 12) + 1;
    const rHouseAspects: Record<number, string[]> = {};
    for (let h = 1; h <= 12; h++) rHouseAspects[h] = [];
    const ASPECT_PLANETS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
    for (const pName of ASPECT_PLANETS) {
      const fromHouse = rPlac[pName];
      if (!fromHouse) continue;
      rHouseAspects[addH(fromHouse, 6)].push(pName);
      if (pName === 'Mars') {
        rHouseAspects[addH(fromHouse, 3)].push(pName);
        rHouseAspects[addH(fromHouse, 7)].push(pName);
      } else if (pName === 'Jupiter') {
        rHouseAspects[addH(fromHouse, 4)].push(pName);
        rHouseAspects[addH(fromHouse, 8)].push(pName);
      } else if (pName === 'Saturn') {
        rHouseAspects[addH(fromHouse, 2)].push(pName);
        rHouseAspects[addH(fromHouse, 9)].push(pName);
      } else if (pName === 'Rahu' || pName === 'Ketu') {
        rHouseAspects[addH(fromHouse, 4)].push(pName);
        rHouseAspects[addH(fromHouse, 8)].push(pName);
      }
    }

    const allRelevantPlanets = new Set<string>();
    for (const laDef of REPORT_LA_DEFS) {
      for (const house of laDef.houses) {
        for (const [p, h] of Object.entries(rPlac)) {
          if (h === house) allRelevantPlanets.add(p);
        }
        for (const p of (rHouseAspects[house] || [])) allRelevantPlanets.add(p);
        if (rLords[house]) allRelevantPlanets.add(rLords[house]);
      }
    }

    const { data: rawRemedies } = await supabase
      .from('remedies')
      .select('id, name, title, description, category, related_entity')
      .eq('related_to', 'planet')
      .in('related_entity', Array.from(allRelevantPlanets))
      .eq('is_active', true)
      .neq('category', 'other');

    if (rawRemedies && rawRemedies.length > 0) {
      type RawRemedy = { title: string; description: string; category: string; house?: number };
      const remByPlanet: Record<string, RawRemedy[]> = {};
      for (const r of rawRemedies) {
        const houseMatch = (r.name as string).match(/House (\d+)/i);
        const house = houseMatch ? parseInt(houseMatch[1], 10) : undefined;
        if (!remByPlanet[r.related_entity]) remByPlanet[r.related_entity] = [];
        remByPlanet[r.related_entity].push({ title: r.title, description: r.description || '', category: r.category || '', house });
      }

      const getBestRemedy = (planet: string, actualHouse: number | undefined): RawRemedy | undefined => {
        const pr = remByPlanet[planet];
        if (!pr || pr.length === 0) return undefined;
        return pr.find(r => r.house === actualHouse)
          || pr.find(r => !r.house || r.house === 0)
          || undefined;
      };

      const getLifestyleRemedy = (planet: string, actualHouse: number | undefined): RawRemedy | undefined => {
        const pr = remByPlanet[planet];
        if (!pr || pr.length === 0) return undefined;
        return pr.find(r => r.house === actualHouse && r.category?.toLowerCase() === 'lifestyle')
          || pr.find(r => (!r.house || r.house === 0) && r.category?.toLowerCase() === 'lifestyle')
          || undefined;
      };

      reportLifeAreaRemedies = {};
      for (const laDef of REPORT_LA_DEFS) {
        const remedyMap = new Map<string, RawRemedy>();
        const allOccupants = Object.entries(rPlac)
          .filter(([, h]) => laDef.houses.includes(h))
          .map(([planet, house]) => ({ planet, house }))
          .sort((a, b) => ((blendedPlanetPct?.[b.planet as PlanetName] ?? 0) - (blendedPlanetPct?.[a.planet as PlanetName] ?? 0)));
        for (const { planet, house } of allOccupants) {
          if (!remedyMap.has(planet)) {
            const best = getBestRemedy(planet, house);
            if (best) remedyMap.set(planet, best);
          }
        }

        const mandatoryPlanets = new Set<string>();

        for (const house of laDef.houses) {
          const occupantsInHouse = allOccupants.filter(o => o.house === house);

          if (occupantsInHouse.length > 0) {
            for (const { planet } of occupantsInHouse) {
              const remedy = remedyMap.get(planet);
              if (!remedy) continue;
              if (EXCLUDED_CATS.includes(remedy.category?.toLowerCase())) continue;
              if (remedy.category?.toLowerCase() === 'lifestyle') {
                mandatoryPlanets.add(planet);
              } else {
                const strength = blendedPlanetPct?.[planet as PlanetName] ?? null;
                if (strength != null && strength < 0) {
                  mandatoryPlanets.add(planet);
                }
              }
            }
          } else {
            const aspectors = (rHouseAspects[house] || []);
            const aspectorsWithRemedies = aspectors.filter(p => remByPlanet[p] && remByPlanet[p].length > 0);

            if (aspectorsWithRemedies.length > 0) {
              for (const planet of aspectorsWithRemedies) {
                mandatoryPlanets.add(planet);
                if (!remedyMap.has(planet)) {
                  const actualHouse = rPlac[planet];
                  const best = getBestRemedy(planet, actualHouse);
                  if (best) remedyMap.set(planet, best);
                }
              }
            } else {
              const lord = rLords[house];
              if (lord && remByPlanet[lord]) {
                const lordActualHouse = rPlac[lord];
                const lifestyleR = getLifestyleRemedy(lord, lordActualHouse);
                if (lifestyleR) {
                  mandatoryPlanets.add(lord);
                  if (!remedyMap.has(lord)) remedyMap.set(lord, lifestyleR);
                }
              }
            }
          }
        }

        const areaRemedies = Array.from(mandatoryPlanets)
          .map(p => {
            const r = remedyMap.get(p);
            if (!r) return null;
            const isImportant = (blendedPlanetPct?.[p as PlanetName] ?? 0) < 0;
            return { title: r.title, description: r.description || undefined, planet: p, isImportant };
          })
          .filter(Boolean) as Array<{ title: string; description?: string; planet: string; isImportant?: boolean }>;

        if (areaRemedies.length > 0) reportLifeAreaRemedies[laDef.name] = areaRemedies;
      }
    }
  } catch (err) {
    console.warn('[fetchLifeAreaRemedies] Failed to fetch life area remedies:', err);
  }
  if (reportLifeAreaRemedies) setSavedLifeAreaRemedies(reportLifeAreaRemedies);
  return reportLifeAreaRemedies;
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [astroData, blendedPlanetPct]);

// Handler for Generate Report
const handleGenerateReport = useCallback(async () => {
  if (!astroData || !d1HouseScores) {
    alert('Please calculate your chart first (Save & Calculate on the left)');
    return;
  }

  // Use activeYogasFromFeeds — already extracted by the Yogas & Predictions tab, same items shown there
  // Exclude sade_sati — reportDataBuilder adds it cleanly via the sadeSati prop (avoids duplicates & wrong severity)
  const getSeverityForRuleKey = (rk: string, score: number | undefined): string => {
    if (rk.startsWith('sade_sati')) return 'caution';
    if (rk.includes('kaal_sarp') || rk.includes('kala_sarpa')) return 'caution';
    if (rk.includes('dosha') || rk.includes('affliction')) return 'caution';
    if (rk.includes('kemdruma') || rk.includes('dur_yoga') || rk.includes('grahan_yoga')) return 'caution';
    if (rk.includes('raj_yoga') || rk.includes('mahapurusha') || rk.includes('gaja_kesari') ||
        rk.includes('hamsa') || rk.includes('malavya') || rk.includes('ruchaka') ||
        rk.includes('bhadra') || rk.includes('shasha') || rk.includes('lakshmi') ||
        rk.includes('vasumati') || rk.includes('amala') || rk.includes('adhi_yoga') ||
        rk.includes('budhaditya') || rk.includes('saraswati') || rk.includes('parvata') ||
        rk.includes('kalanidhi') || rk.includes('sunapha') || rk.includes('anapha') ||
        rk.includes('durudhara')) return 'positive';
    if (rk.includes('vipareeta_raj') || rk.includes('neech_bhang')) return 'positive';
    if (typeof score === 'number' && score > 0) return 'positive';
    if (typeof score === 'number' && score < 0) return 'caution';
    return 'mixed';
  };

  // Normalize ruleKey: strip _natal / _timing suffixes to get base yoga name for dedup
  const normalizeRuleKey = (rk: string) => rk.replace(/_natal$/, '').replace(/_timing$/, '');

  // 1. Permanent natal yogas (isPermanent:true, isActive reflects whether also dasha-activated)
  // Build a normalized set of active ruleKeys for matching
  const activeRuleKeysNorm = new Set(activeYogasFromFeeds.map(f => normalizeRuleKey(f.ruleKey || '')).filter(Boolean));
  const permanentYogaItems = permanentYogasFromFeeds
    .filter(item => !item.ruleKey?.startsWith('sade_sati'))
    .map(item => ({
      name: item.message?.split(':')[0]?.trim() || item.title || 'Unknown Yoga',
      type: item.ruleKey || 'general',
      severity: getSeverityForRuleKey(item.ruleKey || '', item.score),
      description: item.message || item.description || '',
      isPermanent: true,
      isActive: activeRuleKeysNorm.has(normalizeRuleKey(item.ruleKey || '')), // true if currently dasha-activated
      score: item.score || 0,
      private: (item as any).private || false,
    }));

  // 2. Active/timing yogas not already covered by permanent list (deduplicated by normalized ruleKey)
  const permanentRuleKeysNorm = new Set(permanentYogaItems.map(y => normalizeRuleKey(y.type)));
  const activeYogaItems = activeYogasFromFeeds
    .filter(item => !item.ruleKey?.startsWith('sade_sati') && !permanentRuleKeysNorm.has(normalizeRuleKey(item.ruleKey || '')))
    .map(item => ({
      name: item.message?.split(':')[0]?.trim() || 'Unknown Yoga',
      type: item.ruleKey || 'general',
      severity: getSeverityForRuleKey(item.ruleKey || '', item.score),
      description: item.message || '',
      isPermanent: false,
      isActive: true,
      score: item.score || 0,
      private: (item as any).private || false,
    }));

  const yogaItems = [...permanentYogaItems, ...activeYogaItems];

  // ── Fetch ascendant personality + Moon nakshatra descriptions from Supabase ──
  let ascendantPersonality: string | undefined;
  let moonNakshatraDesc: string | undefined;
  let houseSignNotes: Record<number, string> = {};
  try {
    const ascSign = (astroData.asc?.ascendantSign as string || '').toLowerCase();
    const ascCol = `note_sign_${ascSign}`;
    // Build column names for each house's sign (whole-sign system)
    const _signNamesLC = ['aries','taurus','gemini','cancer','leo','virgo','libra','scorpio','sagittarius','capricorn','aquarius','pisces'];
    const houseSignCols = ((astroData.cusps as number[]) || []).map((lon: number) => {
      const sIdx = Math.floor(((lon % 360) + 360) % 360 / 30);
      return `note_sign_${_signNamesLC[sIdx] || ''}`;
    });
    const uniqueHouseSignCols = [...new Set(houseSignCols.filter(Boolean))];
    const allHousesCols = ['id', ...uniqueHouseSignCols].join(',');
    const [houseRes, planetRes, allHousesRes] = await Promise.all([
      supabase.from('houses').select(ascCol).eq('id', 1).single(),
      supabase.from('planets').select('id, name').order('id', { ascending: true }),
      supabase.from('houses').select(allHousesCols).order('id', { ascending: true }),
    ]);
    ascendantPersonality = (houseRes.data as any)?.[ascCol] || undefined;
    // Build house sign notes map
    if (allHousesRes.data) {
      for (let h = 1; h <= 12; h++) {
        const col = houseSignCols[h - 1];
        const row = (allHousesRes.data as any[]).find((r: any) => r.id === h);
        if (col && row?.[col]) houseSignNotes[h] = row[col];
      }
    }

    // Find Moon planet id (case-insensitive for robustness)
    const moonPlanet = (planetRes.data as any[])?.find(
      (p: any) => (p.name || '').toLowerCase() === 'moon'
    );
    const moonPlanetId = moonPlanet?.id;
    if (moonPlanetId) {
      const rawNakName = (astroData.moon?.nakshatra as string) || '';
      const nakCol = `nak_${rawNakName.toLowerCase().replace(/\s+/g, '_')}`;
      const moonHouseNum = (astroData.placements as any)?.Moon?.house as number | undefined;
      // Fetch all rows for Moon — nakshatra columns may be on generic (house_id IS NULL)
      // rows or house-specific rows. Prefer house-specific match, fall back to generic.
      const { data: pihRows } = await supabase
        .from('planet_in_house_predictions')
        .select(`house_id, ${nakCol}`)
        .eq('planet_id', moonPlanetId);
      if (pihRows) {
        const exactRow = moonHouseNum != null
          ? pihRows.find((r: any) => r.house_id === moonHouseNum)
          : undefined;
        const genericRow = pihRows.find((r: any) => !r.house_id);
        const best = exactRow || genericRow;
        moonNakshatraDesc = (best as any)?.[nakCol] || undefined;
      }
    }
  } catch (err) {
    console.warn('[handleGenerateReport] Failed to fetch personality/nakshatra descriptions:', err);
  }

  // Fetch house interpretations (planet-in-house notes) for report — fetch directly here
  // to avoid race condition with the background useEffect that populates houseInterpretations state.
  let reportHouseInterpretations: HouseInterpretation[] | undefined;
  try {
    const longitudes: Partial<Record<PlanetName, number>> = {
      Sun: (astroData.grahas as any).Sun?.lon,
      Moon: (astroData.grahas as any).Moon?.lon,
      Mercury: (astroData.grahas as any).Mercury?.lon,
      Venus: (astroData.grahas as any).Venus?.lon,
      Mars: (astroData.grahas as any).Mars?.lon,
      Jupiter: (astroData.grahas as any).Jupiter?.lon,
      Saturn: (astroData.grahas as any).Saturn?.lon,
      Rahu: (astroData.nodes as any).Rahu,
      Ketu: (astroData.nodes as any).Ketu,
    };
    reportHouseInterpretations = await fetchHouseInterpretations(
      astroData.asc.ascendantSignIndex,
      astroData.placements,
      longitudes
    );
  } catch (err) {
    console.warn('[handleGenerateReport] Failed to fetch house interpretations:', err);
  }

  // Fetch "mandatory" Do's & Don'ts remedies — delegates to the shared fetchLifeAreaRemedies callback
  // (which also populates the Life Areas Widget immediately without needing a full report generation).
  const reportLifeAreaRemedies = await fetchLifeAreaRemedies();

  // Build report data from current chart calculations
  const builtReportData = buildReportData({
    astroData,
    houseScores: d1HouseScores,
    bhavaBala: bhavaBalaRupas,
    birthDetails: {
      name: chartName || savedRow?.full_name || 'Native',
      dateOfBirth: selectedCity && localDateTime
        ? DateTime.fromISO(localDateTime).toFormat('MMMM dd, yyyy')
        : 'Not specified',
      timeOfBirth: localDateTime
        ? DateTime.fromISO(localDateTime).toFormat('HH:mm:ss')
        : 'Not specified',
      location: selectedCity?.label || 'Not specified',
      coordinates: selectedCity
        ? { lat: selectedCity.lat, lon: selectedCity.lon }
        : { lat: 0, lon: 0 },
      timezone: selectedCity?.tz || 'UTC',
      ascendantPersonality,
      moonNakshatraDesc,
      houseSignNotes,
    },
    chartImageUrl: undefined, // Will be added in Phase 2
    yogas: yogaItems,
    dashaContext: dashaContextForFeeds,
    sadeSati: sadeSatiData,
    blendedPlanetPct: blendedPlanetPct ?? undefined,
    functionalMap: functionalNatureData as Record<string, string> ?? undefined,
    houseInterpretations: reportHouseInterpretations ?? houseInterpretations ?? undefined,
    lifeAreaRemedies: reportLifeAreaRemedies,
    transitPositions: transitData?.placements
      ? Object.entries(transitData.placements)
          .filter(([, p]: [string, any]) => p?.house)
          .map(([planet, p]: [string, any]) => ({ planet, house: p.house as number }))
      : undefined,
    feeds: reportFeeds.length > 0 ? reportFeeds as any : undefined,
    houseActivities: housesData
      ? Object.fromEntries(housesData.filter(h => h.activities).map(h => [h.id, h.activities!]))
      : undefined,
    shadbalaPct: shadbalaPct ?? undefined,
    shadbalaRatio: shadbalaRatio ?? undefined,
    ikPhala: ikPhala ?? undefined,
    utcBirthISO: utcDateTime || undefined,
    lordDusthanaPenalties: lordDusthanaPenalties ?? undefined,
  });

  if (builtReportData) {
    setReportV2Data(builtReportData);
    setShowReportModal(true);
  } else {
    alert('Unable to generate report. Please ensure all chart data is computed.');
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [astroData, d1HouseScores, bhavaBalaRupas, selectedCity, localDateTime, savedRow, chartName, dashaContextForFeeds, sadeSatiData, houseInterpretations, utcDateTime]);

// Auto-fetch life area remedies whenever chart data is ready so the Life Areas Widget
// shows them without needing to trigger a full report generation first.
useEffect(() => {
  if (astroData) {
    fetchLifeAreaRemedies();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [astroData, fetchLifeAreaRemedies]);

// ── Prompt Builder data bundle (Admin Console → 🤖 Prompt Builder tab) ──────
const adminPromptData = useMemo(() => {
  if (!astroData || !utcDateTime) return null;
  return {
    chartName: chartName || savedRow?.full_name || 'Unknown',
    utcDateTime: utcDateTime,
    localDateTime: localDateTime || '',
    birthPlace: selectedCity?.label || '',
    lat: selectedCity?.lat ?? 0,
    lon: selectedCity?.lon ?? 0,
    timezone: selectedCity?.tz || 'UTC',
    ascendantSignIndex: astroData.asc?.ascendantSignIndex ?? 1,
    ascendantSign: astroData.asc?.ascendantSign ?? '',
    ascendantNakshatra: astroData.asc?.ascendantNakshatra ?? '',
    ascendantLon: astroData.asc?.siderealLongitudeDeg ?? 0,
    moonSign: astroData.moon?.moonSign ?? '',
    moonNakshatra: astroData.moon?.nakshatra ?? '',
    moonLon: astroData.grahas?.Moon?.lon ?? 0,
    grahas: Object.fromEntries(
      Object.entries(astroData.grahas ?? {}).map(([p, g]) => [p, { lon: (g as any).lon, retrograde: (g as any).retrograde ?? false, speedDegPerDay: (g as any).speedDegPerDay }])
    ),
    nodes: { Rahu: astroData.nodes?.Rahu ?? 0, Ketu: astroData.nodes?.Ketu ?? 0 },
    placements: Object.fromEntries(
      Object.entries(astroData.placements ?? {}).map(([p, v]) => [p, { house: (v as any).house, offsetDeg: (v as any).offsetDeg }])
    ),
    houseInfluenceRows: d1HouseInfluence?.rows ?? [],
    d1HouseScores: (d1HouseScores ?? {}) as Record<number, number>,
    bhavaBala: (bhavaBalaRupas ?? {}) as Record<number, number>,
    lordDusthanaPenalties: (lordDusthanaPenalties ?? {}) as Record<number, any>,
    combust: combust ? Object.fromEntries(
      Object.entries(combust).map(([p, v]) => [p, (v as any).combust === true])
    ) as Record<string, boolean> : {},
    debil: (debil ?? {}) as Record<string, boolean>,
    exalt: (exalt ?? {}) as Record<string, boolean>,
    functionalNature: (functionalNatureData ?? {}) as Record<string, string>,
    blendedPlanetPct: (blendedPlanetPct ?? {}) as Record<string, number>,
    vargaMap: vargaMap ? Object.fromEntries(
      Object.entries(vargaMap as Record<string, any>).filter(([, v]) => v?.ascSign && v?.planetSigns)
        .map(([k, v]) => [k, { ascSign: v.ascSign, planetSigns: v.planetSigns }])
    ) : {},
    dashaContext: dashaContextForFeeds ?? null,
    sadeSati: sadeSatiData ?? null,
    kaalSarp: kaalSarpData ?? null,
    permanentYogas: permanentYogasFromFeeds ?? [],
    activeYogas: activeYogasFromFeeds ?? [],
  };
}, [
  astroData, utcDateTime, localDateTime, selectedCity, chartName, savedRow,
  d1HouseInfluence, d1HouseScores, bhavaBalaRupas, lordDusthanaPenalties,
  combust, debil, exalt, functionalNatureData, blendedPlanetPct,
  vargaMap, dashaContextForFeeds, sadeSatiData, kaalSarpData,
  permanentYogasFromFeeds, activeYogasFromFeeds,
]);

// Functional classification for feeds (benefic/malefic per lagna)
const functionalClassificationForFeeds = useMemo(() => {
  if (!astroData) return undefined;
  const ascSign = astroData.asc?.ascendantSignIndex ?? 1;
  const fmap = classifyFunctionalForLagna(ascSign) as Record<string, 'benefic' | 'malefic' | 'neutral' | 'yoga_karaka'>;
  return fmap as any;
}, [astroData]);

// Aggregate transit positions across the active dasha window (weekly samples)
const transitPositionsForFeeds = useMemo(() => {
  try {
    if (!astroData || !dashaContextForFeeds || !selectedCity) return [] as Array<{ planet: PlanetName; house: number; sign?: number; aspects?: number[] }>;
    const from = DateTime.fromISO(dashaContextForFeeds.window.from);
    const to = DateTime.fromISO(dashaContextForFeeds.window.to);
    if (!from.isValid || !to.isValid || to <= from) return [];

    const daysTotal = Math.max(1, Math.floor(to.diff(from, 'days').days));
    // Aim for ~12-16 samples
    const stepDays = Math.max(1, Math.floor(daysTotal / 14) || 7);
    const samples: string[] = [];
    for (let d = 0; d <= daysTotal; d += stepDays) {
      const iso = from.plus({ days: d }).toUTC().toISO();
      if (iso) samples.push(iso);
    }
    const endIso = to.toUTC().toISO();
    if (endIso && samples[samples.length - 1] !== endIso) samples.push(endIso);

    const seen = new Set<string>();
    const out: Array<{ planet: PlanetName; house: number; sign?: number; aspects?: number[] }> = [];

    for (const iso of samples) {
      const grahas = getGrahaLongitudesAndMotionSidereal(
        { isoDate: iso, latitude: selectedCity.lat, longitude: selectedCity.lon },
        ayanProvider
      );
      const nodes = getRahuKetuSidereal(
        { isoDate: iso, latitude: selectedCity.lat, longitude: selectedCity.lon },
        ayanProvider
      );
      const planetLongs: Partial<Record<PlanetName, number>> = {
        Sun: grahas.Sun.lon,
        Moon: grahas.Moon.lon,
        Mercury: grahas.Mercury.lon,
        Venus: grahas.Venus.lon,
        Mars: grahas.Mars.lon,
        Jupiter: grahas.Jupiter.lon,
        Saturn: grahas.Saturn.lon,
        Rahu: nodes.Rahu,
        Ketu: nodes.Ketu,
      };

      const placements = assignPlanetsToHouses(astroData.cusps, planetLongs);
      const aspects = getAspects(astroData.cusps, planetLongs as any, { mode: 'house', includeNodes: true, nodesUseSeven: true });
      const aspectMap: Record<string, number[]> = {};
      for (const a of aspects) {
        if (!aspectMap[a.from]) aspectMap[a.from] = [];
        aspectMap[a.from].push(a.toHouse);
      }

      (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as PlanetName[]).forEach(p => {
        const house = (placements as any)[p]?.house as number | undefined;
        const lon = planetLongs[p];
        if (!house || lon === undefined) return;
        const key = `${p}:${house}`;
        if (seen.has(key)) return;
        seen.add(key);
        // Calculate sign from longitude (1-12, same as natal placements)
        const sign = Math.floor(lon / 30) + 1;
        out.push({ planet: p, house, sign, aspects: aspectMap[p] });
      });
    }
    return out;
  } catch {
    return [] as Array<{ planet: PlanetName; house: number; sign?: number; aspects?: number[] }>;
  }
}, [astroData, dashaContextForFeeds, selectedCity, ayanProvider]);

// Combustion map for feeds (boolean per planet)
const combustionMapForFeeds = useMemo(() => {
  if (!combust) return undefined;
  const map: Partial<Record<PlanetName, boolean>> = {};
  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as PlanetName[]).forEach(p => {
    const info = (combust as any)[p];
    map[p] = info?.combust ?? false;
  });
  return map as any;
}, [combust]);

// Natal dignity map for feeds — uses debil/exalt boolean maps already on state
// natalPlacementsForContext carries debilitated/exalted booleans; we map to DignityStatus strings
const natalDignityMapForFeeds = useMemo(() => {
  if (!debil || !exalt) return undefined;
  const map: Partial<Record<PlanetName, DignityStatus>> = {};
  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'] as PlanetName[]).forEach(p => {
    if ((exalt as any)[p]) map[p] = 'exalted';
    else if ((debil as any)[p]) map[p] = 'debilitated';
    // own/moolatrikona/friend/enemy would need getPlanetDignity; for now debil/exalt covers extremes
  });
  return map;
}, [debil, exalt]);

// Transit dignity map for feeds — from transitData.dignities (string values)
const transitDignityMapForFeeds = useMemo(() => {
  if (!transitData?.dignities) return undefined;
  const map: Partial<Record<PlanetName, DignityStatus>> = {};
  (Object.entries(transitData.dignities) as [PlanetName, string][]).forEach(([planet, d]) => {
    map[planet] = d as DignityStatus;
  });
  return map;
}, [transitData?.dignities]);

// Memoize natal object for FeedsList to prevent infinite re-renders
const natalForFeeds = useMemo(() => {
  if (!astroData || !d1HouseScores) return null;
  return {
    placements: astroData.placements as Record<string, { house: number; sign: number }>,
    aspects: astroData.aspects.map(a => ({ from: a.from, toHouse: a.toHouse })),
    houseScores: d1HouseScores as any,
    cusps: astroData.cusps,
  };
}, [astroData, d1HouseScores]);

// Memoize location object for FeedsList to prevent recreation on every render
const locationForFeeds = useMemo(() => {
  if (!selectedCity) return undefined;
  return { lat: selectedCity.lat, lon: selectedCity.lon };
}, [selectedCity]);

// Natal placements for future event predictions (DashaContext compatible format)
const natalPlacementsForContext = useMemo(() => {
  if (!astroData || !debil || !exalt) return undefined;
  
  const placements: Partial<Record<PlanetName, {
    house: number;
    aspects?: number[];
    rules?: number[];
    sign?: number;
    debilitated?: boolean;
    exalted?: boolean;
  }>> = {};

  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as PlanetName[]).forEach(planet => {
    const placement = (astroData.placements as any)[planet];
    if (placement) {
      placements[planet] = {
        house: placement.house,
        sign: placement.sign,
        debilitated: (debil as any)[planet] ?? false,
        exalted: (exalt as any)[planet] ?? false,
        // Could add aspects and rules if needed
      };
    }
  });

  return placements as any;
}, [astroData, debil, exalt]);

// Pre-compute future opportunity/caution events for the chat chip
const [futureEventsForChat, setFutureEventsForChat] = useState<{ opportunities: any[]; cautions: any[] } | null>(null);
useEffect(() => {
  if (!utcDateTime || !astroData?.moon?.siderealLongitudeDeg || !d1HouseScores || !natalPlacementsForContext) return;
  let cancelled = false;
  computeFutureEvents({
    birthISO: utcDateTime,
    moonLongDeg: astroData.moon.siderealLongitudeDeg,
    houseScores: d1HouseScores,
    natalPlacements: natalPlacementsForContext,
    combustionMap: combustionMapForFeeds ?? undefined,
    gender: selectedGender,
    ascendantSign: astroData.asc.ascendantSignIndex,
    includeMarriage: true,
  }).then(result => {
    if (!cancelled) setFutureEventsForChat(result);
  }).catch(() => {});
  return () => { cancelled = true; };
}, [utcDateTime, astroData, d1HouseScores, natalPlacementsForContext, combustionMapForFeeds, selectedGender]);
const [debouncedDashaContext, setDebouncedDashaContext] = useState(dashaContextForFeeds);
const [debouncedHouseScores, setDebouncedHouseScores] = useState(d1HouseScores);

// Debounce expensive inputs to prevent recalc on every state change
useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedDashaContext(dashaContextForFeeds);
    setDebouncedHouseScores(d1HouseScores);
  }, 300); // 300ms debounce
  return () => clearTimeout(timer);
}, [dashaContextForFeeds, d1HouseScores]);

const reportFeeds = useMemo(() => {
  const feeds: any[] = [];
  if (debouncedDashaContext && debouncedHouseScores && functionalClassificationForFeeds) {
    try {
      // Build DashaContext matching the structure expected by generateFeeds
      const dashaContext: DashaContext = {
        levels: [
          { 
            level: 'MD', 
            planet: debouncedDashaContext.MD.planet, 
            occupies: debouncedDashaContext.MD.occupies as HouseNum, 
            rules: debouncedDashaContext.MD.rules as HouseNum[], 
            aspects: debouncedDashaContext.MD.aspects as HouseNum[],
            from: debouncedDashaContext.MD.from,
            to: debouncedDashaContext.MD.to
          },
          { 
            level: 'AD', 
            planet: debouncedDashaContext.AD.planet, 
            occupies: debouncedDashaContext.AD.occupies as HouseNum, 
            rules: debouncedDashaContext.AD.rules as HouseNum[], 
            aspects: debouncedDashaContext.AD.aspects as HouseNum[],
            from: debouncedDashaContext.AD.from,
            to: debouncedDashaContext.AD.to
          },
          { 
            level: 'PD', 
            planet: debouncedDashaContext.PD.planet, 
            occupies: debouncedDashaContext.PD.occupies as HouseNum, 
            rules: debouncedDashaContext.PD.rules as HouseNum[], 
            aspects: debouncedDashaContext.PD.aspects as HouseNum[],
            from: debouncedDashaContext.PD.from,
            to: debouncedDashaContext.PD.to
          },
          { 
            level: 'SD', 
            planet: debouncedDashaContext.SD.planet, 
            occupies: debouncedDashaContext.SD.occupies as HouseNum, 
            rules: debouncedDashaContext.SD.rules as HouseNum[], 
            aspects: debouncedDashaContext.SD.aspects as HouseNum[],
            from: debouncedDashaContext.SD.from,
            to: debouncedDashaContext.SD.to
          }
        ],
        window: debouncedDashaContext.window,
        houseScores: debouncedHouseScores as Record<HouseNum, number>,
        lagnaSign: astroData?.asc.ascendantSignIndex,
        natalPlacements: astroData?.placements as any
      };

      // Build transit context from current transit data (for Sade Sati and other transit rules)
      let transitContext: TransitContext;
      if (transitData && transitData.placements && astroData?.asc.ascendantSignIndex) {
        // Convert transitData placements to TransitPosition format
        const ascendantSignIndex = astroData.asc.ascendantSignIndex; // 1-indexed (1-12)
        const transitPositions: any[] = Object.entries(transitData.placements).map(([planetName, placement]: [string, any]) => {
          // Calculate sign from house using whole-sign house system
          // In whole-sign: House 1 = Ascendant sign, House 2 = (Asc sign + 1), etc.
          // sign = ((ascSign - 1 + house - 1) % 12) + 1
          const signIndex = ((ascendantSignIndex - 1 + placement.house - 1) % 12) + 1; // 1-indexed (1-12)
          
          return {
            planet: planetName as PlanetName,
            house: placement.house as HouseNum,
            sign: signIndex // 1-indexed (1-12) for Sade Sati calculations
          };
        });

        // Calculate planetsTransitingDashaOccupied
        const planetsTransitingDashaOccupied: Record<PlanetName, PlanetName[]> = {} as any;
        const dashaHouses = [
          debouncedDashaContext.MD.occupies,
          debouncedDashaContext.AD.occupies,
          debouncedDashaContext.PD.occupies,
          debouncedDashaContext.SD.occupies
        ];
        const dashaPlanets = [
          debouncedDashaContext.MD.planet,
          debouncedDashaContext.AD.planet,
          debouncedDashaContext.PD.planet,
          debouncedDashaContext.SD.planet
        ];
        
        dashaPlanets.forEach((dPlanet, idx) => {
          planetsTransitingDashaOccupied[dPlanet as PlanetName] = transitPositions
            .filter(tp => tp.house === dashaHouses[idx])
            .map(tp => tp.planet);
        });

        // Add Saturn sign transits for Sade Sati calculations
        const saturnPlacement = transitData.placements.Saturn;
        transitContext = {
          positions: transitPositions,
          planetsTransitingDashaOccupied,
          saturnSignTransits: saturnPlacement ? {
            currentSign: ((ascendantSignIndex - 1 + saturnPlacement.house - 1) % 12) + 1,
            // Use accurate ingress/egress from astronomy-engine if available
            ingressDate: saturnTransitsData?.ingressDate || transitDate.toISODate() || '',
            egressDate: saturnTransitsData?.egressDate || transitDate.plus({ years: 2, months: 6 }).toISODate() || ''
          } : undefined
        };
      } else {
        // Fallback to empty transit context if transitData not available
        transitContext = {
          positions: [],
          planetsTransitingDashaOccupied: {} as Record<PlanetName, PlanetName[]>
        };
      }

      // Generate feeds
      const feedItems = generateFeeds({
        dasha: dashaContext,
        transits: transitContext,
        functional: functionalClassificationForFeeds,
        combustionMap: combustionMapForFeeds || undefined,
        natalDignityMap: natalDignityMapForFeeds,
        transitDignityMap: transitDignityMapForFeeds
      });

      // Transform to reportData format
      feedItems.forEach(item => {
        feeds.push({
          id: item.id,
          ruleKey: item.ruleKey,
          lifeArea: item.lifeArea,
          severity: item.severity,
          score: item.score,
          message: item.message,
          shortMessage: item.shortMessage,
          window: item.window,
          evidence: item.evidence,
          private: item.private || false
        });
      });
      
      // Add Kaal Sarp Yoga if present (permanent natal yoga)
      if (kaalSarpData && kaalSarpData.isPresent) {
        feeds.push({
          id: 'kaal_sarp_yoga',
          ruleKey: 'kaal_sarp_yoga',
          message: kaalSarpData.details || `Kaal Sarp Yoga detected (${kaalSarpData.type || 'unknown'} type)`,
          lifeArea: 'karma' as any,
          severity: kaalSarpData.severity === 'full' ? -50 : -30,
          score: kaalSarpData.severity === 'full' ? -50 : -30,
          window: null,
          evidence: [`Kaal Sarp Yoga: ${kaalSarpData.type || 'unknown'} type`, kaalSarpData.details || '']
        });
      }
    } catch (err) {
      console.error('Failed to generate feeds:', err);
    }
  }
  return feeds;
}, [debouncedDashaContext, debouncedHouseScores, functionalClassificationForFeeds, combustionMapForFeeds, natalDignityMapForFeeds, transitDignityMapForFeeds, kaalSarpData, saturnTransitsData]);

// Chat context data (for "Analyse All Life Areas" button)
const chatContextData = useMemo(() => {
  if (!astroData || !d1HouseScores) return null;

  // Name: prefer selected profile's name (chartName handles profile selector correctly),
  // then reportV2Data, then logged-in user's savedRow as last resort.
  const nativeName = chartName?.split(' ')[0]
    ?? reportV2Data?.birthDetails?.name?.split(' ')[0]
    ?? savedRow?.full_name?.split(' ')[0]
    ?? undefined;

  // Age from birth UTC datetime
  let nativeAge: number | undefined;
  try {
    if (utcDateTime) {
      const diff = Date.now() - new Date(utcDateTime).getTime();
      nativeAge = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
    }
  } catch { /* ignore */ }

  // Pre-built combination prediction lines (saturn_*, *_conjunction, *_combination ruleKeys)
  const IS_COMBO = (rk: string) =>
    rk.startsWith('saturn_') || rk.includes('_conjunction') || rk.includes('_combination');
  const seenComboChatKeys = new Set<string>();
  const combinationPredictions = [...permanentYogasFromFeeds, ...reportFeeds]
    .filter((f: any) => {
      const rk: string = f.ruleKey || f.id || '';
      if (!rk || rk.startsWith('sade_sati') || rk.startsWith('kaal_sarp')) return false;
      const normKey = rk.replace(/_natal$/, '').replace(/_timing$/, '');
      if (seenComboChatKeys.has(normKey)) return false;
      seenComboChatKeys.add(normKey);
      return IS_COMBO(rk);
    })
    .sort((a: any, b: any) => Math.abs(b.score ?? 0) - Math.abs(a.score ?? 0))
    .slice(0, 12)
    .map((f: any) => {
      const cName = (f.message || f.ruleKey || '').split(':')[0]?.trim() || 'Combination';
      const pol = (f.score ?? 0) > 0 ? '+' : ((f.score ?? 0) < 0 ? '⚠' : '~');
      const win = f.window?.from && f.window?.to
        ? ` [${new Date(f.window.from).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}–${new Date(f.window.to).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}]`
        : '';
      return `${pol} ${cName} [${f.lifeArea || ''}]${win}: ${(f.evidence?.[0] || f.message || '').substring(0, 160)}`;
    });

  return {
    astroData,
    blendedPlanetPct: blendedPlanetPct ?? undefined,
    shadbalaPct,
    bhavaBalaRupas: bhavaBalaRupas ?? undefined,
    combust: combust ?? undefined,
    debil: debil ?? undefined,
    exalt: exalt ?? undefined,
    d1HouseScores,
    houseInterpretations,
    name: nativeName,
    ageYears: nativeAge,
    combinationPredictions: combinationPredictions.length > 0 ? combinationPredictions : undefined,
    dashaContext: dashaContextForFeeds as any,
    feeds: reportFeeds,
    utcDateTime: utcDateTime ?? undefined,
    selectedCity: selectedCity ?? undefined,
    ayanamsa: ayanProvider.name || 'Lahiri',
    gender: selectedGender,
    vargaData: vargaMap ? Object.entries(vargaMap).reduce((acc, [key, vargaResult]: [string, any]) => {
      if (vargaResult && vargaResult.ascSign && vargaResult.planetSigns) {
        const placements = vargaToPlacements(vargaResult);
        acc[key as any] = {
          ascSign: vargaResult.ascSign,
          planetSigns: vargaResult.planetSigns,
          placements: placements
        };
      }
      return acc;
    }, {} as any) : undefined,
    transitData: transitData ? {
      date: transitData.date,
      placements: transitData.placements,
      dignities: transitData.dignities,
      longitudes: transitData.longitudes,
      retrogrades: transitData.retrogrades,
      combust: transitData.combust,
      ...transitData.signDates,
    } : undefined,
    sadeSati: sadeSatiData,
    kaalSarpYoga: kaalSarpData,
    functionalNature: functionalNatureData as any,
    lordDusthanaPenalties: lordDusthanaPenalties ?? undefined,
    dashaImpactByHouse: dashaImpactByHouse ?? undefined,
    transitImpactByHouse: transitImpactByHouse ?? undefined,
    frictionScoresByPlanet: Object.keys(frictionScoresByPlanet).length > 0 ? frictionScoresByPlanet : undefined,
    houseActivities: housesData
      ? Object.fromEntries(housesData.filter(h => h.activities).map(h => [h.id, h.activities!]))
      : undefined,
    conjunctions: astroData ? (() => {
      // Build conjunction list: houses with 2+ planets co-tenanting
      const houseMap: Record<number, string[]> = {};
      Object.entries(astroData.placements).forEach(([planet, p]) => {
        const h = (p as any).house as number;
        if (!houseMap[h]) houseMap[h] = [];
        houseMap[h].push(planet);
      });
      return Object.entries(houseMap)
        .filter(([, ps]) => ps.length >= 2)
        .map(([h, ps]) => ({ house: Number(h), planets: ps }));
    })() : undefined,
    dashaLordRemedies: chatDashaLordRemedies.length > 0 ? chatDashaLordRemedies : undefined,
    // Pre-computed from d1HouseInfluence — same source as RemediesModal (no recomputation)
    houseLords: d1HouseInfluence ? (() => {
      const lords: Record<number, string> = {};
      d1HouseInfluence.rows.forEach((row: any) => { lords[row.house] = row.lord; });
      return lords;
    })() : undefined,
    houseAspects: d1HouseInfluence ? (() => {
      const aspectMap: Record<number, string[]> = {};
      d1HouseInfluence.rows.forEach((row: any) => {
        aspectMap[row.house] = (row.aspectors || []).map((e: any) => e.planet as string);
      });
      return aspectMap;
    })() : undefined,
  };
}, [astroData, d1HouseScores, blendedPlanetPct, shadbalaPct, bhavaBalaRupas, combust, debil, exalt, 
    houseInterpretations, dashaContextForFeeds, reportFeeds, utcDateTime, selectedCity, selectedGender,
    vargaMap, transitData, sadeSatiData, kaalSarpData, functionalNatureData, ayanProvider, lordDusthanaPenalties,
    dashaImpactByHouse, transitImpactByHouse, frictionScoresByPlanet, housesData,
    reportV2Data, savedRow, chartName, permanentYogasFromFeeds, chatDashaLordRemedies, d1HouseInfluence]);

// Fetch remedies for functionally malefic dasha lords (for chat context)
useEffect(() => {
  if (!dashaContextForFeeds || !functionalNatureData || !blendedPlanetPct) return;
  let cancelled = false;
  (async () => {
    try {
      // Collect current PD + SD lords (and MD/AD as secondary)
      const ctx = dashaContextForFeeds as any;
      const allLords: string[] = [
        ctx?.MD?.planet, ctx?.AD?.planet, ctx?.PD?.planet, ctx?.SD?.planet,
        // Also support levels[] format
        ...(ctx?.levels ? ctx.levels.map((l: any) => l.planet) : [])
      ].filter((p): p is string => !!p && p !== 'Unknown');

      // Filter to functional malefics with net-negative blended strength
      const funcMap = functionalNatureData as Record<string, string>;
      const bp = blendedPlanetPct as Record<string, number>;
      const maleficLords = [...new Set(allLords)].filter(
        p => (funcMap[p] ?? 'neutral') === 'malefic' && (bp[p] ?? 0) < 0
      );

      if (maleficLords.length === 0) {
        if (!cancelled) setChatDashaLordRemedies([]);
        return;
      }

      const { data: rData } = await supabase
        .from('remedies')
        .select('title, description, category, related_entity')
        .in('related_entity', maleficLords);

      if (cancelled || !rData) return;

      // Max 3 remedies per planet
      const remedyMap: Record<string, Array<{ planet: string; title: string; description: string; category: string }>> = {};
      for (const r of rData) {
        if (!remedyMap[r.related_entity]) remedyMap[r.related_entity] = [];
        if (remedyMap[r.related_entity].length < 3) {
          remedyMap[r.related_entity].push({
            planet: r.related_entity,
            title: r.title ?? '',
            description: (r.description ?? '').substring(0, 110),
            category: r.category ?? ''
          });
        }
      }
      const flat = Object.values(remedyMap).flat();
      if (!cancelled) setChatDashaLordRemedies(flat);
    } catch { /* non-critical */ }
  })();
  return () => { cancelled = true; };
}, [dashaContextForFeeds, functionalNatureData, blendedPlanetPct]);

// Merge reportData with generated feeds and house scores
const reportDataWithFeeds = useMemo(() => {
  if (!reportData) return null;
  
  // Patch in house scores to houses array
  const housesWithScores = reportData.houses.map((h: any, idx: number) => ({
    ...h,
    houseNumber: h.number || idx + 1,
    score: d1HouseScores ? d1HouseScores[h.number || idx + 1] ?? 0 : 0
  }));
  
  return {
    ...reportData,
    houses: housesWithScores,
    feeds: reportFeeds,
    houseScores: d1HouseScores
  };
}, [reportData, reportFeeds, d1HouseScores]);




useEffect(() => {
    if (!user) {
      setHouseNames({});
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('houses')
        .select('id, name');
      if (error) {
        console.error('Failed to load house names:', error.message);
        return;
      }
      if (!data || cancelled) return;
      const map: Record<number, string> = {};
      data.forEach((row: any) => {
        if (typeof row?.id === 'number' && row.id >= 1 && row.id <= 12) {
          const raw = typeof row.name === 'string' ? row.name.trim() : '';
          map[row.id] = raw || `House ${row.id}`;
        }
      });
      if (!cancelled) setHouseNames(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  /* ----------------- Notification Center Data ----------------- */
  // handleYogasExtracted / handleNatalYogasExtracted update the states declared above
  const handleYogasExtracted = useCallback((yogas: any[]) => {
    // Only update if the data has actually changed (prevent infinite loop)
    setActiveYogasFromFeeds(prev => {
      // Compare array lengths and some key properties to detect actual changes
      if (prev.length !== yogas.length) return yogas;
      
      // If lengths match, do a shallow comparison of IDs
      const prevIds = prev.map(y => y.id || y.ruleKey).join(',');
      const newIds = yogas.map(y => y.id || y.ruleKey).join(',');
      
      return prevIds === newIds ? prev : yogas;
    });
  }, []);

  // Permanent natal yogas from FeedsList (for report generation)
  const handleNatalYogasExtracted = useCallback((yogas: any[]) => {
    setPermanentYogasFromFeeds(prev => {
      if (prev.length !== yogas.length) return yogas;
      const prevIds = prev.map(y => y.id || y.ruleKey).join(',');
      const newIds = yogas.map(y => y.id || y.ruleKey).join(',');
      return prevIds === newIds ? prev : yogas;
    });
  }, []);

  // Extract notifications from feedItems for NotificationCenter
  const notificationData = useMemo(() => {
    if (!reportFeeds || reportFeeds.length === 0) {
      return {
        opportunities: [] as OpportunityNotification[],
        cautions: [] as CautionNotification[],
        opportunitiesForQueue: [] as OpportunityNotification[],
        cautionsForQueue: [] as CautionNotification[],
        yogas: [] as YogaNotification[],
        yogasForQueue: [] as YogaNotification[],
        predictions: [] as PredictionNotification[],
        profileId: selectedProfileId ?? null,
      };
    }

    // Convert reportFeeds to FeedItem format for extraction
    // IMPORTANT: Convert window {from, to} strings to {start, end} Date objects
    const feedItems = reportFeeds.map(f => ({
      ...f,
      text: f.message || '',
      lifeArea: f.lifeArea || 'general',
      severity: f.severity || 0,
      score: f.score || 0,
      window: f.window ? {
        start: new Date(f.window.from),
        end: new Date(f.window.to)
      } : undefined
    }));

    const opportunities = extractOpportunities(feedItems as any);
    const cautions = extractCautions(feedItems as any);
    // Queue-safe versions: strip private (admin-only) feeds before writing to DB.
    // These are used by the WhatsApp queue effect; the bell icon still uses the full set.
    const feedItemsPublic = feedItems.filter((f: any) => !f.private);
    const opportunitiesForQueue = extractOpportunities(feedItemsPublic as any);
    const cautionsForQueue = extractCautions(feedItemsPublic as any);
    
    // Prepare enhanced context for sophisticated predictions (Phase 1-4)
    let enhancedContext = null;
    if (debouncedDashaContext && functionalClassificationForFeeds && debouncedHouseScores && astroData && transitData) {
      // Extract empty houses (houses with no planets)
      const planetsInHouses = new Set<HouseNum>();
      if (astroData.placements) {
        Object.values(astroData.placements).forEach((planet: any) => {
          if (planet?.house >= 1 && planet?.house <= 12) {
            planetsInHouses.add(planet.house as HouseNum);
          }
        });
      }
      const emptyHouses: HouseNum[] = [];
      for (let h = 1; h <= 12; h++) {
        if (!planetsInHouses.has(h as HouseNum)) {
          emptyHouses.push(h as HouseNum);
        }
      }
      
      // Build TransitContext from transitData
      const transitPositions: any[] = [];
      if (transitData.placements) {
        Object.entries(transitData.placements).forEach(([planetName, data]: [string, any]) => {
          transitPositions.push({
            planet: planetName as PlanetName,
            house: data.house as HouseNum,
            sign: Math.floor((data.lon || 0) / 30),
            aspects: data.aspects
          });
        });
      }
      
      const builtTransitContext = {
        positions: transitPositions,
        planetsTransitingDashaOccupied: {
          Sun: [], Moon: [], Mars: [], Mercury: [],
          Jupiter: [], Venus: [], Saturn: [], Rahu: [], Ketu: []
        } as Record<PlanetName, PlanetName[]>
      };
      
      // Create enhanced context with simplified dasha structure
      enhancedContext = {
        dashaContext: {
          MD: { 
            planet: debouncedDashaContext.MD.planet, 
            level: 'MD' as const,
            rules: debouncedDashaContext.MD.rules as HouseNum[] | undefined,
            occupies: debouncedDashaContext.MD.occupies as HouseNum | undefined,
            aspects: debouncedDashaContext.MD.aspects as HouseNum[] | undefined
          },
          AD: { 
            planet: debouncedDashaContext.AD.planet, 
            level: 'AD' as const,
            rules: debouncedDashaContext.AD.rules as HouseNum[] | undefined,
            occupies: debouncedDashaContext.AD.occupies as HouseNum | undefined,
            aspects: debouncedDashaContext.AD.aspects as HouseNum[] | undefined
          },
          PD: { 
            planet: debouncedDashaContext.PD.planet, 
            level: 'PD' as const,
            rules: debouncedDashaContext.PD.rules as HouseNum[] | undefined,
            occupies: debouncedDashaContext.PD.occupies as HouseNum | undefined,
            aspects: debouncedDashaContext.PD.aspects as HouseNum[] | undefined
          },
          SD: { 
            planet: debouncedDashaContext.SD.planet, 
            level: 'SD' as const,
            rules: debouncedDashaContext.SD.rules as HouseNum[] | undefined,
            occupies: debouncedDashaContext.SD.occupies as HouseNum | undefined,
            aspects: debouncedDashaContext.SD.aspects as HouseNum[] | undefined
          },
          lagnaSign: astroData.asc?.ascendantSignIndex,
          houseScores: debouncedHouseScores
        },
        functionalClassification: functionalClassificationForFeeds,
        houseScores: debouncedHouseScores as Record<HouseNum, number>,
        transitContext: builtTransitContext,
        emptyHouses: emptyHouses,
        // Transit enrichment — wire retrograde/dignity/combust + Moon house signal
        transitRetrogrades: transitData.retrogrades as Partial<Record<string, boolean>> | undefined,
        transitDignities: transitData.dignities as Partial<Record<string, string>> | undefined,
        transitCombust: transitData.combust as Partial<Record<string, boolean>> | undefined,
        moonTransitHouse: (transitData.placements as any)?.Moon?.house as number | undefined,
      } as EnhancedPredictionContext;
    }
    
    // Debug: Log enhanced context
    console.log('[Enhanced Context Debug]', {
      hasContext: !!enhancedContext,
      dashaContext: enhancedContext?.dashaContext ? {
        SD: enhancedContext.dashaContext.SD.planet,
        PD: enhancedContext.dashaContext.PD.planet,
        AD: enhancedContext.dashaContext.AD.planet
      } : null,
      functionalClassification: enhancedContext?.functionalClassification,
      emptyHouses: enhancedContext?.emptyHouses,
      transitPositions: enhancedContext?.transitContext?.positions?.length
    });
    
    const predictions = generatePredictions(feedItems as any, enhancedContext);

    // Debug: Check feed distribution
    const feedStats = {
      total: feedItems.length,
      positive: feedItems.filter(f => f.score > 0).length,
      negative: feedItems.filter(f => f.score < 0).length,
      neutral: feedItems.filter(f => f.score === 0).length
    };
    console.log('[Notification Predictions Debug]', {
      ...feedStats,
      predictionCount: predictions.length,
      predictionSummaries: predictions.map(p => ({ period: p.period, summary: p.summary }))
    });

    // Use active yogas from FeedsList (timingFeeds) - these have dasha-based filtering
    // Now apply NotificationCenter's specific timeline rules (7-day window for upcoming)
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const oneYearLater = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
    
    console.log('[Yoga Debug] Active yogas from FeedsList:', {
      count: activeYogasFromFeeds.length,
      ruleKeys: activeYogasFromFeeds.map(f => f.ruleKey),
      samples: activeYogasFromFeeds.slice(0, 3).map(f => ({
        ruleKey: f.ruleKey,
        message: f.message?.substring(0, 100),
        window: f.window
      }))
    });
    
    // Convert FeedsList timingFeeds to YogaNotification format with timeline filtering
    const timingYogas: YogaNotification[] = activeYogasFromFeeds
      .filter(feed => {
        const rk = feed.ruleKey || '';
        const msg = feed.message || '';
        
        // Filter 1: Only yoga-related feeds (not regular predictions)
        const isYogaRelated = rk.includes('yoga') || 
                              rk.includes('sade_sati') || 
                              msg.toUpperCase().includes('YOGA');
        
        if (!isYogaRelated) return false;
        
        // Filter 2: Apply NotificationCenter timeline rules
        // Include if: currently active OR starting within next 7 days
        if (feed.window?.from) {
          const startDate = new Date(feed.window.from);
          const endDate = feed.window.to ? new Date(feed.window.to) : oneYearLater;
          
          // Currently active: started in past and not yet ended
          const isActive = startDate <= now && endDate >= now;
          
          // Starting soon: starts within next 7 days
          const isUpcoming = startDate > now && startDate <= sevenDaysFromNow;
          
          return isActive || isUpcoming;
        }
        
        // If no window info, include it (fallback - shouldn't happen with timingFeeds)
        return true;
      })
      .map((yoga, idx) => {
        // Extract a cleaner yoga name from the message
        let yogaName = yoga.message || yoga.ruleKey || 'Yoga';
        
        // Try to extract the yoga name from common patterns
        const yogaMatch = yogaName.match(/([A-Z][A-Z\s]+YOGA[^:]*)/i);
        if (yogaMatch) {
          yogaName = yogaMatch[1].trim();
        } else if (yogaName.includes('YOGA')) {
          // Extract everything before the first colon or period after YOGA
          const parts = yogaName.split(/[:.]/);
          yogaName = parts[0].trim();
        }
        
        return {
          id: yoga.id || `yoga-${idx}`,
          type: 'yoga' as const,
          name: yogaName,
          description: yoga.evidence?.join('; ') || yoga.message || '',
          startDate: yoga.window?.from ? new Date(yoga.window.from) : now,
          endDate: yoga.window?.to ? new Date(yoga.window.to) : oneYearLater,
          isPermanent: false
        };
      });

    // Also include permanent natal yogas (Kalanidhi, Vasumati, Parivartana, etc.)
    // These live in permanentYogasFromFeeds and were missing after queue reset.
    const permanentYogaItems: YogaNotification[] = (permanentYogasFromFeeds || [])
      .filter((feed: any) => {
        const rk = feed.ruleKey || '';
        const msg = feed.message || '';
        return rk.includes('yoga') || msg.toUpperCase().includes('YOGA');
      })
      .map((yoga: any, idx: number) => {
        let yogaName = yoga.message || yoga.ruleKey || 'Yoga';
        const yogaMatch = yogaName.match(/([A-Z][A-Z\s]+YOGA[^:]*)/i);
        if (yogaMatch) {
          yogaName = yogaMatch[1].trim();
        } else if (yogaName.includes('YOGA')) {
          yogaName = yogaName.split(/[:.]/)[0].trim();
        }
        return {
          id: yoga.id || `permanent-yoga-${idx}`,
          type: 'yoga' as const,
          name: yogaName,
          description: yoga.evidence?.join('; ') || yoga.message || '',
          startDate: now,
          endDate: oneYearLater,
          isPermanent: true
        };
      });

    // Merge: timing yogas first (most relevant), then permanent, deduplicated by name
    const seenYogaNamesInMemo = new Set<string>();
    const sortedYogasAll: YogaNotification[] = [...timingYogas, ...permanentYogaItems]
      .filter(y => {
        const key = y.name.toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 60);
        if (seenYogaNamesInMemo.has(key)) return false;
        seenYogaNamesInMemo.add(key);
        return true;
      })
      .sort((a, b) => {
        // Sort by start date (nearest first)
        return a.startDate.getTime() - b.startDate.getTime();
      });

    // Bell icon gets the original 8-item cap (unchanged)
    const yogas = sortedYogasAll.slice(0, 8);
    // Queue gets all (uncapped) — edge function digest limits to 5 for the actual message
    const yogasForQueue = sortedYogasAll;

    // Debug logging
    console.log('[Notifications Debug]', {
      totalTimingFeedsFromFeedsList: activeYogasFromFeeds.length,
      yogaRelatedFeeds: activeYogasFromFeeds.filter(f => {
        const rk = f.ruleKey || '';
        const msg = f.message || '';
        return rk.includes('yoga') || rk.includes('sade_sati') || msg.toUpperCase().includes('YOGA');
      }).length,
      afterTimelineFiltering: yogas.length,
      opportunities: opportunities.length,
      cautions: cautions.length,
      predictions: predictions.length,
      yogaSamples: yogas.slice(0, 3).map(y => ({ name: y.name, start: y.startDate.toISOString(), end: y.endDate.toISOString() }))
    });

    return { opportunities, cautions, opportunitiesForQueue, cautionsForQueue, yogas, yogasForQueue, predictions, feedStats, profileId: selectedProfileId ?? null };
  }, [reportFeeds, activeYogasFromFeeds, permanentYogasFromFeeds, debouncedDashaContext, functionalClassificationForFeeds, debouncedHouseScores, astroData, transitData, selectedProfileId]);

  // ── WhatsApp Notification Queue ──
  // Saves computed notifications to DB for WhatsApp delivery.
  // Bell icon is NOT affected — it still reads from notificationData in-memory.
  // Quality filters: only high-signal, recently-opened windows to avoid noise.
  useEffect(() => {
    if (!user?.id || !notificationData) return;
    // Use queue-safe (non-private) variants so admin-only feeds (criminal tendency etc.)
    // are never written to the DB queue and never appear in WhatsApp digests sent to users.
    const { opportunitiesForQueue: opportunities, cautionsForQueue: cautions, yogasForQueue: yogas = [] } = notificationData;
    if (!opportunities.length && !cautions.length && !yogas.length) return;

    // Stale data guard: notificationData was built for a different profile.
    // This happens when selectedProfileId changes before the memo recomputes.
    // Writing in this window would cross-contaminate queue rows.
    if (notificationData.profileId !== (selectedProfileId ?? null)) return;

    // Resolve the chart owner's user ID.
    // For own profile: use the logged-in user's ID.
    // For family/other profiles (admin viewing another user's chart): use owner_id from
    // the birth_profiles record so the queue row joins to the correct profile and phone number.
    const chartOwnerUserId = selectedProfileId === 'my_profile'
      ? user.id
      : (profiles.find(p => p.id === selectedProfileId)?.owner_id ?? user.id);

    // Safety guard: if a family chart's owner resolves back to the current user (i.e. admin
    // created this chart for someone who has no account of their own), skip queue writing.
    // These charts have no independent opt-in — we must not attribute them to the admin's
    // phone, which would cause every such chart to flood the admin's WhatsApp on Sundays.
    // Only the chart OWNER should write their own queue rows (when they log in themselves).
    if (selectedProfileId !== 'my_profile' && chartOwnerUserId === user.id) return;

    // Resolve chart identity.
    // Own profile: birth data lives in the `users` table (id = auth.uid()), NOT birth_profiles.
    // Using savedRow.id would violate the FK. Use null instead (same as resolvedKarmicProfileId).
    // Family profiles: selectedProfileId is a valid birth_profiles.id UUID.
    const birthProfileId = selectedProfileId === 'my_profile'
      ? null
      : (selectedProfileId ?? null);
    const chartName = selectedProfileId === 'my_profile'
      ? (savedRow?.full_name ?? 'My Chart')
      : (profiles.find(p => p.id === selectedProfileId)?.full_name ?? 'Family Chart');

    const now = new Date();
    const fmtDate = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

    // Build dasha chain string to store on every queue row so the edge function
    // can read it directly without re-deriving it from message text.
    // IMPORTANT: use dashaContextForFeeds (non-debounced) not debouncedDashaContext.
    // debouncedDashaContext lags 300ms behind — if the admin switches profiles quickly
    // it still holds the previous profile's dasha when this effect fires, causing the
    // wrong dasha to be written for the new profile's queue rows.
    const dashaChain = (() => {
      if (!dashaContextForFeeds) return null;
      const parts: string[] = [];
      if (dashaContextForFeeds.MD?.planet) parts.push(`${dashaContextForFeeds.MD.planet} MD`);
      if (dashaContextForFeeds.AD?.planet) parts.push(`${dashaContextForFeeds.AD.planet} AD`);
      if (dashaContextForFeeds.PD?.planet) parts.push(`${dashaContextForFeeds.PD.planet} PD`);
      return parts.length > 0 ? parts.join(' · ') : null;
    })();

    // Split concatenated multi-yoga description at the next entry boundary (semicolon + emoji)
    const firstYogaEntry = (raw: string): string => {
      const boundary = raw.search(/\s*;\s*[🌟⭐🔥🎯💫✨🪐]/u);
      return boundary > 0 ? raw.slice(0, boundary) : raw;
    };

    // Extract a named section ("Effects:", "Guidance:", "Remedies:") — first sentence only
    const extractSection = (text: string, label: string): string => {
      const re = new RegExp(`${label}:\\s*([^]*?)(?=\\b(?:Effects|Guidance|Remedies|Duration):|$)`, 'i');
      const m = text.match(re);
      if (!m) return '';
      const sentences = m[1].match(/[^.!?]+[.!?]+/g) ?? [];
      let result = '';
      for (const s of sentences) {
        if ((result + s).length > 120) break;
        result += s;
      }
      return (result || m[1].slice(0, 120)).trim();
    };

    // Strip technical scoring noise
    const stripNoise = (t: string): string =>
      t
        .replace(/BaseImpact=[^\s]+/gi, '')
        .replace(/TransitP=[^\s]+/gi, '')
        .replace(/Shadbala\s*[\d.]+/gi, '')
        .replace(/Score:\s*[+-]?[\d.]+/gi, '')
        .replace(/Gate [A-E]:[^.]+\./gi, '')
        .replace(/NOTE:[^.]+\./gi, '')
        .replace(/Using approximate dates[^;.]*/gi, '')
        .replace(/~[\d.]+ year standard/gi, '')
        .replace(/Calculation was unavailable[^.]*/gi, '')
        .replace(/\d{1,2}[a-z]{2}\s+\w+\s+\d{4}\s*\|\s*\d{1,2}:\d{2}\s*[ap]m/gi, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

    // Human-readable yoga label: strip noisy prefixes/ALL_CAPS artefacts
    const cleanYogaName = (name: string): string =>
      name
        .replace(/SADE SATI\s*[-–:]\s*/i, 'Sade Sati – ')
        .replace(/RISING PHASE/i, 'Rising Phase')
        .replace(/PEAK PHASE/i, 'Peak Phase')
        .replace(/SETTING PHASE/i, 'Setting Phase')
        .replace(/[-_]/g, ' ')
        .replace(/\b([A-Z]{3,})\b/g, w => w[0] + w.slice(1).toLowerCase())
        .replace(/\s{2,}/g, ' ')
        .trim();

    const buildMessage = (
      type: 'opportunity' | 'caution' | 'yoga',
      title: string,
      description: string,
      lifeArea: string | null,
      startDate: Date,
      endDate: Date
    ): string => {
      const until = fmtDate(endDate);
      const cta = '\n\nView full reading → shreemastro.com';

      if (type === 'yoga') {
        const label = cleanYogaName(title);
        const entry = firstYogaEntry(description);
        const effects = stripNoise(extractSection(entry, 'Effects'));
        const guidance = stripNoise(extractSection(entry, 'Guidance'));
        const remedies = stripNoise(extractSection(entry, 'Remedies'));

        const parts: string[] = [];
        if (effects) parts.push(`*Effects:* ${effects}`);
        if (guidance) parts.push(`*What to do:* ${guidance}`);
        if (remedies) parts.push(`*Remedy:* ${remedies}`);

        if (!parts.length) {
          // Fallback: first 2 sentences after the "Name: " prefix
          const fallback = stripNoise(entry.replace(/^[^:]+:\s*/, ''));
          const sentences = fallback.match(/[^.!?]+[.!?]+/g) ?? [];
          parts.push(sentences.slice(0, 2).join(' ').trim() || fallback.slice(0, 180));
        }

        return `*🪐 ${label}*\n\n${parts.join('\n\n')}\n\nActive until *${until}*.${cta}`;
      }

      // opportunity / caution — first 2 clean sentences
      const clean = stripNoise(description);
      const sentences = clean.match(/[^.!?]+[.!?]+/g) ?? [];
      const body = sentences.slice(0, 2).join(' ').trim() || clean.slice(0, 180);

      if (type === 'opportunity') {
        return `*🌟 ${title} – Favourable Period*\n\n${body}\n\nActive until *${until}*.${cta}`;
      }
      return `*⚠️ ${title} – Watch Period*\n\n${body}\n\nActive until *${until}*.${cta}`;
    };

    const rows: any[] = [];

    // Normalize a Date to midnight UTC string – ensures the upsert conflict key is stable
    // across recalculations (avoids millisecond drift creating duplicate queue rows).
    const toDateUTC = (d: Date) => new Date(d.toISOString().slice(0, 10) + 'T00:00:00.000Z').toISOString();

    // Opportunities: deduplicated by life area (highest severity per area), window still active
    const bestOppByArea = new Map<string, typeof opportunities[0]>();
    opportunities
      .filter(n => n.endDate >= now)
      .forEach(n => {
        const key = n.lifeArea || 'general';
        const existing = bestOppByArea.get(key);
        if (!existing || n.severity > existing.severity) bestOppByArea.set(key, n);
      });
    bestOppByArea.forEach(n => {
        rows.push({
          user_id: chartOwnerUserId,
          birth_profile_id: birthProfileId,
          chart_name: chartName,
          type: 'opportunity',
          rule_key: `${n.lifeArea || 'general'}_${n.startDate.toISOString().slice(0, 10)}`,
          title: n.title,
          message: buildMessage('opportunity', n.title, n.description, n.lifeArea, n.startDate, n.endDate),
          window_start: toDateUTC(n.startDate),
          window_end: toDateUTC(n.endDate),
          life_area: n.lifeArea,
          dasha_chain: dashaChain,
          lagna_sign: astroData?.asc?.ascendantSignIndex ?? null,
          moon_sign: astroData?.moon?.moonSignIndex ?? null,
          transit_snapshot: transitData?.placements ? {
            Jupiter: transitData.placements.Jupiter?.house ?? null,
            Saturn:  transitData.placements.Saturn?.house  ?? null,
            Mars:    transitData.placements.Mars?.house    ?? null,
            Mercury: transitData.placements.Mercury?.house ?? null,
            Venus:   transitData.placements.Venus?.house   ?? null,
          } : null,
        });
      });

    // Cautions: window still active, deduplicated by life area (highest severity per area)
    const bestCautionByArea = new Map<string, typeof cautions[0]>();
    cautions
      .filter(n => n.endDate >= now)
      .forEach(n => {
        const key = n.lifeArea || 'general';
        const existing = bestCautionByArea.get(key);
        if (!existing || n.severity > existing.severity) bestCautionByArea.set(key, n);
      });
    bestCautionByArea.forEach(n => {
        rows.push({
          user_id: chartOwnerUserId,
          birth_profile_id: birthProfileId,
          chart_name: chartName,
          type: 'caution',
          rule_key: `${n.lifeArea || 'general'}_${n.startDate.toISOString().slice(0, 10)}`,
          title: n.title,
          message: buildMessage('caution', n.title, n.description, n.lifeArea, n.startDate, n.endDate),
          window_start: toDateUTC(n.startDate),
          window_end: toDateUTC(n.endDate),
          life_area: n.lifeArea,
          dasha_chain: dashaChain,
          lagna_sign: astroData?.asc?.ascendantSignIndex ?? null,
          moon_sign: astroData?.moon?.moonSignIndex ?? null,
          transit_snapshot: transitData?.placements ? {
            Jupiter: transitData.placements.Jupiter?.house ?? null,
            Saturn:  transitData.placements.Saturn?.house  ?? null,
            Mars:    transitData.placements.Mars?.house    ?? null,
            Mercury: transitData.placements.Mercury?.house ?? null,
            Venus:   transitData.placements.Venus?.house   ?? null,
          } : null,
        });
      });

    // Yogas: include all still active, deduped by name, skip if message is only a NOTE warning
    const seenYogaNames = new Set<string>();
    yogas
      .filter(n => n.endDate >= now)
      .forEach(n => {
        // Use normalized name as rule_key so duplicate yoga entries (same name, diff IDs) don't all insert
        const nameKey = n.name.toLowerCase().replace(/\s+/g, '_').slice(0, 80);
        if (seenYogaNames.has(nameKey)) return;
        seenYogaNames.add(nameKey);

        rows.push({
          user_id: chartOwnerUserId,
          birth_profile_id: birthProfileId,
          chart_name: chartName,
          type: 'yoga',
          rule_key: nameKey,
          title: n.name,
          message: buildMessage('yoga', n.name, n.description, null, n.startDate, n.endDate),
          window_start: toDateUTC(n.startDate),
          window_end: toDateUTC(n.endDate),
          life_area: null,
          dasha_chain: dashaChain,
          lagna_sign: astroData?.asc?.ascendantSignIndex ?? null,
          moon_sign: astroData?.moon?.moonSignIndex ?? null,
          transit_snapshot: transitData?.placements ? {
            Jupiter: transitData.placements.Jupiter?.house ?? null,
            Saturn:  transitData.placements.Saturn?.house  ?? null,
            Mars:    transitData.placements.Mars?.house    ?? null,
            Mercury: transitData.placements.Mercury?.house ?? null,
            Venus:   transitData.placements.Venus?.house   ?? null,
          } : null,
        });
      });

    console.log(`[WhatsApp Queue] ${rows.length} rows to upsert`, rows.map(r => `${r.type}:${r.title}`));
    if (!rows.length) return;

    // Delete stale unsent rows for this chart before re-inserting.
    // Rows already sent (whatsapp_sent_at IS NOT NULL) are preserved.
    const doWrite = async () => {
      // Build delete as two separate queries to avoid nullable-column reassignment issues
      if (birthProfileId === null) {
        const { error: delError } = await supabase
          .from('whatsapp_notification_queue')
          .delete()
          .eq('user_id', chartOwnerUserId)
          .is('birth_profile_id', null)
          .is('whatsapp_sent_at', null);
        if (delError) { console.error('[WhatsApp Queue] delete error:', delError.message); return; }
      } else {
        const { error: delError } = await supabase
          .from('whatsapp_notification_queue')
          .delete()
          .eq('user_id', chartOwnerUserId)
          .eq('birth_profile_id', birthProfileId)
          .is('whatsapp_sent_at', null);
        if (delError) { console.error('[WhatsApp Queue] delete error:', delError.message); return; }
      }

      const { error } = await supabase
        .from('whatsapp_notification_queue')
        .insert(rows);
      if (error) console.error('[WhatsApp Queue] insert error:', error.message, JSON.stringify(rows[0]));
      else console.log(`[WhatsApp Queue] inserted ${rows.length} rows OK`);
    };
    doWrite();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationData, user?.id, selectedProfileId, savedRow?.id, dashaContextForFeeds]);

  // Navigation handler for notifications
  const handleNotificationNavigate = useCallback((section: 'current-period' | 'yogas', itemId?: string) => {
    if (section === 'current-period' && itemId) {
      setPendingScrollTarget({ itemId, highlightColor: '#fff3cd', fallbackId: 'section-active-yogas' });
      setActiveTab('dasha');
    } else if (section === 'yogas' && itemId) {
      setPendingScrollTarget({ itemId, highlightColor: '#e0f2fe', fallbackId: 'section-active-yogas' });
      setActiveTab('feeds');
    }
  }, []);

  // Mobile chat handlers
  const handleOpenMobileChat = useCallback(() => {
    if (isMobile) {
      setMobileChatOpen(true);
      setSelectedMobileLifeArea(null); // Start with life area selection
    }
  }, [isMobile]);

  const handleSelectMobileLifeArea = useCallback((lifeArea: LifeAreaKey) => {
    setSelectedMobileLifeArea(lifeArea);
  }, []);

  const handleGetMobileOverview = useCallback(() => {
    if (selectedMobileLifeArea) {
      const area = LIFE_AREAS[selectedMobileLifeArea];
      const overviewPrompt = `Give me a comprehensive overview of ${area.label} based on my chart. Analyze relevant houses (${area.houses.map(h => `H${h}`).join(', ')}) and divisional charts (${area.vargas.join(', ')}). Include strengths, challenges, timing, and current dasha impact with specific percentages and dates.`;
      // The chat interface will handle sending this
      return overviewPrompt;
    }
    return '';
  }, [selectedMobileLifeArea]);

  const handleCloseMobileChat = useCallback(() => {
    setMobileChatOpen(false);
    setSelectedMobileLifeArea(null);
  }, []);

  const handleBackToLifeAreaList = useCallback(() => {
    setSelectedMobileLifeArea(null);
  }, []);

  /* ----------------- Auth Guards ----------------- */
  const unauthView = (
    <div style={{ padding: 24 }}>
      <AuthPanel initialMode={authMode} selectedPlan={selectedPlan} initialError={authError || undefined} />
    </div>
  );
  
  // Standalone public routes — accessible without auth (for Meta Privacy Policy URL etc.)
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
  if (path === '/privacy' || path === '/terms') {
    return (
      <CssVarsProvider theme={joyTheme}>
        <TermsPage onClose={() => window.history.back()} />
      </CssVarsProvider>
    );
  }

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  
  // Show splash screen for unauthenticated users (first time only)
  if (!user && showSplash) {
    return (
      <>
        <SplashScreen onComplete={() => {
          setShowSplash(false);
          setShowLanding(true);
          sessionStorage.setItem('splashDismissed', 'true');
        }} />
        <PWAInstallPrompt />
      </>
    );
  }
  
  // Show landing page after splash is dismissed
  if (!user && showLanding) {
    return (
      <>
        <LandingPage 
          onStartTrial={() => {
            setAuthMode('trial');
            setShowLanding(false);
            sessionStorage.setItem('landingDismissed', 'true');
          }}
          onSelectPlan={(plan) => {
            setSelectedPlan(plan);
            localStorage.setItem('preSelectedPlan', plan); // Persist for login page
            setAuthMode(plan === 'trial' ? 'trial' : 'paid');
            setShowLanding(false);
            sessionStorage.setItem('landingDismissed', 'true');
          }}
          onLogin={() => {
            setAuthMode('login');
            setShowLanding(false);
            sessionStorage.setItem('landingDismissed', 'true');
          }}
        />
        <PWAInstallPrompt />
      </>
    );
  }
  
  if (!user) return (
    <>
      {unauthView}
      <PWAInstallPrompt />
    </>
  );

  // Show pending approval banner for users awaiting admin approval (previously deleted users)
  const showPendingApproval = subscription?.status === 'pending_approval';

  // Show loading spinner while terms check is in progress — prevents flash of main UI
  if (isCheckingTerms) {
    return (
      <CssVarsProvider theme={joyTheme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress size="lg" variant="soft" />
        </Box>
      </CssVarsProvider>
    );
  }

  // Show terms overlay if user hasn't accepted current version
  // — WelcomeWizard takes priority for users with no birth profile
  const showWizard =
    isMobile &&
    user !== null &&
    !isCheckingTerms &&
    hasAcceptedTerms !== null &&
    savedRowLoaded &&
    profilesLoaded &&
    savedRow === null &&
    profiles.length === 0 &&
    astroData === null;

  if (showWizard) {
    return (
      <CssVarsProvider theme={joyTheme}>
        <WelcomeWizard
          userId={user.id}
          userEmail={user.email ?? undefined}
          termsVersion={currentTermsVersion}
          termsNeeded={hasAcceptedTerms === false}
          onTermsAccept={handleTermsAccept}
          astroData={astroData}
          onDismiss={() => {
            // Refetch savedRow so the wizard condition clears naturally
            fetchMyUserProfile(user.id).then(row => {
              setSavedRow(row as DbUserRow | null);
            });
          }}
        />
      </CssVarsProvider>
    );
  }

  if (hasAcceptedTerms === false) {
    return (
      <CssVarsProvider theme={joyTheme}>
        {showPendingApproval && <PendingApprovalBanner userEmail={user.email ?? undefined} />}
        <TermsOverlay
          userId={user.id}
          userEmail={user.email ?? undefined}
          currentVersion={currentTermsVersion}
          onAccept={handleTermsAccept}
          onDecline={handleTermsDecline}
        />
        <PWAInstallPrompt />
      </CssVarsProvider>
    );
  }

  /* ----------------- Main UI via AppShell (Joy only) ----------------- */
  return (
    <CssVarsProvider theme={joyTheme}>
      {/* Expired trial hard block — shown before anything else */}
      {subscription?.status === 'expired' && profile?.role !== 'admin' && (
        <ExpiredTrialOverlay
          onSignOut={() => { /* user becomes null after signOut, App re-renders automatically */ }}
          onUpgradeSuccess={() => forceRefresh()}
        />
      )}

      {/* Pending Approval Banner - Show prominently if user needs admin approval */}
      {showPendingApproval && <PendingApprovalBanner userEmail={user.email ?? undefined} />}

      {/* WhatsApp Opt-in Banner - Show once to users who haven't opted in yet */}
      {!showPendingApproval && whatsAppOptedIn === false && (
        <WhatsAppOptInBanner
          userId={user.id}
          hasOptedIn={false}
          onEnable={() => setShowWhatsAppModal(true)}
        />
      )}

      {/* WhatsApp Opt-in Modal - Opened via Settings menu or banner */}
      {showWhatsAppModal && (
        <WhatsAppOptInModal
          open={showWhatsAppModal}
          userId={user.id}
          onClose={() => setShowWhatsAppModal(false)}
          onSaved={(phone, optedIn) => setWhatsAppOptedIn(!!(optedIn && phone))}
        />
      )}

      {/* Life Timing Alerts / Notification Preferences — shown after first chart save */}
      {showNotifPreferences && user && (
        <NotificationPreferences
          open={showNotifPreferences}
          userId={user.id}
          onClose={() => setShowNotifPreferences(false)}
        />
      )}

      {/* My Subscription Modal - Opened via Settings menu or ?subscribe=1 deeplink */}
      <MySubscriptionModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        userId={user.id}
        onUpgradeSuccess={() => { setShowSubscriptionModal(false); forceRefresh(); }}
        chatCreditsRemaining={chatCreditsRemaining ?? undefined}
        onBuyCredits={() => { setShowSubscriptionModal(false); setReopenSubscriptionAfterCredits(true); setShowBuyCreditsModal(true); }}
      />
      
      {/* Mobile Chat Interface - Render FIRST to overlay everything */}
      {isMobile && mobileChatOpen && !selectedMobileLifeArea && (
        <MobileChatList
          onSelectLifeArea={handleSelectMobileLifeArea}
          onClose={handleCloseMobileChat}
        />
      )}

      {isMobile && mobileChatOpen && selectedMobileLifeArea && chatContextData && (
        <MobileChatInterface
          lifeArea={selectedMobileLifeArea}
          contextData={chatContextData}
          userRole={profile?.role}
          onBack={handleBackToLifeAreaList}
          onGoHome={handleCloseMobileChat}
          chartInsightData={astroData ? {
            astroData,
            d1HouseScores: d1HouseScores ?? undefined,
            bhavaBalaRupas: bhavaBalaRupas ?? undefined,
            dashaContext: dashaContextForFeeds ?? undefined,
            utcDateTime: utcDateTime ?? undefined,
            vargaMap: vargaMap ?? undefined,
            activeYogas: (() => {
              const all = [
                ...(permanentYogasFromFeeds ?? []),
                ...(activeYogasFromFeeds ?? []),
              ];
              const seen = new Set<string>();
              return all.filter((f: any) => {
                const k = (f.ruleKey || f.id || '').replace(/_natal$/, '').replace(/_timing$/, '');
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
              });
            })(),
            combust: combust ? Object.fromEntries(Object.entries(combust).map(([k, v]) => [k, (v as any).combust ?? false])) : undefined,
            debil: debil ?? undefined,
            exalt: exalt ?? undefined,
            functionalNature: functionalNatureData as Record<string, string> ?? undefined,
            houseInterpretations: houseInterpretations ?? undefined,
            feeds: reportFeeds ?? undefined,
            futureEvents: futureEventsForChat ?? undefined,
            shadbalaPct: blendedPlanetPct as Record<string, number> ?? undefined,
            svPct: svCalibratedPct as Record<string, number> ?? undefined,
            shadbalaTotal: shadbalaPct ? Object.fromEntries(
              Object.entries(shadbalaPct).map(([p, v]) => [p, (v as any).totalPct])
            ) as Record<string, number> : undefined,
            ishtaKashta: ishtaKashtaFooter as Record<string, { ishta: number; kashta: number }> ?? undefined,
            bhavaSandhi: bhavaSandhiMultipliers as Record<string, number> ?? undefined,
            houseLords: d1HouseInfluence ? (() => {
              const m: Record<number, string> = {};
              d1HouseInfluence.rows.forEach((r: any) => { if (r.lord) m[r.house] = r.lord; });
              return m;
            })() : undefined,
            houseAspects: d1HouseInfluence ? (() => {
              const m: Record<number, string[]> = {};
              d1HouseInfluence.rows.forEach((r: any) => {
                m[r.house] = (r.aspectors || []).map((e: any) => e.planet as string);
              });
              return m;
            })() : undefined,
          } : undefined}
        />
      )}

      {/* AppShell - hidden when mobile chat is open */}
      {!(isMobile && mobileChatOpen) && (
      <AppShell
        title="ShreemAstro.com"
        userLabel={chartName ?? ((user as any)?.user_metadata?.full_name || (user as any)?.full_name || (user as any)?.name || 'User')}
        userEmail={(user as any)?.email ?? (user as any)?.phone ?? undefined}
        chartName={chartName ?? undefined}
        isAdmin={!!(profile?.role === 'admin')}
        canUseCalculator={!!(profile && (profile.role === 'admin' || profile.approved_family_quota > 0 || subscription?.status === 'active'))}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onStartTour={() => {
          // Only start tour if this tab has a tour defined
          const tourableTabs: Array<'calculator' | 'feeds' | 'vastu' | 'chat' | 'admin' | 'family'> = 
            ['calculator', 'feeds', 'vastu', 'chat', 'admin', 'family'];
          if (tourableTabs.includes(activeTab as any)) {
            startTour(activeTab as any);
          }
        }}
        onOpenMobileChat={isMobile ? handleOpenMobileChat : undefined}
        homeAudioGuide={
          <AudioGuide
            src="/audio/guide-home.mp3"
            label="Overview"
            size="sm"
            variant="red"
            onTimeUpdate={handleHomeAudioTime}
          />
        }
        profileOptions={profileOptions}
        selectedProfileId={selectedProfileId}
        onProfileChange={(id) => {
          setSelectedProfileId(id);
          if (id) loadUserSelectionById(id);
        }}
        opportunities={subscription?.status === 'expired' && profile?.role !== 'admin' ? [] : notificationData.opportunities}
        cautions={subscription?.status === 'expired' && profile?.role !== 'admin' ? [] : notificationData.cautions}
        yogas={subscription?.status === 'expired' && profile?.role !== 'admin' ? [] : notificationData.yogas}
        predictions={subscription?.status === 'expired' && profile?.role !== 'admin' ? [] : notificationData.predictions}
        onNotificationNavigate={subscription?.status === 'expired' && profile?.role !== 'admin' ? undefined : handleNotificationNavigate}
        birthDate={birthDate}
        feedStats={subscription?.status === 'expired' && profile?.role !== 'admin' ? undefined : notificationData.feedStats}
        showQuickAccess={!!astroData}
        aiCreditsGeneral={aiCreditsGeneral}
        aiCreditsLifeArea={aiCreditsLifeArea}
        chatCreditsRemaining={chatCreditsRemaining}
        onBuyCredits={() => setShowBuyCreditsModal(true)}
        onOpenWhatsAppOptIn={() => setShowWhatsAppModal(true)}
        onOpenMySubscription={() => setShowSubscriptionModal(true)}
        subscriptionPlan={subscription?.plan}
        subscriptionDaysRemaining={subscription?.daysRemaining}
        subscriptionLabel={
          subscription?.plan === 'gold' ? 'My Gold Plan'
          : subscription?.plan === 'silver' ? 'My Silver Plan'
          : subscription?.plan === 'admin' ? 'Admin Access'
          : 'My Free Trial'
        }
        onQuickSwotAnalysis={handleAnalyseChart}
        onQuickGetRemedies={handleGetRemedies}
        onQuickCurrentPeriodAnalysis={handleQuickCurrentPeriodAnalysis}
        onQuickLifeAreasDashboard={handleQuickLifeAreasDashboard}
        onQuickYogasAnalysis={handleQuickYogasAnalysis}
        onQuickDashaTimeline={handleQuickDashaTimeline}
        onQuickFutureEventPredictor={handleQuickFutureEvents}
        onQuickPlanetImpact={handleQuickPlanetImpact}
        onQuickActivitiesBalance={handleQuickActivitiesBalance}
        onQuickMarriageCompatibility={handleQuickMarriageCompatibility}
        onQuickFamilyScoreCard={handleQuickFamilyScoreCard}
        onQuickLifeEventFinder={handleQuickLifeEventFinder}
        onQuickPersonalMuhurta={handleQuickPersonalMuhurta}
        onQuickHouseDashboard={handleQuickHouseDashboard}
        onQuickFloatingChart={handleQuickFloatingChart}
        onGenerateReport={handleGenerateReport}
        onRefreshAccess={async () => {
          const { data, error } = await supabase
            .from('profiles')
            .select('role, approved_family_quota')
            .eq('id', user!.id)
            .maybeSingle();
          if (error) { setProfileErr(error.message); return; }
          if (!data) {
            const { error: insErr } = await supabase
              .from('profiles')
              .upsert({ id: user!.id }, { onConflict: 'id' });
            if (insErr) { setProfileErr(insErr.message); return; }
          }
          const { data: d2, error: e2 } = await supabase
            .from('profiles')
            .select('role, approved_family_quota')
            .eq('id', user!.id)
            .maybeSingle();
          if (e2) { setProfileErr(e2.message); return; }
          setProfile(asProfile(d2));
        }}
      >

        {/* BEGIN ADD: MY CHART tab (Divine D1 + D9 in 15cm x 10cm boxes via <MyChart/>) */}
        {activeTab === 'mychart' && (
          <Box sx={{ pt: { xs: 1, sm: 3, md: 4 }, mt: { xs: 0, sm: 1.5 }, display: 'flex', justifyContent: 'center', alignItems: 'center', scrollMarginTop: { xs: '100px', sm: '80px' } }}>
            <MyChart
              birth={chartBirth}
              ascLonD1={astroData?.asc.siderealLongitudeDeg ?? null}
              cuspsD1={astroData?.cusps ?? null}
              grahaLon={grahaLon}
              combustMap={combust}
              debilMap={debil}
              exaltMap={exalt}
              aspects={d1AspectEdges.map(edge => ({
                from: edge.from as PlanetName,
                toHouse: edge.toHouse,
                kind: 'auto',
              }))}
              getPlanetColor={(p) => funcColor(p)}
              shadbalaPct={shadbalaPct ?? undefined}
              shadbalaRatio={shadbalaRatio ?? undefined}
              ishtaKashta={ishtaKashtaFooter ?? undefined /* {ishta:0..60, kashta:0..60} per planet */}
              ikPhala={ikPhala ?? undefined}
              blendedPct={blendedPlanetPct ?? undefined /* -100..+100 per planet from your blended formula */}
              bhavaSandhiMultipliers={bhavaSandhiMultipliers ?? undefined /* 0.80-1.10 multipliers when global mode ON */}
              houseRows={d1HouseInfluence?.rows ?? undefined}
              aspectsD1={d1AspectEdges}
              kaalSarpYoga={kaalSarpData}
              sadeSati={sadeSatiData}
            />
            
            {/* Quick Navigation FAB */}
            {astroData && (
              <QuickNavigationFAB sections={MYCHART_SECTIONS} position="bottom-right" />
            )}
          </Box>
        )}
        {/* END ADD */}

        {/* ------------- CALCULATOR TAB ------------- */}
        {activeTab === 'calculator' && (
          <>
            {profile && (profile.role === 'admin' || profile.approved_family_quota > 0 || subscription?.status === 'active') ? (
              <Box sx={{ pt: { xs: 1, sm: 3, md: 4 }, scrollMarginTop: { xs: '100px', sm: '80px' } }}>
                {/* Toolbar: Tabs + Varga dropdown - Responsive */}
                <Box sx={{ 
                  mt: 1.5, 
                  mb: 1.5, 
                  display: 'flex', 
                  gap: { xs: 0.5, sm: 1 }, 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}>
                  {/* Shared compact pill style for mobile tabs */}
                  {(['d1','interactive','divisional','transit','chalit','vastu'] as const).map(() => null).filter(() => false)}
                  {(
                    [
                      { key: 'd1',          mobileLabel: 'Birth',    desktopLabel: 'Birth Details' },
                      { key: 'interactive', mobileLabel: 'Chart',    desktopLabel: 'Interactive Chart' },
                      { key: 'divisional',  mobileLabel: 'Divn.',    desktopLabel: 'Divisional' },
                      { key: 'transit',     mobileLabel: 'Transit',  desktopLabel: 'Transit' },
                      { key: 'chalit',      mobileLabel: 'Chalit',   desktopLabel: 'Chalit' },
                      { key: 'vastu',       mobileLabel: 'Vastu',    desktopLabel: 'Vastu' },
                    ] as Array<{ key: typeof calcTab; mobileLabel: string; desktopLabel: string }>
                  ).map(({ key, mobileLabel, desktopLabel }) => (
                    <React.Fragment key={key}>
                      <Button
                        size="sm"
                        variant={calcTab === key ? 'solid' : 'plain'}
                        color={calcTab === key ? 'primary' : 'neutral'}
                        onClick={() => setCalcTab(key)}
                        sx={{
                          fontSize: { xs: '0.82rem', sm: '0.875rem' },
                          px: { xs: 1.25, sm: 1.5 },
                          py: { xs: '5px', sm: undefined },
                          minWidth: 'auto',
                          borderRadius: { xs: '20px', sm: 'sm' },
                          minHeight: { xs: '30px', sm: undefined },
                          fontWeight: calcTab === key ? 700 : 500,
                          ...(calcTab !== key && {
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'neutral.100', color: 'text.primary' },
                          }),
                        }}
                      >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{desktopLabel}</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{mobileLabel}</Box>
                      </Button>
                      {key === 'divisional' && calcTab === 'divisional' && (
                        <JoySelect
                          size="sm"
                          value={vargaKey}
                          onChange={(_, v) => v && setVargaKey(v as VargaKey)}
                          sx={{ minWidth: { xs: 60, sm: 110 }, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}
                        >
                          {VARGAS.map(v => (
                            <Option key={v} value={v}>{v}</Option>
                          ))}
                        </JoySelect>
                      )}
                    </React.Fragment>
                  ))}
                </Box>

                {/* Two-column layout (Joy Grid) */}
                <Grid container spacing={2} sx={{ alignItems: 'flex-start', mt: 0.5, position: 'relative' }}>
                  {/* Vertical separator — pinned at true column midpoint */}
                  {!isMobile && (
                    <Box sx={{
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      bottom: 0,
                      width: '1px',
                      bgcolor: 'var(--joy-palette-neutral-outlinedBorder)',
                      transform: 'translateX(-50%)',
                      pointerEvents: 'none',
                    }} />
                  )}
                  {/* LEFT PANE */}
                  <Grid xs={12} md={6}>
                    <Box sx={{ pr: { md: 2 } }}>
                      {calcTab === 'd1' ? (
                        <Box>
                          {/* Trial banner - show countdown for trial users */}
                          {user && <TrialBanner userId={user.id} />}
                          
                          {/* Quota warning - show when approaching chart limit */}
                          {user && <QuotaWarning userId={user.id} type="charts" threshold={0} />}
                          
                          {/* Birth details form - emits Save & Calculate - Collapsible on mobile */}
                          <Box id="section-birth-details">
                            {isMobile && astroData && (
                              <Button
                                size="sm"
                                variant="outlined"
                                onClick={() => setIsBirthDetailsCollapsed(!isBirthDetailsCollapsed)}
                                sx={{ 
                                  mb: 1, 
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: '1rem',
                                  py: 1,
                                }}
                              >
                                <span>{isBirthDetailsCollapsed ? '👤 Show Birth Details' : '👤 Hide Birth Details'}</span>
                                <span style={{ fontSize: '1.2em' }}>{isBirthDetailsCollapsed ? '▼' : '▲'}</span>
                              </Button>
                            )}
                            <Box
                              data-audio-cue="home-birth-details"
                              sx={{ 
                              display: isMobile && isBirthDetailsCollapsed ? 'none' : 'block',
                              transition: 'all 0.3s ease'
                            }}>
                              <UserForm />
                            </Box>
                          </Box>

                          {/* Advanced Chart OR Dashboard - Show on mobile (classic style) */}
                          {isMobile && astroData && combust && exalt && debil && (
                            <Box id="section-advanced-chart-mobile" sx={{ mt: 2, mb: 2 }}>
                              {/* Action buttons - always visible - Compact on mobile */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, width: '100%', justifyContent: 'flex-start' }}>
                                <button
                                  data-audio-cue="home-show-chart-btn"
                                  onClick={() => setShowMobileDashboard(!showMobileDashboard)}
                                  className="gradient-border-button"
                                  style={{ flexShrink: 0, padding: '2px' }}
                                >
                                  <div className="gradient-border-button-inner" style={{ fontSize: '13px', gap: '4px', padding: '6px 8px' }}>
                                    <span style={{ fontSize: '14px', lineHeight: 1 }}>{showMobileDashboard ? '🔄' : '📊'}</span>
                                    <span>{showMobileDashboard ? 'Chart' : 'Dashboard'}</span>
                                  </div>
                                </button>
                                <button
                                  data-audio-cue="home-analyse-chart-btn"
                                  onClick={handleAnalyseChart}
                                  className="gradient-border-button"
                                  style={{ flexShrink: 0, padding: '2px' }}
                                >
                                  <div className="gradient-border-button-inner" style={{ fontSize: '13px', gap: '4px', padding: '6px 8px' }}>
                                    <span style={{ fontSize: '14px', lineHeight: 1 }}>🔮</span>
                                    <span>Analyse</span>
                                  </div>
                                </button>
                                <button
                                  data-audio-cue="home-get-remedies-btn"
                                  onClick={handleGetRemedies}
                                  className="gradient-border-button"
                                  style={{ flexShrink: 0, padding: '2px' }}
                                >
                                  <div className="gradient-border-button-inner" style={{ fontSize: '13px', gap: '4px', padding: '6px 8px' }}>
                                    <span style={{ fontSize: '14px', lineHeight: 1 }}>🌿</span>
                                    <span>Remedies</span>
                                  </div>
                                </button>
                                {/* House Guide audio player */}
                                <AudioGuide
                                  src="/audio/guide-houses.mp3"
                                  label="House"
                                  size="sm"
                                  variant="red"
                                />
                              </Box>

                              {/* Chart or Dashboard content */}
                              {!showMobileDashboard ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <AdvancedNorthIndianChart
                                    chartData={{
                                      planets: Object.entries(astroData.placements).map(([name, placement]) => ({
                                        name: name as PlanetName,
                                        house: placement.house,
                                        rasi: ((astroData.asc.ascendantSignIndex - 1 + (placement.house - 1)) % 12) + 1,
                                        degree: placement.offsetDeg,
                                        retro: (name === 'Mercury' || name === 'Venus' || name === 'Mars' || name === 'Jupiter' || name === 'Saturn') 
                                          ? astroData.grahas[name as 'Mercury'|'Venus'|'Mars'|'Jupiter'|'Saturn'].retrograde 
                                          : false,
                                        combust: (combust as any)?.[name]?.combust ?? false,
                                        exalted: (exalt as any)?.[name] ?? false,
                                        debilitated: (debil as any)?.[name] ?? false,
                                      })),
                                      ascendant: {
                                        rasi: astroData.asc.ascendantSignIndex,
                                        degree: astroData.asc.siderealLongitudeDeg % 30
                                      },
                                      aspects: (() => {
                                        const allAspects: Array<{from: PlanetName; toHouse: number; kind: string}> = [];
                                        const addAspect = (from: PlanetName, fromHouse: number, offset: number, kind: string) => {
                                          const toHouse = ((fromHouse - 1 + offset) % 12) + 1;
                                          allAspects.push({ from, toHouse, kind });
                                        };
                                        
                                        (['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'] as const).forEach(pName => {
                                          const placement = astroData.placements[pName];
                                          if (!placement?.house) return;
                                          const fromHouse = placement.house;
                                          
                                          addAspect(pName, fromHouse, 6, '7th aspect');
                                          
                                          if (pName === 'Mars') {
                                            addAspect(pName, fromHouse, 3, '4th aspect');
                                            addAspect(pName, fromHouse, 7, '8th aspect');
                                          } else if (pName === 'Jupiter') {
                                            addAspect(pName, fromHouse, 4, '5th aspect');
                                            addAspect(pName, fromHouse, 8, '9th aspect');
                                          } else if (pName === 'Saturn') {
                                            addAspect(pName, fromHouse, 2, '3rd aspect');
                                            addAspect(pName, fromHouse, 9, '10th aspect');
                                          } else if (pName === 'Rahu' || pName === 'Ketu') {
                                            addAspect(pName, fromHouse, 4, '5th aspect');
                                            addAspect(pName, fromHouse, 8, '9th aspect');
                                          }
                                        });
                                        
                                        return allAspects;
                                      })()
                                    }}
                                    personName={undefined}
                                    size={280}
                                    backgroundImage="/chart-backgrounds/north-indian-classic.svg"
                                    showExport={false}
                                    onAnalyseChart={handleAnalyseChart}
                                    onGetRemedies={handleGetRemedies}
                                    userRole={profile?.role}
                                  />
                                </Box>
                              ) : (
                                d1HouseScores && bhavaBalaRupas && astroData && combust && (
                                  <Box id="section-house-dashboard" data-audio-cue="home-house-dashboard">
                                    <TransitDateSlider
                                      value={transitDate}
                                      onChange={setTransitDate}
                                    />
                                    <HouseScoresCompactView 
                                      d1HouseScores={d1HouseScores}
                                      bhavaBalaRupas={bhavaBalaRupas}
                                      houseNames={Object.fromEntries(
                                        Array.from({ length: 12 }, (_, i) => {
                                          const h = (i + 1) as HouseNum;
                                          return [h, (HOUSE_TO_LIFE as any)[h]?.[0] || `House ${h}`];
                                        })
                                      )}
                                      dashaContext={dashaContextForFeeds ? {
                                        MD: { planet: dashaContextForFeeds.MD.planet, occupies: dashaContextForFeeds.MD.occupies, rules: dashaContextForFeeds.MD.rules, aspects: dashaContextForFeeds.MD.aspects },
                                        AD: { planet: dashaContextForFeeds.AD.planet, occupies: dashaContextForFeeds.AD.occupies, rules: dashaContextForFeeds.AD.rules, aspects: dashaContextForFeeds.AD.aspects },
                                        PD: { planet: dashaContextForFeeds.PD.planet, occupies: dashaContextForFeeds.PD.occupies, rules: dashaContextForFeeds.PD.rules, aspects: dashaContextForFeeds.PD.aspects },
                                        SD: { planet: dashaContextForFeeds.SD.planet, occupies: dashaContextForFeeds.SD.occupies, rules: dashaContextForFeeds.SD.rules, aspects: dashaContextForFeeds.SD.aspects }
                                      } : undefined}
                                      ascendantSignIndex={astroData.asc?.ascendantSignIndex}
                                      combustion={combust}
                                      planetPlacements={Object.fromEntries(
                                        Object.entries(astroData.placements).map(([planet, data]: [string, any]) => {
                                          const pName = planet as PlanetName;
                                          let dignity: 'exalted' | 'debilitated' | undefined;
                                          
                                          if (exalt[pName]) {
                                            dignity = 'exalted';
                                          } else if (debil[pName]) {
                                            dignity = 'debilitated';
                                          }
                                          
                                          return [planet, { house: data.house, dignity }];
                                        })
                                      )}
                                      dashaImpactByHouse={dashaImpactByHouse}
                                      transitImpactByHouse={transitImpactByHouse}
                                      transitPlanetHouses={transitData?.placements
                                        ? Object.entries(transitData.placements)
                                            .filter(([, p]: [string, any]) => p?.house)
                                            .map(([planet, p]: [string, any]) => ({ planet, house: p.house as number }))
                                        : undefined}
                                      planetStrengthPct={blendedPlanetPct ?? undefined}
                                      transitDignities={transitData?.dignities as Partial<Record<string, string>> ?? null}
                                      transitAscLagnaSign={transitData?.ascLagnaSign ?? null}
                                      transitCombust={transitData?.combust ?? null}
                                      transitRetrograde={transitData?.retrogrades ?? null}
                                      frictionScoresByPlanet={Object.keys(frictionScoresByPlanet).length > 0 ? frictionScoresByPlanet : undefined}
                                      onKarmicPatternClick={() => setShowKarmicPattern(true)}
                                      houseInterpretations={houseInterpretations}
                                    />
                                  </Box>
                                )
                              )}
                            </Box>
                          )}

                        </Box>
                      ) : calcTab === 'divisional' ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography level="title-md" sx={{ mt: 0, mb: 0, color: 'text.primary', fontWeight: 500 }}>
                              Divisional: {vargaKey}
                            </Typography>
                            <Tooltip title="Divisional charts (vargas) provide detailed insights into specific life areas. D9 (Navamsa) for marriage, D10 for career, D30 for challenges. Each division reveals hidden patterns and timing." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
                              <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 20, minHeight: 20, padding: '2px' }}>
                                <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {astroData && divData ? (
                            <Box sx={{ maxWidth: '100%', display: 'flex', justifyContent: 'flex-start', ...(isMobile && { overflowX: 'auto' }) }}>
                              {/* Match the right D1 card's physical size */}
                              <Box
                                sx={{
                                  width: isMobile ? '15cm' : `${D1_W_CM}cm`,
                                  height: isMobile ? '10cm' : `${D1_H_CM}cm`,
                                  minWidth: isMobile ? '15cm' : undefined,
                                  maxWidth: isMobile ? undefined : undefined,
                                  border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                  borderRadius: 'md',
                                  bgcolor: 'background.surface',
                                  p: `${D1_PAD_PX}px`,
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Box
                                  sx={{
                                    position: 'relative',
                                    flex: isMobile ? undefined : 1,
                                    width: isMobile ? undefined : undefined,
                                    height: isMobile ? undefined : undefined,
                                    minHeight: 0,
                                    overflow: isMobile ? 'visible' : 'visible',
                                    '& svg:not(.ni-grid-overlay) text': { display: 'none !important' },
                                    '& svg line, & svg path, & svg polyline, & svg polygon, & svg rect': {
                                      vectorEffect: 'non-scaling-stroke',
                                    },
                                  }}
                                >
                                  <NorthIndianChartVedicUMD
                                    cusps={divData.cusps}
                                    placements={divData.placements}
                                    widthCm={isMobile ? 15 : undefined}
                                    heightCm={isMobile ? 10 : undefined}
                                    widthPx={isMobile ? undefined : D1_INNER_W_PX}
                                    heightPx={isMobile ? undefined : D1_INNER_H_PX}
                                    scalePolicy={isMobile ? "fit" : "fit"}
                                    ascLonDeg={divData.ascLon}
                                    getPlanetColor={(p) => funcColor(p)}
                                    includeNodeSpecialAspects={true}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                p: 1.25,
                                borderRadius: 'md',
                                border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                                bgcolor: 'neutral.softBg'
                              }}
                            >
                              Calculate D1 first (Save &amp; Calculate), then pick a varga here to view it.
                            </Box>
                          )}
                        </Box>
                      ) : calcTab === 'transit' ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2, flexWrap: 'nowrap' }}>
                            <Tooltip title="Shows current planetary positions and how they affect your natal chart. Transits trigger events based on your birth chart. Compare with natal to see active influences." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
                              <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 20, minHeight: 20, padding: '2px', flexShrink: 0 }}>
                                <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
                              </IconButton>
                            </Tooltip>
                            {astroData && (
                              <>
                                <Button size="sm" variant="soft" color="neutral" onClick={() => moveTransitDate({ weeks: -1 })} sx={isMobile ? { fontSize: '10px', px: 0.6, py: 0.25, minHeight: 'unset' } : {}}>-W</Button>
                                <Button size="sm" variant="soft" color="neutral" onClick={() => moveTransitDate({ days: -1 })} sx={isMobile ? { fontSize: '10px', px: 0.6, py: 0.25, minHeight: 'unset' } : {}}>-D</Button>
                                <input
                                  type="date"
                                  value={transitDate.toFormat('yyyy-MM-dd')}
                                  onChange={(e) => {
                                    const newDate = DateTime.fromISO(e.target.value);
                                    if (newDate.isValid) {
                                      setTransitDate(newDate.set({ 
                                        hour: transitDate.hour, 
                                        minute: transitDate.minute 
                                      }));
                                    }
                                  }}
                                  style={{
                                    padding: isMobile ? '2px 3px' : '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                    fontSize: isMobile ? '11px' : '14px',
                                    maxWidth: isMobile ? '112px' : undefined,
                                  }}
                                />
                                <input
                                  type="time"
                                  value={transitDate.toFormat('HH:mm')}
                                  onChange={(e) => {
                                    const [hours, minutes] = e.target.value.split(':').map(Number);
                                    setTransitDate(transitDate.set({ hour: hours, minute: minutes }));
                                  }}
                                  style={{
                                    padding: isMobile ? '2px 3px' : '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                    fontSize: isMobile ? '11px' : '14px',
                                    maxWidth: isMobile ? '78px' : undefined,
                                  }}
                                />
                                <Button size="sm" variant="soft" color="neutral" onClick={() => moveTransitDate({ days: 1 })} sx={isMobile ? { fontSize: '10px', px: 0.6, py: 0.25, minHeight: 'unset' } : {}}>+D</Button>
                                <Button size="sm" variant="soft" color="neutral" onClick={() => moveTransitDate({ weeks: 1 })} sx={isMobile ? { fontSize: '10px', px: 0.6, py: 0.25, minHeight: 'unset' } : {}}>+W</Button>
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  onClick={() => setTransitDate(DateTime.now())}
                                  sx={isMobile ? { fontSize: '10px', px: 0.6, py: 0.25, minHeight: 'unset' } : {}}
                                >
                                  Now
                                </Button>
                              </>
                            )}
                          </Box>

                          {astroData && transitData ? (
                            <Box>
                              <Box sx={{ maxWidth: '100%', display: 'flex', justifyContent: 'flex-start', ...(isMobile && { overflowX: 'auto' }) }}>
                                <Box
                                  sx={{
                                    width: isMobile ? '15cm' : `${D1_W_CM}cm`,
                                    height: isMobile ? '10cm' : `${D1_H_CM}cm`,
                                    minWidth: isMobile ? '15cm' : undefined,
                                    maxWidth: isMobile ? undefined : undefined,
                                    border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                    borderRadius: 'md',
                                    bgcolor: 'background.surface',
                                    p: `${D1_PAD_PX}px`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      flex: isMobile ? undefined : 1,
                                      width: isMobile ? undefined : undefined,
                                      height: isMobile ? undefined : undefined,
                                      minHeight: 0,
                                      overflow: isMobile ? 'visible' : 'visible',
                                      '& svg:not(.ni-grid-overlay) text': { display: 'none !important' },
                                      '& svg line, & svg path, & svg polyline, & svg polygon, & svg rect': {
                                        vectorEffect: 'non-scaling-stroke',
                                      },
                                    }}
                                  >
                                    <NorthIndianChartVedicUMD
                                      cusps={transitData.cusps}
                                      placements={transitData.placements}
                                      widthCm={isMobile ? 15 : undefined}
                                      heightCm={isMobile ? 10 : undefined}
                                      widthPx={isMobile ? undefined : D1_INNER_W_PX}
                                      heightPx={isMobile ? undefined : D1_INNER_H_PX}
                                      scalePolicy={isMobile ? "fit" : "fit"}
                                      ascLonDeg={transitData.ascLon}
                                      dignityMap={transitData.dignities}
                                      getPlanetColor={(p) => funcColor(p)}
                                      includeNodeSpecialAspects={true}
                                      onPlanetHover={handleTransitPlanetHover}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                p: 1.25,
                                borderRadius: 'md',
                                border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                                bgcolor: 'neutral.softBg'
                              }}
                            >
                            Calculate D1 first (Save &amp; Calculate), then view transit chart here.
                          </Box>
                        )}
                        {/* Transit planet hover tooltip */}
                        {hoveredTransitPlanet && transitHoverTooltipData && (() => {
                          const DIGNITY_COLORS: Record<string, string> = {
                            exalted: '#4ade80', moolatrikona: '#a78bfa', own: '#60a5fa',
                            friend: '#a3e635', neutral: '#9ca3af', enemy: '#fb923c', debilitated: '#f87171',
                          };
                          const DIGNITY_SHORT: Record<string, string> = {
                            exalted: '↑ Exalted', moolatrikona: '★ Mooltri', own: '● Own',
                            friend: '+ Friend', neutral: '○ Neutral', enemy: '− Enemy', debilitated: '↓ Debil',
                          };
                          const DASHA_BADGE_COLORS: Record<string, string> = {
                            MD: '#f59e0b', AD: '#fb923c', PD: '#a78bfa', SD: '#f472b6',
                          };
                          const { planet, currentHouse, currentSignName, currentDignity,
                            nextHouse, nextSignName, nextDignity, exitsOnFormatted,
                            aspectHouses } = transitHoverTooltipData;
                          const blendedStrength = blendedPlanetPct?.[planet as PlanetName];

                          // Dasha roles for this planet
                          const dashaRoles: Array<'MD'|'AD'|'PD'|'SD'> = [];
                          if (dashaContextForFeeds?.MD.planet === planet) dashaRoles.push('MD');
                          if (dashaContextForFeeds?.AD.planet === planet) dashaRoles.push('AD');
                          if (dashaContextForFeeds?.PD.planet === planet) dashaRoles.push('PD');
                          if (dashaContextForFeeds?.SD.planet === planet) dashaRoles.push('SD');

                          // D1 natal info — pick highest active level
                          const topLevel = dashaRoles[0];
                          const d1Info = topLevel ? dashaContextForFeeds?.[topLevel] : undefined;

                          const showLeft = !isMobile && transitTooltipPos.x + 300 > window.innerWidth;
                          return (
                            <>
                              {/* Mobile backdrop — tap to dismiss */}
                              {isMobile && (
                                <Box
                                  onClick={() => setHoveredTransitPlanet(null)}
                                  sx={{
                                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                    zIndex: 9998, bgcolor: 'rgba(0,0,0,0.45)', cursor: 'pointer',
                                  }}
                                />
                              )}
                            <Box
                              sx={{
                                position: 'fixed',
                                ...(isMobile ? {
                                  // Mobile: centered popup, touch-friendly
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  width: 'min(92vw, 320px)',
                                  maxWidth: 'none',
                                  minWidth: 'unset',
                                  pointerEvents: 'auto',
                                  borderRadius: '14px',
                                } : {
                                  // Desktop: cursor-following, doesn't block interaction
                                  top: Math.max(8, Math.min(transitTooltipPos.y - 10, window.innerHeight - 300)),
                                  left: showLeft ? transitTooltipPos.x - 290 : transitTooltipPos.x + 18,
                                  minWidth: 205,
                                  maxWidth: 280,
                                  pointerEvents: 'none',
                                  borderRadius: '10px',
                                }),
                                zIndex: 9999,
                                bgcolor: 'rgba(15, 15, 28, 0.97)',
                                color: '#f0f0f0',
                                p: '10px 14px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.55)',
                                border: '1px solid rgba(255,255,255,0.13)',
                                fontSize: '12px',
                                lineHeight: 1.5,
                              }}
                            >
                              {/* Header: planet + dasha badges */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: '4px' }}>
                                <Box sx={{ fontWeight: 700, fontSize: '13px', color: '#fff' }}>{planet}</Box>
                                {dashaRoles.map(role => (
                                  <Box key={role} sx={{
                                    fontSize: '9px', fontWeight: 700, px: '5px', py: '1px',
                                    borderRadius: '4px', bgcolor: DASHA_BADGE_COLORS[role] + '33',
                                    color: DASHA_BADGE_COLORS[role], border: `1px solid ${DASHA_BADGE_COLORS[role]}55`,
                                    lineHeight: 1.4,
                                  }}>{role}</Box>
                                ))}
                                {!isMobile && <Box sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px', ml: 'auto' }}>transit</Box>}
                                {isMobile && (
                                  <Box
                                    onClick={() => setHoveredTransitPlanet(null)}
                                    sx={{
                                      ml: 'auto', cursor: 'pointer', fontSize: '18px', lineHeight: 1,
                                      color: 'rgba(255,255,255,0.55)', px: '4px',
                                      '&:active': { color: '#fff' },
                                    }}
                                  >✕</Box>
                                )}
                              </Box>

                              {/* Current position */}
                              {(currentHouse || currentSignName) && (
                                <Box sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', mb: '7px' }}>
                                  {currentSignName}{currentHouse ? ` H${currentHouse}` : ''}
                                  {currentDignity && (
                                    <Box component="span" sx={{ color: DIGNITY_COLORS[currentDignity] ?? '#9ca3af', ml: '5px' }}>
                                      {DIGNITY_SHORT[currentDignity] ?? currentDignity}
                                    </Box>
                                  )}
                                </Box>
                              )}

                              <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.09)', pt: '7px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {/* Next ingress + destination on one line if both short */}
                                {exitsOnFormatted && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                    <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', flexShrink: 0 }}>Next entry</Box>
                                    <Box sx={{ color: '#e8c97a', fontWeight: 700, textAlign: 'right', fontSize: '12px' }}>{exitsOnFormatted}</Box>
                                  </Box>
                                )}
                                {(nextSignName || nextHouse) && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', flexShrink: 0 }}>Into</Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'flex-end' }}>
                                      <Box sx={{ color: '#93c5fd', fontWeight: 600 }}>{nextSignName}{nextHouse ? ` H${nextHouse}` : ''}</Box>
                                      {nextDignity && (
                                        <Box sx={{ color: DIGNITY_COLORS[nextDignity] ?? '#fff', fontWeight: 600, fontSize: '11px' }}>
                                          {DIGNITY_SHORT[nextDignity] ?? nextDignity}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                                {/* Transit aspects from next house */}
                                {aspectHouses.length > 0 && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                    <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', flexShrink: 0 }}>Transit aspects</Box>
                                    <Box sx={{ color: '#f9a8d4', fontWeight: 600, textAlign: 'right' }}>{aspectHouses.map(h => `H${h}`).join(' · ')}</Box>
                                  </Box>
                                )}

                                {/* Dasha D1 info */}
                                {d1Info && (
                                  <>
                                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.09)', mt: '2px', pt: '6px' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: '4px' }}>
                                        <Box sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)' }}>Dasha</Box>
                                        <Box sx={{
                                          fontSize: '9px', fontWeight: 700, px: '4px', py: '1px',
                                          borderRadius: '3px', bgcolor: DASHA_BADGE_COLORS[topLevel!] + '33',
                                          color: DASHA_BADGE_COLORS[topLevel!],
                                        }}>{topLevel}</Box>
                                        <Box sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)' }}>D1 natal role</Box>
                                      </Box>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        {(d1Info.rules as number[])?.length > 0 && (
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', flexShrink: 0 }}>Rules</Box>
                                            <Box sx={{ color: '#c4b5fd', fontWeight: 600, textAlign: 'right' }}>{(d1Info.rules as number[]).map(h => `H${h}`).join(' · ')}</Box>
                                          </Box>
                                        )}
                                        {d1Info.occupies && (
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', flexShrink: 0 }}>Occupies</Box>
                                            <Box sx={{ color: '#7dd3fc', fontWeight: 600 }}>H{d1Info.occupies}</Box>
                                          </Box>
                                        )}
                                        {(d1Info.aspects as number[])?.length > 0 && (
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', flexShrink: 0 }}>Aspects</Box>
                                            <Box sx={{ color: '#6ee7b7', fontWeight: 600, textAlign: 'right' }}>{(d1Info.aspects as number[]).map(h => `H${h}`).join(' · ')}</Box>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </>
                                )}

                                {/* Natal strength */}
                                {blendedStrength !== undefined && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                    <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', flexShrink: 0 }}>Natal strength</Box>
                                    <Box sx={{ color: '#86efac', fontWeight: 600 }}>{Math.round(blendedStrength)}%</Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            </>
                          );
                        })()}
                        </Box>
                      ) : calcTab === 'chalit' ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography level="title-md" sx={{ mt: 0, mb: 0, color: 'text.primary', fontWeight: 500 }}>
                              Bhava Chalit (Equal House System)
                            </Typography>
                            <Tooltip title="Equal house system where each house is exactly 30 degrees. Planets may shift houses compared to whole-sign system. Useful for precise cusp analysis and event timing." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
                              <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 20, minHeight: 20, padding: '2px' }}>
                                <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {astroData ? (() => {
                            // Calculate Bhava Chalit using equal house cusps
                            const chalitCusps = getEqualHouseCuspsSidereal(astroData.asc.siderealLongitudeDeg);
                            
                            // For display purposes, we need the midpoints (actual cusps)
                            const chalitCuspMidpoints = chalitCusps.map(start => (start + 15) % 360);
                            
                            // Reassign planets to houses based on equal house cusps
                            const planetLongs = {
                              Sun: astroData.grahas.Sun.lon,
                              Moon: astroData.moon.siderealLongitudeDeg,
                              Mercury: astroData.grahas.Mercury.lon,
                              Venus: astroData.grahas.Venus.lon,
                              Mars: astroData.grahas.Mars.lon,
                              Jupiter: astroData.grahas.Jupiter.lon,
                              Saturn: astroData.grahas.Saturn.lon,
                              Rahu: astroData.nodes.Rahu,
                              Ketu: astroData.nodes.Ketu
                            };
                            
                            const chalitPlacements = assignPlanetsToHouses(chalitCusps, planetLongs);
                            
                            // Build retro map
                            const chalitRetroMap: Record<PlanetName, boolean> = {
                              Sun: false,
                              Moon: false,
                              Mercury: astroData.grahas.Mercury.retrograde,
                              Venus: astroData.grahas.Venus.retrograde,
                              Mars: astroData.grahas.Mars.retrograde,
                              Jupiter: astroData.grahas.Jupiter.retrograde,
                              Saturn: astroData.grahas.Saturn.retrograde,
                              Rahu: false,
                              Ketu: false
                            };
                            
                            return (
                              <Box>
                                <Box
                                  sx={{
                                    p: 1.25,
                                    mb: 2,
                                    borderRadius: 'md',
                                    border: '1px solid var(--joy-palette-primary-outlinedBorder)',
                                    bgcolor: 'primary.softBg'
                                  }}
                                >
                                  <Typography level="body-sm">
                                    <strong>Bhava Chalit</strong> uses equal house cusps (30° each) from the exact ascendant degree.
                                    Planets may shift houses compared to whole-sign system. Useful for precise house placement timing.
                                  </Typography>
                                </Box>

                                <Box sx={{ maxWidth: '100%', display: 'flex', justifyContent: 'flex-start', ...(isMobile && { overflowX: 'auto' }) }}>
                                  <Box
                                    sx={{
                                      width: isMobile ? '15cm' : `${D1_W_CM}cm`,
                                      height: isMobile ? '10cm' : `${D1_H_CM}cm`,
                                      minWidth: isMobile ? '15cm' : undefined,
                                      maxWidth: isMobile ? undefined : undefined,
                                      border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                      borderRadius: 'md',
                                      bgcolor: 'background.surface',
                                      p: `${D1_PAD_PX}px`,
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'relative',
                                        flex: isMobile ? undefined : 1,
                                        width: isMobile ? undefined : undefined,
                                        height: isMobile ? undefined : undefined,
                                        minHeight: 0,
                                        overflow: isMobile ? 'visible' : 'visible',
                                        '& svg:not(.ni-grid-overlay) text': { display: 'none !important' },
                                        '& svg line, & svg path, & svg polyline, & svg polygon, & svg rect': {
                                          vectorEffect: 'non-scaling-stroke',
                                        },
                                      }}
                                    >
                                      <NorthIndianChartVedicUMD
                                        cusps={chalitCuspMidpoints}
                                        placements={chalitPlacements}
                                        widthCm={isMobile ? 15 : undefined}
                                        heightCm={isMobile ? 10 : undefined}
                                        widthPx={isMobile ? undefined : D1_INNER_W_PX}
                                        heightPx={isMobile ? undefined : D1_INNER_H_PX}
                                        scalePolicy={isMobile ? "fit" : "fit"}
                                        ascLonDeg={astroData.asc.siderealLongitudeDeg}
                                        retroMap={chalitRetroMap}
                                        combustMap={combust ?? {}}
                                        debilMap={debil ?? {}}
                                        exaltMap={exalt ?? {}}
                                        aspects={astroData.aspects ?? []}
                                        getPlanetColor={(p) => funcColor(p)}
                                        includeNodeSpecialAspects={true}
                                      />
                                    </Box>
                                  </Box>
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                  <Typography level="title-sm" sx={{ mb: 1 }}>
                                    House Cusps (Equal House System)
                                  </Typography>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                      borderRadius: 'md',
                                      bgcolor: 'background.surface'
                                    }}
                                  >
                                    <Grid container spacing={1}>
                                      {chalitCuspMidpoints.map((cuspMidpoint: number, idx: number) => {
                                        const houseNum = idx + 1;
                                        const signIdx = Math.floor(cuspMidpoint / 30);
                                        const signName = RASHI_NAMES[signIdx];
                                        const degrees = cuspMidpoint % 30;
                                        return (
                                          <Grid xs={6} sm={4} md={3} key={houseNum}>
                                            <Typography level="body-xs">
                                              <strong>H{houseNum}:</strong> {signName} {degrees.toFixed(2)}°
                                            </Typography>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })() : (
                            <Box
                              sx={{
                                p: 1.25,
                                borderRadius: 'md',
                                border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                                bgcolor: 'neutral.softBg'
                              }}
                            >
                              Calculate D1 first (Save &amp; Calculate), then view Bhava Chalit chart here.
                            </Box>
                          )}
                        </Box>
                      ) : calcTab === 'vastu' ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography level="title-md" sx={{ mt: 0, mb: 0, color: 'text.primary', fontWeight: 500 }}>
                              Vastu Directional Analysis
                            </Typography>
                            <Tooltip title="Ancient Indian science of directional harmony. Each planet rules a direction (Jupiter=NE, Sun=E, etc.). Align your living/work spaces with planetary energies for better results in life areas." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
                              <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 20, minHeight: 20, padding: '2px' }}>
                                <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {astroData && houseLords && simplePlacements ? (
                            <Box>
                              <VastuChart
                                placements={simplePlacements}
                                houseLords={houseLords}
                                ascSign={astroData.asc.ascendantSignIndex}
                                functionalBenefics={functionalBenefics ?? undefined}
                                exaltedPlanets={exalt ?? undefined}
                                shadbalaScores={shadbalaFooter ?? undefined}
                                blendedScores={blendedPlanetPct ?? undefined}
                              />
                              
                              <VastuLifeAreasTable
                                placements={simplePlacements}
                                houseLords={houseLords}
                                houseNames={houseNames}
                                functionalBenefics={functionalBenefics ?? undefined}
                                exaltedPlanets={exalt ?? undefined}
                                shadbalaScores={shadbalaFooter ?? undefined}
                                blendedScores={blendedPlanetPct ?? undefined}
                                transitPlacements={transitData?.placements
                                  ? (Object.fromEntries(
                                      Object.entries(transitData.placements).map(([p, v]) => [p, (v as { house: number }).house])
                                    ) as Record<string, number>)
                                  : undefined}
                              />

                              <VastuRoomPlanner
                                placements={simplePlacements}
                                houseLords={houseLords}
                                ascSign={astroData.asc.ascendantSignIndex}
                                functionalBenefics={functionalBenefics ?? undefined}
                                functionalMalefics={functionalMalefics ?? undefined}
                                yogakarakas={yogakarakas ?? undefined}
                                exaltedPlanets={exalt ?? undefined}
                                shadbalaScores={shadbalaFooter ?? undefined}
                                blendedScores={blendedPlanetPct ?? undefined}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                p: 1.25,
                                borderRadius: 'md',
                                border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                                bgcolor: 'neutral.softBg'
                              }}
                            >
                              Calculate D1 first (Save &amp; Calculate), then view Vastu chart here.
                            </Box>
                          )}
                        </Box>
                      ) : calcTab === 'interactive' ? (
                        <Box>
                          {astroData && combust && exalt && debil ? (
                            (() => {
                              // Prepare chart data for AdvancedNorthIndianChart (same as My Chart page)
                              const chartData = {
                                planets: (['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'] as const).map(pName => {
                                  const placement = astroData.placements[pName];
                                  const grahaData = pName === 'Rahu' || pName === 'Ketu' 
                                    ? null 
                                    : astroData.grahas[pName as 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn'];
                                  
                                  const lon = pName === 'Rahu' ? astroData.nodes.Rahu : 
                                              pName === 'Ketu' ? astroData.nodes.Ketu : 
                                              grahaData?.lon ?? 0;
                                  
                                  const sign = Math.floor(((lon % 360) + 360) % 360 / 30) + 1;
                                  const degInSign = ((lon % 360) + 360) % 360 % 30;
                                  
                                  const nakPadaObj = getNakshatraPadaFromLongitude(lon);
                                  const nakIndex0 = Math.floor(((lon % 360) + 360) % 360) / (360 / 27);
                                  const nakshatraName = NAKSHATRA_NAMES[Math.floor(nakIndex0)];
                                  
                                  const combustInfo = (combust as any)?.[pName];
                                  const isCombust = combustInfo?.combust ?? false;
                                  const retro = grahaData?.retrograde ?? false;
                                  
                                  const isExalted = (exalt as any)?.[pName] ?? false;
                                  const isDebilitated = (debil as any)?.[pName] ?? false;
                                  const blendedStrength = blendedPlanetPct?.[pName];

                                  return {
                                    name: pName,
                                    house: placement?.house ?? 1,
                                    rasi: sign,
                                    degree: degInSign,
                                    nakshatra: nakshatraName,
                                    nakshatraPada: nakPadaObj.pada,
                                    retro,
                                    combust: isCombust,
                                    exalted: isExalted,
                                    debilitated: isDebilitated,
                                    blendedPct: blendedStrength
                                  };
                                }),
                                ascendant: {
                                  rasi: astroData.asc.ascendantSignIndex,
                                  degree: astroData.asc.siderealLongitudeDeg % 30
                                },
                                aspects: (() => {
                                  const allAspects: Array<{from: PlanetName; toHouse: number; kind: string}> = [];
                                  const addAspect = (from: PlanetName, fromHouse: number, offset: number, kind: string) => {
                                    const toHouse = ((fromHouse - 1 + offset) % 12) + 1;
                                    allAspects.push({ from, toHouse, kind });
                                  };
                                  
                                  (['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'] as const).forEach(pName => {
                                    const placement = astroData.placements[pName];
                                    if (!placement?.house) return;
                                    const fromHouse = placement.house;
                                    
                                    addAspect(pName, fromHouse, 6, '7th aspect');
                                    
                                    if (pName === 'Mars') {
                                      addAspect(pName, fromHouse, 3, '4th aspect');
                                      addAspect(pName, fromHouse, 7, '8th aspect');
                                    } else if (pName === 'Jupiter') {
                                      addAspect(pName, fromHouse, 4, '5th aspect');
                                      addAspect(pName, fromHouse, 8, '9th aspect');
                                    } else if (pName === 'Saturn') {
                                      addAspect(pName, fromHouse, 2, '3rd aspect');
                                      addAspect(pName, fromHouse, 9, '10th aspect');
                                    } else if (pName === 'Rahu' || pName === 'Ketu') {
                                      addAspect(pName, fromHouse, 4, '5th aspect');
                                      addAspect(pName, fromHouse, 8, '9th aspect');
                                    }
                                  });
                                  
                                  return allAspects;
                                })()
                              };

                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%' }}>
                                  {/* Chart + Side Panel Row */}
                                  <Box sx={{ 
                                    display: 'flex', 
                                    gap: 2, 
                                    alignItems: 'flex-start', 
                                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                                    maxWidth: '100%',
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{ flex: '0 0 auto', maxWidth: { xs: '100%', md: '350px' } }}>
                                      <AdvancedNorthIndianChart
                                        chartData={chartData}
                                        personName={chartName || 'Unknown'}
                                        size={isMobile ? 280 : 320}
                                        showExport={true}
                                        selectedPlanet={selectedPlanetInProfile}
                                        onPlanetSelect={(planet) => {
                                          setSelectedPlanetInProfile(planet);
                                          if (planet) setSelectedHouseInProfile(null);
                                        }}
                                        onHouseSelect={(house) => {
                                          setSelectedHouseInProfile(house);
                                          if (house) setSelectedPlanetInProfile(null);
                                        }}
                                        userRole={profile?.role}
                                        audioGuide={
                                          <AudioGuide
                                            src="/audio/guide-interactive-chart.mp3"
                                            label="Chart Guide"
                                            size="sm"
                                            variant="red"
                                            onTimeUpdate={handleChartAudioTime}
                                          />
                                        }
                                      />
                                    </Box>
                                    
                                    <Box sx={{ 
                                      flex: '1 1 auto', 
                                      minWidth: { xs: '100%', md: 220 }, 
                                      maxWidth: { xs: '100%', md: 300 }, 
                                      maxHeight: { xs: 'auto', md: 420 }, 
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                      mt: { xs: 0, md: 3.5 }
                                    }}>
                                      {selectedPlanetInProfile ? (
                                        <PlanetDetailsSidePanel
                                          planetData={chartData.planets.find(p => p.name === selectedPlanetInProfile) || null}
                                          aspects={chartData.aspects}
                                          lagnaRashi={chartData.ascendant.rasi}
                                          getPlanetColor={(p) => funcColor(p)}
                                          compact={true}
                                        />
                                      ) : selectedHouseInProfile ? (() => {
                                        // Calculate sign number for selected house
                                        const signNumber = ((chartData.ascendant.rasi - 1 + (selectedHouseInProfile - 1)) % 12) + 1;
                                        
                                        // Sign lord mapping
                                        const SIGN_LORD_MAP: Record<number, PlanetName> = {
                                          1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
                                          7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
                                        };
                                        
                                        const lordName = SIGN_LORD_MAP[signNumber];
                                        const lordPlanetData = chartData.planets?.find((p: any) => p.name === lordName);
                                        const lordHouse = lordPlanetData?.house || 1;
                                        const houseScore = d1HouseScores?.[selectedHouseInProfile];
                                        
                                        return (
                                          <HouseDetailsSidePanel
                                            houseNumber={selectedHouseInProfile}
                                            signNumber={signNumber}
                                            houseScore={houseScore}
                                            lordName={lordName}
                                            lordHouse={lordHouse}
                                            houseInterpretations={houseInterpretations || undefined}
                                            bhavaBalaRupas={bhavaBalaRupas || undefined}
                                          />
                                        );
                                      })() : null}
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })()
                          ) : (
                            <Box
                              sx={{
                                p: 1.25,
                                borderRadius: 'md',
                                border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                                bgcolor: 'neutral.softBg'
                              }}
                            >
                              Calculate D1 first (Save &amp; Calculate), then view Interactive Chart here.
                            </Box>
                          )}
                        </Box>
                      ) : null}

                      {/* Life Areas Dashboard - shows consolidated scores for major life areas */}
                      {/* Decoupled from calcTab so it's always visible */}
                      {d1HouseScores && astroData && (
                        <Box id="section-life-areas-default" data-audio-cue="home-life-areas" sx={{ mt: 2 }}>
                          <LifeAreasDashboard 
                          houseScores={d1HouseScores}
                          bhavaBala={bhavaBalaRupas}
                          aiCreditsLifeArea={aiCreditsLifeArea}
                          dashaImpactByHouse={dashaImpactByHouse}
                          transitImpactByHouse={transitImpactByHouse}
                          remedies={savedLifeAreaRemedies}
                          frictionScoresByPlanet={Object.keys(frictionScoresByPlanet).length > 0 ? frictionScoresByPlanet : undefined}
                          planetPlacements={astroData ? Object.fromEntries(Object.entries(astroData.placements).map(([p, d]: [string, any]) => [p, { house: d.house }])) : undefined}
                          ascendantSignIndex={astroData?.asc?.ascendantSignIndex}
                          onGetOverview={(lifeAreaName: string) => {
                            const lifeAreaKey = lifeAreaName.toLowerCase().replace(/ /g, '_') as LifeAreaKey;
                            setCurrentLifeAreaKey(lifeAreaKey);
                            setLifeAreaOverviewOutput('');
                            setLifeAreaOverviewError('');
                            setLifeAreaOverviewLoading(true);
                            setShowLifeAreaOverviewModal(true);
                            callLifeAreaOverview(lifeAreaKey);
                          }}
                          onNavigateToRemedies={() => {
                            setActiveTab('mychart');
                            // Scroll to Life Areas Summary section with retry logic
                            const scrollToSection = (attempts = 0) => {
                              const element = document.querySelector('[data-section="life-areas-remedies"]');
                              if (element) {
                                // Scroll to top of page first, then to element
                                window.scrollTo({ top: 0, behavior: 'auto' });
                                setTimeout(() => {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 50);
                              } else if (attempts < 10) {
                                setTimeout(() => scrollToSection(attempts + 1), 150);
                              }
                            };
                            setTimeout(() => scrollToSection(), 300);
                          }}
                          onNavigateToLifeAreaRemedies={(lifeAreaName: string) => {
                            setActiveTab('mychart');
                            // Scroll to specific life area with fallback to section header
                            const scrollToArea = (attempts = 0) => {
                              // First try to find the specific life area
                              const areaElement = document.querySelector(`[data-life-area="${lifeAreaName}"]`);
                              if (areaElement) {
                                // Scroll to top of page first, then to element with offset for mobile header
                                window.scrollTo({ top: 0, behavior: 'auto' });
                                setTimeout(() => {
                                  const rect = areaElement.getBoundingClientRect();
                                  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                  const isMobile = window.innerWidth < 900;
                                  const offset = isMobile ? 80 : 100; // Extra offset for mobile sticky header
                                  window.scrollTo({
                                    top: rect.top + scrollTop - offset,
                                    behavior: 'smooth'
                                  });
                                }, 50);
                                return;
                              }
                              
                              // Fallback: try to scroll to section header
                              const sectionElement = document.querySelector('[data-section="life-areas-remedies"]');
                              if (sectionElement) {
                                // Scroll to top of page first, then to element
                                window.scrollTo({ top: 0, behavior: 'auto' });
                                setTimeout(() => {
                                  const rect = sectionElement.getBoundingClientRect();
                                  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                  const isMobile = window.innerWidth < 900;
                                  const offset = isMobile ? 80 : 100;
                                  window.scrollTo({
                                    top: rect.top + scrollTop - offset,
                                    behavior: 'smooth'
                                  });
                                }, 50);
                                return;
                              }
                              
                              // Retry if neither found and attempts remaining
                              if (attempts < 10) {
                                setTimeout(() => scrollToArea(attempts + 1), 150);
                              }
                            };
                            setTimeout(() => scrollToArea(), 300);
                          }}
                        />
                        </Box>
                      )}
                    </Box>
                  </Grid>                  {/* RIGHT PANE: North Indian D1 chart + Divine preview + Vimshottari */}
                  {/* Hide entire chart section on mobile - already shown in calculator tab */}
                  {!isMobile && (
                  <Grid xs={12} md={6}>
                    <Box sx={{ pl: { md: 2 } }}>
                      {/* Style chip selector - only shown when chart is visible (not dashboard) */}
                      {!showCompactHouseScores && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        <Chip
                          size="sm"
                          variant={chartStyle === 'default' ? 'solid' : 'outlined'}
                          onClick={() => setChartStyle('default')}
                          sx={{ cursor: 'pointer', fontSize: '0.7rem', minHeight: '22px' }}
                        >
                          Default
                        </Chip>
                        <Chip
                          size="sm"
                          variant={chartStyle === 'classic' ? 'solid' : 'outlined'}
                          onClick={() => setChartStyle('classic')}
                          sx={{ cursor: 'pointer', fontSize: '0.7rem', minHeight: '22px' }}
                        >
                          📜 Classic
                        </Chip>
                        <Chip
                          size="sm"
                          variant={chartStyle === 'modern' ? 'solid' : 'outlined'}
                          onClick={() => setChartStyle('modern')}
                          sx={{ cursor: 'pointer', fontSize: '0.7rem', minHeight: '22px' }}
                        >
                          ✨ Modern
                        </Chip>
                        <Chip
                          size="sm"
                          variant={chartStyle === 'royal' ? 'solid' : 'outlined'}
                          onClick={() => setChartStyle('royal')}
                          sx={{ cursor: 'pointer', fontSize: '0.7rem', minHeight: '22px' }}
                        >
                          👑 Royal
                        </Chip>
                      </Box>
                      )}
                      
                      {astroData ? (
                        <>
                          {chartStyle === 'default' ? (
                            <>
                              {/* Default UMD chart */}
                              {/* BEGIN: fixed-cm Joy card for D1 (15cm x 10cm) */}
                              <Box
                                id="section-house-dashboard"
                                data-audio-cue="home-house-dashboard"
                                sx={{
                                  width: showCompactHouseScores ? '100%' : (isMobile ? `${D1_INNER_W_PX + D1_PAD_PX * 2}px` : `${D1_W_CM}cm`),
                                  height: showCompactHouseScores ? 'auto' : (isMobile ? `${D1_INNER_H_PX + D1_PAD_PX * 2}px` : `${D1_H_CM + 1}cm`),
                                  minHeight: showCompactHouseScores ? 'auto' : undefined,
                                  maxWidth: '100%',
                                  border: showCompactHouseScores ? 'none' : '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                  borderRadius: showCompactHouseScores ? 0 : 'md',
                              bgcolor: showCompactHouseScores ? 'transparent' : 'background.surface',
                              p: showCompactHouseScores ? 0 : `${D1_PAD_PX}px`,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                flex: isMobile ? undefined : 1,
                                width: isMobile ? `${D1_INNER_W_PX}px` : undefined,
                                height: showCompactHouseScores ? 'auto' : (isMobile ? `${D1_INNER_H_PX}px` : undefined),
                                minHeight: showCompactHouseScores ? 'auto' : 0,
                                overflow: isMobile ? 'hidden' : 'visible',
                                '& svg:not(.ni-grid-overlay) text': { display: 'none !important' },
                                '& svg line, & svg path, & svg polyline, & svg polygon, & svg rect': {
                                  vectorEffect: 'non-scaling-stroke',
                                },
                              }}
                            >
                              <NorthIndianChartVedicUMD
                                cusps={astroData.cusps}
                                placements={astroData.placements}
                                widthPx={D1_INNER_W_PX}
                                heightPx={D1_INNER_H_PX}
                                scalePolicy={isMobile ? "no-downscale" : "fit"}
                                ascLonDeg={astroData.asc.siderealLongitudeDeg}
                                retroMap={{
                                  Sun: astroData.grahas.Sun.retrograde,
                                  Moon: astroData.grahas.Moon.retrograde,
                                  Mercury: astroData.grahas.Mercury.retrograde,
                                  Venus: astroData.grahas.Venus.retrograde,
                                  Mars: astroData.grahas.Mars.retrograde,
                                  Jupiter: astroData.grahas.Jupiter.retrograde,
                                  Saturn: astroData.grahas.Saturn.retrograde,
                                }}
                                combustMap={combust ?? undefined}
                                debilMap={debil ?? undefined}
                                exaltMap={exalt ?? undefined}
                                includeNodeSpecialAspects={true}
                                getPlanetColor={(p) => funcColor(p)}
                                onAnalyseChart={handleAnalyseChart}
                                onGetRemedies={handleGetRemedies}
                                d1HouseScores={d1HouseScores ?? undefined}
                                bhavaBalaRupas={bhavaBalaRupas ?? undefined}
                                houseNames={Object.fromEntries(
                                  Array.from({ length: 12 }, (_, i) => {
                                    const h = (i + 1) as HouseNum;
                                    return [h, (HOUSE_TO_LIFE as any)[h]?.[0] || `House ${h}`];
                                  })
                                )}
                                onCompactViewChange={setShowCompactHouseScores}
                                dashaContext={dashaContextForFeeds}
                                defaultShowCompact={true}
                                dashaImpactByHouse={dashaImpactByHouse}
                                transitImpactByHouse={transitImpactByHouse}
                                planetStrengthPct={blendedPlanetPct ?? undefined}
                                transitDate={transitDate}
                                onTransitDateChange={setTransitDate}
                                frictionScoresByPlanet={Object.keys(frictionScoresByPlanet).length > 0 ? frictionScoresByPlanet : undefined}
                                onKarmicPatternClick={() => setShowKarmicPattern(true)}
                                houseInterpretations={houseInterpretations}
                                houseAudioGuide={
                                  <AudioGuide
                                    src="/audio/guide-houses.mp3"
                                    label="House Guide"
                                    variant="red"
                                  />
                                }
                              />
                            </Box>
                          </Box>
                          {/* END: fixed-cm Joy card */}
                            </>
                          ) : (
                            /* Advanced styled chart */
                            <AdvancedNorthIndianChart
                              chartData={{
                                planets: Object.entries(astroData.placements).map(([name, placement]) => ({
                                  name: name as PlanetName,
                                  house: placement.house,
                                  rasi: ((astroData.asc.ascendantSignIndex - 1 + (placement.house - 1)) % 12) + 1,
                                  degree: placement.offsetDeg,
                                  retro: (name === 'Mercury' || name === 'Venus' || name === 'Mars' || name === 'Jupiter' || name === 'Saturn') 
                                    ? astroData.grahas[name as 'Mercury'|'Venus'|'Mars'|'Jupiter'|'Saturn'].retrograde 
                                    : false,
                                  combust: (combust as any)?.[name]?.combust ?? false,
                                  exalted: (exalt as any)?.[name] ?? false,
                                  debilitated: (debil as any)?.[name] ?? false,
                                })),
                                ascendant: {
                                  rasi: astroData.asc.ascendantSignIndex,
                                  degree: astroData.asc.siderealLongitudeDeg % 30
                                },
                                // Compute ALL house aspects (same as Interactive Chart tab)
                                aspects: (() => {
                                  const allAspects: Array<{from: PlanetName; toHouse: number; kind: string}> = [];
                                  const addAspect = (from: PlanetName, fromHouse: number, offset: number, kind: string) => {
                                    const toHouse = ((fromHouse - 1 + offset) % 12) + 1;
                                    allAspects.push({ from, toHouse, kind });
                                  };
                                  
                                  // For each planet, add all their aspects to houses
                                  (['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'] as const).forEach(pName => {
                                    const placement = astroData.placements[pName];
                                    if (!placement?.house) return;
                                    const fromHouse = placement.house;
                                    
                                    // Everyone aspects 7th house
                                    addAspect(pName, fromHouse, 6, '7th aspect');
                                    
                                    // Special aspects
                                    if (pName === 'Mars') {
                                      addAspect(pName, fromHouse, 3, '4th aspect');
                                      addAspect(pName, fromHouse, 7, '8th aspect');
                                    } else if (pName === 'Jupiter') {
                                      addAspect(pName, fromHouse, 4, '5th aspect');
                                      addAspect(pName, fromHouse, 8, '9th aspect');
                                    } else if (pName === 'Saturn') {
                                      addAspect(pName, fromHouse, 2, '3rd aspect');
                                      addAspect(pName, fromHouse, 9, '10th aspect');
                                    } else if (pName === 'Rahu' || pName === 'Ketu') {
                                      addAspect(pName, fromHouse, 4, '5th aspect');
                                      addAspect(pName, fromHouse, 8, '9th aspect');
                                    }
                                  });
                                  
                                  return allAspects;
                                })()
                              }}
                              personName={undefined}
                              size={isMobile ? 280 : 380}
                              backgroundImage={
                                chartStyle === 'classic' ? '/chart-backgrounds/north-indian-classic.svg' :
                                chartStyle === 'modern' ? '/chart-backgrounds/north-indian-modern.svg' :
                                '/chart-backgrounds/north-indian-royal.svg'
                              }
                              showExport={false}
                              onAnalyseChart={handleAnalyseChart}
                              onGetRemedies={handleGetRemedies}
                              userRole={profile?.role}
                            />
                          )}

                          {/* ===== D1 House Scores - Available for ALL chart styles ===== */}
                          {!showCompactHouseScores && d1HouseScores && dashaContextForFeeds && d1HouseInfluence && (
                            <Box id="section-house-analysis-default" sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Typography level="title-sm" sx={{ opacity: 0.85 }}>
                                  D1 House Scores (- blended planetary influence)
                                </Typography>
                                <Tooltip 
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>How to read this panel:</Typography>
                                      <Typography level="body-xs" sx={{ mb: 0.5 }}>• <strong>Green (+)</strong>: Favorable planetary influence - good results expected</Typography>
                                      <Typography level="body-xs" sx={{ mb: 0.5 }}>• <strong>Red (−)</strong>: Challenging influence - obstacles or difficulties</Typography>
                                      <Typography level="body-xs" sx={{ mb: 0.5 }}>• <strong>Higher numbers</strong>: Stronger effects (good or bad)</Typography>
                                      <Typography level="body-xs" sx={{ mb: 0.5 }}>• <strong>Planet Impact</strong>: Combined strength of planets in that house</Typography>
                                      <Typography level="body-xs" sx={{ mb: 0.5 }}>• <strong>Interpretive chips</strong>: ✅ Ideal (strong + positive), ⚠️ Intense (strong + negative), 🔵 Strong (high natural strength), 🟡 Limited (positive but weak), ❌ Weak (negative + weak), ⚪ Neutral</Typography>
                                      <Typography level="body-xs" sx={{ mb: 0.5 }}>• <strong>Lord in Xth</strong>: Shows which house the house lord is placed in</Typography>
                                      <Typography level="body-xs">• <strong>⚡ Dasha activation</strong>: House is currently activated by your running dasha periods</Typography>
                                    </Box>
                                  }
                                  placement="top"
                                  arrow
                                  variant="soft"
                                  size="sm"
                                  sx={{ maxWidth: 420 }}
                                >
                                  <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
                                    <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                  gap: 6,
                                  fontSize: 12
                                }}
                              >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(h => {
                                  // Helper: Check if house is activated by dasha planets
                                  const getHouseActivation = () => {
                                    const activations: Array<{ level: string; planet: string; reason: string }> = [];
                                    const dashaLevels = [
                                      { level: 'MD', data: dashaContextForFeeds.MD },
                                      { level: 'AD', data: dashaContextForFeeds.AD },
                                      { level: 'PD', data: dashaContextForFeeds.PD },
                                      { level: 'SD', data: dashaContextForFeeds.SD }
                                    ];
                                    
                                    for (const { level, data } of dashaLevels) {
                                      const reasons: string[] = [];
                                      if (data.occupies === h) reasons.push('occupies');
                                      if (data.rules?.includes(h)) reasons.push('rules');
                                      if (data.aspects?.includes(h)) reasons.push('aspects');
                                      if (reasons.length > 0) {
                                        activations.push({ level, planet: data.planet, reason: reasons.join(', ') });
                                      }
                                    }
                                    return activations;
                                  };
                                  
                                  const houseActivation = getHouseActivation();
                                  const isActivated = houseActivation.length > 0;
                                  
                                  // Get house lord and its placement from d1HouseInfluence data
                                  const houseRow = d1HouseInfluence?.rows?.[h - 1];
                                  const lordPlanet = houseRow?.lord;
                                  let lordHouse = 1;
                                  if (lordPlanet && astroData?.placements) {
                                    const lordPlacement = astroData.placements[lordPlanet as keyof typeof astroData.placements];
                                    lordHouse = lordPlacement?.house ?? 1;
                                  }
                                  
                                  const v = d1HouseScores[h] ?? 0;
                                  const bhavaBala = bhavaBalaRupas?.[h];
                                  const displayValue = bhavaBala != null ? bhavaBala : v;
                                  const col =
                                    displayValue > 8 ? '#15803d' :
                                    displayValue > 0 ? '#16a34a' :
                                    displayValue < -8 ? '#b91c1c' :
                                    displayValue < 0 ? '#dc2626' : '#475569';
                                  const sign = displayValue > 0 ? '+' : displayValue < 0 ? '-' : '';
                                  const mag = Math.abs(displayValue).toFixed(1);
                                  const label = houseNames[h];
                                  const header = label ? `H${h} * ${label}` : `H${h}`;
                                  const tooltip = label ? `${label} (House ${h})` : `House ${h}`;
                                  
                                  return (
                                    <div key={h}
                                      style={{
                                        border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                        borderRadius: 6,
                                        padding: '6px 8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        background: 'var(--joy-palette-background-surface)'
                                      }}
                                      title={`${tooltip} * aspects weighted by occupancy (empty=1.0, occupied=0.5)`}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ 
                                          opacity: 0.75,
                                          fontWeight: 600,
                                          letterSpacing: '0.3px',
                                          fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                                        }}>
                                          {header}
                                        </span>
                                        <strong style={{ 
                                          color: col,
                                          fontWeight: 700,
                                          letterSpacing: '-0.5px',
                                          fontFamily: '"SF Pro Display", "Inter", system-ui, sans-serif'
                                        }}>
                                          {sign}{mag}%
                                        </strong>
                                      </div>
                                      {/* Interpretive chip logic */}
                                      {(() => {
                                        const highBhava = bhavaBala != null ? bhavaBala > 50 : false;
                                        const positiveImpact = v >= 5;
                                        const negativeImpact = v <= -5;
                                        const neutralImpact = !positiveImpact && !negativeImpact;
                                        
                                        let chipLabel = '';
                                        let chipColor = '';
                                        let chipBg = '';
                                        let chipBorder = '';
                                        
                                        if (bhavaBala != null) {
                                          if (highBhava && positiveImpact) {
                                            chipLabel = '✅ Ideal';
                                            chipColor = '#16a34a';
                                            chipBg = '#dcfce720';
                                            chipBorder = '1px solid #86efac50';
                                          } else if (highBhava && negativeImpact) {
                                            chipLabel = '⚠️ Intense';
                                            chipColor = '#dc2626';
                                            chipBg = '#fee2e220';
                                            chipBorder = '1px solid #fca5a550';
                                          } else if (highBhava && neutralImpact) {
                                            chipLabel = '🔵 Strong';
                                            chipColor = '#2563eb';
                                            chipBg = '#dbeafe20';
                                            chipBorder = '1px solid #93c5fd50';
                                          } else if (!highBhava && positiveImpact) {
                                            chipLabel = '🟡 Limited';
                                            chipColor = '#ca8a04';
                                            chipBg = '#fef9c320';
                                            chipBorder = '1px solid #fde04750';
                                          } else if (!highBhava && negativeImpact) {
                                            chipLabel = '❌ Weak';
                                            chipColor = '#dc2626';
                                            chipBg = '#fee2e220';
                                            chipBorder = '1px solid #fca5a550';
                                          } else if (!highBhava && neutralImpact) {
                                            chipLabel = '⚪ Neutral';
                                            chipColor = '#64748b';
                                            chipBg = '#f1f5f920';
                                            chipBorder = '1px solid #cbd5e150';
                                          }
                                        }
                                        
                                        return (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {/* First line: Planet Impact + chip */}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                              {v !== 0 && (
                                                <div style={{ 
                                                  display: 'inline-block',
                                                  padding: '2px 6px',
                                                  borderRadius: 4,
                                                  fontSize: 10,
                                                  fontWeight: 600,
                                                  backgroundColor: v > 0 ? '#16a34a15' : '#dc262615',
                                                  color: v > 0 ? '#16a34a' : '#dc2626',
                                                  border: v > 0 ? '1px solid #16a34a30' : '1px solid #dc262630'
                                                }}>
                                                  Planet Impact: {v > 0 ? '+' : ''}{Math.round(v)}%
                                                </div>
                                              )}
                                              {chipLabel && (
                                                <div style={{ 
                                                  display: 'inline-block',
                                                  padding: '2px 6px',
                                                  borderRadius: 4,
                                                  fontSize: 9,
                                                  fontWeight: 700,
                                                  backgroundColor: chipBg,
                                                  color: chipColor,
                                                  border: chipBorder
                                                }}>
                                                  {chipLabel}
                                                </div>
                                              )}
                                            </div>
                                            
                                            {/* Second line: Lord + Dasha activation */}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                              <div style={{ 
                                                display: 'inline-block',
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                fontSize: 10,
                                                fontWeight: 600,
                                                backgroundColor: '#dbeafe',
                                                color: '#1e40af',
                                                border: '1px solid #93c5fd'
                                              }}>
                                                Lord in {lordHouse}{lordHouse === 1 ? 'st' : lordHouse === 2 ? 'nd' : lordHouse === 3 ? 'rd' : 'th'}
                                              </div>
                                              {isActivated && (
                                                <>
                                                  <Tooltip 
                                                    title={
                                                      <div style={{ fontSize: 11 }}>
                                                        <strong>Activated by Current Dasha:</strong>
                                                        {houseActivation.map((act, idx) => (
                                                          <div key={idx} style={{ marginTop: 2 }}>
                                                            • <strong>{act.level}</strong> ({act.planet}): {act.reason}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    }
                                                    placement="top"
                                                    arrow
                                                  >
                                                    <span style={{ fontSize: 12, cursor: 'help' }}>⚡</span>
                                                  </Tooltip>
                                                  <div style={{ 
                                                    display: 'inline-block',
                                                    padding: '2px 4px',
                                                    borderRadius: 3,
                                                    fontSize: 8,
                                                    fontWeight: 700,
                                                    backgroundColor: '#f3e8ff',
                                                    color: '#7c3aed',
                                                    border: '1px solid #c4b5fd'
                                                  }}>
                                                    {houseActivation.map(act => act.level).join('/')}
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                            </Box>
                          )}

                          {/* Vimshottari Dasha snapshot */}
                          <Divider sx={{ my: 1.5 }} />
                          {utcDateTime && selectedCity && (
                            <>
                              <Box id="section-dasha-timeline-default" data-audio-cue="home-vimshottari">
                                <VimshottariDashaTimeline
                                  isoDate={utcDateTime}
                                  latitude={selectedCity.lat}
                                  longitude={selectedCity.lon}
                                  ascSign={astroData.asc.ascendantSignIndex}
                                  externalHouseScores={d1HouseScores}
                                  blendedPct={blendedPlanetPct ?? undefined}
                                  audioGuide={<AudioGuide src="/audio/guide-vimshottari.mp3" label="Dasha Guide" variant="red" />}
                                />
                              </Box>
                              {/* Analyse My Current Time — CTA after desktop timeline */}
                              <Box
                                data-audio-cue="section-analyse-current-time"
                                sx={{
                                mt: 2,
                                p: 2, borderRadius: 'lg',
                                background: 'linear-gradient(135deg, #5c6bc0 0%, #7b1fa2 100%)',
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'flex-start', gap: 1.5,
                              }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography level="title-sm" sx={{ color: 'white', fontWeight: 700, mb: 0.25 }}>
                                    ⚡ Analyse My Current Time
                                  </Typography>
                                  <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.82)' }}>
                                    6-week forecast · Moon tracking · Life area scores · 3-month summary
                                  </Typography>
                                </Box>
                                <Button
                                  variant="solid" size="sm"
                                  onClick={handleQuickCurrentPeriodAnalysis}
                                  disabled={profile?.role !== 'admin' && getRemainingQuota('chat_general') <= 0}
                                  sx={{
                                    flexShrink: 0, bgcolor: 'white', color: '#5c6bc0',
                                    fontWeight: 700, fontSize: '0.8rem', width: '100%',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                    '&:disabled': { bgcolor: 'rgba(255,255,255,0.4)', color: '#5c6bc0' },
                                  }}
                                >
                                  🔮 Analyse Now
                                </Button>
                                {profile?.role !== 'admin' && (
                                  <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.75)', width: '100%', textAlign: 'center' }}>
                                    {getRemainingQuota('chat_general') > 0
                                      ? `${getRemainingQuota('chat_general')} AI analyse${getRemainingQuota('chat_general') === 1 ? '' : 's'} remaining this month`
                                      : 'Monthly quota reached — upgrade your plan for more analyses'}
                                  </Typography>
                                )}
                              </Box>
                            </>
                          )}
                        </>
                      ) : (
                        <Box
                          sx={{
                            p: 1.25, borderRadius: 'md',
                            border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                            bgcolor: 'neutral.softBg'
                          }}
                        >
                          Fill the Birth Details on the left (Main Chart) and click <em>Save &amp; Calculate</em> to see the chart here.
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  )}
                </Grid>

                {/* Vimshottari Dasha Timeline - Show on mobile (hidden in right pane for mobile) */}
                {isMobile && utcDateTime && selectedCity && astroData && (
                  <>
                    <Box id="section-dasha-timeline-mobile" sx={{ mt: 2 }}>
                      <VimshottariDashaTimeline
                        isoDate={utcDateTime}
                        latitude={selectedCity.lat}
                        longitude={selectedCity.lon}
                        ascSign={astroData.asc.ascendantSignIndex}
                        externalHouseScores={d1HouseScores}
                        showFocusTable={false}
                        audioGuide={<AudioGuide src="/audio/guide-vimshottari.mp3" label="Dasha Guide" variant="red" />}
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{
                        p: 2, borderRadius: 'lg',
                        background: 'linear-gradient(135deg, #5c6bc0 0%, #7b1fa2 100%)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'flex-start', gap: 1.5,
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography level="title-sm" sx={{ color: 'white', fontWeight: 700, mb: 0.25 }}>
                            ⚡ Analyse My Current Time
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.82)' }}>
                            6-week forecast · Moon tracking · Life area scores · 3-month summary
                          </Typography>
                        </Box>
                        <Button
                          variant="solid" size="sm"
                          onClick={handleQuickCurrentPeriodAnalysis}
                          disabled={profile?.role !== 'admin' && getRemainingQuota('chat_general') <= 0}
                          sx={{
                            flexShrink: 0, bgcolor: 'white', color: '#5c6bc0',
                            fontWeight: 700, fontSize: '0.8rem', width: '100%',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            '&:disabled': { bgcolor: 'rgba(255,255,255,0.4)', color: '#5c6bc0' },
                          }}
                        >
                          🔮 Analyse Now
                        </Button>
                        {profile?.role !== 'admin' && (
                          <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.75)', width: '100%', textAlign: 'center' }}>
                            {getRemainingQuota('chat_general') > 0
                              ? `${getRemainingQuota('chat_general')} AI analyse${getRemainingQuota('chat_general') === 1 ? '' : 's'} remaining this month`
                              : 'Monthly quota reached — upgrade your plan for more analyses'}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </>
                )}

                {/* Below-the-fold details (unchanged) */}
                {astroData && (
                  <Box sx={{ mt: 2, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                    {/* Widget-based view */}
                    <Box sx={{ mb: 3 }} id="section-detailed-analysis" data-audio-cue="home-widgets-area">
                      <CalculatorWidgets
                        astroData={astroData}
                        selectedCity={selectedCity}
                        utcDateTime={utcDateTime || ''}
                        localDateTime={utcDateTime || ''}
                        chartName={chartName || undefined}
                        d1HouseScores={d1HouseScores}
                        bhavaBalaRupas={bhavaBalaRupas}
                        shadbalaFooter={shadbalaFooter}
                        ishtaKashtaFooter={ishtaKashtaFooter}
                        blendedPlanetPct={blendedPlanetPct}
                        houseNames={{}}
                        combust={combust ? Object.fromEntries(
                          Object.entries(combust).map(([k, v]) => [k, v.combust])
                        ) : null}
                        debil={debil}
                        exalt={exalt}
                        conjunctDebil={conjunctDebil}
                        conjunctExalt={conjunctExalt}
                        funcColor={funcColor}
                        d1AspectEdges={d1AspectEdges}
                        shIn={shIn}
                        vargaMap={vargaMap}
                        sadeSati={sadeSatiData}
                        kaalSarpYoga={kaalSarpData}
                        dashaContext={debouncedDashaContext}
                        functionalClassification={functionalClassificationForFeeds}
                        combustionMap={combustionMapForFeeds}
                        activeYogasFromFeeds={activeYogasFromFeeds}
                        onStartWidgetTour={startWidgetTour}
                        userId={user?.id}
                        selectedProfileId={selectedProfileId}
                        onNavigateToBirthDetails={() => {
                          setActiveTab('calculator');
                          // Scroll to birth details section after tab switch
                          setTimeout(() => {
                            document.getElementById('section-birth-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }}
                        dashaImpactByHouse={dashaImpactByHouse}
                        transitImpactByHouse={transitImpactByHouse}
                        transitPlanetHouses={transitData?.placements
                          ? Object.entries(transitData.placements)
                              .filter(([, p]: [string, any]) => p?.house)
                              .map(([planet, p]: [string, any]) => ({ planet, house: p.house as number }))
                          : undefined}
                        planetStrengthPct={blendedPlanetPct ?? undefined}
                        reportLifeAreaRemedies={savedLifeAreaRemedies}
                        onAnalyseCurrentTime={handleQuickCurrentPeriodAnalysis}
                        isAdmin={profile?.role === 'admin'}
                        remainingAnalyses={getRemainingQuota('chat_general')}
                        functionalNature={functionalNatureData as Record<string, string> ?? undefined}
                        houseInterpretations={houseInterpretations}
                      />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Typography level="h4" sx={{ mb: 2 }}>
                      Raw Data View
                    </Typography>

                    {selectedCity && utcDateTime && (
                      <>
                        <Typography level="title-md">Location Details</Typography>
                        <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>City:</strong> {selectedCity.label}</p>
                        <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Latitude:</strong> {selectedCity.lat}</p>
                        <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Longitude:</strong> {selectedCity.lon}</p>
                        <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Timezone:</strong> {selectedCity.tz}</p>
                        <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>UTC Time:</strong> {utcDateTime}</p>
                        <Divider sx={{ my: 1.25 }} />
                      </>
                    )}

                    <Typography level="title-md">Astrological Info</Typography>

                    <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Moon Nakshatra:</strong> {astroData.moon.nakshatra} ({astroData.moon.nakIndex})
                      <span> * Pada {getNakshatraPadaFromLongitude(astroData.moon.siderealLongitudeDeg).pada}</span>
                    </p>
                    <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Moon Rashi (Sign):</strong> {astroData.moon.moonSign} ({astroData.moon.moonSignIndex})</p>
                    <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Moon Sidereal Longitude:</strong> {astroData.moon.siderealLongitudeFormatted}
                      &nbsp;<em>({formatLonWithSign(astroData.moon.siderealLongitudeDeg)})</em>
                    </p>

                    <Divider sx={{ my: 1.25 }} />

                    <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Ascendant Sign (Lagna):</strong> {astroData.asc.ascendantSign} ({astroData.asc.ascendantSignIndex})</p>
                    <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Ascendant Nakshatra:</strong> {astroData.asc.ascendantNakshatra} ({astroData.asc.nakIndex})
                      <span> * Pada {getNakshatraPadaFromLongitude(astroData.asc.siderealLongitudeDeg).pada}</span>
                    </p>
                    <p style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Ascendant Sidereal Longitude:</strong> {astroData.asc.siderealLongitudeFormatted}
                      &nbsp;<em>({formatLonWithSign(astroData.asc.siderealLongitudeDeg)})</em>
                    </p>

                    <Divider sx={{ my: 1.25 }} />

                    <Typography level="title-md">House Cusps (Whole Sign)</Typography>
                    <ol style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      {astroData.cusps.map((lon, i) => {
                        const idx0 = Math.floor(((lon % 360) + 360) % 360 / 30);
                        const signName = RASHI_NAMES[idx0];
                        return (
                          <li key={i} style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                            House {i + 1}: {signName}
                          </li>
                        );
                      })}
                    </ol>

                    <Divider sx={{ my: 1.25 }} />

                    <Typography level="title-md">Graha Longitudes (sidereal)</Typography>
                    <ul style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      {Object.entries(astroData.grahas).map(([name, info]) => {
                        const retro = info.retrograde ? ' R' : '';
                        const combustInfo = combust ? (combust as any)[name as keyof CombustionMap] : undefined;
                        const combustTag  = combustInfo?.combust ? ' *' : '';
                        const isDeb = debil ? (debil as any)[name as PlanetName] : undefined;
                        const isEx  = exalt ? (exalt as any)[name as PlanetName] : undefined;
                        return (
                          <li key={name} style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                            <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>{name}:</strong>{' '}
                            {info.lon.toFixed(2)}deg{retro}{combustTag}
                            {isEx && <span style={{ color:'#16a34a' }}> ^</span>}
                            {isDeb && <span style={{ color:'#dc2626', fontWeight:600 }}> v</span>}
                            &nbsp;<em>({formatLonWithSign(info.lon)})</em>
                          </li>
                        );
                      })}
                    </ul>

                    <Divider sx={{ my: 1.25 }} />

                    <Typography level="title-md">Nodes (sidereal)</Typography>
                    <ul style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      <li style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Rahu:</strong> {astroData.nodes.Rahu.toFixed(2)}deg <em>({formatLonWithSign(astroData.nodes.Rahu)})</em></li>
                      <li style={{ color: 'rgba(0, 0, 0, 0.75)' }}><strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Ketu:</strong> {astroData.nodes.Ketu.toFixed(2)}deg <em>({formatLonWithSign(astroData.nodes.Ketu)})</em></li>
                    </ul>

                    <Divider sx={{ my: 1.25 }} />

                    <Typography level="title-md">Planets in Houses</Typography>
                    <ul style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                      {Object.entries(astroData.placements).map(([name, p]) => (
                        <li key={name} style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
                          <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>{name}:</strong> House {p.house} (at {p.offsetDeg.toFixed(2)}deg into house)
                        </li>
                      ))}
                    </ul>

                    <Divider sx={{ my: 1.25 }} />

                    <Typography level="title-md">Aspects (Parasari house-based)</Typography>
                    {(() => {
                      // Use canonical Parashari aspects utility
                      const { computeAllParashariAspects, formatAspectsForDisplay } = require('./utils/parashariAspects');
                      const allAspects = computeAllParashariAspects(astroData.placements, true);
                      const lines: string[] = formatAspectsForDisplay(allAspects);
                      return <ul style={{ color: 'rgba(0, 0, 0, 0.75)' }}>{lines.map((t: string, i: number) => <li key={i} style={{ color: 'rgba(0, 0, 0, 0.75)' }}>{t}</li>)}</ul>;
                    })()}
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.softBg', border: '1px solid var(--joy-palette-warning-outlinedBorder)', borderRadius: 'md' }}>
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Access pending.</strong> Once the admin approves your request, the calculator unlocks.
              </Box>
            )}

            {/* Quick Navigation FAB */}
            {astroData && (
              <QuickNavigationFAB sections={CALCULATOR_SECTIONS} mobileSections={CALCULATOR_SECTIONS_MOBILE} position="bottom-right" />
            )}
          </>
        )}

        {/* ------------- DASHA TAB ------------- */}
        {activeTab === 'dasha' && (
          <Box sx={{ pt: { xs: 1, sm: 3, md: 4 }, scrollMarginTop: { xs: '100px', sm: '80px' } }}>
            {/* Info banner for Dasha */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.softBg', border: '1px solid', borderColor: 'warning.outlinedBorder', borderRadius: 'sm' }}>
              <Typography level="title-md" sx={{ fontWeight: 700, color: 'warning.plainColor', mb: 1, fontFamily: '"Cinzel", "Garamond", serif' }}>
                ℹ️ Understanding Your Dasha Periods
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.700', lineHeight: 1.6 }}>
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Dasha System:</strong> Your life is divided into planetary periods (dashas). Each planet rules for specific years, bringing its unique energy and themes.
                <br />
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Levels:</strong> MD (Mahadasha) = main life theme for 6-20 years, AD (Antardasha) = sub-period flavor, PD = specific events, SD = exact timing triggers.
                <br />
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Timeline:</strong> The chart shows your entire life's dasha sequence. Current period is highlighted. Use this to understand past patterns and future trends.
                <br />
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>House Focus:</strong> Shows which life areas (houses) get activated during each period based on where the ruling planet sits and what it aspects.
              </Typography>
            </Box>
            {astroData && selectedCity && utcDateTime ? (
              <>
                <Box id="section-dasha-timeline">
                  <VimshottariDashaTimeline
                    isoDate={utcDateTime}
                    latitude={selectedCity.lat}
                    longitude={selectedCity.lon}
                    ascSign={astroData.asc.ascendantSignIndex}
                    blendedPct={blendedPlanetPct ?? undefined}
                    externalHouseScores={d1HouseScores}
                    showFocusTable={true}
                    externalAspects={d1AspectEdges}
                    audioGuide={<AudioGuide src="/audio/guide-vimshottari.mp3" label="Dasha Guide" variant="red" />}
                  />
                </Box>

                {/* Analyse My Current Time — prominent CTA right after timeline */}
                <Box
                  data-audio-cue="section-analyse-current-time"
                  sx={{
                    my: 2.5,
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 'lg',
                    background: 'linear-gradient(135deg, #5c6bc0 0%, #7b1fa2 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography level="title-sm" sx={{ color: 'white', fontWeight: 600 }}>
                    ⚡ Get your personalised week-by-week forecast
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.85)', maxWidth: 520 }}>
                    Comprehensive analysis: 6-week breakdown with Moon tracking, dasha transitions, life area scores & 3-month summary
                  </Typography>
                  <Button
                    variant="solid"
                    size="lg"
                    sx={{
                      minWidth: { xs: '100%', sm: 320 },
                      bgcolor: 'white',
                      color: '#5c6bc0',
                      fontWeight: 700,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.92)',
                        transform: 'translateY(-1px)',
                        boxShadow: 'lg',
                      },
                      transition: 'all 0.2s ease',
                    }}
                    onClick={handleQuickCurrentPeriodAnalysis}
                    startDecorator={<span style={{ fontSize: '1.1rem' }}>🔮</span>}
                  >
                    Analyse My Current Time
                  </Button>
                  {profile?.role !== 'admin' && (
                    <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                      {getRemainingQuota('chat_general') > 0
                        ? `${getRemainingQuota('chat_general')} AI analyse${getRemainingQuota('chat_general') === 1 ? '' : 's'} remaining this month`
                        : 'Monthly quota reached — upgrade your plan for more analyses'}
                    </Typography>
                  )}
                </Box>

                {/* Active Yogas & Predictions (mobile-tuned) */}
                {activeYogasFromFeeds && activeYogasFromFeeds.length > 0 && (
                  <Box id="section-active-yogas" sx={{ mt: 4 }}>
                    <Typography level="h4" sx={{ mb: 2, fontWeight: 700, fontFamily: '"Cinzel", "Garamond", serif', color: '#b83050' }}>
                      ⏰ Active Yogas & Predictions
                    </Typography>
                    
                    {/* Controls Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Switch
                        checked={showUniqueActiveYogas}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setShowUniqueActiveYogas(event.target.checked)}
                        size="sm"
                      />
                      <Typography level="body-sm" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Show unique yogas only
                      </Typography>
                    </Box>
                    
                    <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      These predictions are triggered by the current dasha period (PD/SD level) and activated yogas. 
                      They indicate time-sensitive opportunities and challenges for this specific window.
                    </Typography>
                    <Stack spacing={3}>
                      {(() => {
                        // Apply unique filter if toggle is on
                        type FeedItem = typeof activeYogasFromFeeds[number];
                        let feedsToGroup = activeYogasFromFeeds;
                        
                        if (showUniqueActiveYogas) {
                          const seen = new Set<string>();
                          feedsToGroup = activeYogasFromFeeds.filter(feed => {
                            const key = feed.ruleKey || feed.message.split(':')[0];
                            if (seen.has(key)) return false;
                            seen.add(key);
                            return true;
                          });
                        }
                        
                        // Group timingFeeds by life area
                        const grouped = feedsToGroup.reduce((acc, item) => {
                          const area = (item.lifeArea || 'General') as string;
                          if (!acc[area]) acc[area] = [];
                          acc[area].push(item);
                          return acc;
                        }, {} as Record<string, FeedItem[]>);

                        return (Object.entries(grouped) as [string, FeedItem[]][]).map(([lifeArea, items]) => (
                          <Box key={lifeArea}>
                            <Typography level="h4" sx={{ mb: 1.5, textTransform: 'capitalize', color: 'warning.600', fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700 }}>
                              {lifeArea.replace(/_/g, ' ')}
                            </Typography>
                            <Stack spacing={1.5}>
                              {items.map((item: FeedItem) => {
                                const isSadeSati = item.ruleKey?.startsWith('sade_sati');
                                const isActivatedYoga = item.ruleKey?.includes('yoga') && !isSadeSati;
                                
                                // Extract transit support from evidence field
                                let transitSupport: number | null = null;
                                if (item.evidence && item.evidence.length > 0) {
                                  const transitEvidence = item.evidence.find((e: string) => e.includes('TransitP='));
                                  if (transitEvidence) {
                                    const match = transitEvidence.match(/TransitP=([-\d.]+)/);
                                    if (match) {
                                      transitSupport = parseFloat(match[1]);
                                    }
                                  }
                                }

                                const severityColor = (severity: string) => {
                                  if (severity === 'alert') return 'danger';
                                  if (severity === 'caution') return 'warning';
                                  if (severity === 'mixed') return 'neutral';
                                  return 'success';
                                };

                                const severityLabel = (severity: string) => {
                                  if (severity === 'alert') return '🔴 Alert';
                                  if (severity === 'caution') return '⚠️ Caution';
                                  if (severity === 'mixed') return '⚡ Mixed';
                                  return '✨ Positive';
                                };
                                
                                return (
                                  <Card
                                    key={item.id}
                                    id={item.id}
                                    data-feed-id={item.id}
                                    variant="outlined"
                                    sx={{
                                      borderLeft: `4px solid var(--joy-palette-${severityColor(item.severity)}-500)`,
                                      boxShadow: isActivatedYoga ? 'lg' : 'sm',
                                      scrollMarginTop: '100px',
                                      p: { xs: 1, sm: 2 },          // tighter mobile padding
                                      width: '100%',
                                      boxSizing: 'border-box',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0 }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                                          {isSadeSati && (
                                            <Chip size="sm" variant="solid" color="warning">
                                              🪐 Sade Sati
                                            </Chip>
                                          )}
                                          {isActivatedYoga && (
                                            <Chip size="sm" variant="solid" color="success">
                                              ⚡ Yoga Active
                                            </Chip>
                                          )}
                                          <Chip size="sm" color={severityColor(item.severity) as any}>
                                            {severityLabel(item.severity)}
                                          </Chip>
                                          <Chip size="sm" variant="soft">
                                            {item.score?.toFixed(1) || 0}
                                          </Chip>
                                          {transitSupport !== null && (
                                            <Chip
                                              size="sm"
                                              variant="soft"
                                              color={transitSupport >= 10 ? 'success' : transitSupport <= -10 ? 'danger' : 'neutral'}
                                            >
                                              {transitSupport > 0 ? '+' : ''}{transitSupport.toFixed(1)} transit
                                            </Chip>
                                          )}
                                          {item.window && (
                                            <Chip size="sm" variant="outlined">
                                              📅 {new Date(item.window.from).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – {new Date(item.window.to).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                                            </Chip>
                                          )}
                                        </Box>
                                        <Typography level="body-md" sx={{ mt: 0.75, fontSize: { xs: '0.85rem', sm: '0.9375rem' }, lineHeight: 1.5 }}>
                                          {item.message}
                                        </Typography>
                                        {item.evidence && item.evidence.length > 0 && (
                                          <Typography level="body-xs" sx={{ mt: 0.75, opacity: 0.7, wordBreak: 'break-word' }}>
                                            {item.evidence.join('; ')}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Card>
                                );
                              })}
                            </Stack>
                          </Box>
                        ));
                      })()}
                    </Stack>
                  </Box>
                )}

                {/* Life Event Timeline Analyzer - Full Width in Dasha Tab */}
                {astroData && d1HouseScores && dashaContextForFeeds && utcDateTime && (
                  <Box id="section-life-event-timeline" sx={{ mt: 4 }}>
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontFamily: '"Cinzel", "Garamond", serif', color: '#b83050', fontWeight: 700 }}>
                        <TimelineIcon />
                        Life Event Probability Analysis
                      </Typography>
                      <LifeEventTimelineAnalyzer
                        astroData={astroData}
                        d1HouseScores={d1HouseScores}
                        bhavaBalaRupas={bhavaBalaRupas || undefined}
                        dashaContext={dashaContextForFeeds}
                        utcDateTime={utcDateTime || undefined}
                        vargaMap={vargaMap || undefined}
                        activeYogas={activeYogasFromFeeds}
                        combust={combust ? Object.fromEntries(
                          Object.entries(combust).map(([k, v]) => [k, v.combust])
                        ) : undefined}
                        debil={debil ?? undefined}
                        exalt={exalt ?? undefined}
                        functionalNature={functionalNatureData as Record<string, string> ?? undefined}
                        houseInterpretations={houseInterpretations}
                      />
                    </Card>
                  </Box>
                )}

                {/* Analyse All Life Areas Button */}
                <Box 
                  id="section-life-areas-analysis"
                  sx={{ 
                    mt: 3, 
                    mb: 2,
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: 'background.level1',
                    borderRadius: 'lg',
                    border: '2px dashed',
                    borderColor: 'primary.outlinedBorder'
                  }}
                >
                  <Typography level="body-sm" sx={{ textAlign: 'center', opacity: 0.85 }}>
                    Want a comprehensive AI analysis of all life areas for your current dasha period?
                  </Typography>
                  <Button
                    variant="solid"
                    color="primary"
                    size="lg"
                    sx={{
                      minWidth: { xs: '100%', sm: 280 },
                      fontWeight: 600,
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      px: { xs: 2, sm: 3 },
                      boxShadow: 'md',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg'
                      },
                      transition: 'all 0.2s ease'
                    }}
                    onClick={async () => {
                      setShowLifeAreasAnalysis(!showLifeAreasAnalysis);
                      
                      // If opening and no response yet, fetch it
                      if (!showLifeAreasAnalysis && !lifeAreasAnalysisResponse) {
                        try {
                          const comprehensivePrompt = `Provide a comprehensive analysis of ALL life areas for my current situation. Include:

1. Current Dasha Impact: Analyze my active Mahadasha, Antardasha, Pratyantardasha, and Sookshma dasha periods with specific percentages and timing
2. Active Yogas & Planet Combinations: List all active yogas with strength ratings and their practical effects
3. Transit Analysis: Current planetary transits and their impact on different life areas with specific dates
4. Sade Sati Status: Check if active and analyze current phase, challenges, and remedies if applicable
5. Life Area Breakdown: Rate each area (Career, Finance, Relationships, Health, Spirituality, etc.) on a scale of 1-10 with reasons
6. Blended Strength Analysis: Use house scores and planet percentages to show precise strengths
7. Cautions & Risks: Highlight specific areas requiring attention with timeframes
8. Opportunities: Best periods for different activities in the coming months

Use numbers, percentages, and dates to make it practical and easy to understand. Be specific and actionable.`;

                          // Build context - reportFeeds already contains ALL feeds including yogas from generateFeedsV2
                          const context = chatContextData ? buildChatContext(chatContextData, {
                            includeFeeds: true,
                            includeInterpretations: true,
                            includeVargas: true
                          }) : null;

                          if (!context) {
                            setLifeAreasAnalysisResponse('Unable to build chart context. Please calculate your chart first.');
                            return;
                          }

                          // Call chat API
                          const { data: { session } } = await supabase.auth.getSession();
                          if (!session) {
                            setLifeAreasAnalysisResponse('Please sign in to use this feature.');
                            return;
                          }

                          setLifeAreasAnalysisResponse('Analyzing all life areas... This may take 30-45 seconds for comprehensive analysis.');

                          // Check subscription limits
                          if (!canAskChatQuestion('general')) {
                            setLifeAreasAnalysisResponse('Chat limit reached! Upgrade your plan to continue using AI analysis.');
                            return;
                          }

                          const response = await fetch(
                            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${session.access_token}`
                              },
                              body: JSON.stringify({
                                messages: [{ role: 'user', content: comprehensivePrompt }],
                                context,
                                model: 'gpt-4o', // Use GPT-4o for comprehensive analysis
                                questionType: 'general'
                              })
                            }
                          );

                          if (!response.ok) {
                            const errorData = await response.json().catch(() => ({}));
                            throw new Error(errorData.error || `HTTP ${response.status}`);
                          }

                          // Handle streaming response
                          const contentType = response.headers.get('content-type') || '';
                          if (contentType.includes('text/event-stream')) {
                            const reader = response.body?.getReader();
                            const decoder = new TextDecoder();
                            let fullContent = '';

                            if (reader) {
                              let lineBuffer = '';
                              while (true) {
                                const { done, value } = await reader.read();
                                if (done) break;
                                lineBuffer += decoder.decode(value, { stream: true });
                                const lines = lineBuffer.split('\n');
                                lineBuffer = lines.pop() ?? '';
                                for (const line of lines) {
                                  if (line.startsWith('data: ')) {
                                    try {
                                      const parsed = JSON.parse(line.slice(6));
                                      if (parsed.content) { fullContent += parsed.content; setLifeAreasAnalysisResponse(fullContent); }
                                    } catch {}
                                  }
                                }
                              }
                              if (lineBuffer.startsWith('data: ')) {
                                try { const p = JSON.parse(lineBuffer.slice(6)); if (p.content) { fullContent += p.content; setLifeAreasAnalysisResponse(fullContent); } } catch {}
                              }
                            }
                          } else {
                            // Non-streaming response
                            const result = await response.json();
                            setLifeAreasAnalysisResponse(result.reply || 'No response received.');
                          }
                        } catch (err: any) {
                          console.error('[LifeAreasAnalysis] Error:', err);
                          setLifeAreasAnalysisResponse(`Error: ${err.message}`);
                        }
                      }
                    }}
                    startDecorator={showLifeAreasAnalysis ? null : <span style={{ fontSize: '1.2rem' }}>🌟</span>}
                    endDecorator={showLifeAreasAnalysis ? <span style={{ fontSize: '1rem' }}>▲</span> : <span style={{ fontSize: '1rem' }}>▼</span>}
                  >
                    {showLifeAreasAnalysis ? 'Hide Analysis' : '🔮 Current Life Areas Analysis'}
                  </Button>
                  {!showLifeAreasAnalysis && (
                    <Typography level="body-xs" sx={{ 
                      textAlign: 'center', 
                      opacity: 0.65, 
                      fontStyle: 'italic',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      px: { xs: 1, sm: 0 }
                    }}>
                      Powered by GPT-4o • Includes dashas, yogas, transits & personalized insights
                    </Typography>
                  )}
                </Box>

                {/* Inline Analysis Response */}
                {showLifeAreasAnalysis && (
                  <Box
                    sx={{
                      mt: 2,
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: 'background.level1',
                      borderRadius: 'md',
                      border: '1px solid',
                      borderColor: 'primary.outlinedBorder',
                      maxHeight: { xs: 'calc(100vh - 300px)', sm: 'none' },
                      overflow: { xs: 'auto', sm: 'visible' }
                    }}
                  >
                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {lifeAreasAnalysisResponse || 'Loading analysis...'}
                    </Typography>
                  </Box>
                )}

                {/* Future Event Predictor - Opportunity & Caution Windows */}
                {astroData.moon && d1HouseScores && (
                  <Box id="section-future-events" sx={{ mt: 4 }}>
                    <Typography level="h4" sx={{ mb: 2, fontWeight: 700, fontFamily: '"Cinzel", "Garamond", serif', color: '#b83050' }}>
                      🔮 Future Event Predictor for upcoming months/years
                    </Typography>
                    <FutureEventAlerts
                      birthISO={utcDateTime}
                      moonLongDeg={astroData.moon.siderealLongitudeDeg}
                      houseScores={d1HouseScores}
                      natalPlacements={natalPlacementsForContext}
                      combustionMap={combustionMapForFeeds}
                      gender={selectedGender}
                      ascendantSign={astroData.asc.ascendantSignIndex}
                    />
                  </Box>
                )}

                {/* Dasha Focus Overview Table */}
                <DashaFocusOverview
                    key={`home-dasha-focus-${utcDateTime}`}
                    isoDate={utcDateTime}
                    latitude={selectedCity.lat}
                    longitude={selectedCity.lon}
                    ascSign={astroData.asc.ascendantSignIndex}
                    blendedPct={blendedPlanetPct ?? undefined}
                    externalHouseScores={d1HouseScores}
                    externalAspects={d1AspectEdges}
                    onAnalyseCurrentTime={handleQuickCurrentPeriodAnalysis}
                    isAdmin={profile?.role === 'admin'}
                    remainingAnalyses={getRemainingQuota('chat_general')}
                  />

                {/* Quick Navigation FAB */}
                {astroData && selectedCity && utcDateTime && (
                  <QuickNavigationFAB sections={DASHA_SECTIONS} position="bottom-right" />
                )}
              </>
            ) : (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 'md',
                  border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                  bgcolor: 'neutral.softBg'
                }}
              >
                Select your Birth Chart first or add the birth details in the Home page to view.
              </Box>
            )}
          </Box>
        )}

        {/* ------------- FEEDS TAB (Always render FeedsList to populate activeYogasFromFeeds data) ------------- */}
        <Box sx={{ 
          pt: { xs: 1, sm: 3, md: 4 }, 
          scrollMarginTop: { xs: '100px', sm: '80px' },
          display: activeTab === 'feeds' ? 'block' : 'none'
        }}>
            {/* Info banner for Feeds */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.softBg', border: '1px solid', borderColor: 'primary.outlinedBorder', borderRadius: 'sm' }}>
              <Typography level="title-md" sx={{ fontWeight: 600, color: 'primary.plainColor', mb: 1 }}>
                ℹ️ How to Read Your Predictions
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.700', lineHeight: 1.6 }}>
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Scoring:</strong> Numbers show event strength. Positive (+) = opportunities/gains, Negative (-) = challenges/losses. Higher numbers = stronger effects.
                <br />
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Time Windows:</strong> Predictions are dated - they're active during specific dasha periods (planetary cycles).
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Life Areas:</strong> Each prediction targets specific topics like career, relationships, health, wealth. Colors indicate benefic (green) vs malefic (red) influences.
                <br />
                <strong style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600 }}>Evidence:</strong> Click any prediction to see step-by-step scoring breakdown showing why this event is predicted.
              </Typography>
            </Box>
            {astroData && d1HouseScores ? (
              <>
                <FeedsList
                  natal={natalForFeeds}
                  transit={null}
                  transitPositions={transitPositionsForFeeds}
                  functionalClassification={functionalClassificationForFeeds}
                  combustionMap={combustionMapForFeeds}
                  debilMap={debil ?? undefined}
                  exaltMap={exalt ?? undefined}
                  dashaContext={dashaContextForFeeds}
                  location={locationForFeeds}
                  ayanamsaProvider={ayanProvider}
                  birthDate={utcDateTime ?? undefined}
                  ascSign={astroData.asc.ascendantSignIndex}
                  blendedPct={blendedPlanetPct ?? undefined}
                  gender={selectedGender}
                  userRole={profile?.role}
                  kaalSarpYoga={kaalSarpData}
                  onYogasExtracted={handleYogasExtracted}
                  onNatalYogasExtracted={handleNatalYogasExtracted}
                />
                
                {/* Quick Navigation FAB */}
                {astroData && d1HouseScores && (
                  <QuickNavigationFAB sections={FEEDS_SECTIONS} mobileSections={FEEDS_SECTIONS_MOBILE} position="bottom-right" />
                )}
              </>
            ) : (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 'md',
                  border: '1px dashed var(--joy-palette-neutral-outlinedBorder)',
                  bgcolor: 'neutral.softBg'
                }}
              >
                Select your Birth Chart first or add the birth details in the Home page to view.
              </Box>
            )}
        </Box>

        {/* ------------- CHAT TAB ------------- */}
        {/* Keep mounted after first visit so messages/state survive tab switches */}
        {activeTab === 'chat' && (chatEverVisited.current = true)}
        {chatEverVisited.current && (
          <Box sx={{
            display: activeTab === 'chat' ? undefined : 'none',
            pt: { xs: 1, md: 0 },
            mt: { xs: 1.5, md: 0 },
            p: { xs: 2, md: 0 },
            scrollMarginTop: { xs: '100px', sm: '80px' }
          }}>
            {/* Info banner — mobile only (desktop layout embeds its own header) */}
            {isMobile && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'success.softBg', border: '1px solid', borderColor: 'success.outlinedBorder', borderRadius: 'sm' }}>
                <Typography level="body-xs" sx={{ color: 'neutral.700', lineHeight: 1.5, fontSize: '0.75rem' }}>
                  <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>ℹ️ How your chat is personalized:</Typography>
                  • <strong>Natal Chart:</strong> All planet positions, signs, houses, aspects, dignities (exaltation/debilitation/combustion)
                  <br />
                  • <strong>Current Dashas:</strong> MD, AD, PD, SD periods with timing and planetary strengths
                  <br />
                  • <strong>House Scores:</strong> Blended strength percentages for all 12 houses
                  <br />
                  • <strong>Divisional Charts:</strong> D9, D10, D60 for focused life area analysis
                  <br />
                  • <strong>Yogas:</strong> Classical combinations detected in your chart
                  <br />
                  • <strong>Transits:</strong> Current planetary movements and their impact
                  <br />
                  • <strong>Predictions:</strong> Gate-based event forecasts with timing windows
                  <br />
                  • <strong>Cache Intelligence:</strong> Same questions within 7 days return instantly
                </Typography>
              </Box>
            )}
            {/* Chat usage warnings */}
            {user && (
              <>
                <QuotaWarning userId={user.id} type="chat_general" threshold={1} />
                <QuotaWarning userId={user.id} type="chat_life_area" threshold={1} />
              </>
            )}

            {astroData && d1HouseScores ? (
              (() => {
                // Use the outer chatContextData useMemo (includes name, ageYears,
                // combinationPredictions, dashaLordRemedies, houseLords, houseAspects)
                return !isMobile ? (
                  <Suspense fallback={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 120px)' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size="lg" sx={{ '--CircularProgress-trackColor': 'rgba(180,50,80,0.12)', '--CircularProgress-progressColor': '#b43250' }} />
                        <Typography level="body-sm" sx={{ mt: 2, color: 'text.tertiary' }}>Loading your chart…</Typography>
                      </Box>
                    </Box>
                  }>
                    <ChatDesktopLayout
                      contextData={chatContextData}
                      userRole={profile?.role}
                      chatCreditsRemaining={chatCreditsRemaining}
                      onBuyCredits={() => setShowBuyCreditsModal(true)}
                      onGoToInsights={() => setActiveTab('feeds')}
                      userName={chartName ?? undefined}
                      onRegisterBackHandler={(fn) => { chatBackHandlerRef.current = fn; }}
                      chartInsightData={{
                        astroData,
                        d1HouseScores: d1HouseScores ?? undefined,
                        bhavaBalaRupas: bhavaBalaRupas ?? undefined,
                        dashaContext: dashaContextForFeeds ?? undefined,
                        utcDateTime: utcDateTime ?? undefined,
                        vargaMap: vargaMap ?? undefined,
                        activeYogas: (() => {
                          const all = [
                            ...(permanentYogasFromFeeds ?? []),
                            ...(activeYogasFromFeeds ?? []),
                          ];
                          const seen = new Set<string>();
                          return all.filter((f: any) => {
                            const k = (f.ruleKey || f.id || '').replace(/_natal$/, '').replace(/_timing$/, '');
                            if (seen.has(k)) return false;
                            seen.add(k);
                            return true;
                          });
                        })(),
                        combust: combust ? Object.fromEntries(Object.entries(combust).map(([k, v]) => [k, (v as any).combust ?? false])) : undefined,
                        debil: debil ?? undefined,
                        exalt: exalt ?? undefined,
                        functionalNature: functionalNatureData as Record<string, string> ?? undefined,
                        houseInterpretations: houseInterpretations ?? undefined,
                        feeds: reportFeeds ?? undefined,
                        futureEvents: futureEventsForChat ?? undefined,
                        shadbalaPct: blendedPlanetPct as Record<string, number> ?? undefined,
                        svPct: svCalibratedPct as Record<string, number> ?? undefined,
                        shadbalaTotal: shadbalaPct ? Object.fromEntries(
                          Object.entries(shadbalaPct).map(([p, v]) => [p, (v as any).totalPct])
                        ) as Record<string, number> : undefined,
                        ishtaKashta: ishtaKashtaFooter as Record<string, { ishta: number; kashta: number }> ?? undefined,
                        bhavaSandhi: bhavaSandhiMultipliers as Record<string, number> ?? undefined,
                        houseLords: d1HouseInfluence ? (() => {
                          const m: Record<number, string> = {};
                          d1HouseInfluence.rows.forEach((r: any) => { if (r.lord) m[r.house] = r.lord; });
                          return m;
                        })() : undefined,
                        houseAspects: d1HouseInfluence ? (() => {
                          const m: Record<number, string[]> = {};
                          d1HouseInfluence.rows.forEach((r: any) => {
                            m[r.house] = (r.aspectors || []).map((e: any) => e.planet as string);
                          });
                          return m;
                        })() : undefined,
                      }}
                    />
                  </Suspense>
                ) : null; /* Mobile: handled by MobileChatList/MobileChatInterface overlay */
              })()
            ) : (
              /* ── No astroData: still loading or genuinely new user ── */
              !isMobile ? (
                /* Desktop: show loader until we know for sure there is no data */
                (!savedRowLoaded || !profilesLoaded || (savedRowLoaded && profilesLoaded && (savedRow !== null || profiles.length > 0))) ? (
                  /* Data is still being fetched or auto-select hasn't computed the chart yet */
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 120px)' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress
                        size="lg"
                        sx={{
                          '--CircularProgress-trackColor': 'rgba(180,50,80,0.12)',
                          '--CircularProgress-progressColor': '#b43250',
                        }}
                      />
                      <Typography level="body-sm" sx={{ mt: 2, color: 'text.tertiary' }}>
                        Loading your chart…
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  /* Confirmed: no saved data → show onboarding form */
                  <Suspense fallback={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 120px)' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size="lg" sx={{ '--CircularProgress-trackColor': 'rgba(180,50,80,0.12)', '--CircularProgress-progressColor': '#b43250' }} />
                        <Typography level="body-sm" sx={{ mt: 2, color: 'text.tertiary' }}>Loading your chart…</Typography>
                      </Box>
                    </Box>
                  }>
                    <ChatDesktopLayout
                      contextData={null}
                      userRole={profile?.role}
                      noBirthData
                      onGoToDashboard={() => setActiveTab('calculator')}
                      chatCreditsRemaining={chatCreditsRemaining}
                      onBuyCredits={() => setShowBuyCreditsModal(true)}
                      onGoToInsights={() => setActiveTab('feeds')}
                      userName={chartName ?? undefined}
                      onRegisterBackHandler={(fn) => { chatBackHandlerRef.current = fn; }}
                    />
                  </Suspense>
                )
              ) : (
                /* ── Mobile: simple prompt to add birth details ── */
                <Box sx={{ p: 3, borderRadius: 'md', border: '2px dashed', borderColor: 'neutral.outlinedBorder', bgcolor: 'neutral.softBg', textAlign: 'center' }}>
                  <Typography level="h4" sx={{ mb: 1 }}>Add Birth Details First</Typography>
                  <Typography level="body-md" sx={{ color: 'text.tertiary', mb: 2 }}>
                    Go to <strong>Dashboard</strong>, fill in birth details, and tap <strong>Save & Calculate</strong> to start.
                  </Typography>
                  <Button size="sm" variant="outlined" onClick={() => setActiveTab('calculator')}>
                    Go to Dashboard
                  </Button>
                </Box>
              )
            )}
          </Box>
        )}

        {/* ------------- FAMILY TAB ------------- */}
        {activeTab === 'family' && user && (
          <Suspense fallback={<Box sx={{ p: 2 }}>Loading Family Manager...</Box>}>
            <FamilyManager userId={user.id} approvedQuota={effectiveMaxCharts} />
          </Suspense>
        )}

        {/* ------------- ADMIN TAB (admins only) ------------- */}
        {activeTab === 'admin' && profile?.role === 'admin' && (
          <Suspense fallback={<Box sx={{ p: 2 }}>Loading Admin Console...</Box>}>
            <AdminConsole 
              chartData={reportDataWithFeeds}
              blendedPlanetPct={blendedPlanetPct ?? undefined}
              onSwitchToCalculator={() => setActiveTab('calculator')}
              promptData={adminPromptData}
            />
          </Suspense>
        )}

        {/* ------------- PROFILE TAB ------------- */}
      </AppShell>
      )}

      {/* Buy Chat Credits Modal */}
      {showBuyCreditsModal && user && (
        <BuyChatCreditsModal
          open={showBuyCreditsModal}
          onClose={() => {
            setShowBuyCreditsModal(false);
            if (reopenSubscriptionAfterCredits) {
              setReopenSubscriptionAfterCredits(false);
              setShowSubscriptionModal(true);
            }
          }}
          onSuccess={() => { refreshSubscription(); }}
          userId={user.id}
          userEmail={user.email ?? ''}
          currentCredits={chatCreditsRemaining ?? 0}
        />
      )}

      {/* Karmic Pattern Detection Modal - lazy, app-level */}
      {showKarmicPattern && user && (
        <Suspense fallback={null}>
          <KarmicPatternDetection
            open={showKarmicPattern}
            onClose={() => setShowKarmicPattern(false)}
            userId={user.id}
            birthProfileId={resolvedKarmicProfileId}
            previousResults={planetResults.length > 0 ? planetResults : undefined}
            saveResponse={saveKarmicResponse}
            existingRoundId={latestRoundId}
            onComplete={reloadKarmicScores}
          />
        </Suspense>
      )}

      {/* Tour Guide Component - Desktop only */}
      {!isMobile && (
        <TourGuide
          steps={currentTourSteps}
          run={isTourRunning}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      )}

      {/* Video Tutorial Modal */}
      <VideoTutorial
        open={isVideoOpen}
        onClose={closeTutorial}
        video={currentVideo}
      />

      {/* Floating Chart Modal (Admin-only) */}
      {profile?.role === 'admin' && astroData && (
        <FloatingChartModal
          open={showFloatingChart}
          onClose={() => setShowFloatingChart(false)}
          chartData={{
            planets: Object.entries(astroData.placements).map(([name, placement]) => ({
              name: name as PlanetName,
              house: placement.house,
              rasi: ((astroData.asc.ascendantSignIndex - 1 + (placement.house - 1)) % 12) + 1,
              degree: placement.offsetDeg,
              retro: (name === 'Mercury' || name === 'Venus' || name === 'Mars' || name === 'Jupiter' || name === 'Saturn')
                ? astroData.grahas[name as 'Mercury'|'Venus'|'Mars'|'Jupiter'|'Saturn'].retrograde
                : false,
              combust: (combust as any)?.[name]?.combust ?? false,
              exalted: (exalt as any)?.[name] ?? false,
              debilitated: (debil as any)?.[name] ?? false,
              blendedPct: blendedPlanetPct?.[name as PlanetName],
            })),
            ascendant: {
              rasi: astroData.asc.ascendantSignIndex,
              degree: astroData.asc.siderealLongitudeDeg % 30
            },
            aspects: d1AspectEdges,
          }}
          personName={chartName || undefined}
          houseScores={d1HouseScores || undefined}
          houseInterpretations={houseInterpretations || undefined}
          bhavaBalaRupas={bhavaBalaRupas || undefined}
        />
      )}

      {/* SWOT Analysis Modal */}
      <Modal open={showSwotModal} onClose={() => !swotLoading && setShowSwotModal(false)} sx={{ zIndex: 10003 }}>
        <ModalDialog
          sx={{
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {!swotLoading && <ModalClose />}
          <Typography level="h4" sx={{ mb: 2 }}>
            📊 SWOT Analysis of Your Birth Chart
          </Typography>
          
          {swotLoading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-md" sx={{ mb: 2, fontWeight: 600 }}>
                ✨ ShreemAstro AI is analyzing your chart...
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                Generating comprehensive insights based on your chart
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                This may take 20-40 seconds for deep reasoning
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CircularProgress size="lg" variant="soft" />
              </Box>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                Powered by advanced AI models
              </Typography>
            </Box>
          )}

          {swotError && (
            <Box sx={{ mb: 2 }}>
              <Chip color="danger" variant="soft">
                ❌ {swotError}
              </Chip>
            </Box>
          )}

          {!swotLoading && swotOutput && (
            <>
              <Sheet
                variant="soft"
                sx={{
                  p: 3,
                  borderRadius: 'md',
                  mb: 2,
                  flex: 1,
                  overflowY: 'auto',
                  fontFamily: '"Georgia", "Palatino Linotype", serif',
                  fontSize: '0.95rem',
                  lineHeight: 1.85,
                  color: 'text.primary',
                  '& strong': { fontWeight: 700, color: 'text.primary' },
                  '& em': { fontStyle: 'italic' },
                  // Section headings (STRENGTHS, WEAKNESSES etc.)
                  '& p': { mb: 1.5, mt: 0 },
                }}
              >
                {swotOutput.split('\n').map((line, i) => {
                  const isSectionHeading = /^(STRENGTHS|WEAKNESSES|OPPORTUNITIES|THREATS|CONCLUDING SYNTHESIS|OPENING SYNTHESIS|REMEDIES|CONCLUSION|SWOT ANALYSIS)/i.test(line.trim());
                  const isSubHeading = /^\*\*[^*]+\*\*$/.test(line.trim());
                  const cleanLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>');
                  if (!line.trim()) return <Box key={i} sx={{ mb: 1 }} />;
                  if (isSectionHeading) return (
                    <Typography key={i} level="title-lg" sx={{ mt: 2.5, mb: 1, fontFamily: '"Georgia", serif', fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
                      {line.trim()}
                    </Typography>
                  );
                  if (isSubHeading) return (
                    <Typography key={i} level="title-sm" sx={{ mt: 1.5, mb: 0.5, fontFamily: '"Georgia", serif', fontWeight: 700 }}
                      dangerouslySetInnerHTML={{ __html: cleanLine }} />
                  );
                  return <Box key={i} component="p" sx={{ mb: 1.5, mt: 0, fontFamily: '"Georgia", serif', fontSize: '0.95rem', lineHeight: 1.85 }}
                    dangerouslySetInnerHTML={{ __html: cleanLine }} />;
                })}
              </Sheet>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                {profile?.role === 'admin' && (
                  <Button
                    variant="outlined"
                    color="neutral"
                    onClick={handleCopySwotOutput}
                    startDecorator={<span>📋</span>}
                  >
                    Copy Analysis
                  </Button>
                )}
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleGoToChat}
                  startDecorator={<span>💬</span>}
                >
                  Continue in Chat
                </Button>
              </Box>
            </>
          )}
        </ModalDialog>
      </Modal>

      {/* Current Period Analysis Modal */}
      <Modal open={showCurrentPeriodModal} onClose={() => !currentPeriodLoading && setShowCurrentPeriodModal(false)} sx={{ zIndex: 10003 }}>
        <ModalDialog
          sx={{
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {!currentPeriodLoading && <ModalClose />}
          <Typography level="h4" sx={{ mb: 2 }}>
            🔮 Current Period & Life Areas Analysis
          </Typography>
          
          {currentPeriodLoading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-md" sx={{ mb: 2, fontWeight: 600 }}>
                ✨ ShreemAstro AI is analyzing your chart...
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                Generating comprehensive insights across all life areas
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                This may take 30-45 seconds for comprehensive analysis
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CircularProgress size="lg" variant="soft" />
              </Box>
              <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                Powered by advanced AI models
              </Typography>
            </Box>
          )}

          {currentPeriodError && (
            <Box sx={{ mb: 2 }}>
              <Chip color="danger" variant="soft">
                ❌ {currentPeriodError}
              </Chip>
            </Box>
          )}

          {currentPeriodOutput && (
            <>
              <Sheet
                variant="soft"
                sx={{
                  p: 2,
                  borderRadius: 'md',
                  mb: 2,
                  flex: 1,
                  overflowY: 'auto',
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' }
                }}
              >
                {currentPeriodOutput}
              </Sheet>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={() => {
                    setShowCurrentPeriodModal(false);
                    setActiveTab('chat');
                  }}
                  startDecorator={<span>💬</span>}
                >
                  Continue in Chat
                </Button>
              </Box>
            </>
          )}
        </ModalDialog>
      </Modal>

      {/* Life Area Overview Modal */}
      {currentLifeAreaKey && (
        <Modal 
          open={showLifeAreaOverviewModal} 
          onClose={() => !lifeAreaOverviewLoading && setShowLifeAreaOverviewModal(false)}
          sx={{ zIndex: 10002 }}
        >
          <ModalDialog
            sx={{
              maxWidth: '900px',
              width: '90%',
              maxHeight: { xs: 'calc(100vh - 100px)', sm: '90vh' },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              mt: { xs: '70px', sm: '20px' },
              mb: { xs: '10px', sm: '20px' }
            }}
          >
            <ModalClose disabled={lifeAreaOverviewLoading} />
            <Typography level="h4" sx={{ mb: 2, flexShrink: 0 }}>
              📊 {LIFE_AREAS[currentLifeAreaKey]?.label || currentLifeAreaKey} Overview
            </Typography>
            
            {lifeAreaOverviewLoading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography level="body-md" sx={{ mb: 2, fontWeight: 600 }}>
                  ✨ ShreemAstro AI is analyzing {LIFE_AREAS[currentLifeAreaKey]?.label || currentLifeAreaKey}...
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                  Generating comprehensive insights based on your chart
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                  This may take 15-25 seconds for comprehensive analysis
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <CircularProgress size="lg" variant="soft" />
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                  Powered by advanced AI models
                </Typography>
              </Box>
            )}

            {lifeAreaOverviewError && (
              <Box sx={{ p: 2, bgcolor: 'danger.softBg', borderRadius: 'sm', flexShrink: 0 }}>
                <Typography level="body-sm" sx={{ color: 'danger.plainColor' }}>
                  ❌ {lifeAreaOverviewError}
                </Typography>
              </Box>
            )}

            {lifeAreaOverviewOutput && (
              <Sheet
                variant="soft"
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  p: 2,
                  borderRadius: 'md',
                  minHeight: 0,
                  maxHeight: '100%',
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                  // Ensure smooth scrolling on mobile
                  WebkitOverflowScrolling: 'touch',
                  // Add padding at bottom to ensure last line is visible
                  pb: 3
                }}
              >
                {lifeAreaOverviewOutput}
              </Sheet>
            )}
          </ModalDialog>
        </Modal>
      )}

      {/* Report Generator Modal */}
      {reportV2Data && (
        <ReportGenerator
          open={showReportModal}
          onClose={() => setShowReportModal(false)}
          reportData={reportV2Data}
          astroData={astroData}
          vargaMap={vargaMap}
          onNavigate={(tab) => {
            setShowReportModal(false);
            setActiveTab(tab as any);
          }}
          isAdmin={profile?.role === 'admin'}
          onAddSwotAnalysis={callReportSwotAnalysis}
          onAddDetailedAnalysis={callReportDetailedAnalysis}
          onAddCurrentPeriodAnalysis={callReportCurrentPeriod}
          onAddYearForecast={callReportYearForecast}
          swotContent={reportSwotContent}
          swotLoading={reportSwotLoading}
          detailedContent={reportDetailedContent}
          detailedLoading={reportDetailedLoading}
          currentPeriodContent={reportCurrentPeriodContent}
          currentPeriodLoading={reportCurrentPeriodLoading}
          yearForecastContent={reportYearForecastContent}
          yearForecastLoading={reportYearForecastLoading}
          reportAiCacheKey={reportAiCacheKey}
          config={{
            type: 'comprehensive',
            includeSections: ['birth-details', 'chart-d1', 'planet-details', 'planet-factors', 'house-scores', 'd1-house-scores', 'life-areas', 'yogas', 'dasha-timeline', 'year-forecast', 'opportunity-windows'],
            includeAIAnalysis: false,
            title: 'Comprehensive Vedic Report',
          }}
        />
      )}

      {/* Remedies Modal */}
      {astroData && d1HouseInfluence && (
        <RemediesModal
          open={showRemediesModal}
          onClose={() => setShowRemediesModal(false)}
          houseScores={d1HouseScores ?? {}}
          placements={(() => {
            const simplePlacements: Record<string, number> = {};
            Object.entries(astroData.placements).forEach(([planet, { house }]) => {
              simplePlacements[planet] = house;
            });
            return simplePlacements;
          })()}
          houseLords={(() => {
            const lords: Record<number, PlanetName> = {};
            d1HouseInfluence.rows.forEach(row => {
              lords[row.house] = row.lord as PlanetName;
            });
            return lords;
          })()}
          houseAspects={(() => {
            const aspectMap: Record<number, string[]> = {};
            d1HouseInfluence.rows.forEach(row => {
              const aspecters = row.aspectors || [];
              aspectMap[row.house] = aspecters.map((entry: any) => entry.planet as string);
            });
            return aspectMap;
          })()}
          planetStrengths={blendedPlanetPct as any}
          getPlanetColor={(p) => funcColor(p)}
        />
      )}

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </CssVarsProvider>
  );
};

export default App;
// ===== END FILE: src/App.tsx =====
